import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-cpp';
import { Download, Plus, Minus, Trash2, Zap, Lightbulb, AlertCircle, RotateCw, RotateCcw, Wrench, Info, Grid3X3, Save, CircuitBoard, Code2, Play, Square, ChevronDown, ChevronUp, Maximize2, Minimize2, Copy, Volume2, VolumeX, Upload, Undo2, Redo2, Eye, EyeOff } from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { adsAPI } from '../services/api';
import { apiPath, appUrl, assetPath } from '../utils/appPaths';
import { prepareInlineSvgMarkup } from '../utils/inlineSvg';
import {
  DEFAULT_ARDUINO_SIMULATION_HEX,
  DEFAULT_ARDUINO_SIMULATION_SKETCH,
} from '../utils/arduinoSimulationDefaults';
import { compileArduinoSketch } from '../utils/avr8Compiler';
import {
  ArduinoUnoSimulator,
  ARDUINO_UNO_PIN_ORDER,
} from '../utils/avr8Simulator';
import {
  inspectArduinoSimulationCircuit,
} from '../utils/projectBuilderSimulationCircuit';

import {
  get_project_pins_with_marker_keys,
} from '../utils/projectBuilderPins';
import {
  generate_project_wiring_suggestions,
} from '../utils/projectBuilderSuggestions';
import { normalize_board } from '../utils/boardNormalizer';
import { analyze_circuit, generate_wiring_steps } from '../utils/circuitAnalysis';
import { downloadSvgElementAsPng } from '../utils/svgExport';
import {
  BUILT_IN_BREADBOARD_PASSIVE,
  BUILT_IN_DC_POWER_PASSIVE,
  BUILT_IN_LCD_16X2_PASSIVE,
  BUILT_IN_BUZZER_PASSIVE,
  BUILT_IN_LED_PASSIVE,
  BUILT_IN_RESISTOR_PASSIVE,
  BUILT_IN_SEVEN_SEGMENT_DISPLAY_PASSIVE,
  BUILT_IN_SERVO_PASSIVE,
  coercePassiveOhmsValue,
  getDefaultPassiveValue,
  getPassiveInstanceName,
  getPassiveSelectedValueLabel,
  normalizePassiveComponentDefinition,
  passiveSupportsSelectableValues,
  resolvePassiveComponentRuntimeSimulationModel,
  resolveVariableResistorSimulationState,
} from '../utils/projectBuilderPassives';
import { preparePassiveSvgBuilderMarkup } from '../utils/projectBuilderPassiveSvg';
import {
  BREADBOARD_TEMPLATE_TYPE,
  getBreadboardBusKeyForPinKey,
  getBreadboardConnectedMarkerKeys,
  getBreadboardMarkerKeysForPinKey,
  getBreadboardOppositeTerminalPinKeys,
  getBreadboardPlacementMatches,
  isBreadboardComponent,
  stripBreadboardMarkerIndex,
} from '../utils/projectBuilderBreadboard';
import { create_passive_buzzer_audio_manager } from '../utils/passiveBuzzerAudio';
import {
  getSevenSegmentCommonTypeLabel,
  getSevenSegmentDigitPinKeys,
  normalizeSevenSegmentCommonType,
  normalizeSevenSegmentDigitCount,
  normalizeSevenSegmentSegmentStates,
  resolveSevenSegmentCanvasSize,
  SEVEN_SEGMENT_COMMON_TYPE_OPTIONS,
  SEVEN_SEGMENT_DEFAULT_COMMON_TYPE,
} from '../utils/sevenSegmentDisplay';
import {
  advanceLcd16x2SimulationFromPinTransitionEvents,
  buildLcd16x2SignalSnapshot,
  getBlankLcd16x2VisibleRows,
  getLcd16x2VisibleRows,
} from '../utils/lcd16x2Simulation';
import {
  decodeProjectFileContents,
  encodeProjectFileContents,
  getProjectImportFallbackName,
  getProjectStoredSimulationCode,
  PROJECT_FILE_ACCEPT,
  sanitizeProjectExportFileName,
} from '../utils/projectBuilderProjectFiles';
import { usePageSeo } from '../utils/seo';
import { AD_SLOT_PROJECT_BUILDER_BANNER, findAdRecordBySlot } from '../utils/adSlots';
import BuilderTutorialPanel from '../features/tutorials/BuilderTutorialPanel';
import {
  getBuilderLesson,
  getDefaultBuilderLessonId,
} from '../features/tutorials/lessonRegistry';
import {
  clearLessonProgress,
  loadLessonProgress,
  saveLessonProgress,
} from '../features/tutorials/tutorialStorage';
import {
  emitLessonAnalyticsEvent,
  postLessonMessage,
} from '../features/tutorials/tutorialEvents';
import {
  buildBreadboardConnectivityModel,
  getConnectedHoleIds,
  isBreadboardGroundRailHole,
  isBreadboardPositiveRailHole,
  isBreadboardTerminalHole,
  parseEndpointId,
} from '../features/tutorials/breadboardConnectivity';
import {
  analyzeLessonLedCircuit,
  classifyTraceSelection,
  getBreadboardHoleReference,
  getLessonStepIndex,
  validateLessonStep,
} from '../features/tutorials/lessonValidation';

const SVG_LENGTH_TO_PX = {
  px: 1,
  in: 96,
  mm: 96 / 25.4,
  cm: 96 / 2.54,
  pt: 96 / 72,
  pc: 16,
  q: 96 / 101.6,
};

const PROJECT_BUILDER_SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const BOARD_ICON_SVG_CSS = `
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"],
  .pin-hole,
  .pin-rect {
    display: none !important;
  }

  svg {
    overflow: visible !important;
  }
`;

const DESCRIPTION_MAX_LENGTH = 180;
const DEFAULT_BOARD_TARGET_WIDTH = 260;
const MIN_BOARD_TARGET_WIDTH = 80;
const MAX_BOARD_TARGET_WIDTH = 900;
const PROJECT_BUILDER_SEO_DESCRIPTION = 'Design electronics projects visually with the MicroPinouts Project Builder. Add boards and components, wire connections, analyze circuits, and simulate Arduino code in one workspace.';
const PROJECT_BUILDER_SEO_KEYWORDS = 'project builder, electronics project builder, circuit builder, arduino circuit builder, microcontroller wiring tool, circuit analysis, arduino simulation, micropinouts';
const PROJECT_BUILDER_WORKSPACE_MIN_HEIGHT = 'calc(100vh - 220px)';
const PROJECT_BUILDER_HISTORY_LIMIT = 30;
const COMPONENT_SCALE_MIN = 0.25;
const COMPONENT_SCALE_MAX = 3;
const COMPONENT_SCALE_STEP = 0.05;
const BREADBOARD_DEFAULT_SCALE = 1.45;
const BREADBOARD_COMPONENT_SNAP_RADIUS = 14;
const BREADBOARD_COMPONENT_CONTACT_RADIUS = 3;

const getDefaultPassiveScale = (definition) => (
  definition?.templateType === BREADBOARD_TEMPLATE_TYPE
    ? BREADBOARD_DEFAULT_SCALE
    : 1
);

const isComponentHidden = (component) => (
  Boolean(component?.hidden || component?.isHidden)
);

const isComponentVisible = (component) => !isComponentHidden(component);

const normalizeComponentScale = (value, fallback = 1) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0
    ? numericValue
    : fallback;
};

const getLessonHighlightToneColor = (tone) => {
  switch (tone) {
    case 'positive':
      return '#dc2626';
    case 'ground':
      return '#2563eb';
    case 'conflict':
      return '#b91c1c';
    case 'hint':
      return '#7c3aed';
    case 'selected':
      return '#f59e0b';
    case 'placement':
      return '#16a34a';
    case 'reference':
      return '#64748b';
    case 'trace':
      return '#0f766e';
    default:
      return '#2c9fa4';
  }
};

const resolveLessonDefaultZoom = (lesson, fallbackZoom) => {
  const lessonZoom = Number(
    lesson?.initialProject?.canvas?.zoomLevel ??
    lesson?.workspace?.initialZoom
  );

  return Number.isFinite(lessonZoom) && lessonZoom > 0
    ? lessonZoom
    : fallbackZoom;
};

const getRuntimeLessonAuthoring = (lesson) => (
  lesson?.authoring && typeof lesson.authoring === 'object'
    ? lesson.authoring
    : {}
);

const getRuntimeLessonStarterMode = (lesson) => String(
  getRuntimeLessonAuthoring(lesson).starterCircuitMode ||
  lesson?.starterCircuitMode ||
  ''
).trim();

const lessonStepAllowsCodePanel = (step) => Boolean(
  step?.allowedActions?.viewCode ||
  step?.allowedActions?.editCode ||
  step?.allowedActions?.runCode
);

const normalizeLessonStarterSignatureNumber = (value) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? Number(numericValue.toFixed(3)) : null;
};

const buildLessonStarterComponentSignature = (component) => ({
  id: String(component?.id || ''),
  type: String(component?.type || ''),
  passiveKey: String(component?.passiveKey || ''),
  passiveType: String(component?.passiveType || ''),
  boardId: String(component?.boardId ?? component?.board?.id ?? ''),
  boardSlug: String(component?.boardSlug ?? component?.board?.slug ?? ''),
  boardName: String(component?.boardName ?? component?.board?.name ?? ''),
  boardLookupKind: String(component?.boardLookup?.kind || ''),
  x: normalizeLessonStarterSignatureNumber(component?.x),
  y: normalizeLessonStarterSignatureNumber(component?.y),
  scale: normalizeLessonStarterSignatureNumber(component?.scale),
  rotation: normalizeLessonStarterSignatureNumber(component?.rotation),
  targetWidth: normalizeLessonStarterSignatureNumber(component?.targetWidth),
  selectedValue: String(component?.selectedValue ?? ''),
  color: String(component?.color || ''),
  locked: Boolean(component?.locked),
});

const buildLessonStarterConnectionSignature = (connection) => ({
  id: String(connection?.id || ''),
  fromPartId: String(connection?.from?.partId || ''),
  fromPinKey: String(connection?.from?.pinKey || ''),
  toPartId: String(connection?.to?.partId || ''),
  toPinKey: String(connection?.to?.pinKey || ''),
});

const buildLessonStarterCanvasSignature = (lesson) => {
  const canvas = lesson?.initialProject?.canvas && typeof lesson.initialProject.canvas === 'object'
    ? lesson.initialProject.canvas
    : {};

  return {
    zoomLevel: normalizeLessonStarterSignatureNumber(canvas.zoomLevel ?? lesson?.workspace?.initialZoom),
    showGrid: Boolean(canvas.showGrid),
    snapToGrid: Boolean(canvas.snapToGrid),
    gridSize: normalizeLessonStarterSignatureNumber(canvas.gridSize),
  };
};

const buildLessonStarterSimulationSignature = (lesson) => ({
  code: String(
    lesson?.initialProject?.simulation?.code ||
    lesson?.simulation?.code ||
    lesson?.simulationCode ||
    ''
  ).replace(/\r\n?/g, '\n'),
});

const buildLessonStarterSignature = (lesson) => {
  if (!lesson) {
    return '';
  }

  const initialProject = lesson.initialProject || {};
  const components = Array.isArray(initialProject.components)
    ? initialProject.components
      .map(buildLessonStarterComponentSignature)
      .sort((left, right) => left.id.localeCompare(right.id))
    : [];
  const connections = Array.isArray(initialProject.connections)
    ? initialProject.connections
      .map(buildLessonStarterConnectionSignature)
      .sort((left, right) => left.id.localeCompare(right.id))
    : [];

  return JSON.stringify({
    mode: getRuntimeLessonStarterMode(lesson),
    components,
    connections,
    canvas: buildLessonStarterCanvasSignature(lesson),
    simulation: buildLessonStarterSimulationSignature(lesson),
  });
};

const clampComponentScale = (value) => {
  const normalizedValue = normalizeComponentScale(value, 1);

  return Number(
    Math.min(
      COMPONENT_SCALE_MAX,
      Math.max(COMPONENT_SCALE_MIN, normalizedValue)
    ).toFixed(2)
  );
};

const formatComponentScalePercent = (value) => (
  `${Math.round(clampComponentScale(value) * 100)}%`
);

const SIMULATION_EDITOR_PADDING = 16;

const LED_OVERCURRENT_LIMIT_MA = 20;

const ANALYSIS_SEVERITY_STYLES = {
  error: {
    border: '#fecaca',
    background: '#fef2f2',
    text: '#b91c1c',
    badge: '#dc2626',
  },
  warning: {
    border: '#fde68a',
    background: '#fffbeb',
    text: '#92400e',
    badge: '#f59e0b',
  },
  suggestion: {
    border: '#bfdbfe',
    background: '#eff6ff',
    text: '#1d4ed8',
    badge: '#2563eb',
  },
  info: {
    border: '#dbeafe',
    background: '#f8fbff',
    text: '#1e40af',
    badge: '#3b82f6',
  },
  pass: {
    border: '#bbf7d0',
    background: '#f0fdf4',
    text: '#166534',
    badge: '#16a34a',
  },
};

if (!Prism.languages.arduino) {
  Prism.languages.arduino = Prism.languages.extend('cpp', {});

  Prism.languages.insertBefore('arduino', 'function', {
    'arduino-directive': {
      pattern: /^[ \t]*#\s*[a-z_]\w*(?:[^\r\n]*)/im,
      alias: 'property',
    },
    'arduino-structure': {
      pattern: /\b(?:setup|loop)\b/,
      alias: 'function',
    },
    'arduino-api': {
      pattern: /\b(?:pinMode|digitalWrite|digitalRead|analogWrite|analogRead|analogReference|analogReadResolution|tone|noTone|pulseIn|pulseInLong|shiftIn|shiftOut|attachInterrupt|detachInterrupt|interrupts|noInterrupts|delay|delayMicroseconds|millis|micros|map|constrain|min|max|abs|sq|sqrt|pow|sin|cos|tan|random|randomSeed|bitRead|bitSet|bitClear|bitWrite|lowByte|highByte|sizeof|begin|beginTransmission|endTransmission|requestFrom|available|read|peek|print|println|write|flush|setTimeout)\b/,
      alias: 'function',
    },
    'arduino-object': {
      pattern: /\b(?:Serial|Serial1|Serial2|Serial3|Wire|SPI|EEPROM)\b/,
      alias: 'class-name',
    },
    'arduino-constant': {
      pattern: /\b(?:HIGH|LOW|INPUT|OUTPUT|INPUT_PULLUP|LED_BUILTIN|true|false|CHANGE|FALLING|RISING|DEFAULT|EXTERNAL|INTERNAL|INTERNAL1V1|INTERNAL2V56|DEC|HEX|OCT|BIN|LSBFIRST|MSBFIRST)\b/,
      alias: 'constant',
    },
    'arduino-type': {
      pattern: /\b(?:byte|word|boolean|String)\b/,
      alias: 'class-name',
    },
  });
}

const highlightArduinoCode = (source) => (
  Prism.highlight(
    String(source || ''),
    Prism.languages.arduino,
    'arduino'
  )
);

const snapValueToGrid = (value, size) => {
  const numericValue = Number(value);
  const numericSize = Number(size);

  if (!Number.isFinite(numericValue) || !Number.isFinite(numericSize) || numericSize <= 0) {
    return numericValue;
  }

  return Number((Math.round(numericValue / numericSize) * numericSize).toFixed(2));
};

const cloneProjectBuilderHistoryValue = (value) => {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch (error) {
      // Fall through to the JSON clone below.
    }
  }

  return JSON.parse(JSON.stringify(value));
};

// eslint-disable-next-line no-unused-vars
const getResistorBands = (valueOhms) => {
  const value = Number(valueOhms);
  if (!Number.isFinite(value) || value <= 0) {
    return ['brown', 'black', 'black', 'gold']; // 10Ω fallback
  }

  const digits = String(Math.round(value)).replace(/^0+/, '');
  const first = Number(digits[0] || 1);
  const second = Number(digits[1] || 0);
  const multiplier = Math.max(digits.length - 2, 0);

  const digitColors = [
    'black', 'brown', 'red', 'orange', 'yellow',
    'green', 'blue', 'violet', 'gray', 'white',
  ];

  return [
    digitColors[first],
    digitColors[second],
    digitColors[multiplier],
    'gold',
  ];
};

const ProjectBuilderContent = styled.div`
  padding: ${props => props.$embed ? '12px 0 18px' : 'clamp(24px, 3vw, 40px) 0 44px'};
  background:
    linear-gradient(180deg, #f8fafc 0px, rgba(248, 250, 252, 0) 220px),
    #ffffff;
`;

const ProjectBuilderShell = styled.div`
  width: ${props => props.$embed ? 'min(100% - 16px, 1480px)' : 'min(calc(var(--site-max-width) - 24px), calc(100% - (var(--site-gutter) * 2)))'};
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 0;
  min-width: 0;
  max-width: 430px;
`;

const TitleWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const Title = styled.h1`
  color: #1f2937;
  margin: 0;
  font-size: 2.5rem;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  padding: 3px 9px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  font-size: 0.66rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0;
  font-size: 0.97rem;
  line-height: 1.55;
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$hasBanner ? 'minmax(260px, 430px) minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: ${props => props.$hasBanner ? '22px' : '18px'};
  align-items: ${props => props.$hasBanner ? 'center' : 'start'};
  margin-bottom: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    align-items: start;
  }

  @media (max-width: 640px) {
    margin-bottom: 20px;
  }
`;

const HelperPanel = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px 20px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;

  @media (max-width: 640px) {
    padding: 14px 16px;
    border-radius: 12px;
  }
`;

const HelperTitle = styled.h2`
  margin: 0 0 8px 0;
  color: #111827;
  font-size: 1.05rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 480px) {
    font-size: 0.98rem;
  }
`;

const HelperText = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;

  @media (max-width: 480px) {
    font-size: 0.82rem;
  }
`;

const HelperActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
  }
`;

const HelperButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  background: ${props => props.$primary ? '#2C9FA4' : '#eef2f7'};
  color: ${props => props.$primary ? '#ffffff' : '#334155'};
  font-weight: 800;
  font-size: 0.84rem;
  cursor: pointer;

  &:hover {
    background: ${props => props.$primary ? '#1c6568' : '#e2e8f0'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 10px 12px;
  }
`;

const HelperSecondaryButton = styled(HelperButton)`
  @media (max-width: 480px) {
    display: none;
  }
`;

const ProjectStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  margin-top: 14px;

  @media (max-width: 480px) {
    margin-top: 12px;
  }
`;

const ProjectStat = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  text-align: center;

  @media (max-width: 480px) {
    padding: 8px 6px;
  }
`;

const ProjectStatValue = styled.div`
  color: #111827;
  font-size: 1.1rem;
  font-weight: 900;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ProjectStatLabel = styled.div`
  margin-top: 5px;
  color: #64748b;
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  @media (max-width: 480px) {
    font-size: 0.62rem;
  }
`;

const ProjectContent = styled.div`
  display: grid;
  grid-template-columns: ${props => props.$tutorialMode
    ? 'minmax(300px, 340px) minmax(0, 1fr)'
    : 'minmax(0, 1fr) minmax(320px, 350px)'};
  gap: 24px;
  align-items: start;
  min-height: ${PROJECT_BUILDER_WORKSPACE_MIN_HEIGHT};
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const LessonSideColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;

  @media (max-width: 1024px) {
    order: 2;
  }
`;

const SponsoredBannerCard = styled.div`
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  width: 100%;
  min-width: 0;
  justify-self: stretch;
  align-self: center;

  @media (max-width: 900px) {
    width: 100%;
    justify-self: stretch;
  }
`;

const SponsoredBannerLink = styled.a`
  display: block;
  width: 100%;
  height: 100%;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const SponsoredBannerImage = styled.img`
  width: 100%;
  height: clamp(82px, 9vw, 108px);
  display: block;
  object-fit: cover;
  object-position: center;

  @media (max-width: 900px) {
    height: clamp(96px, 24vw, 140px);
  }
`;

const CanvasWorkspaceColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const CanvasToolbarDock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

const CanvasContainer = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #dbe3ef;
  border-radius: 16px;
  padding: 0;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  overflow: hidden;
  position: relative;
  align-self: stretch;
  width: 100%;
  min-width: 0;
  min-height: clamp(560px, calc(100vh - 250px), 920px);

  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  @media (max-width: 1024px) {
    min-height: clamp(480px, 68vh, 760px);
  }

  @media (max-width: 640px) {
    min-height: clamp(420px, 62vh, 620px);
    border-radius: 14px;
  }
`;

const SVGCanvas = styled.svg`
  width: 100%;
  height: auto;
  aspect-ratio: 1100 / 720;

  background-color: #ffffff;
  background-image: ${props => {
    const dots = 'radial-gradient(circle at 1px 1px, rgba(203, 213, 225, 0.82) 0.9px, transparent 1px)';

    if (!props.$showGrid) {
      return dots;
    }

    return `
      ${dots},
      linear-gradient(rgba(148, 163, 184, 0.18) 1px, transparent 1px),
      linear-gradient(90deg, rgba(148, 163, 184, 0.18) 1px, transparent 1px)
    `;
  }};

  background-size: ${props => {
    const size = Math.max(6, Number(props.$gridSize || 10));
    const dotSize = `${Math.max(18, Math.round(size * 1.8))}px ${Math.max(18, Math.round(size * 1.8))}px`;
    const majorSize = `${size * 5}px ${size * 5}px`;

    return props.$showGrid
      ? `${dotSize}, ${majorSize}, ${majorSize}`
      : dotSize;
  }};
  background-position: 0 0;

  border: none;
  border-radius: 14px;
  cursor: ${props => {
    if (props.$wireDropStatus === 'valid') return 'copy';
    if (props.$wireDropStatus === 'invalid') return 'not-allowed';
    return 'default';
  }};
  display: block;

  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  .builder-pin-hitbox,
  .builder-pin-hitbox:hover {
    cursor: crosshair !important;
    pointer-events: all !important;
    touch-action: none;
  }

  .builder-component-drag-hitbox,
  .builder-component-drag-hitbox:hover,
  .builder-component-drag-pin-hitbox,
  .builder-component-drag-pin-hitbox:hover {
    cursor: grab !important;
    pointer-events: all !important;
    touch-action: none;
  }

  .builder-component-drag-hitbox:active,
  .builder-component-drag-pin-hitbox:active {
    cursor: grabbing !important;
  }

  .builder-wire-drop-valid-pin-hitbox,
  .builder-wire-drop-valid-pin-hitbox:hover {
    cursor: copy !important;
  }

  .builder-wire-drop-invalid-pin-hitbox,
  .builder-wire-drop-invalid-pin-hitbox:hover {
    cursor: not-allowed !important;
  }

  .builder-pin-hover-dot {
    pointer-events: none;
    filter: drop-shadow(0 0 6px rgba(37, 99, 235, 0.5));
  }

  .builder-pin-hover-dot-valid {
    filter: drop-shadow(0 0 6px rgba(34, 197, 94, 0.5));
  }

  .builder-pin-hover-dot-invalid {
    filter: drop-shadow(0 0 6px rgba(239, 68, 68, 0.45));
  }
`;

const CanvasOverlayStack = styled.div`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  pointer-events: none;
`;

const CanvasEmptyState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  pointer-events: none;
  z-index: 1;
`;

const CanvasEmptyStateCard = styled.div`
  display: grid;
  gap: 8px;
  justify-items: center;
  width: min(320px, 100%);
  padding: 18px 20px;
  border: 1px dashed #dbe3ef;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.82);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
  backdrop-filter: blur(3px);
  text-align: center;

  @media (max-width: 640px) {
    padding: 16px 18px;
    border-radius: 14px;
  }
`;

const CanvasEmptyStateIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #eff6ff;
  color: #2563eb;
`;

const CanvasEmptyStateTitle = styled.div`
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.35;
`;

const CanvasEmptyStateHint = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.86rem;
  line-height: 1.55;
`;

const CanvasToolbarToggle = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    align-self: flex-start;
    min-height: 42px;
    padding: 10px 14px;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.96);
    color: #0f172a;
    font-size: 0.85rem;
    font-weight: 700;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
    cursor: pointer;
    pointer-events: auto;

    &:hover {
      background: #eff6ff;
      border-color: #3b82f6;
      color: #1d4ed8;
    }
  }
`;

const CanvasToolbar = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px 12px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  padding: 12px 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid #dbe3ef;
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
  pointer-events: auto;

  @media (max-width: 768px) {
    display: ${props => props.$mobileOpen ? 'flex' : 'none'};
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    padding: 12px;
  }
`;

const CanvasToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
  padding-right: 14px;
  margin-right: 2px;
  margin-left: ${props => props.$alignEnd ? 'auto' : '0'};
  border-right: 1px solid #e5e7eb;

  &:last-child {
    padding-right: 0;
    margin-right: 0;
    border-right: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
    padding-right: 0;
    margin-right: 0;
    border-right: none;
    gap: 8px;

    & + & {
      padding-top: 10px;
      border-top: 1px solid #eef2f7;
    }
  }
`;

const CanvasToolbarSectionTitle = styled.span`
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CanvasToolbarPopoverAnchor = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CanvasToolButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: ${props => props.$iconOnly ? '38px' : 'auto'};
  min-width: 38px;
  height: 38px;
  padding: ${props => props.$iconOnly ? '0' : '0 12px'};
  border: 1px solid ${props => props.$active ? '#93c5fd' : '#dbe3ef'};
  background: ${props => props.$active ? '#eff6ff' : '#ffffff'};
  color: ${props => props.$active ? '#1d4ed8' : '#0f172a'};
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1d4ed8;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    height: 40px;
    min-width: ${props => props.$iconOnly ? '40px' : 'unset'};
  }
`;

const CanvasToolbarPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 230px;
  padding: 10px;
  border: 1px solid #dbe3ee;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 30;

  @media (max-width: 768px) {
    left: 0;
    right: auto;
    width: 100%;
    min-width: 0;
  }
`;

const CanvasToolbarPopoverTitle = styled.p`
  margin: 0;
  color: #475569;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

const CanvasToolbarPopoverButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 40px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #eff6ff;
    border-color: #3b82f6;
    color: #1d4ed8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ZoomLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 58px;
  min-height: 38px;
  padding: 0 10px;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  background: #f8fafc;
  text-align: center;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 800;

  @media (max-width: 768px) {
    height: 40px;
    white-space: nowrap;
  }
`;
const CanvasToolbarActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 38px;
  padding: 8px 14px;
  border: 1px solid ${props => {
    if (props.$primary) return '#2563eb';
    if (props.$active) return '#93c5fd';
    return '#dbe3ef';
  }};
  background: ${props => {
    if (props.$primary) return '#2563eb';
    if (props.$active) return '#eff6ff';
    return '#ffffff';
  }};
  color: ${props => {
    if (props.$primary) return '#ffffff';
    if (props.$active) return '#1d4ed8';
    return '#0f172a';
  }};
  border-radius: 10px;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#1d4ed8' : '#f8fafc'};
    border-color: ${props => props.$primary ? '#2563eb' : '#cbd5e1'};
    color: ${props => props.$primary ? '#ffffff' : '#1d4ed8'};
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    min-height: 40px;
    padding: 8px 10px;
    justify-content: center;
    white-space: normal;
    text-align: center;
    font-size: 0.78rem;
  }
`;

const CanvasToolbarStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${props => props.$tone === 'warning' ? '#fff7ed' : '#eff6ff'};
  color: ${props => props.$tone === 'warning' ? '#c2410c' : '#1d4ed8'};
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CanvasToolbarCheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid ${props => props.$checked ? '#93c5fd' : '#dbe3ef'};
  border-radius: 10px;
  background: ${props => props.$checked ? '#eff6ff' : '#ffffff'};
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  font-size: 12px;
  font-weight: 700;
  color: ${props => props.$checked ? '#1d4ed8' : '#334155'};
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2563eb;
  }

  @media (max-width: 768px) {
    justify-content: flex-start;
    min-height: 40px;
  }
`;

const CanvasToolbarField = styled.label`
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  border: 1px solid #dbe3ef;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 40px;
  }
`;

const CanvasToolbarFieldLabel = styled.span`
  padding: 0 10px;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const CanvasToolbarNumberInput = styled.input`
  width: 68px;
  height: 38px;
  box-sizing: border-box;
  padding: 8px 10px;
  border: none;
  border-left: 1px solid #e5e7eb;
  border-radius: 0;
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
  background: #ffffff;

  &:focus {
    outline: none;
    box-shadow: inset 0 0 0 2px rgba(59, 130, 246, 0.16);
  }

  @media (max-width: 768px) {
    height: 40px;
  }
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const PanelSection = styled.div`
  background: linear-gradient(180deg, #ffffff 0%, #fcfdff 100%);
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
`;

const SectionTitle = styled.h3`
  margin: 0 0 14px 0;
  color: #1f2937;
  font-size: 1rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const WiringList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc((60px * 3) + (10px * 2));
  overflow-y: auto;
  padding-right: 4px;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const WiringItem = styled.div`
  background: ${props => props.$selected ? '#eff6ff' : '#f9fafb'};
  border: 1px solid ${props => props.$selected ? '#93c5fd' : '#e5e7eb'};
  border-radius: 8px;
  padding: 10px;
  min-height: 60px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr) auto auto;
  gap: 8px;
  align-items: center;
  font-size: 0.9rem;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  box-shadow: ${props => props.$selected ? '0 0 0 2px rgba(37, 99, 235, 0.12)' : 'none'};
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: ${props => props.$clickable ? '#eff6ff' : '#f9fafb'};
    border-color: ${props => props.$clickable ? '#bfdbfe' : '#e5e7eb'};
  }
`;

const WiringEndpoint = styled.div`
  min-width: 0;
`;

const WiringEndpointBoard = styled.div`
  color: #374151;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WiringEndpointPin = styled.div`
  color: #6b7280;
  font-size: 0.78rem;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WiringArrow = styled.div`
  color: #9ca3af;
  font-weight: 700;
`;

const WireTag = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 62px;
  padding: 4px 7px;
  border-radius: 999px;
  background: ${props => props.$color || '#e5e7eb'};
  color: white;
  font-size: 0.66rem;
  font-weight: 800;
  text-transform: uppercase;
  line-height: 1;
  white-space: nowrap;
`;

const SuggestedWiringBox = styled.div`
  margin-bottom: 12px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  border-radius: 10px;
  overflow: hidden;
`;

const CanvasSuggestionPanel = styled(SuggestedWiringBox)`
  margin-bottom: 0;
  width: min(100%, 520px);
  pointer-events: auto;
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.14);

  @media (max-width: 768px) {
    width: 100%;
    max-height: 48vh;
  }
`;

const SuggestedWiringActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-bottom: 1px solid #bfdbfe;
  background: rgba(255, 255, 255, 0.42);
`;

const SuggestedWiringHeader = styled.div`
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid #bfdbfe;
`;

const SuggestedWiringTitle = styled.div`
  color: #1d4ed8;
  font-size: 0.9rem;
  font-weight: 800;
`;

const SuggestedWiringList = styled.div`
  display: grid;
  gap: 8px;
  padding: 10px;
  max-height: 280px;
  overflow-y: auto;
`;

const SuggestedConnectionItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px;
  align-items: center;
`;

const SuggestionCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const PanelHelperText = styled.p`
  margin: 8px 0 0 0;
  color: #6b7280;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const AnalysisSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
`;

const AnalysisSummaryCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  padding: 12px;
  text-align: center;
`;

const AnalysisSummaryCount = styled.div`
  color: #111827;
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1;
`;

const AnalysisSummaryLabel = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AnalysisGroup = styled.div`
  margin-top: 14px;
`;

const AnalysisGroupTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 0.95rem;
  font-weight: 800;
`;

const AnalysisResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AnalysisResultButton = styled.button`
  width: 100%;
  border-radius: 10px;
  border: 1px solid ${props => ANALYSIS_SEVERITY_STYLES[props.$severity]?.border || '#e5e7eb'};
  background: ${props => ANALYSIS_SEVERITY_STYLES[props.$severity]?.background || '#f9fafb'};
  color: ${props => ANALYSIS_SEVERITY_STYLES[props.$severity]?.text || '#1f2937'};
  padding: 12px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${props => props.$active ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none'};

  &:hover {
    transform: translateY(-1px);
  }
`;

const AnalysisResultHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const AnalysisResultTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.35;
`;

const AnalysisResultBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 68px;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${props => ANALYSIS_SEVERITY_STYLES[props.$severity]?.badge || '#64748b'};
  color: white;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const AnalysisResultMeta = styled.div`
  margin-top: 6px;
  color: #6b7280;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AnalysisResultMessage = styled.p`
  margin: 8px 0 0 0;
  color: #374151;
  font-size: 0.85rem;
  line-height: 1.5;
`;

const WiringStepsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WiringStepButton = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: flex-start;
  border: 1px solid ${props => props.$active ? '#93c5fd' : '#e5e7eb'};
  background: ${props => props.$active ? '#eff6ff' : '#f9fafb'};
  color: #1f2937;
  border-radius: 10px;
  padding: 12px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const WiringStepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: #2563eb;
  color: white;
  font-size: 0.8rem;
  font-weight: 800;
`;

const WiringStepText = styled.p`
  margin: 0;
  color: #374151;
  font-size: 0.86rem;
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.8rem;
  flex-shrink: 0;
  transition: transform 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #dc2626;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: ${props => props.$primary ? '40px' : '38px'};
  background: ${props => props.$primary ? '#2C9FA4' : '#f8fafc'};
  color: ${props => props.$primary ? '#ffffff' : '#334155'};
  border: 1px solid ${props => props.$primary ? '#2C9FA4' : '#dbe3ef'};
  padding: 11px 14px;
  border-radius: 10px;
  font-weight: ${props => props.$primary ? 700 : 600};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${props => props.$primary ? '0 6px 14px rgba(44, 159, 164, 0.16)' : 'none'};
  
  &:hover:not(:disabled) {
    background: ${props => props.$primary ? '#1c6568' : '#eef2f7'};
    border-color: ${props => props.$primary ? '#1c6568' : '#cbd5e1'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const AddComponentModal = styled.div`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 20px 0;
  color: #1f2937;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #374151;
  font-weight: 500;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  
  &.cancel {
    background: #f3f4f6;
    color: #1f2937;
    
    &:hover {
      background: #e5e7eb;
    }
  }
  
  &.confirm {
    background: #2c9fa4;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 1.1rem;
`;

const ErrorContainer = styled.div`
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #dc2626;
`;

const ErrorText = styled.div`
  flex: 1;
`;

const RetryButton = styled.button`
  margin-top: 8px;
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    background: #b91c1c;
  }
`;

const EmptyStateText = styled.p`
  display: grid;
  gap: 4px;
  padding: 14px 12px;
  border: 1px dashed #e2e8f0;
  border-radius: 10px;
  background: #fafcff;
  color: #64748b;
  margin: 0;
  text-align: center;
  font-size: 0.88rem;
  line-height: 1.55;

  strong {
    color: #334155;
    font-size: 0.9rem;
    font-weight: 700;
  }
`;

const PropertyRow = styled.div`
  display: grid;
  grid-template-columns: 110px minmax(0, 1fr);
  gap: 10px;
  align-items: center;

  ${Label} {
    margin-bottom: 0;
  }

  input[type="color"] {
    width: 100%;
    height: 38px;
    padding: 3px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
  }

  ${Select} {
    width: 100%;
  }
`;

const InspectorSliderPanel = styled.div`
  margin-top: 12px;
  padding: 12px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
`;

const InspectorSliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InspectorRangeInput = styled.input`
  flex: 1 1 auto;
  width: 100%;
  accent-color: #2c9fa4;
`;

const InspectorSliderReadout = styled.div`
  min-width: 64px;
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 700;
  text-align: right;
`;

const InspectorSliderMeta = styled.div`
  margin-top: 8px;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.5;
`;

const InspectorInlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 36px;
  padding: 8px 12px;
  border: 1px solid ${props => props.$active ? '#93c5fd' : '#cbd5e1'};
  border-radius: 8px;
  background: ${props => props.$active ? '#eff6ff' : '#ffffff'};
  color: ${props => props.$active ? '#1d4ed8' : '#334155'};
  font-size: 0.82rem;
  font-weight: 800;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #3b82f6;
    color: #1d4ed8;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const CanvasComponentSliderCard = styled.div`
  width: 92px;
  height: 152px;
  padding: 10px 10px 12px;
  border: 1px solid #bae6fd;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 28px rgba(14, 165, 233, 0.18);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  pointer-events: auto;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
`;

const CanvasComponentSliderLabel = styled.div`
  color: #0369a1;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1;
`;

const CanvasComponentSliderTrackWrap = styled.div`
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 82px;
`;

const CanvasComponentSliderInput = styled.input`
  width: 94px;
  accent-color: #0ea5e9;
  transform: rotate(-90deg);
  transform-origin: center;
`;

const CanvasComponentSliderValue = styled.div`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
  line-height: 1;
`;

const CanvasComponentSliderHint = styled.div`
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
`;

const BOARD_EDITOR_SVG_CSS = `
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"] {
    display: none !important;
  }

  .pin-hole,
  .pin-rect {
    cursor: crosshair !important;
    fill: rgba(255, 255, 255, 0.96) !important;
    stroke: #475569 !important;
    stroke-width: 1.8 !important;
    pointer-events: all !important;
  }

  .pin-hole:hover,
  .pin-rect:hover {
    fill: rgba(37, 99, 235, 0.14) !important;
    stroke: #2563eb !important;
    stroke-width: 2.6 !important;
  }

  .pin-rect.breadboard-pin-hitbox,
  .pin-rect.breadboard-pin-hitbox:hover {
    fill: transparent !important;
    stroke: transparent !important;
    stroke-width: 0 !important;
  }

  svg {
    overflow: visible !important;
  }

  #connector0pin.pin-rect,
  #connector1pin.pin-rect,
  #connector2pin.pin-rect {
    fill: transparent !important;
    stroke: transparent !important;
  }
`;

const BoardSvgHost = styled.div`
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
  pointer-events: auto;
  margin: 0;
  padding: 0;

  > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    display: block !important;
    pointer-events: auto !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }
`;

const ComponentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc((60px * 3) + (10px * 2));
  overflow-y: auto;
  padding-right: 4px;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;
`;

const ComponentListItem = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;

  align-items: stretch;

  min-width: 0;
  min-height: 66px;

  background: ${props => props.$selected ? '#eff6ff' : props.$muted ? '#f8fafc' : '#ffffff'};
  border: 1px solid ${props => props.$selected ? '#93c5fd' : '#e5e7eb'};
  opacity: ${props => props.$muted ? 0.68 : 1};
  border-radius: 10px;
  padding: 12px;
  box-shadow: ${props => props.$selected ? '0 0 0 1px rgba(147, 197, 253, 0.4)' : '0 1px 2px rgba(15, 23, 42, 0.04)'};
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${props => props.$selected ? '#93c5fd' : '#dbe3ef'};
    transform: translateY(-1px);
  }
`;

const ComponentListName = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  min-width: 0;

  display: block;
  text-align: left;

  color: #1f2937;
  font-size: 0.94rem;
  font-weight: 700;
  line-height: 1.35;
  cursor: pointer;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2563eb;
  }
`;

const ComponentListIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: 100%;
    max-height: 100%;
  }
`;

const ComponentListIconSvg = styled.div`
  width: 100%;
  height: 100%;
  pointer-events: none;

  svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }

  .pin-hole,
  .pin-rect,
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"] {
    display: none !important;
  }
`;

const ModalTabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 20px;
`;

const ModalTab = styled.button`
  padding: 10px;
  border-radius: 8px;
  border: 1px solid ${props => props.$active ? '#2c9fa4' : '#e5e7eb'};
  background: ${props => props.$active ? '#eff6ff' : '#f9fafb'};
  color: ${props => props.$active ? '#1d4ed8' : '#374151'};
  font-weight: 600;
  cursor: pointer;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchResults = styled.div`
  margin-top: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow-y: auto;
  background: #ffffff;

  /*
    5 result rows:
    each row is roughly 62px tall
  */
  height: 310px;

  /*
    Scrollbar only appears when needed.
  */
  scrollbar-gutter: stable;
`;

const SearchResultItem = styled.button`
  width: 100%;
  border: none;
  border-bottom: 1px solid #f1f5f9;
  background: ${props => props.$selected ? '#eff6ff' : '#ffffff'};
  padding: 10px;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  text-align: left;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const SearchResultIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }

  .pin-hole,
  .pin-rect,
  [data-micropinouts-callout="1"],
  [data-micropinouts-callout-style="1"] {
    display: none !important;
  }
`;

const SearchResultText = styled.div`
  min-width: 0;
`;

const SearchResultName = styled.div`
  color: #1f2937;
  font-weight: 600;
  font-size: 0.95rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchResultMeta = styled.div`
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SearchResultEmpty = styled.div`
  padding: 14px;
  color: #9ca3af;
  text-align: center;
  font-size: 0.9rem;
`;

const SearchResultLoading = styled.div`
  padding: 14px;
  color: #6b7280;
  text-align: center;
  font-size: 0.9rem;
  background: #f9fafb;
`;

const SearchSpinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  margin-right: 8px;
  border: 2px solid #d1d5db;
  border-top-color: #2563eb;
  border-radius: 50%;
  vertical-align: -2px;
  animation: spin 0.7s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const AnalysisModalOverlay = styled.div`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  inset: 0;
  z-index: 2500;
  background: rgba(15, 23, 42, 0.58);
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 0;
    align-items: stretch;
  }
`;

const AnalysisModalContent = styled.div`
  width: min(980px, 100%);
  max-height: min(86vh, 900px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-height: none;
    height: 100vh;
    border-radius: 0;
  }
`;

const AnalysisModalHeader = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
`;

const AnalysisModalTitle = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.25rem;
`;

const AnalysisModalBody = styled.div`
  padding: 18px 22px 22px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const AnalysisModalTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 22px 0;
  border-bottom: 1px solid #e5e7eb;
  overflow-x: auto;
  flex-shrink: 0;
`;

const AnalysisModalTab = styled.button`
  border: none;
  border-bottom: 3px solid ${props => props.$active ? '#2563eb' : 'transparent'};
  background: transparent;
  color: ${props => props.$active ? '#1d4ed8' : '#6b7280'};
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
`;

const ComponentItemActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-left: auto;
`;

const ExamplesModalOverlay = styled(AnalysisModalOverlay)``;

const ExamplesModalContent = styled(AnalysisModalContent)`
  width: min(860px, 100%);
  max-height: min(82vh, 860px);
`;

const ExamplesModalHeader = styled(AnalysisModalHeader)``;

const ExamplesModalBody = styled(AnalysisModalBody)`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ExamplesList = styled.div`
  display: grid;
  gap: 12px;
`;

const ExampleLibraryCard = styled.button`
  width: 100%;
  border: 1px solid #dbe3ee;
  border-radius: 16px;
  background: #ffffff;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: #93c5fd;
    box-shadow: 0 14px 28px rgba(37, 99, 235, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ExampleLibraryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const ExampleLibraryTitleWrap = styled.div`
  min-width: 0;
`;

const ExampleLibraryTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
`;

const ExampleLibraryDescription = styled.p`
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const ExampleLibraryMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ExampleLibraryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  background: ${props => props.$tone === 'code' ? '#eef2ff' : '#f8fafc'};
  color: ${props => props.$tone === 'code' ? '#4338ca' : '#334155'};
  border: 1px solid ${props => props.$tone === 'code' ? '#c7d2fe' : '#dbe3ee'};
  font-size: 0.78rem;
  font-weight: 800;
`;

const ExampleLibraryAction = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.85rem;
  font-weight: 800;
  white-space: nowrap;
`;

const ExampleLibraryEmpty = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 20px;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.92rem;
  line-height: 1.6;
`;

const SimulationModalOverlay = styled(AnalysisModalOverlay)`
  background: rgba(15, 23, 42, 0.58);
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 0;
    align-items: stretch;
  }
`;

const SimulationModalContent = styled(AnalysisModalContent)`
  width: min(1120px, 100%);
  max-height: min(90vh, 980px);
  height: min(90vh, 980px);
  pointer-events: auto;
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.2);

  @media (max-width: 768px) {
    width: 100%;
    max-height: none;
    height: 100vh;
    border-radius: 0;
  }
`;

const SimulationModalHeader = styled(AnalysisModalHeader)``;

const SimulationModalBody = styled(AnalysisModalBody)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const SimulationHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const SimulationHeaderButton = styled.button`
  border: none;
  background: #f3f4f6;
  color: #374151;
  border-radius: 999px;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

const SimulationWorkspaceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  flex: ${props => props.$expanded ? '1 1 auto' : '0 0 auto'};
  overflow: ${props => props.$expanded ? 'hidden' : 'visible'};
`;

const SimulationWorkspaceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SimulationWorkspaceActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SimulationWorkspaceNotice = styled.div`
  border: 1px dashed #93c5fd;
  border-radius: 14px;
  background: #f8fbff;
  padding: 16px;
  color: #475569;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SimulationExpandedEditorFrame = styled.div`
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: stretch;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  background: #0f172a;
  overflow: hidden;

  &:focus-within {
    border-color: #38bdf8;
    box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.18);
  }
`;

const SimulationExpandedLineNumberGutter = styled.div`
  flex: 0 0 60px;
  border-right: 1px solid rgba(148, 163, 184, 0.18);
  background: #020617;
  color: #64748b;
  overflow: hidden;
  user-select: none;
  pointer-events: none;
`;

const SimulationExpandedEditorViewport = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
`;

const SimulationExpandedLineNumberTrack = styled.div`
  padding: ${SIMULATION_EDITOR_PADDING}px 10px ${SIMULATION_EDITOR_PADDING}px 12px;
  will-change: transform;
`;

const SimulationExpandedLineNumber = styled.div`
  height: 25.6px;
  font-family: Consolas, "Courier New", monospace;
  font-size: 16px;
  line-height: 25.6px;
  font-variant-numeric: tabular-nums;
  text-align: right;
`;

const SimulationEditor = styled(Editor).attrs(() => ({
  padding: 16,
  textareaClassName: 'simulation-editor__textarea',
  preClassName: 'simulation-editor__pre',
}))`
  width: 100%;
  min-height: ${props => (
    props.$expanded
      ? '0'
      : props.$compact
        ? '190px'
        : '260px'
  )};
  flex: ${props => props.$expanded ? '1 1 0' : '0 0 auto'};
  border: ${props => props.$expanded ? 'none' : '1px solid #cbd5e1'};
  border-radius: ${props => props.$expanded ? '0' : '14px'};
  background: ${props => props.$expanded ? 'transparent' : '#0f172a'};
  font-size: 16px;
  line-height:  25.6px;
  font-family: Consolas, "Courier New", monospace;
  tab-size: 2;
  overflow: ${props => props.$expanded ? 'visible' : 'auto'};

  &,
  & * {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
    tab-size: inherit;
  }

  & .simulation-editor__pre,
  & .simulation-editor__textarea {
    min-height: ${props => (
      props.$expanded
        ? '100%'
        : props.$compact
          ? '190px'
          : '260px'
    )};
    white-space: ${props => props.$expanded ? 'pre' : 'pre-wrap'} !important;
    word-break: normal !important;
    overflow-wrap: ${props => props.$expanded ? 'normal' : 'anywhere'} !important;
  }

  & .simulation-editor__pre {
    margin: 0 !important;
    color: #e2e8f0;
    pointer-events: none;
  }

  & .simulation-editor__textarea {
    outline: none !important;
    color: transparent !important;
    caret-color: #e2e8f0;
    resize: ${props => props.$expanded ? 'none' : 'vertical'} !important;
  }

  & .simulation-editor__textarea::selection,
  & .simulation-editor__textarea *::selection {
    background: rgba(56, 189, 248, 0.26);
  }

  & .token.comment,
  & .token.block-comment,
  & .token.prolog,
  & .token.doctype,
  & .token.cdata {
    color: #64748b;
  }

  & .token.property,
  & .token.tag,
  & .token.boolean,
  & .token.number,
  & .token.constant,
  & .token.symbol,
  & .token.deleted {
    color: #f59e0b;
  }

  & .token.selector,
  & .token.attr-name,
  & .token.string,
  & .token.char,
  & .token.builtin,
  & .token.inserted {
    color: #86efac;
  }

  & .token.operator,
  & .token.entity,
  & .token.url,
  & .token.variable {
    color: #cbd5e1;
  }

  & .token.atrule,
  & .token.attr-value,
  & .token.function,
  & .token.class-name {
    color: #7dd3fc;
  }

  & .token.keyword {
    color: #c084fc;
  }

  & .token.regex,
  & .token.important {
    color: #fb7185;
  }

  & .token.punctuation {
    color: #94a3b8;
  }

  &:focus-within {
    outline: none;
    ${props => props.$expanded ? '' : `
      border-color: #38bdf8;
      box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.18);
    `}
  }
`;

const SimulationToolbar = styled.div`
  display: flex;
  flex-direction: ${props => props.$compact ? 'column' : 'row'};
  align-items: ${props => props.$compact ? 'stretch' : 'center'};
  justify-content: ${props => props.$compact ? 'flex-start' : 'space-between'};
  gap: 12px;
  flex-wrap: ${props => props.$compact ? 'nowrap' : 'wrap'};
`;

const SimulationActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: ${props => props.$compact ? 'nowrap' : 'wrap'};
`;

const SimulationRunButton = styled.button`
  border: none;
  border-radius: 12px;
  width: ${props => props.$iconOnly ? '42px' : 'auto'};
  min-width: ${props => props.$iconOnly ? '42px' : '0'};
  height: ${props => props.$iconOnly ? '42px' : 'auto'};
  padding: ${props => props.$iconOnly ? '0' : '11px 16px'};
  background: #2c9fa4;
  color: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$iconOnly ? '0' : '8px'};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #228388;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SimulationSecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  width: ${props => props.$iconOnly ? '42px' : 'auto'};
  min-width: ${props => props.$iconOnly ? '42px' : '0'};
  height: ${props => props.$iconOnly ? '42px' : 'auto'};
  padding: ${props => props.$iconOnly ? '0' : '11px 16px'};
  background: #ffffff;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.$iconOnly ? '0' : '8px'};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    border-color: #93c5fd;
    background: #f8fbff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SimulationModalActionButton = styled(SimulationSecondaryButton)`
  padding: 9px 14px;
`;

const SimulationStatus = styled.div`
  color: ${props => (
    props.$tone === 'muted'
      ? '#64748b'
      : props.$tone === 'error'
        ? '#b91c1c'
        : props.$tone === 'success'
          ? '#166534'
          : '#0f172a'
  )};
  font-size: 0.88rem;
  line-height: 1.5;
`;

const SimulationGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
`;

const SimulationPanelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SimulationPanel = styled.section`
  border: 1px solid #dbe4f0;
  border-radius: 14px;
  background: #f8fafc;
  padding: 14px 16px;
`;

const SimulationPanelTitle = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 800;
`;

const SimulationPanelMeta = styled.p`
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const SimulationCheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 800;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
    accent-color: #2c9fa4;
  }
`;

const SimulationPre = styled.pre`
  margin: 12px 0 0 0;
  padding: 12px;
  border-radius: 12px;
  background: #0f172a;
  color: #e2e8f0;
  font-size: 0.82rem;
  line-height: 1.55;
  font-family: Consolas, "Courier New", monospace;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 90px;
  max-height: 220px;
  overflow: auto;
`;

const SimulationLedRow = styled.div`
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SimulationLedLabel = styled.div`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
`;

const SimulationLedIndicator = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: ${props => props.$active ? '#16a34a' : '#cbd5e1'};
  box-shadow: ${props => props.$active ? '0 0 0 5px rgba(34, 197, 94, 0.16)' : 'none'};
  border: 1px solid ${props => props.$active ? '#15803d' : '#94a3b8'};
`;

const SimulationInteractionList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SimulationInteractionCard = styled.div`
  border: 1px solid ${props => props.$active ? '#86efac' : '#dbe4f0'};
  border-radius: 12px;
  background: ${props => props.$active ? '#f0fdf4' : '#ffffff'};
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SimulationInteractionTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const SimulationInteractionName = styled.div`
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 800;
`;

const SimulationInteractionMeta = styled.div`
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
`;

const SimulationInteractionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${props => props.$active ? '#dcfce7' : '#e2e8f0'};
  color: ${props => props.$active ? '#166534' : '#475569'};
  font-size: 0.72rem;
  font-weight: 800;
`;

const SimulationInteractionButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SimulationInteractionSliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SimulationSliderInput = styled.input`
  flex: 1 1 180px;
  accent-color: #0ea5e9;
`;

const SimulationSliderReadout = styled.div`
  min-width: 64px;
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
  text-align: right;
`;

const SimulationPinGrid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(82px, 1fr));
  gap: 10px;
`;

const SimulationPinCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${props => (
    props.$state === 'high'
      ? '#86efac'
      : props.$state === 'pwm'
        ? '#7dd3fc'
      : props.$state === 'low'
        ? '#cbd5e1'
        : '#bfdbfe'
  )};
  background: ${props => (
    props.$state === 'high'
      ? '#f0fdf4'
      : props.$state === 'pwm'
        ? '#f0f9ff'
      : props.$state === 'low'
        ? '#ffffff'
        : '#eff6ff'
  )};
  padding: 10px;
`;

const SimulationPinName = styled.div`
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 900;
`;

const SimulationPinValue = styled.div`
  margin-top: 4px;
  color: #475569;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SimulationRuntimeGrid = styled.div`
  margin-top: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
`;

const SimulationRuntimeStat = styled.div`
  border: 1px solid #dbe4f0;
  border-radius: 12px;
  background: #ffffff;
  padding: 10px 12px;
`;

const SimulationRuntimeLabel = styled.div`
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SimulationRuntimeValue = styled.div`
  margin-top: 6px;
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 900;
`;

const SimulationDockPanel = styled.div`
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: ${props => (
    props.$lessonMode
      ? 'min(620px, calc(100vh - 120px))'
      : 'calc(100vh - 120px)'
  )};

  @media (max-width: 1024px) {
    max-height: none;
  }
`;

const SimulationDockHeader = styled.div`
  padding: 16px 18px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
`;

const SimulationDockBody = styled.div`
  padding: 16px 18px 18px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SimulationDockCollapsedBody = styled.div`
  padding: 14px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SimulationWarningBox = styled.div`
  border: 1px solid #fde68a;
  background: #fffbeb;
  color: #92400e;
  border-radius: 12px;
  padding: 12px 14px;
  font-size: 0.86rem;
  line-height: 1.5;
  font-weight: 600;

  strong {
    display: block;
    color: #78350f;
    font-size: 0.9rem;
    margin-bottom: 4px;
  }
`;

const ModalCloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #f3f4f6;
  color: #374151;
  border-radius: 999px;
  width: 36px;
  height: 36px;
  cursor: pointer;
  font-size: 0;
  line-height: 0;

  &::before {
    content: '\\00D7';
    font-size: 1.2rem;
    line-height: 1;
  }

  &:hover {
    background: #e5e7eb;
  }
`;

const Copyright = styled.div`
  text-align: center;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.9rem;
`;

const RESISTOR_VALUE_OPTIONS = [
  { label: '10 Ω', value: 10 },
  { label: '22 Ω', value: 22 },
  { label: '47 Ω', value: 47 },
  { label: '100 Ω', value: 100 },
  { label: '220 Ω', value: 220 },
  { label: '330 Ω', value: 330 },
  { label: '470 Ω', value: 470 },
  { label: '1 kΩ', value: 1000 },
  { label: '2.2 kΩ', value: 2200 },
  { label: '4.7 kΩ', value: 4700 },
  { label: '10 kΩ', value: 10000 },
  { label: '22 kΩ', value: 22000 },
  { label: '47 kΩ', value: 47000 },
  { label: '100 kΩ', value: 100000 },
  { label: '1 MΩ', value: 1000000 },
];
const LED_SVG_URL = assetPath('/components/LED-3mm-red-leg.svg');
const SERVO_SVG_URL = assetPath('/components/servo.svg');
const LCD_16X2_SVG_URL = assetPath('/components/lcd-GDM1602K.svg');
const SEVEN_SEGMENT_SVG_URL = assetPath('/components/7-segment display.svg');
const BREADBOARD_SVG_URL = assetPath('/components/breadboard2.svg');
const DEFAULT_SERVO_TRAVEL_DEGREES_PER_SECOND = 180;
const SERVO_ANGLE_ANIMATION_EPSILON_DEGREES = 0.1;
const MAX_SERVO_ANIMATION_FRAME_DELTA_MS = 80;
const LED_COLOR_OPTIONS = [
  { label: 'Red', value: '#E60000' },
  { label: 'Green', value: '#16A34A' },
  { label: 'Blue', value: '#2563EB' },
  { label: 'Yellow', value: '#FACC15' },
  { label: 'Orange', value: '#F97316' },
  { label: 'White', value: '#E5E7EB' },
];
const POTENTIOMETER_VALUE_OPTIONS = [
  { label: '1 kΩ', value: 1000 },
  { label: '5 kΩ', value: 5000 },
  { label: '10 kΩ', value: 10000 },
  { label: '20 kΩ', value: 20000 },
  { label: '50 kΩ', value: 50000 },
  { label: '100 kΩ', value: 100000 },
];
const PROJECT_BUILDER_STORAGE_KEY = 'micropinouts_project_builder_projects_v1';
const PROJECT_BUILDER_LAST_PROJECT_KEY = 'micropinouts_project_builder_last_project_id';
const PROJECT_BUILDER_SCHEMA_VERSION = 1;
const DEFAULT_PROJECT_BUILDER_SETTINGS = Object.freeze({
  arduino_simulation_enabled: false,
});
const API_RESPONSE_PREVIEW_LENGTH = 160;
const LESSON_STARTER_HYDRATION_TIMEOUT_MS = 8000;
const CANVAS_COMPONENT_SLIDER_WIDTH = 92;
const CANVAS_COMPONENT_SLIDER_HEIGHT = 152;
const CANVAS_COMPONENT_SLIDER_GAP = 14;
const CANVAS_COMPONENT_SLIDER_MARGIN = 12;
const SIMULATION_ANALOG_PIN_NAMES = ARDUINO_UNO_PIN_ORDER
  .map((pin) => pin.name)
  .filter((pinName) => /^A[0-5]$/.test(pinName));

const withProjectBuilderTimeout = (promise, timeoutMs, label) => {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return Promise.resolve(promise);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs}ms.`));
    }, timeoutMs);

    Promise.resolve(promise)
      .then((value) => {
        clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
};

const clampSimulationSliderValue = (value, minimum, maximum) => (
  Math.min(maximum, Math.max(minimum, value))
);

const clampUnitInterval = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return clampSimulationSliderValue(numericValue, 0, 1);
};

const normalizeServoTravelDegreesPerSecond = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return DEFAULT_SERVO_TRAVEL_DEGREES_PER_SECOND;
  }

  return numericValue;
};

const buildLogicalConnectionSignature = (connections = []) => (
  (Array.isArray(connections) ? connections : [])
    .map((connection) => [
      String(connection?.id || ''),
      String(connection?.from?.partId || ''),
      String(connection?.from?.pinKey || ''),
      String(connection?.to?.partId || ''),
      String(connection?.to?.pinKey || ''),
    ].join(':'))
    .join('|')
);

const isFiniteCanvasPoint = (point) => (
  point &&
  Number.isFinite(point.x) &&
  Number.isFinite(point.y)
);

const getProjectComponentDisplayName = (component) => (
  component?.board?.name ||
  component?.name ||
  'Component'
);

const buildBreadboardPlacementConnections = (components = [], pinLayoutByPartId = {}) => {
  const safeComponents = Array.isArray(components) ? components : [];
  const snapRadiusSquared = BREADBOARD_COMPONENT_CONTACT_RADIUS * BREADBOARD_COMPONENT_CONTACT_RADIUS;
  const breadboardTargets = safeComponents
    .filter((component) => isComponentVisible(component) && isBreadboardComponent(component))
    .flatMap((component) => (
      Object.entries(pinLayoutByPartId?.[component.id] || {})
        .filter(([, point]) => isFiniteCanvasPoint(point))
        .map(([pinKey, point]) => ({
          component,
          pinKey,
          point,
        }))
    ));

  if (breadboardTargets.length === 0) {
    return [];
  }

  return safeComponents
    .filter((component) => (
      isComponentVisible(component) &&
      component?.type === 'passive' &&
      !isBreadboardComponent(component)
    ))
    .flatMap((component) => (
      Object.entries(pinLayoutByPartId?.[component.id] || {})
        .filter(([, point]) => isFiniteCanvasPoint(point))
        .map(([pinKey, point]) => {
          let bestTarget = null;
          let bestDistanceSquared = Infinity;

          breadboardTargets.forEach((target) => {
            const deltaX = target.point.x - point.x;
            const deltaY = target.point.y - point.y;
            const distanceSquared = (deltaX * deltaX) + (deltaY * deltaY);

            if (
              distanceSquared <= snapRadiusSquared &&
              distanceSquared < bestDistanceSquared
            ) {
              bestTarget = target;
              bestDistanceSquared = distanceSquared;
            }
          });

          if (!bestTarget) {
            return null;
          }

          return {
            id: [
              'breadboard-contact',
              component.id,
              pinKey,
              bestTarget.component.id,
              bestTarget.pinKey,
            ].join(':'),
            implicit: true,
            isBreadboardPlacementContact: true,
            from: {
              partId: component.id,
              boardName: getProjectComponentDisplayName(component),
              pinKey,
            },
            to: {
              partId: bestTarget.component.id,
              boardName: getProjectComponentDisplayName(bestTarget.component),
              pinKey: bestTarget.pinKey,
            },
          };
        })
        .filter(Boolean)
    ));
};

const rotateCanvasPoint = (x, y, centerX, centerY, rotationDegrees) => {
  const angle = (Number(rotationDegrees || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const deltaX = x - centerX;
  const deltaY = y - centerY;

  return {
    x: centerX + (deltaX * cos) - (deltaY * sin),
    y: centerY + (deltaX * sin) + (deltaY * cos),
  };
};

const getCanvasSliderOverlayLayout = ({
  canvasHeight,
  canvasWidth,
  componentX,
  componentY,
  footprintHeight,
  footprintWidth,
  rotation,
  visibleBounds,
}) => {
  if (!visibleBounds) {
    return null;
  }

  const centerX = footprintWidth / 2;
  const centerY = footprintHeight / 2;
  const corners = [
    { x: visibleBounds.x, y: visibleBounds.y },
    { x: visibleBounds.x + visibleBounds.width, y: visibleBounds.y },
    { x: visibleBounds.x + visibleBounds.width, y: visibleBounds.y + visibleBounds.height },
    { x: visibleBounds.x, y: visibleBounds.y + visibleBounds.height },
  ].map((point) => rotateCanvasPoint(point.x, point.y, centerX, centerY, rotation));

  const xValues = corners.map((point) => point.x);
  const yValues = corners.map((point) => point.y);
  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const minLocalX = CANVAS_COMPONENT_SLIDER_MARGIN - componentX;
  const maxLocalX = (
    canvasWidth -
    CANVAS_COMPONENT_SLIDER_MARGIN -
    CANVAS_COMPONENT_SLIDER_WIDTH -
    componentX
  );
  const minLocalY = CANVAS_COMPONENT_SLIDER_MARGIN - componentY;
  const maxLocalY = (
    canvasHeight -
    CANVAS_COMPONENT_SLIDER_MARGIN -
    CANVAS_COMPONENT_SLIDER_HEIGHT -
    componentY
  );
  const preferredRightX = maxX + CANVAS_COMPONENT_SLIDER_GAP;
  const preferredLeftX = minX - CANVAS_COMPONENT_SLIDER_GAP - CANVAS_COMPONENT_SLIDER_WIDTH;
  const unclampedX = (
    componentX + preferredRightX + CANVAS_COMPONENT_SLIDER_WIDTH <= canvasWidth - CANVAS_COMPONENT_SLIDER_MARGIN
  )
    ? preferredRightX
    : preferredLeftX;
  const clampedX = clampSimulationSliderValue(
    unclampedX,
    Math.min(minLocalX, maxLocalX),
    Math.max(minLocalX, maxLocalX)
  );
  const preferredY = minY + ((maxY - minY - CANVAS_COMPONENT_SLIDER_HEIGHT) / 2);
  const clampedY = clampSimulationSliderValue(
    preferredY,
    Math.min(minLocalY, maxLocalY),
    Math.max(minLocalY, maxLocalY)
  );

  return {
    height: CANVAS_COMPONENT_SLIDER_HEIGHT,
    width: CANVAS_COMPONENT_SLIDER_WIDTH,
    x: clampedX,
    y: clampedY,
  };
};

const formatSimulationSliderNumber = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  const absoluteValue = Math.abs(numericValue);

  if (absoluteValue >= 100) {
    return String(Math.round(numericValue));
  }

  if (absoluteValue >= 10) {
    return String(Math.round(numericValue * 10) / 10);
  }

  return String(Math.round(numericValue * 100) / 100);
};

const formatSimulationResistanceText = (valueOhms) => {
  const numericValue = Number(valueOhms);

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  if (numericValue >= 1000000) {
    return `${Math.round((numericValue / 1000000) * 100) / 100} MOhm`;
  }

  if (numericValue >= 1000) {
    return `${Math.round((numericValue / 1000) * 100) / 100} kOhm`;
  }

  return `${Math.round(numericValue * 100) / 100} ohm`;
};

const formatSimulationStimulusText = (value, unit = '') => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return unit ? `0 ${unit}` : '0';
  }

  const formattedValue = formatSimulationSliderNumber(numericValue);
  return unit ? `${formattedValue} ${unit}` : formattedValue;
};

const getSimulationSliderState = (simulationModel, totalResistanceOhms = null) => {
  const parameters = simulationModel?.parameters || {};
  const variableResistorState = resolveVariableResistorSimulationState(
    parameters,
    { totalResistanceOhms }
  );
  const stimulusLabel = String(variableResistorState.stimulusLabel || '').trim() || 'Stimulus';
  const stimulusUnit = String(variableResistorState.stimulusUnit || '').trim();
  const stimulusReadout = formatSimulationStimulusText(
    variableResistorState.controlValue,
    stimulusUnit
  );
  const resistanceReadout = formatSimulationResistanceText(
    variableResistorState.resolvedResistanceOhms
  );

  if (variableResistorState.hasStimulusControl) {
    return {
      controlHintText: resistanceReadout ? `Resistance: ${resistanceReadout}` : 'Resistance not resolved.',
      controlReadoutText: stimulusReadout,
      controlValue: variableResistorState.controlValue,
      label: stimulusLabel,
      max: variableResistorState.controlMax,
      min: variableResistorState.controlMin,
      percent: variableResistorState.controlPercent,
      position: variableResistorState.position,
      ratio: variableResistorState.positionRatio,
      resistanceText: resistanceReadout,
      runtimeText: resistanceReadout
        ? `${stimulusReadout} -> ${resistanceReadout}`
        : stimulusReadout,
      step: variableResistorState.controlStep,
    };
  }

  return {
    controlHintText: `Position: ${variableResistorState.position.toFixed(2)}`,
    controlReadoutText: `${variableResistorState.controlPercent}%`,
    controlValue: variableResistorState.controlValue,
    label: 'Position',
    max: variableResistorState.controlMax,
    min: variableResistorState.controlMin,
    percent: variableResistorState.controlPercent,
    position: variableResistorState.position,
    ratio: variableResistorState.positionRatio,
    resistanceText: resistanceReadout,
    runtimeText: `${variableResistorState.controlPercent}% (${variableResistorState.position.toFixed(2)})`,
    step: variableResistorState.controlStep,
  };
};

const getSimulationSliderCopy = (simulationModel, totalResistanceOhms = null) => {
  const variableResistorState = resolveVariableResistorSimulationState(
    simulationModel?.parameters || {},
    { totalResistanceOhms }
  );

  if (variableResistorState.hasStimulusControl) {
    const stimulusLabel = String(variableResistorState.stimulusLabel || '').trim() || 'Stimulus';
    const formulaHint = variableResistorState.stimulusFormula
      ? ' A custom formula maps that stimulus into the active resistance.'
      : ' A linear mapping turns that stimulus into the active resistance.';

    return {
      canvasHintPrefix: stimulusLabel,
      canvasLabel: stimulusLabel,
      inspectorDescription: `This simulates ${stimulusLabel.toLowerCase()} for a variable-resistor sensor.${formulaHint}`,
      inspectorLabel: stimulusLabel,
    };
  }

  const terminalRoles = new Set(
    Object.values(simulationModel?.terminalRoles || {})
      .map((role) => String(role || '').trim().toLowerCase())
      .filter(Boolean)
  );
  const isPotentiometerModel = (
    terminalRoles.has('end_a') &&
    terminalRoles.has('end_b') &&
    terminalRoles.has('wiper')
  );

  return isPotentiometerModel
    ? {
        canvasHintPrefix: 'Wiper',
        canvasLabel: 'Knob',
        inspectorDescription: 'This simulates turning the potentiometer knob and updates the wiper position used by `analogRead()`.',
        inspectorLabel: 'Knob Position',
      }
    : {
        canvasHintPrefix: 'Position',
        canvasLabel: 'Slider',
        inspectorDescription: 'This simulates changing the variable resistance used by the analog circuit model.',
        inspectorLabel: 'Slider Position',
      };
};

const getPassiveNoSimulationTag = (item) => Boolean(item?.showNoSimulationTag);

const createInitialSimulationPinStates = () => (
  Object.fromEntries(
    ARDUINO_UNO_PIN_ORDER.map((pin) => [
      pin.name,
      {
        ...pin,
        logicLevel: 'low',
        mode: 'input',
        rawState: null,
        sampledHigh: false,
        state: 'input_low',
        isHigh: false,
        isOutput: false,
        outputMode: 'input',
        outputVoltageRatio: null,
        averageVoltage: null,
        pwmChannel: '',
        pwmDutyCycle: null,
        pwmEnabled: false,
        pwmTimer: '',
        pwmValue: null,
        toneAgeMicros: null,
        toneDetected: false,
        toneEdgeSampleCount: 0,
        toneFrequencyHz: null,
      },
    ])
  )
);

const getTrackedLcdTransitionPinNames = (simulationCircuitAnalysis) => (
  Array.from(
    new Set(
      (Array.isArray(simulationCircuitAnalysis?.lcdStates)
        ? simulationCircuitAnalysis.lcdStates
        : []
      ).flatMap((lcdState) => (
        ['rs', 'rw', 'e', 'd4', 'd5', 'd6', 'd7']
          .map((signalKey) => String(lcdState?.signals?.[signalKey]?.pinName || '').trim())
          .filter(Boolean)
      ))
    )
  )
);

const buildSimulationCompilerOutput = (result) => {
  const sections = [
    typeof result?.stdout === 'string' ? result.stdout.trim() : '',
    typeof result?.stderr === 'string' ? result.stderr.trim() : '',
  ].filter(Boolean);

  return sections.join('\n\n').trim();
};

const compactSketchWhitespace = (source) => String(source || '')
  .replace(/\s+/g, ' ')
  .trim();

const normalizeArduinoExamplePinToken = (pinName) => {
  const normalizedPinName = String(pinName || 'LED_BUILTIN').trim().toUpperCase();

  if (!normalizedPinName || normalizedPinName === 'LED_BUILTIN') {
    return 'LED_BUILTIN';
  }

  const digitalMatch = normalizedPinName.match(/^D(\d{1,2})$/);

  if (digitalMatch) {
    return String(Number(digitalMatch[1]));
  }

  const analogMatch = normalizedPinName.match(/^A([0-5])$/);

  if (analogMatch) {
    return `A${analogMatch[1]}`;
  }

  return normalizedPinName;
};

const buildArduinoBlinkExampleSketch = (pinName = 'LED_BUILTIN') => {
  const pinToken = normalizeArduinoExamplePinToken(pinName);

  return [
    'void setup() {',
    `  pinMode(${pinToken}, OUTPUT);`,
    '}',
    '',
    'void loop() {',
    `  digitalWrite(${pinToken}, HIGH);`,
    '  delay(1000);',
    `  digitalWrite(${pinToken}, LOW);`,
    '  delay(1000);',
    '}',
  ].join('\n');
};

const AUTO_ARDUINO_BLINK_SKETCH_PATTERN = /^void setup\(\) \{ pinMode\(([^,]+), OUTPUT\); \} void loop\(\) \{ digitalWrite\(\1, HIGH\); delay\(1000\); digitalWrite\(\1, LOW\); delay\(1000\); \}$/;

const isAutoGeneratedBlinkSketch = (source) => (
  AUTO_ARDUINO_BLINK_SKETCH_PATTERN.test(compactSketchWhitespace(source))
);

const normalizeArduinoSketchSource = (source) => (
  String(source || '').replace(/\r\n?/g, '\n')
);

const getPersistableArduinoSketchSource = (source) => {
  const normalizedSource = normalizeArduinoSketchSource(source);

  if (!normalizedSource.trim() || isAutoGeneratedBlinkSketch(normalizedSource)) {
    return '';
  }

  return normalizedSource;
};

const getSavedProjectArduinoSketchSource = (project) => {
  const savedCode = normalizeArduinoSketchSource(
    project?.simulation?.code || project?.simulationCode || ''
  );

  return savedCode.trim()
    ? savedCode
    : DEFAULT_ARDUINO_SIMULATION_SKETCH;
};

const getCssSafeId = (value) => (
  String(value || '')
    .replace(/[^a-zA-Z0-9_-]/g, '_')
);

const addSvgRootClass = (svgMarkup, className) => {
  const markup = String(svgMarkup || '');

  if (!markup || !className) {
    return markup;
  }

  return markup.replace(/<svg\b([^>]*)>/i, (match, attrs = '') => {
    const classMatch = attrs.match(/\bclass=(["'])(.*?)\1/i);

    if (classMatch) {
      const quote = classMatch[1];
      const existingClass = classMatch[2];

      return match.replace(
        classMatch[0],
        `class=${quote}${existingClass} ${className}${quote}`
      );
    }

    return `<svg${attrs} class="${className}">`;
  });
};

const hexColorToRgb = (color) => {
  const normalized = String(color || '').trim();
  const shortHexMatch = normalized.match(/^#([0-9a-f]{3})$/i);
  const fullHexMatch = normalized.match(/^#([0-9a-f]{6})$/i);

  if (shortHexMatch) {
    const [r, g, b] = shortHexMatch[1].split('');
    return {
      r: Number.parseInt(`${r}${r}`, 16),
      g: Number.parseInt(`${g}${g}`, 16),
      b: Number.parseInt(`${b}${b}`, 16),
    };
  }

  if (fullHexMatch) {
    const hex = fullHexMatch[1];

    return {
      r: Number.parseInt(hex.slice(0, 2), 16),
      g: Number.parseInt(hex.slice(2, 4), 16),
      b: Number.parseInt(hex.slice(4, 6), 16),
    };
  }

  return null;
};

const rgbToHexColor = ({ r, g, b }) => (
  `#${[r, g, b]
    .map((value) => (
      Math.min(255, Math.max(0, Math.round(value)))
        .toString(16)
        .padStart(2, '0')
    ))
    .join('')}`
);

const mixHexColors = (baseColor, mixColor, ratio = 0.5) => {
  const baseRgb = hexColorToRgb(baseColor);
  const mixRgb = hexColorToRgb(mixColor);
  const mixRatio = clampUnitInterval(ratio);

  if (!baseRgb || !mixRgb) {
    return baseColor || mixColor || '#ffffff';
  }

  return rgbToHexColor({
    r: (baseRgb.r * (1 - mixRatio)) + (mixRgb.r * mixRatio),
    g: (baseRgb.g * (1 - mixRatio)) + (mixRgb.g * mixRatio),
    b: (baseRgb.b * (1 - mixRatio)) + (mixRgb.b * mixRatio),
  });
};

const buildActiveLedPreviewCss = (color, scopeSelector = '', brightness = 1) => {
  const scope = scopeSelector ? `${scopeSelector} ` : '';
  const intensity = clampUnitInterval(brightness);
  const glowTight = (1.5 + (intensity * 3)).toFixed(2);
  const glowMedium = (4 + (intensity * 7)).toFixed(2);
  const glowWide = (7 + (intensity * 10)).toFixed(2);
  const pulseTight = (glowTight * 1.35).toFixed(2);
  const pulseMedium = (glowMedium * 1.28).toFixed(2);
  const pulseWide = (glowWide * 1.22).toFixed(2);
  const filterBrightness = (1 + (intensity * 0.18)).toFixed(3);
  const pulseBrightness = (1 + (intensity * 0.30)).toFixed(3);
  const filterSaturation = (1 + (intensity * 0.55)).toFixed(3);
  const pulseSaturation = (1 + (intensity * 0.72)).toFixed(3);
  const pulseName = `pb-led-glow-pulse-${String(color || 'active').replace(/[^a-z0-9_-]/gi, '') || 'active'}`;
  const outerShellColor = mixHexColors(color, '#ffffff', 0.54 - (intensity * 0.16));
  const lowerShellColor = mixHexColors(color, '#f4f4f5', 0.46 - (intensity * 0.14));
  const rimColor = mixHexColors(color, '#fafafa', 0.62 - (intensity * 0.18));
  const topTintOpacity = (0.32 + (intensity * 0.48)).toFixed(3);
  const midTintOpacity = (0.72 + (intensity * 0.20)).toFixed(3);
  const shadowTintOpacity = (0.28 + (intensity * 0.42)).toFixed(3);
  const rimTintOpacity = (0.24 + (intensity * 0.56)).toFixed(3);
  const shellOpacity = (0.56 - (intensity * 0.22)).toFixed(3);
  const lowerShellOpacity = (0.88 - (intensity * 0.46)).toFixed(3);
  const neutralRimOpacity = (0.20 - (intensity * 0.08)).toFixed(3);

  return `
    @keyframes ${pulseName} {
      0% {
        filter:
          saturate(${filterSaturation})
          brightness(${filterBrightness})
          drop-shadow(0 0 ${glowTight}px ${color})
          drop-shadow(0 0 ${glowMedium}px ${color})
          drop-shadow(0 0 ${glowWide}px ${color});
      }

      100% {
        filter:
          saturate(${pulseSaturation})
          brightness(${pulseBrightness})
          drop-shadow(0 0 ${pulseTight}px ${color})
          drop-shadow(0 0 ${pulseMedium}px ${color})
          drop-shadow(0 0 ${pulseWide}px ${color});
      }
    }

    ${scope}#g12 {
      filter:
        saturate(${filterSaturation})
        brightness(${filterBrightness})
        drop-shadow(0 0 ${glowTight}px ${color})
        drop-shadow(0 0 ${glowMedium}px ${color})
        drop-shadow(0 0 ${glowWide}px ${color});
      animation: ${pulseName} 1.25s ease-in-out infinite alternate;
    }

    @media (prefers-reduced-motion: reduce) {
      ${scope}#g12 {
        animation: none;
      }
    }

    ${scope}#path16 {
      fill: ${outerShellColor} !important;
      opacity: ${shellOpacity} !important;
    }

    ${scope}#path18 {
      fill: ${lowerShellColor} !important;
      opacity: ${lowerShellOpacity} !important;
    }

    ${scope}#ellipse24 {
      fill: ${rimColor} !important;
      opacity: ${neutralRimOpacity} !important;
    }

    ${scope}#color_path14 {
      fill: ${color} !important;
      opacity: ${topTintOpacity} !important;
    }

    ${scope}#color_path20,
    ${scope}#color_path32 {
      fill: ${color} !important;
      opacity: ${midTintOpacity} !important;
    }

    ${scope}#color_path22,
    ${scope}#color_ellipse26 {
      fill: ${color} !important;
      opacity: ${rimTintOpacity} !important;
    }

    ${scope}#color_path22 {
      opacity: ${shadowTintOpacity} !important;
    }
  `;
};

const formatSimulationPotentialSummary = (potentials) => {
  const labels = Array.from(
    new Set(
      (Array.isArray(potentials) ? potentials : [])
        .map((potential) => (
          potential?.label
            ? `${String(potential.level || 'unknown').toUpperCase()} ${potential.label}`
            : ''
        ))
        .filter(Boolean)
    )
  );

  return labels.length > 0 ? labels.join(', ') : 'none';
};

const formatSimulationBoardPinSummary = (pins) => {
  const labels = Array.from(
    new Set(
      (Array.isArray(pins) ? pins : [])
        .map((pin) => {
          const label = String(pin?.resolvedLabel || pin?.displayName || 'unknown').trim();
          const componentName = String(pin?.componentName || 'Board').trim();
          const canvasPinBase = String(pin?.canvasPinBase || pin?.canvasPinKey || '').trim();
          const canvasSuffix = canvasPinBase ? ` [canvas ${canvasPinBase}]` : '';

          return `${componentName}: ${label}${canvasSuffix}`;
        })
        .filter(Boolean)
    )
  );

  return labels.length > 0 ? labels.join('; ') : 'none';
};

const getSimulationInteractionLabel = (interactionType) => {
  switch (String(interactionType || 'none')) {
    case 'momentary_press':
      return 'Momentary press';
    case 'toggle':
      return 'Toggle switch';
    case 'slider':
      return 'Slider input';
    case 'none':
    default:
      return 'No interaction';
  }
};

const formatSimulationAnalogVoltage = (voltage) => {
  const numericVoltage = Number(voltage);

  if (!Number.isFinite(numericVoltage)) {
    return 'unknown';
  }

  return `${numericVoltage.toFixed(2)}V`;
};

const formatSevenSegmentStateSummary = (segmentStates) => {
  const summaries = (Array.isArray(segmentStates) ? segmentStates : [])
    .map((digitState, index) => {
      const activeSegments = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'dp']
        .filter((segmentKey) => Boolean(digitState?.[segmentKey]));

      return `${index + 1}:${activeSegments.length > 0 ? activeSegments.join('') : 'off'}`;
    })
    .filter(Boolean);

  return summaries.length > 0 ? summaries.join(' / ') : 'none';
};

const formatSevenSegmentPinConnectionSummary = (pinSources) => {
  const summary = Object.entries(pinSources || {})
    .map(([pinKey, source]) => {
      const boardPinSummary = formatSimulationBoardPinSummary(source?.boardPins);
      return boardPinSummary === 'none' ? null : `${pinKey}:${boardPinSummary}`;
    })
    .filter(Boolean);

  return summary.length > 0 ? summary.join(', ') : 'none';
};

const formatSimulationOutputState = (pinState) => {
  const toneFrequencyHz = Number(pinState?.toneFrequencyHz);
  const toneSuffix = pinState?.toneDetected && Number.isFinite(toneFrequencyHz)
    ? ` | tone ${Math.round(toneFrequencyHz)} Hz`
    : '';

  if (pinState?.outputMode === 'pwm') {
    const dutyCyclePercent = Math.round(clampUnitInterval(pinState.pwmDutyCycle) * 100);
    const averageVoltage = Number(pinState.averageVoltage);
    const voltageText = Number.isFinite(averageVoltage)
      ? ` · ${formatSimulationAnalogVoltage(averageVoltage)} avg`
      : '';

    return `PWM ${dutyCyclePercent}%${voltageText}${toneSuffix}`;
  }

  if (pinState?.isOutput) {
    return `OUTPUT ${pinState.isHigh ? 'HIGH' : 'LOW'}${toneSuffix}`;
  }

  return '';
};

const formatSimulationDigitalInputState = (inputAnalysis) => {
  const status = String(inputAnalysis?.status || 'floating').trim().toLowerCase();

  if (status === 'high') {
    return `HIGH (${formatSimulationPotentialSummary(inputAnalysis?.potentials)})`;
  }

  if (status === 'low') {
    return `LOW (${formatSimulationPotentialSummary(inputAnalysis?.potentials)})`;
  }

  if (status === 'conflict') {
    return `CONFLICT (${formatSimulationPotentialSummary(inputAnalysis?.potentials)})`;
  }

  return 'FLOATING';
};

const formatSimulationAnalogInputState = (inputAnalysis) => {
  const status = String(inputAnalysis?.status || 'floating').trim().toLowerCase();

  if (status === 'resolved' && Number.isFinite(Number(inputAnalysis?.voltage))) {
    const sourceLabels = Array.from(
      new Set(
        (Array.isArray(inputAnalysis?.sourceAnalyses) ? inputAnalysis.sourceAnalyses : [])
          .map((analysis) => String(analysis?.componentName || '').trim())
          .filter(Boolean)
      )
    );
    const sourceSuffix = sourceLabels.length > 0
      ? ` via ${sourceLabels.join(', ')}`
      : '';

    return `ADC ${inputAnalysis?.adcValue ?? '?'} (${formatSimulationAnalogVoltage(inputAnalysis.voltage)})${sourceSuffix}`;
  }

  if (status === 'conflict') {
    return `CONFLICT (${formatSimulationPotentialSummary(inputAnalysis?.potentials)})`;
  }

  return 'FLOATING';
};

const normalizeSimulationPinName = (value) => {
  const text = String(value || '').trim().toUpperCase();

  if (!text) {
    return '';
  }

  if (text === 'LED_BUILTIN') {
    return 'D13';
  }

  const digitalMatch = text.match(/\bD?([0-9]{1,2})\b/);

  if (digitalMatch) {
    const pinNumber = Number(digitalMatch[1]);

    if (pinNumber >= 0 && pinNumber <= 13) {
      return `D${pinNumber}`;
    }
  }

  const analogMatch = text.match(/\bA([0-5])\b/);
  if (analogMatch) {
    return `A${analogMatch[1]}`;
  }

  return '';
};

const getLedTerminalPinState = (boardPins = [], pinStates = {}) => {
  for (const boardPin of Array.isArray(boardPins) ? boardPins : []) {
    const candidates = [
      boardPin.pinName,
      boardPin.resolvedLabel,
      boardPin.displayName,
      boardPin.canvasPinBase,
      boardPin.canvasPinKey,
    ];

    for (const candidate of candidates) {
      const pinName = normalizeSimulationPinName(candidate);

      if (pinName && pinStates[pinName]) {
        return pinStates[pinName];
      }
    }
  }

  return null;
};

const getLedTerminalFixedLevel = (potentials = [], boardPins = []) => {
  const text = [
    ...(Array.isArray(potentials) ? potentials : []).map((potential) => (
      `${potential?.level || ''} ${potential?.label || ''}`
    )),
    ...(Array.isArray(boardPins) ? boardPins : []).map((pin) => (
      `${pin?.resolvedLabel || ''} ${pin?.displayName || ''} ${pin?.canvasPinBase || ''}`
    )),
  ].join(' ').toUpperCase();

  if (/\b(GND|GROUND|0V)\b/.test(text)) {
    return false;
  }

  if (/\b(5V|VCC|VIN|3V3|3\.3V|HIGH)\b/.test(text)) {
    return true;
  }

  return null;
};

const getLedTerminalVoltageLevel = ({
  boardPins,
  potentials,
  pinStates,
}) => {
  const outputPinState = getLedTerminalPinState(boardPins, pinStates);

  if (outputPinState?.isOutput) {
    const outputVoltageRatio = Number(outputPinState.outputVoltageRatio);

    if (Number.isFinite(outputVoltageRatio)) {
      return clampUnitInterval(outputVoltageRatio);
    }

    return outputPinState.isHigh ? 1 : 0;
  }

  const fixedLevel = getLedTerminalFixedLevel(potentials, boardPins);

  return typeof fixedLevel === 'boolean'
    ? (fixedLevel ? 1 : 0)
    : null;
};

const resolveLedBrightnessFromSimulationPins = (ledPath, pinStates) => {
  const anodeLevel = getLedTerminalVoltageLevel({
    boardPins: ledPath.anodeBoardPins,
    potentials: ledPath.anodePotentials,
    pinStates,
  });

  const cathodeLevel = getLedTerminalVoltageLevel({
    boardPins: ledPath.cathodeBoardPins,
    potentials: ledPath.cathodePotentials,
    pinStates,
  });

  if (!Number.isFinite(anodeLevel) || !Number.isFinite(cathodeLevel)) {
    return ledPath.isOn ? 1 : 0;
  }

  return clampUnitInterval(anodeLevel - cathodeLevel);
};

const resolveLedIsOnFromSimulationPins = (ledPath, pinStates) => (
  resolveLedBrightnessFromSimulationPins(ledPath, pinStates) > 0.01
);

const buildSimulationCircuitDebugText = (
  analysis,
  connections = [],
  interactionSummaries = []
) => {
  const formatConnectionPinKey = (pinKey) => String(pinKey || '').replace(/__\d+$/, '');
  const unoBoards = Array.isArray(analysis?.unoBoards) ? analysis.unoBoards : [];
  const analogInputsByPinName = analysis?.analogInputsByPinName || {};
  const buzzerStates = Array.isArray(analysis?.buzzerStates) ? analysis.buzzerStates : [];
  const digitalInputsByPinName = analysis?.digitalInputsByPinName || {};
  const lcdStates = Array.isArray(analysis?.lcdStates) ? analysis.lcdStates : [];
  const ledPaths = Array.isArray(analysis?.ledPaths) ? analysis.ledPaths : [];
  const sevenSegmentStates = Array.isArray(analysis?.sevenSegmentStates) ? analysis.sevenSegmentStates : [];
  const servoStates = Array.isArray(analysis?.servoStates) ? analysis.servoStates : [];
  const safeInteractionSummaries = Array.isArray(interactionSummaries)
    ? interactionSummaries
    : [];
  const wireRows = (Array.isArray(connections) ? connections : [])
    .map((connection) => {
      const fromBoard = String(connection?.from?.boardName || 'Component').trim();
      const fromPin = formatConnectionPinKey(connection?.from?.pinKey);
      const toBoard = String(connection?.to?.boardName || 'Component').trim();
      const toPin = formatConnectionPinKey(connection?.to?.pinKey);

      return `${fromBoard}: ${fromPin} -> ${toBoard}: ${toPin}`;
    })
    .filter(Boolean);

  if (unoBoards.length === 0) {
    return 'No Arduino Uno detected on the canvas.';
  }

  const lines = [
    `Detected Uno boards: ${unoBoards.map((board) => board.boardName || board.componentName).join(', ')}`,
    `Preferred Blink target: ${analysis?.preferredExternalLedPinName || 'none detected yet'}`,
    '',
  ];

  if (wireRows.length > 0) {
    lines.push('Connections panel wires:');
    wireRows.forEach((wireRow, index) => {
      lines.push(`${index + 1}. ${wireRow}`);
    });
    lines.push('');
  } else {
    lines.push('Connections panel wires: none');
    lines.push('');
  }

  if (safeInteractionSummaries.length > 0) {
    lines.push('Interactive parts:');
    safeInteractionSummaries.forEach((entry, index) => {
      lines.push(`${index + 1}. ${entry.componentName || `Interactive Part ${index + 1}`}`);
      lines.push(`   Interaction: ${entry.interactionLabel || entry.interaction || 'Unknown'}`);
      lines.push(`   Runtime state: ${entry.runtimeStateLabel || (entry.isActive ? 'ACTIVE' : 'IDLE')}`);
      lines.push(`   Terminals: ${entry.terminalRoles || 'none'}`);
      if (entry.notes) {
        lines.push(`   Notes: ${entry.notes}`);
      }
      lines.push('');
    });
  } else {
    lines.push('Interactive parts: none');
    lines.push('');
  }

  const drivenInputRows = Object.values(digitalInputsByPinName)
    .filter((inputAnalysis) => String(inputAnalysis?.status || 'floating').trim().toLowerCase() !== 'floating');

  if (drivenInputRows.length > 0) {
    lines.push('Resolved Uno external inputs:');
    drivenInputRows.forEach((inputAnalysis) => {
      lines.push(
        `${inputAnalysis.pinName}: ${formatSimulationDigitalInputState(inputAnalysis)}`
      );
    });
    lines.push('');
  }

  const drivenAnalogRows = Object.values(analogInputsByPinName)
    .filter((inputAnalysis) => String(inputAnalysis?.status || 'floating').trim().toLowerCase() !== 'floating');

  if (drivenAnalogRows.length > 0) {
    lines.push('Resolved Uno analog inputs:');
    drivenAnalogRows.forEach((inputAnalysis) => {
      lines.push(
        `${inputAnalysis.pinName}: ${formatSimulationAnalogInputState(inputAnalysis)}`
      );
    });
    lines.push('');
  }

  if (servoStates.length > 0) {
    lines.push('Servo outputs:');

    servoStates.forEach((servoState, index) => {
      lines.push(`${index + 1}. ${servoState.componentName || `Servo ${index + 1}`}`);
      lines.push(`   Wiring status: ${servoState.driveDescription || 'Unknown'}`);
      lines.push(`   Signal reaches: ${formatSimulationBoardPinSummary(servoState.signalBoardPins)}`);
      lines.push(`   Power reaches: ${formatSimulationBoardPinSummary(servoState.powerBoardPins)}`);
      lines.push(`   Ground reaches: ${formatSimulationBoardPinSummary(servoState.groundBoardPins)}`);
      lines.push(`   Drive mode: ${servoState.driveMode || 'floating'}`);
      lines.push(`   Horn angle: ${Math.round(Number(servoState.angleDegrees) || 0)} deg`);
      lines.push('');
    });
  }

  if (buzzerStates.length > 0) {
    lines.push('Buzzer outputs:');

    buzzerStates.forEach((buzzerState, index) => {
      const detectedFrequencyHz = Number(buzzerState.estimatedFrequencyHz);
      const runtimeState = buzzerState.isActive && Number.isFinite(detectedFrequencyHz)
        ? `ACTIVE (${Math.round(detectedFrequencyHz)} Hz)`
        : 'SILENT';

      lines.push(`${index + 1}. ${buzzerState.componentName || `Buzzer ${index + 1}`}`);
      lines.push(`   Wiring status: ${buzzerState.driveDescription || 'Unknown'}`);
      lines.push(`   Signal reaches: ${formatSimulationBoardPinSummary(buzzerState.signalBoardPins)}`);
      lines.push(`   Ground reaches: ${formatSimulationBoardPinSummary(buzzerState.groundBoardPins)}`);
      lines.push(`   Runtime state: ${runtimeState}`);
      lines.push('');
    });
  }

  if (lcdStates.length > 0) {
    lines.push('LCD outputs:');

    lcdStates.forEach((lcdState, index) => {
      lines.push(`${index + 1}. ${lcdState.componentName || `LCD ${index + 1}`}`);
      lines.push(`   Wiring status: ${lcdState.driveDescription || 'Unknown'}`);
      lines.push(`   Powered: ${lcdState.isPowered ? 'YES' : 'NO'}`);
      lines.push(`   4-bit bus resolved: ${lcdState.is4BitBusResolved ? 'YES' : 'NO'}`);
      lines.push(`   RS reaches: ${formatSimulationBoardPinSummary(lcdState.signals?.rs?.boardPins)}`);
      lines.push(`   E reaches: ${formatSimulationBoardPinSummary(lcdState.signals?.e?.boardPins)}`);
      lines.push(
        `   Data reaches: D4 ${formatSimulationBoardPinSummary(lcdState.signals?.d4?.boardPins)}, D5 ${formatSimulationBoardPinSummary(lcdState.signals?.d5?.boardPins)}, D6 ${formatSimulationBoardPinSummary(lcdState.signals?.d6?.boardPins)}, D7 ${formatSimulationBoardPinSummary(lcdState.signals?.d7?.boardPins)}`
      );
      lines.push('');
    });
  }

  if (sevenSegmentStates.length > 0) {
    lines.push('7-segment displays:');

    sevenSegmentStates.forEach((displayState, index) => {
      lines.push(`${index + 1}. ${displayState.componentName || `7-Segment Display ${index + 1}`}`);
      lines.push(`   Digits: ${displayState.digitCount || 1}`);
      lines.push(`   Type: ${displayState.commonTypeLabel || 'Common Cathode'}`);
      lines.push(`   Wiring status: ${displayState.driveDescription || 'Unknown'}`);
      lines.push(`   Powered: ${displayState.isPowered ? 'YES' : 'NO'}`);
      lines.push(`   Connected segment pins: ${formatSevenSegmentPinConnectionSummary(displayState.segmentPins)}`);
      lines.push(`   Connected digit pins: ${formatSevenSegmentPinConnectionSummary(displayState.digitPins)}`);
      lines.push(`   Active segment pins: ${(displayState.activeSegmentPins || []).join(', ') || 'none'}`);
      lines.push(`   Active digit pins: ${(displayState.activeDigitPins || []).join(', ') || 'none'}`);
      lines.push(`   Segment state: ${formatSevenSegmentStateSummary(displayState.segmentStates)}`);
      lines.push('');
    });
  }

  if (ledPaths.length === 0) {
    if (
      servoStates.length === 0 &&
      buzzerStates.length === 0 &&
      lcdStates.length === 0 &&
      sevenSegmentStates.length === 0
    ) {
      lines.push('No LED, buzzer, servo, LCD, or 7-segment output components detected on the canvas.');
    }
    return lines.join('\n');
  }

  lines.push('LED paths:');

  ledPaths.forEach((ledPath, index) => {
    lines.push(`${index + 1}. ${ledPath.componentName || `LED ${index + 1}`}`);
    lines.push(`   Wiring status: ${ledPath.driveDescription || 'Unknown'}`);
    lines.push(`   Resolved example pin: ${ledPath.preferredPinName || 'none'}`);
    lines.push(`   Anode reaches: ${formatSimulationBoardPinSummary(ledPath.anodeBoardPins)}`);
    lines.push(`   Cathode reaches: ${formatSimulationBoardPinSummary(ledPath.cathodeBoardPins)}`);
    lines.push(`   Anode potentials: ${formatSimulationPotentialSummary(ledPath.anodePotentials)}`);
    lines.push(`   Cathode potentials: ${formatSimulationPotentialSummary(ledPath.cathodePotentials)}`);
    lines.push(`   Canvas LED state: ${ledPath.isOn ? 'ON' : 'OFF'}`);
    lines.push('');
  });

  return lines.join('\n').trim();
};

const createProjectId = () =>
  `project_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const formatProjectLoadIssueList = (items) => {
  const uniqueItems = Array.from(
    new Set(
      (Array.isArray(items) ? items : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean)
    )
  );

  if (uniqueItems.length === 0) {
    return '';
  }

  if (uniqueItems.length <= 2) {
    return uniqueItems.join(', ');
  }

  return `${uniqueItems.slice(0, 2).join(', ')} +${uniqueItems.length - 2} more`;
};

const formatPassiveTemplateLabel = (value) => (
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase())
    .trim() || 'Basic Part'
);

const resolveSimulationDigitalInputValue = (pinState, inputAnalysis) => {
  if (pinState?.isOutput) {
    return null;
  }

  if (inputAnalysis?.status === 'conflict') {
    return null;
  }

  if (inputAnalysis?.level === 'high') {
    return true;
  }

  if (inputAnalysis?.level === 'low') {
    return false;
  }

  const inputMode = String(pinState?.mode || pinState?.state || 'input')
    .trim()
    .toLowerCase();

  return (
    inputMode === 'input_pullup' ||
    inputMode === 'input_pullup_high' ||
    inputMode === 'input_pullup_low'
  );
};

const syncArduinoSimulationDigitalInputs = ({
  appliedLevelsRef,
  digitalInputsByPinName,
  pinStates,
  runner,
} = {}) => {
  const appliedLevels = appliedLevelsRef?.current;

  if (!runner || !(appliedLevels instanceof Map)) {
    return;
  }

  const pendingInputUpdates = {};
  let hasPendingUpdates = false;

  ARDUINO_UNO_PIN_ORDER.forEach((pinDefinition) => {
    const pinName = pinDefinition.name;
    const pinState = pinStates?.[pinName] || null;

    if (pinState?.isOutput) {
      appliedLevels.delete(pinName);
      return;
    }

    const desiredValue = resolveSimulationDigitalInputValue(
      pinState,
      digitalInputsByPinName?.[pinName]
    );

    if (typeof desiredValue !== 'boolean') {
      appliedLevels.delete(pinName);
      return;
    }

    if (appliedLevels.get(pinName) === desiredValue) {
      return;
    }

    appliedLevels.set(pinName, desiredValue);
    pendingInputUpdates[pinName] = desiredValue;
    hasPendingUpdates = true;
  });

  if (!hasPendingUpdates) {
    return;
  }

  if (typeof runner.setDigitalInputs === 'function') {
    runner.setDigitalInputs(pendingInputUpdates);
    return;
  }

  Object.entries(pendingInputUpdates).forEach(([pinName, value]) => {
    runner.setDigitalInput?.(pinName, value);
  });
};

const syncArduinoSimulationAnalogInputs = ({
  analogInputsByPinName,
  appliedVoltagesRef,
  runner,
} = {}) => {
  const appliedVoltages = appliedVoltagesRef?.current;

  if (!runner || !(appliedVoltages instanceof Map)) {
    return;
  }

  const pendingInputUpdates = {};
  let hasPendingUpdates = false;

  SIMULATION_ANALOG_PIN_NAMES.forEach((pinName) => {
    const inputAnalysis = analogInputsByPinName?.[pinName];
    const desiredVoltage = inputAnalysis?.status === 'resolved' && Number.isFinite(Number(inputAnalysis?.voltage))
      ? Math.min(5, Math.max(0, Number(inputAnalysis.voltage)))
      : 0;
    const wasApplied = appliedVoltages.has(pinName);
    const currentVoltage = Number(appliedVoltages.get(pinName) || 0);

    if (
      inputAnalysis?.status === 'resolved' &&
      wasApplied &&
      Math.abs(currentVoltage - desiredVoltage) < 1e-6
    ) {
      return;
    }

    if (inputAnalysis?.status === 'resolved') {
      appliedVoltages.set(pinName, desiredVoltage);
    } else {
      appliedVoltages.delete(pinName);

      if (!wasApplied && Math.abs(desiredVoltage) < 1e-6) {
        return;
      }
    }

    pendingInputUpdates[pinName] = desiredVoltage;
    hasPendingUpdates = true;
  });

  if (!hasPendingUpdates) {
    return;
  }

  if (typeof runner.setAnalogInputs === 'function') {
    runner.setAnalogInputs(pendingInputUpdates);
    return;
  }

  Object.entries(pendingInputUpdates).forEach(([pinName, value]) => {
    runner.setAnalogInput?.(pinName, value);
  });
};

const normalizeProjectBuilderSettings = (value) => {
  const rawValue = value?.arduino_simulation_enabled ?? value?.arduinoSimulationEnabled;

  return {
    arduino_simulation_enabled:
      rawValue === true ||
      rawValue === 1 ||
      rawValue === '1',
  };
};

const normalizeProjectBuilderExampleRecord = (value) => {
  const projectPayload = value?.project_payload ?? value?.projectPayload ?? null;
  const componentCount = Number(
    value?.component_count ??
    value?.componentCount ??
    (Array.isArray(projectPayload?.components) ? projectPayload.components.length : 0)
  );
  const connectionCount = Number(
    value?.connection_count ??
    value?.connectionCount ??
    (Array.isArray(projectPayload?.connections) ? projectPayload.connections.length : 0)
  );
  const hasSimulationCodeValue = value?.has_simulation_code ?? value?.hasSimulationCode;

  return {
    id: value?.id ?? null,
    title: String(value?.title || ''),
    description: String(value?.description || ''),
    sourceFileName: String(value?.source_file_name ?? value?.sourceFileName ?? ''),
    componentCount: Number.isFinite(componentCount) ? componentCount : 0,
    connectionCount: Number.isFinite(connectionCount) ? connectionCount : 0,
    hasSimulationCode:
      hasSimulationCodeValue === true ||
      hasSimulationCodeValue === 1 ||
      hasSimulationCodeValue === '1',
    projectPayload,
    simulationCode: getProjectStoredSimulationCode(projectPayload),
    updatedAt: String(value?.updated_at ?? value?.updatedAt ?? ''),
  };
};

const getResponseContentType = (response) => {
  if (typeof response?.headers?.get !== 'function') {
    return '';
  }

  return response.headers.get('content-type') || '';
};

const getApiResponsePreview = (bodyText) => {
  const normalizedText = String(bodyText || '').replace(/\s+/g, ' ').trim();

  if (!normalizedText) {
    return '';
  }

  if (/^\s*</.test(normalizedText)) {
    return ' The response starts with an HTML document.';
  }

  return ` Response preview: "${normalizedText.slice(0, API_RESPONSE_PREVIEW_LENGTH)}"`;
};

const getApiResponsePath = (response) => {
  const responseUrl = String(response?.url || '').trim();

  if (!responseUrl) {
    return '';
  }

  try {
    const baseUrl = typeof window !== 'undefined'
      ? window.location.href
      : 'http://localhost/';

    return new URL(responseUrl, baseUrl).pathname;
  } catch (error) {
    return responseUrl;
  }
};

const getApiRoutingHint = (response) => {
  const responsePath = getApiResponsePath(response);

  if (!responsePath.includes('/api-php/')) {
    return '';
  }

  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    window.location.port === '3000'
  ) {
    return ' Start the PHP backend through Apache/XAMPP or add a development proxy for /api-php.';
  }

  return ` Confirm ${responsePath} is routed to api-php/index.php, not the React app fallback.`;
};

const readApiJsonResponse = async (response, label) => {
  const bodyText = await response.text();
  const contentType = getResponseContentType(response);
  const trimmedBodyText = String(bodyText || '').trim();
  const isHtmlResponse = /^\s*</.test(bodyText) || contentType.toLowerCase().includes('text/html');
  let parsedBody = null;
  let parseError = null;

  if (trimmedBodyText) {
    try {
      parsedBody = JSON.parse(bodyText);
    } catch (error) {
      parseError = error;
    }
  }

  if (!response.ok) {
    const apiMessage = parsedBody && typeof parsedBody === 'object'
      ? (parsedBody.message || parsedBody.error || '')
      : '';
    const nonJsonNote = parseError
      ? ` The server returned ${isHtmlResponse ? 'HTML' : 'non-JSON'} instead of JSON.`
      : '';

    const apiError = new Error(
      `${label} failed: ${response.status} ${response.statusText}${apiMessage ? ` - ${apiMessage}` : ''}.` +
      `${nonJsonNote}${nonJsonNote ? getApiRoutingHint(response) : ''}${getApiResponsePreview(bodyText)}`
    );

    apiError.status = response.status;
    apiError.apiMessage = apiMessage;
    throw apiError;
  }

  if (!trimmedBodyText) {
    throw new Error(`${label} returned an empty response instead of JSON.${getApiRoutingHint(response)}`);
  }

  if (parseError) {
    throw new Error(
      `${label} returned ${isHtmlResponse ? 'HTML' : 'non-JSON'} instead of JSON.` +
      `${contentType ? ` Content-Type was ${contentType}.` : ''}` +
      `${getApiRoutingHint(response)}${getApiResponsePreview(bodyText)}`
    );
  }

  return parsedBody;
};

const fetchApiJson = async (url, label, options) => {
  const response = await fetch(url, options);
  return readApiJsonResponse(response, label);
};

const ADMIN_LESSON_PREVIEW_STORAGE_PREFIX = 'micropinouts.adminLessonPreview.';

const getAdminLessonPreviewStorageKey = (lessonId) => (
  `${ADMIN_LESSON_PREVIEW_STORAGE_PREFIX}${String(lessonId || '').trim()}`
);

const getBrowserStorage = (storageName) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window[storageName] || null;
  } catch (error) {
    return null;
  }
};

const getManagedLessonLookupErrorMessage = (lessonError, lessonId) => {
  const status = Number(lessonError?.status);
  const apiMessage = String(lessonError?.apiMessage || lessonError?.message || '');

  if (status === 404 && /builder lesson not found|lesson not found/i.test(apiMessage)) {
    return [
      `Public lesson "${lessonId}" has not been saved and published in Admin yet.`,
      'JSON examples in the setup guide do not create API records automatically.',
      'Create it under Admin > Lessons, enable Published, then reload this URL.',
      'For an unsaved draft, open Preview Draft or Preview Embed from the lesson editor.',
    ].join(' ');
  }

  return lessonError?.message || 'Lesson not found.';
};

const normalizeManagedBuilderLessonPayload = (payload) => {
  const responsePayload = payload?.data || payload;
  const lessonPayload = responsePayload?.lesson_payload || responsePayload?.lessonPayload || responsePayload;

  if (!lessonPayload || Array.isArray(lessonPayload) || typeof lessonPayload !== 'object') {
    return null;
  }

  if (!lessonPayload.id || !Array.isArray(lessonPayload.steps)) {
    return null;
  }

  return lessonPayload;
};

const parseStoredAdminLessonPreviewPayload = (rawPreviewPayload, storage, storageKey) => {
  const parsedPreviewPayload = JSON.parse(rawPreviewPayload);
  const hasPreviewEnvelope = Boolean(
    parsedPreviewPayload?.lessonPayload ||
    parsedPreviewPayload?.lesson_payload
  );
  const savedAt = Number(parsedPreviewPayload?.savedAt || 0);

  if (hasPreviewEnvelope) {
    const expiresAt = Number(parsedPreviewPayload.expiresAt || 0);

    if (Number.isFinite(expiresAt) && expiresAt > 0 && Date.now() > expiresAt) {
      try {
        storage?.removeItem(storageKey);
      } catch (error) {
        console.error('Error clearing expired lesson preview payload:', error);
      }

      return null;
    }
  }

  const lessonPayload = normalizeManagedBuilderLessonPayload(parsedPreviewPayload);

  if (!lessonPayload) {
    return null;
  }

  return {
    lessonPayload,
    savedAt: Number.isFinite(savedAt) ? savedAt : 0,
  };
};

const readAdminLessonPreviewPayload = (lessonId) => {
  if (typeof window === 'undefined' || !lessonId) {
    return null;
  }

  const storageKey = getAdminLessonPreviewStorageKey(lessonId);
  const previewCandidates = [];

  for (const storageName of ['sessionStorage', 'localStorage']) {
    const storage = getBrowserStorage(storageName);

    if (!storage) {
      continue;
    }

    try {
      const rawPreviewPayload = storage.getItem(storageKey);

      if (!rawPreviewPayload) {
        continue;
      }

      const previewCandidate = parseStoredAdminLessonPreviewPayload(
        rawPreviewPayload,
        storage,
        storageKey
      );

      if (previewCandidate) {
        previewCandidates.push(previewCandidate);
      }
    } catch (error) {
      console.error(`Error reading lesson preview payload from ${storageName}:`, error);
    }
  }

  if (previewCandidates.length === 0) {
    return null;
  }

  return previewCandidates
    .sort((left, right) => right.savedAt - left.savedAt)[0]
    .lessonPayload;
};

const getSavedProjectsFromStorage = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(PROJECT_BUILDER_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error reading saved projects:', error);
    return [];
  }
};

const saveProjectsToStorage = (projects) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(
    PROJECT_BUILDER_STORAGE_KEY,
    JSON.stringify(projects)
  );
};

const ProjectBuilder = () => {
  const wireWidth = 5.5;
  const tipRadius = 5.5;
  const tipStroke = 3.0;
  const CANVAS_WIDTH = 1100;
  const CANVAS_HEIGHT = 720;

  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.1;
  const BASE_ZOOM = 0.6;
  const DEBUG_PROJECT_BUILDER_DRAG = false;
  const location = useLocation();
  const navigate = useNavigate();
  const routeParams = useParams();
  const routeSearchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const routeLessonId = routeParams.lessonId || '';
  const lessonModeRequested = (
    routeSearchParams.get('mode') === 'lesson' ||
    Boolean(routeLessonId)
  );
  const requestedLessonId = (
    routeSearchParams.get('lesson') ||
    routeLessonId ||
    getDefaultBuilderLessonId()
  );
  const adminLessonPreviewRequested = routeSearchParams.get('preview') === 'admin-draft';
  const builtInLesson = lessonModeRequested
    ? getBuilderLesson(requestedLessonId)
    : null;
  const [managedLessonLookup, setManagedLessonLookup] = useState({
    lessonId: '',
    loading: false,
    lesson: null,
    error: '',
  });
  const managedLessonMatchesRequest = managedLessonLookup.lessonId === requestedLessonId;
  const managedLesson = managedLessonMatchesRequest ? managedLessonLookup.lesson : null;
  const managedLessonLookupResolved = Boolean(
    !lessonModeRequested ||
    (managedLessonMatchesRequest && !managedLessonLookup.loading)
  );
  const isManagedLessonLookupPending = Boolean(
    lessonModeRequested &&
    !managedLessonLookupResolved
  );
  const fallbackLesson = adminLessonPreviewRequested ? null : builtInLesson;
  const activeLesson = lessonModeRequested
    ? (isManagedLessonLookupPending ? null : (managedLesson || fallbackLesson))
    : null;
  const activeLessonSource = managedLesson
    ? (adminLessonPreviewRequested ? 'preview' : 'managed')
    : (fallbackLesson ? 'builtin' : '');
  const isLessonMode = Boolean(lessonModeRequested && activeLesson);
  const isUnknownLessonMode = Boolean(lessonModeRequested && !activeLesson && !isManagedLessonLookupPending);
  const isLessonLookupLoading = Boolean(lessonModeRequested && isManagedLessonLookupPending);
  const isEmbedMode = Boolean(isLessonMode && routeSearchParams.get('embed') === '1');
  const restartLessonRequested = routeSearchParams.get('restart') === '1';
  const requestedStepId = routeSearchParams.get('step') || '';

  usePageSeo({
    title: isLessonMode ? `${activeLesson.title} | MicroPinouts Tutorial` : 'Project Builder | MicroPinouts',
    description: isLessonMode
      ? (activeLesson.description || PROJECT_BUILDER_SEO_DESCRIPTION)
      : PROJECT_BUILDER_SEO_DESCRIPTION,
    keywords: PROJECT_BUILDER_SEO_KEYWORDS,
    canonicalUrl: isLessonMode
      ? appUrl(`/builder/?mode=lesson&lesson=${activeLesson.id}`)
      : appUrl('/project-builder'),
    ogType: 'website',
  });

  const [components, setComponents] = useState([]);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [componentSearch, setComponentSearch] = useState('');
  const [passiveSearch, setPassiveSearch] = useState('');
  const [selectedBoardToAdd, setSelectedBoardToAdd] = useState('');
  const [componentAddMode, setComponentAddMode] = useState('board'); // board | passive
  const [boards, setBoards] = useState([]);
  const [projectBuilderSettings, setProjectBuilderSettings] = useState(DEFAULT_PROJECT_BUILDER_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bannerAd, setBannerAd] = useState(null);
  const canvasRef = useRef(null);
  const boardIconCacheRef = useRef({});
  const boardLocalPinLayoutCacheRef = useRef(new Map());
  const breadboardLocalPinLayoutCacheRef = useRef(new Map());
  const breadboardPreparedSvgCacheRef = useRef(new Map());
  const boardSvgRequestsRef = useRef(new Map());
  const [zoomLevel, setZoomLevel] = useState(BASE_ZOOM);
  const [wheelZoomEnabled, setWheelZoomEnabled] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(10);
  const [isMobileToolbarOpen, setIsMobileToolbarOpen] = useState(false);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const isProjectDirtyRef = useRef(false);
  const hasWorkspaceContentRef = useRef(false);
  const saveCurrentProjectRef = useRef(null);
  const saveStatusTimeoutRef = useRef(null);
  const moreOptionsRef = useRef(null);
  const projectFileInputRef = useRef(null);
  const partHostRefs = useRef({});
  const dragRef = useRef(null);
  const componentDragAnimationFrameRef = useRef(0);
  const pendingComponentDragFrameRef = useRef(null);
  const selectionDragRef = useRef(null);
  const componentScaleEditRef = useRef(null);
  const breadboardHoverLayerRef = useRef(null);
  const breadboardHoverStateRef = useRef(null);
  const partRotateRefs = useRef({});
  const simulationRunnerRef = useRef(null);
  const simulationAudioManagerRef = useRef(null);
  const simulationActiveBuzzerIdsRef = useRef(new Set());
  const lcdSimulationStateByComponentIdRef = useRef({});
  const simulationPendingPinTransitionEventsRef = useRef([]);
  const simulationRequestIdRef = useRef(0);
  const simulationInteractionPointerMapRef = useRef(new Map());
  const simulationAppliedDigitalInputLevelsRef = useRef(new Map());
  const simulationAppliedAnalogInputVoltagesRef = useRef(new Map());
  const simulationCopyFeedbackTimeoutRef = useRef(null);
  const expandedSimulationEditorScrollTopRef = useRef(0);
  const expandedSimulationLineNumberTrackRef = useRef(null);
  const servoAnimationFrameRef = useRef(0);
  const animatedServoAnglesByComponentIdRef = useRef({});
  const breadboardPlacementConnectionsRef = useRef([]);
  const suggestionPinsCacheRef = useRef([]);
  const ledCurrentWarningCacheRef = useRef({});
  const runtimeSimulationComponentsCacheRef = useRef([]);
  const simulationCircuitAnalysisCacheRef = useRef(null);
  const logicalConnectionsCacheRef = useRef({
    signature: '',
    value: [],
  });
  const connectionsRef = useRef([]);
  const [pinLayoutByPartId, setPinLayoutByPartId] = useState({});
  const wireDragRef = useRef(null);
  const rewireDragRef = useRef(null);
  const wireCornerDragRef = useRef(null);
  const suppressCanvasClickRef = useRef(false);
  const didDragComponentRef = useRef(false);
  const [wireDropStatus, setWireDropStatus] = useState(null);
  const [pinHoverTarget, setPinHoverTarget] = useState(null);
  const [wirePreview, setWirePreview] = useState(null);
  const [connections, setConnections] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [savedProjects, setSavedProjects] = useState([]);
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [projectBuilderExamples, setProjectBuilderExamples] = useState([]);
  const [projectBuilderExamplesLoaded, setProjectBuilderExamplesLoaded] = useState(false);
  const [projectBuilderExamplesLoading, setProjectBuilderExamplesLoading] = useState(false);
  const [projectBuilderExamplesError, setProjectBuilderExamplesError] = useState('');
  const [loadingExampleId, setLoadingExampleId] = useState(null);
  const [selectedComponentIds, setSelectedComponentIds] = useState(new Set());
  const [isComponentDragActive, setIsComponentDragActive] = useState(false);
  const [passiveComponents, setPassiveComponents] = useState([]);
  const [selectedPassiveKey, setSelectedPassiveKey] = useState('led');
  const [newPassiveValue, setNewPassiveValue] = useState('');
  const [newSevenSegmentCommonType, setNewSevenSegmentCommonType] = useState(SEVEN_SEGMENT_DEFAULT_COMMON_TYPE);
  const [ledSvg, setLedSvg] = useState('');
  const [servoSvg, setServoSvg] = useState('');
  const [lcd16x2Svg, setLcd16x2Svg] = useState('');
  const [sevenSegmentSvg, setSevenSegmentSvg] = useState('');
  const [breadboardSvg, setBreadboardSvg] = useState('');
  const [newLedColor, setNewLedColor] = useState('#E60000');
  const [showPassiveEditModal, setShowPassiveEditModal] = useState(false);
  const [passiveEditComponentId, setPassiveEditComponentId] = useState(null);
  const [passiveEditType, setPassiveEditType] = useState('resistor');
  const [passiveEditLedColor, setPassiveEditLedColor] = useState('#E60000');
  const [passiveEditValue, setPassiveEditValue] = useState('');
  const [passiveEditSevenSegmentCommonType, setPassiveEditSevenSegmentCommonType] = useState(SEVEN_SEGMENT_DEFAULT_COMMON_TYPE);
  const [deferredComponentSearch, setDeferredComponentSearch] = useState('');
  const [isSearchingComponents, setIsSearchingComponents] = useState(false);
  const [suggestedConnections, setSuggestedConnections] = useState([]);
  const [showSuggestedConnections, setShowSuggestedConnections] = useState(false);
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState(new Set());
  const [analysisResults, setAnalysisResults] = useState([]);
  const [analysisSummary, setAnalysisSummary] = useState({
    error: 0,
    warning: 0,
    suggestion: 0,
    info: 0,
    pass: 0,
  });
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showSimulationModal, setShowSimulationModal] = useState(false);
  const [showSimulationWorkspaceModal, setShowSimulationWorkspaceModal] = useState(false);
  const [isSimulationPanelCollapsed, setIsSimulationPanelCollapsed] = useState(false);
  const [simulationCode, setSimulationCode] = useState(DEFAULT_ARDUINO_SIMULATION_SKETCH);
  const [simulationCopyState, setSimulationCopyState] = useState('idle');
  const [simulationPhase, setSimulationPhase] = useState('idle');
  const [simulationStatus, setSimulationStatus] = useState('');
  const [simulationCompilerOutput, setSimulationCompilerOutput] = useState('');
  const [simulationSerialOutput, setSimulationSerialOutput] = useState('');
  const [showLessonSerialMonitor, setShowLessonSerialMonitor] = useState(false);
  const [simulationBuiltinLedOn, setSimulationBuiltinLedOn] = useState(false);
  const [simulationAudioEnabled, setSimulationAudioEnabled] = useState(true);
  const [simulationPinStates, setSimulationPinStates] = useState(() => createInitialSimulationPinStates());
  const [simulationSnapshotVersion, setSimulationSnapshotVersion] = useState(0);
  const [animatedServoAnglesByComponentId, setAnimatedServoAnglesByComponentId] = useState({});
  const [simulatedLcdStatesByComponentId, setSimulatedLcdStatesByComponentId] = useState({});
  const [simulationElapsedTime, setSimulationElapsedTime] = useState('0 ms');
  const [simulationCpuCycles, setSimulationCpuCycles] = useState(0);
  const [activeSimulationInteractionIds, setActiveSimulationInteractionIds] = useState(new Set());
  const [analysisModalTab, setAnalysisModalTab] = useState('analysis'); // analysis | steps
  const [selectedAnalysisResultId, setSelectedAnalysisResultId] = useState('');
  const [wiringSteps, setWiringSteps] = useState([]);
  const [highlightedAnalysisWireIds, setHighlightedAnalysisWireIds] = useState(new Set());
  const [highlightedAnalysisPinIds, setHighlightedAnalysisPinIds] = useState([]);
  const lessonInitKeyRef = useRef('');
  const lessonRuntimeReadyRef = useRef(false);
  const lessonRuntimeStageRef = useRef('');
  const lessonRuntimeErrorRef = useRef('');
  const lessonCompletedStepEventsRef = useRef(new Set());
  const [lessonStepIndex, setLessonStepIndex] = useState(0);
  const [lessonHintUsageByStepId, setLessonHintUsageByStepId] = useState({});
  const [lessonCompletedStepIds, setLessonCompletedStepIds] = useState(new Set());
  const [lessonSelections, setLessonSelections] = useState({});
  const [lessonSelectedHole, setLessonSelectedHole] = useState(null);
  const [lessonInspectedTargets, setLessonInspectedTargets] = useState(new Set());
  const [lessonTraceEnabled, setLessonTraceEnabled] = useState(false);
  const [lessonCodeViewedStepIds, setLessonCodeViewedStepIds] = useState(new Set());
  const [lessonCodeEditedStepIds, setLessonCodeEditedStepIds] = useState(new Set());
  const [lessonCodeRunsByStepId, setLessonCodeRunsByStepId] = useState({});
  const [lessonStartedAt, setLessonStartedAt] = useState(Date.now());
  const [lessonResumeMessage, setLessonResumeMessage] = useState('');
  const [lessonCompleted, setLessonCompleted] = useState(false);
  const componentsRef = useRef(components);
  const selectedItemRef = useRef(selectedItem);
  const selectedComponentIdsRef = useRef(selectedComponentIds);
  const undoStackRef = useRef(undoStack);
  const redoStackRef = useRef(redoStack);
  const isApplyingHistoryRef = useRef(false);
  connectionsRef.current = connections;
  componentsRef.current = components;
  selectedItemRef.current = selectedItem;
  selectedComponentIdsRef.current = selectedComponentIds;
  undoStackRef.current = undoStack;
  redoStackRef.current = redoStack;
  const hasWorkspaceContent = components.length > 0 || connections.length > 0;
  const clearWireDropFeedback = useCallback(() => {
    setWireDropStatus(null);
    setPinHoverTarget(null);
  }, []);

  const applyComponentDragFrameToComponents = useCallback((currentComponents, frame) => {
    if (!frame || !Array.isArray(frame.componentIds) || !frame.startPositions) {
      return currentComponents;
    }

    const draggedIds = new Set(frame.componentIds);
    let didChange = false;
    const nextComponents = (Array.isArray(currentComponents) ? currentComponents : []).map((component) => {
      if (!draggedIds.has(component.id)) {
        return component;
      }

      const start = frame.startPositions[component.id];

      if (!start) {
        return component;
      }

      const nextX = start.x + frame.dx;
      const nextY = start.y + frame.dy;

      if (Number(component.x) === Number(nextX) && Number(component.y) === Number(nextY)) {
        return component;
      }

      didChange = true;

      return {
        ...component,
        x: nextX,
        y: nextY,
      };
    });

    return didChange ? nextComponents : currentComponents;
  }, []);
  const scheduleComponentDragFrame = useCallback((frame) => {
    pendingComponentDragFrameRef.current = frame;

    if (componentDragAnimationFrameRef.current) {
      return;
    }

    componentDragAnimationFrameRef.current = requestAnimationFrame(() => {
      componentDragAnimationFrameRef.current = 0;

      const nextFrame = pendingComponentDragFrameRef.current;
      pendingComponentDragFrameRef.current = null;

      if (!nextFrame) {
        return;
      }

      setComponents((current) => {
        const nextComponents = applyComponentDragFrameToComponents(current, nextFrame);

        if (nextComponents !== current) {
          componentsRef.current = nextComponents;
        }

        return nextComponents;
      });
    });
  }, [applyComponentDragFrameToComponents]);
  const flushPendingComponentDragFrame = useCallback(() => {
    if (componentDragAnimationFrameRef.current) {
      cancelAnimationFrame(componentDragAnimationFrameRef.current);
      componentDragAnimationFrameRef.current = 0;
    }

    const frame = pendingComponentDragFrameRef.current;
    pendingComponentDragFrameRef.current = null;

    if (!frame) {
      return false;
    }

    const currentComponents = componentsRef.current || [];
    const nextComponents = applyComponentDragFrameToComponents(currentComponents, frame);

    if (nextComponents === currentComponents) {
      return false;
    }

    componentsRef.current = nextComponents;
    setComponents(nextComponents);
    return true;
  }, [applyComponentDragFrameToComponents]);
  const cancelPendingComponentDragFrame = useCallback(() => {
    if (componentDragAnimationFrameRef.current) {
      cancelAnimationFrame(componentDragAnimationFrameRef.current);
      componentDragAnimationFrameRef.current = 0;
    }

    pendingComponentDragFrameRef.current = null;
  }, []);
  useEffect(() => {
    return () => {
      cancelPendingComponentDragFrame();
    };
  }, [cancelPendingComponentDragFrame]);
  const breadboardPlacementConnections = useMemo(() => {
    if (isComponentDragActive) {
      return breadboardPlacementConnectionsRef.current;
    }

    const nextConnections = buildBreadboardPlacementConnections(
      components,
      pinLayoutByPartId
    );

    breadboardPlacementConnectionsRef.current = nextConnections;
    return nextConnections;
  }, [components, isComponentDragActive, pinLayoutByPartId]);
  const logicalConnectionSignature = useMemo(
    () => buildLogicalConnectionSignature([
      ...connections,
      ...breadboardPlacementConnections,
    ]),
    [breadboardPlacementConnections, connections]
  );
  const logicalConnections = useMemo(() => {
    if (logicalConnectionsCacheRef.current.signature === logicalConnectionSignature) {
      return logicalConnectionsCacheRef.current.value;
    }

    const nextValue = [
      ...(Array.isArray(connections) ? connections : []),
      ...breadboardPlacementConnections,
    ].map((connection) => ({
      id: connection.id,
      from: connection.from,
      to: connection.to,
      ...(connection.implicit ? { implicit: true } : {}),
      ...(connection.isBreadboardPlacementContact
        ? { isBreadboardPlacementContact: true }
        : {}),
    }));

    logicalConnectionsCacheRef.current = {
      signature: logicalConnectionSignature,
      value: nextValue,
    };

    return nextValue;
    }, [breadboardPlacementConnections, connections, logicalConnectionSignature]);

  const createWorkspaceHistorySnapshot = useCallback(() => {
    const snapshot = {
      components: cloneProjectBuilderHistoryValue(componentsRef.current || []),
      connections: cloneProjectBuilderHistoryValue(connectionsRef.current || []),
      selectedItem: selectedItemRef.current
        ? cloneProjectBuilderHistoryValue(selectedItemRef.current)
        : null,
      selectedComponentIds: Array.from(selectedComponentIdsRef.current || []),
    };

    return {
      ...snapshot,
      signature: JSON.stringify({
        components: snapshot.components,
        connections: snapshot.connections,
      }),
    };
  }, []);

  const pushUndoSnapshot = useCallback((snapshot) => {
    if (!snapshot || isApplyingHistoryRef.current) {
      return;
    }

    setUndoStack((current) => {
      if (current[current.length - 1]?.signature === snapshot.signature) {
        return current;
      }

      const next = [...current, snapshot];

      return next.length > PROJECT_BUILDER_HISTORY_LIMIT
        ? next.slice(next.length - PROJECT_BUILDER_HISTORY_LIMIT)
        : next;
    });
    setRedoStack([]);
  }, []);

  const restoreWorkspaceHistorySnapshot = useCallback((snapshot) => {
    if (!snapshot) {
      return;
    }

    isApplyingHistoryRef.current = true;

    cancelPendingComponentDragFrame();
    dragRef.current = null;
    selectionDragRef.current = null;
    componentScaleEditRef.current = null;
    breadboardHoverStateRef.current = null;
    if (breadboardHoverLayerRef.current) {
      while (breadboardHoverLayerRef.current.firstChild) {
        breadboardHoverLayerRef.current.removeChild(breadboardHoverLayerRef.current.firstChild);
      }
    }
    wireDragRef.current = null;
    rewireDragRef.current = null;
    wireCornerDragRef.current = null;
    suppressCanvasClickRef.current = false;
    didDragComponentRef.current = false;
    setIsComponentDragActive(false);

    setComponents(cloneProjectBuilderHistoryValue(snapshot.components || []));
    setConnections(cloneProjectBuilderHistoryValue(snapshot.connections || []));
    setSelectedItem(
      snapshot.selectedItem
        ? cloneProjectBuilderHistoryValue(snapshot.selectedItem)
        : null
    );
    setSelectedComponentIds(new Set(snapshot.selectedComponentIds || []));
    setWirePreview(null);
    clearWireDropFeedback();
    setSelectionBox(null);
    setSuggestedConnections([]);
    setSelectedSuggestionIds(new Set());
    setShowSuggestedConnections(false);
    setHighlightedAnalysisWireIds(new Set());
    setHighlightedAnalysisPinIds([]);
    setSelectedAnalysisResultId('');
    setShowMoreOptions(false);

    requestAnimationFrame(() => {
      isApplyingHistoryRef.current = false;
    });
  }, [cancelPendingComponentDragFrame, clearWireDropFeedback]);

  const undoProjectEdit = useCallback(() => {
    if (simulationPhase === 'running' || isApplyingHistoryRef.current) {
      return;
    }

    const previousSnapshot = undoStackRef.current[undoStackRef.current.length - 1];

    if (!previousSnapshot) {
      return;
    }

    const currentSnapshot = createWorkspaceHistorySnapshot();

    setUndoStack((current) => current.slice(0, -1));
    setRedoStack((current) => {
      const next = [...current, currentSnapshot];

      return next.length > PROJECT_BUILDER_HISTORY_LIMIT
        ? next.slice(next.length - PROJECT_BUILDER_HISTORY_LIMIT)
        : next;
    });

    restoreWorkspaceHistorySnapshot(previousSnapshot);
  }, [createWorkspaceHistorySnapshot, restoreWorkspaceHistorySnapshot, simulationPhase]);

  const redoProjectEdit = useCallback(() => {
    if (simulationPhase === 'running' || isApplyingHistoryRef.current) {
      return;
    }

    const nextSnapshot = redoStackRef.current[redoStackRef.current.length - 1];

    if (!nextSnapshot) {
      return;
    }

    const currentSnapshot = createWorkspaceHistorySnapshot();

    setRedoStack((current) => current.slice(0, -1));
    setUndoStack((current) => {
      const next = [...current, currentSnapshot];

      return next.length > PROJECT_BUILDER_HISTORY_LIMIT
        ? next.slice(next.length - PROJECT_BUILDER_HISTORY_LIMIT)
        : next;
    });

    restoreWorkspaceHistorySnapshot(nextSnapshot);
  }, [createWorkspaceHistorySnapshot, restoreWorkspaceHistorySnapshot, simulationPhase]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  const getPinSearchText = (pin) => (
    [
      pin?.pin_name,
      pin?.pinName,
      pin?.resolvedLabel,
      pin?.displayName,
      pin?.canvasPinBase,
      pin?.canvasPinKey,
      pin?.pin_role,
      pin?.primary_function,
      pin?.primaryFunction,
      ...(Array.isArray(pin?.capability_tags) ? pin.capability_tags : []),
    ]
      .join(' ')
      .toUpperCase()
  );

  const isBoardPowerSourcePin = (pin) => {
    if (pin?.component_type !== 'board') {
      return false;
    }

    const text = getPinSearchText(pin);

    return /\b(5V|VCC|VIN|3V3|3\.3V|POWER-5V|POWER-3V3|POWER-VIN)\b/.test(text);
  };

  const isBoardGroundPin = (pin) => {
    if (pin?.component_type !== 'board') {
      return false;
    }

    const text = getPinSearchText(pin);

    return /\b(GND|GROUND|0V)\b/.test(text);
  };

  const isBoardDrivenPin = (pin) => {
    if (pin?.component_type !== 'board') {
      return false;
    }

    const text = getPinSearchText(pin);

    return (
      isBoardPowerSourcePin(pin) ||
      /\b(GPIO|DIGITAL|D\d+|PWM|OUTPUT)\b/.test(text)
    );
  };

  const getResistorOhms = (component) => {
    const value = Number(component?.valueOhms);

    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const getBoardSourceVoltage = (pin) => {
    const text = getPinSearchText(pin);

    if (/\b(3V3|3\.3V|POWER-3V3)\b/.test(text)) {
      return 3.3;
    }

    if (/\b(5V|VCC|POWER-5V)\b/.test(text)) {
      return 5;
    }

    if (/\bVIN|POWER-VIN\b/.test(text)) {
      return 5; // safe fallback for Arduino Uno-style examples
    }

    return 5;
  };

  const getLedForwardVoltage = (ledPin) => {
    const text = getPinSearchText(ledPin);

    if (/\bBLUE\b/.test(text)) return 3.0;
    if (/\bWHITE\b/.test(text)) return 3.0;
    if (/\bGREEN\b/.test(text)) return 2.1;
    if (/\bYELLOW\b/.test(text)) return 2.0;
    if (/\bORANGE\b/.test(text)) return 2.0;

    return 2.0; // red/default
  };

  const getResistorOhmsFromPin = (pin, components) => {
    const component = components.find(
      (item) => item.id === pin?.component_id
    );

    const value = Number(component?.valueOhms);

    return Number.isFinite(value) && value > 0 ? value : null;
  };

  const calculateLedCurrentMa = ({
    sourcePin,
    resistorPin,
    ledPin,
    components,
  }) => {
    const supplyVoltage = getBoardSourceVoltage(sourcePin);
    const forwardVoltage = getLedForwardVoltage(ledPin);
    const resistorOhms = getResistorOhmsFromPin(resistorPin, components);

    if (!Number.isFinite(resistorOhms) || resistorOhms <= 0) {
      return null;
    }

    return Math.max(0, ((supplyVoltage - forwardVoltage) / resistorOhms) * 1000);
  };

  const isLedComponent = (component) => (
    component?.type === 'passive' &&
    (
      component?.passiveType === 'led' ||
      component?.passiveKey === 'led'
    )
  );

  const isResistorComponent = (component) => (
    component?.type === 'passive' &&
    (
      component?.passiveType === 'resistor' ||
      component?.passiveKey === 'resistor'
    )
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const ledCurrentWarningByComponentId = useMemo(() => {
    if (isComponentDragActive) {
      return ledCurrentWarningCacheRef.current;
    }

    const componentById = new Map(
      components.map((component) => [component.id, component])
    );

    const pins = get_project_pins_with_marker_keys(
      components,
      pinLayoutByPartId
    );

    const pinByEndpointKey = new Map(
      pins.map((pin) => [`${pin.component_id}:${pin.marker_key}`, pin])
    );

    const getPin = (endpoint) => (
      pinByEndpointKey.get(`${endpoint?.partId}:${endpoint?.pinKey}`) || null
    );

    const getOtherEndpoint = (connection, partId) => (
      connection.from?.partId === partId
        ? connection.to
        : connection.to?.partId === partId
          ? connection.from
          : null
    );

    const getConnectionsForPart = (partId) => (
      connections.filter((connection) => (
        connection.from?.partId === partId ||
        connection.to?.partId === partId
      ))
    );

    const result = {};

    const ledComponents = components.filter(isLedComponent);

    ledComponents.forEach((ledComponent) => {
      const ledConnections = getConnectionsForPart(ledComponent.id);

      if (ledConnections.length === 0) {
        return;
      }

      const directBoardPins = ledConnections
        .map((connection) => getPin(getOtherEndpoint(connection, ledComponent.id)))
        .filter((pin) => pin?.component_type === 'board');

      const directResistorEndpoints = ledConnections
        .map((connection) => getOtherEndpoint(connection, ledComponent.id))
        .filter((endpoint) => isResistorComponent(componentById.get(endpoint?.partId)));

      const directDrivenPins = directBoardPins.filter(isBoardDrivenPin);
      const directGroundPins = directBoardPins.filter(isBoardGroundPin);

      /**
       * Case 1:
       * Board source/GPIO -> LED -> GND
       * No resistor touching the LED.
       */
      if (
        directResistorEndpoints.length === 0 &&
        directDrivenPins.length > 0 &&
        directGroundPins.length > 0
      ) {
        result[ledComponent.id] = {
          type: 'missing_resistor',
          severity: 'warning',
          label: 'No resistor',
          message: 'LED appears connected directly between a source pin and ground without a current-limiting resistor.',
        };

        return;
      }

      /**
       * Case 2:
       * Board source/GPIO -> resistor -> LED -> GND
       * or
       * GND -> resistor -> LED -> Board source/GPIO
       */
      directResistorEndpoints.forEach((resistorEndpoint) => {
        if (result[ledComponent.id]?.type === 'over_current') {
          return;
        }

        const resistorComponent = componentById.get(resistorEndpoint.partId);
        const resistorOhms = getResistorOhms(resistorComponent);

        if (!Number.isFinite(resistorOhms) || resistorOhms <= 0) {
          result[ledComponent.id] = {
            type: 'missing_resistor',
            severity: 'warning',
            label: 'Bad resistor',
            message: 'LED has a resistor connection, but the resistor value is missing or invalid.',
          };

          return;
        }

        const resistorConnections = getConnectionsForPart(resistorEndpoint.partId);

        const resistorBoardPins = resistorConnections
          .map((connection) => getPin(getOtherEndpoint(connection, resistorEndpoint.partId)))
          .filter((pin) => pin?.component_type === 'board');

        const sourcePin = [
          ...directBoardPins,
          ...resistorBoardPins,
        ].find(isBoardDrivenPin);

        const groundPin = [
          ...directBoardPins,
          ...resistorBoardPins,
        ].find(isBoardGroundPin);

        if (!sourcePin || !groundPin) {
          return;
        }

        const ledPin = ledConnections
          .map((connection) => getPin(
            connection.from?.partId === ledComponent.id
              ? connection.from
              : connection.to?.partId === ledComponent.id
                ? connection.to
                : null
          ))
          .find(Boolean);

        const supplyVoltage = getBoardSourceVoltage(sourcePin);
        const forwardVoltage = getLedForwardVoltage(ledPin, ledComponent);
        const currentMa = Math.max(
          0,
          ((supplyVoltage - forwardVoltage) / resistorOhms) * 1000
        );

        if (currentMa > LED_OVERCURRENT_LIMIT_MA) {
          result[ledComponent.id] = {
            type: 'over_current',
            severity: 'error',
            label: `${Math.round(currentMa)} mA`,
            currentMa,
            limitMa: LED_OVERCURRENT_LIMIT_MA,
            message: `Estimated LED current is ${Math.round(currentMa)} mA, above the ${LED_OVERCURRENT_LIMIT_MA} mA limit.`,
          };
        }
      });
    });

    ledCurrentWarningCacheRef.current = result;
    return result;
  }, [components, connections, isComponentDragActive, pinLayoutByPartId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const isArduinoSimulationEnabled = projectBuilderSettings.arduino_simulation_enabled;
  const activeLessonSimulationCode = useMemo(
    () => getProjectStoredSimulationCode(activeLesson?.initialProject || {}),
    [activeLesson]
  );
  const lessonHasSimulationCodePanel = Boolean(
    isLessonMode &&
    isArduinoSimulationEnabled
  );
  const isSimulationCompiling = simulationPhase === 'compiling';
  const canStopSimulation = isSimulationCompiling || simulationPhase === 'running';
  const isCircuitEditingLocked = simulationPhase === 'running';
  const isCircuitEditingLockedRef = useRef(isCircuitEditingLocked);
  isCircuitEditingLockedRef.current = isCircuitEditingLocked;
  const availablePassiveComponents = useMemo(() => {
    const normalizedPassives = (Array.isArray(passiveComponents) ? passiveComponents : [])
      .map(normalizePassiveComponentDefinition)
      .filter(Boolean)
      .filter((definition) => definition.componentKey && definition.componentKey !== 'led');
    const customKeys = new Set(
      normalizedPassives
        .map((definition) => String(definition.componentKey || '').trim())
        .filter(Boolean)
    );
    const builtInPassives = [
      BUILT_IN_BREADBOARD_PASSIVE,
      BUILT_IN_BUZZER_PASSIVE,
      BUILT_IN_DC_POWER_PASSIVE,
      BUILT_IN_LCD_16X2_PASSIVE,
      BUILT_IN_LED_PASSIVE,
      BUILT_IN_RESISTOR_PASSIVE,
      BUILT_IN_SEVEN_SEGMENT_DISPLAY_PASSIVE,
      BUILT_IN_SERVO_PASSIVE,
    ].filter((definition) => !customKeys.has(definition.componentKey));

    return [...builtInPassives, ...normalizedPassives];
  }, [passiveComponents]);
  const passiveComponentMap = useMemo(
    () => new Map(
      availablePassiveComponents.map((definition) => [definition.componentKey, definition])
    ),
    [availablePassiveComponents]
  );
  const lessonAllowedComponentKeys = useMemo(
    () => new Set(
      isLessonMode && Array.isArray(activeLesson?.availableComponents)
        ? activeLesson.availableComponents
        : []
    ),
    [activeLesson, isLessonMode]
  );
  const componentPickerPassiveComponents = useMemo(() => {
    const visiblePickerComponents = isLessonMode
      ? availablePassiveComponents
      : availablePassiveComponents.filter((definition) => (
          definition.componentKey !== 'breadboard' &&
          definition.templateType !== BREADBOARD_TEMPLATE_TYPE
        ));

    if (!isLessonMode || lessonAllowedComponentKeys.size === 0) {
      return visiblePickerComponents;
    }

    return visiblePickerComponents.filter((definition) => (
      lessonAllowedComponentKeys.has(definition.componentKey) ||
      lessonAllowedComponentKeys.has(definition.templateType)
    ));
  }, [availablePassiveComponents, isLessonMode, lessonAllowedComponentKeys]);
  const selectedPassiveDefinition = useMemo(
    () => (
      componentPickerPassiveComponents.find((definition) => (
        definition.componentKey === selectedPassiveKey
      )) ||
      componentPickerPassiveComponents[0] ||
      BUILT_IN_LED_PASSIVE
    ),
    [componentPickerPassiveComponents, selectedPassiveKey]
  );
  const getLessonComponentLimit = useCallback((componentKey) => {
    if (!isLessonMode || !activeLesson?.componentLimits || !componentKey) {
      return Infinity;
    }

    const rawLimit = activeLesson.componentLimits[String(componentKey)];
    const limit = Number(rawLimit);

    return Number.isFinite(limit) && limit >= 0 ? limit : Infinity;
  }, [activeLesson, isLessonMode]);
  const getLessonComponentCount = useCallback((componentKey) => {
    const normalizedKey = String(componentKey || '');

    if (!normalizedKey) {
      return 0;
    }

    return components.filter((component) => (
      [
        component?.passiveKey,
        component?.passiveType,
        component?.templateType,
        component?.type,
      ].some((candidateKey) => String(candidateKey || '') === normalizedKey)
    )).length;
  }, [components]);
  const getLessonComponentAddStatus = useCallback((componentKey) => {
    const normalizedKey = String(componentKey || '');
    const limit = getLessonComponentLimit(normalizedKey);
    const count = getLessonComponentCount(normalizedKey);
    const disabled = Number.isFinite(limit) && count >= limit;

    return {
      count,
      disabled,
      limit,
      message: disabled
        ? `Only one ${normalizedKey === 'led' ? 'LED' : normalizedKey || 'part'} is allowed in this lesson.`
        : '',
    };
  }, [getLessonComponentCount, getLessonComponentLimit]);
  const selectedPassiveLessonLimitStatus = useMemo(() => {
    if (!isLessonMode || !selectedPassiveDefinition) {
      return null;
    }

    return getLessonComponentAddStatus(
      selectedPassiveDefinition.componentKey || selectedPassiveDefinition.templateType
    );
  }, [getLessonComponentAddStatus, isLessonMode, selectedPassiveDefinition]);
  const expandedSimulationLineNumbers = useMemo(() => (
    Array.from(
      { length: Math.max(1, String(simulationCode || '').split(/\r?\n/).length) },
      (_, index) => index + 1
    )
  ), [simulationCode]);
  const simulationCopyButtonLabel = simulationCopyState === 'copied'
    ? 'Copied'
    : simulationCopyState === 'error'
      ? 'Copy Failed'
      : 'Copy Code';
  const simulationPinList = useMemo(
    () => ARDUINO_UNO_PIN_ORDER.map((pin) => simulationPinStates[pin.name] || {
      ...pin,
      logicLevel: 'low',
      mode: 'input',
      rawState: null,
      sampledHigh: false,
      state: 'input_low',
      isHigh: false,
      isOutput: false,
      outputMode: 'input',
      outputVoltageRatio: null,
      averageVoltage: null,
      pwmChannel: '',
      pwmDutyCycle: null,
      pwmEnabled: false,
      pwmTimer: '',
      pwmValue: null,
      toneAgeMicros: null,
      toneDetected: false,
      toneEdgeSampleCount: 0,
      toneFrequencyHz: null,
    }),
    [simulationPinStates]
  );
  const showSaveStatusMessage = (message) => {
    setSaveStatus(message);

    if (saveStatusTimeoutRef.current) {
      window.clearTimeout(saveStatusTimeoutRef.current);
    }

    saveStatusTimeoutRef.current = window.setTimeout(() => {
      setSaveStatus('');
      saveStatusTimeoutRef.current = null;
    }, 2500);
  };
  const simulationInteractiveComponents = useMemo(() => (
    components
      .filter((component) => component?.type === 'passive')
      .map((component) => {
        const passiveDefinition = passiveComponentMap.get(component.passiveKey) ||
          passiveComponentMap.get(component.passiveType) ||
          null;
        const simulationModel = resolvePassiveComponentRuntimeSimulationModel(
          component,
          passiveDefinition
        );
        const interaction = String(simulationModel?.interaction || 'none').trim();

        if (!['momentary_press', 'toggle', 'slider'].includes(interaction)) {
          return null;
        }

        return {
          component,
          simulationModel,
          interaction,
        };
      })
      .filter(Boolean)
  ), [components, passiveComponentMap]);
  const simulationInteractiveComponentMap = useMemo(
    () => new Map(
      simulationInteractiveComponents.map((entry) => [
        entry.component.id,
        entry,
      ])
    ),
    [simulationInteractiveComponents]
  );
  const simulationInteractionSummaries = useMemo(() => (
    simulationInteractiveComponents.map((entry) => {
      const isActive = activeSimulationInteractionIds.has(entry.component.id);
      const sliderState = entry.interaction === 'slider'
        ? getSimulationSliderState(entry.simulationModel, entry.component?.valueOhms)
        : null;

      return {
        componentId: entry.component.id,
        componentName: entry.component.name || entry.component.passiveKey || 'Interactive Part',
        interaction: entry.interaction,
        interactionLabel: getSimulationInteractionLabel(entry.interaction),
        isActive,
        notes: String(entry.simulationModel?.notes || '').trim(),
        runtimeStateLabel: entry.interaction === 'slider'
          ? (sliderState?.runtimeText || '')
          : (isActive ? 'ACTIVE' : 'IDLE'),
        sliderMax: sliderState?.max ?? 1,
        sliderMin: sliderState?.min ?? 0,
        sliderPercent: sliderState?.percent ?? 50,
        sliderReadoutText: sliderState?.controlReadoutText ?? '50%',
        sliderValue: sliderState?.controlValue ?? 0.5,
        sliderStep: sliderState?.step ?? 0.01,
        terminalRoles: Object.entries(entry.simulationModel?.terminalRoles || {})
          .map(([pinKey, role]) => `${pinKey}:${role}`)
          .join(', '),
      };
    })
  ), [activeSimulationInteractionIds, simulationInteractiveComponents]);
  const passiveEditComponent = useMemo(
    () => components.find((component) => component.id === passiveEditComponentId) || null,
    [components, passiveEditComponentId]
  );
  const passiveEditDefinition = useMemo(() => {
    if (!passiveEditComponent) {
      return passiveEditType === 'led' ? BUILT_IN_LED_PASSIVE : null;
    }

    return (
      passiveComponentMap.get(passiveEditComponent.passiveKey) ||
      passiveComponentMap.get(passiveEditComponent.passiveType) ||
      (passiveEditComponent.passiveType === 'led' ? BUILT_IN_LED_PASSIVE : null)
    );
  }, [passiveComponentMap, passiveEditComponent, passiveEditType]);
  const simulationStatusTone = useMemo(() => {
    if (simulationPhase === 'error') {
      return 'error';
    }

    if (simulationPhase === 'running') {
      return 'success';
    }

    if (!simulationStatus) {
      return 'muted';
    }

    return undefined;
  }, [simulationPhase, simulationStatus]);

  const getSimulationAudioManager = () => {
    if (!simulationAudioManagerRef.current) {
      simulationAudioManagerRef.current = create_passive_buzzer_audio_manager();
    }

    return simulationAudioManagerRef.current;
  };

  const stopSimulationAudio = () => {
    simulationActiveBuzzerIdsRef.current = new Set();

    if (!simulationAudioManagerRef.current) {
      return;
    }

    simulationAudioManagerRef.current.stop_all_buzzers();
  };

  const unlockSimulationAudio = async (shouldEnable = simulationAudioEnabled) => {
    if (!shouldEnable) {
      return false;
    }

    try {
      return await getSimulationAudioManager().ensure_audio_context_unlocked();
    } catch {
      return false;
    }
  };

  const handleSimulationAudioToggle = async () => {
    const nextEnabled = !simulationAudioEnabled;
    setSimulationAudioEnabled(nextEnabled);

    if (!nextEnabled) {
      stopSimulationAudio();
      return;
    }

    await unlockSimulationAudio(nextEnabled);
  };

  const disposeSimulationRunner = () => {
    stopSimulationAudio();

    if (simulationRunnerRef.current) {
      simulationRunnerRef.current.dispose();
      simulationRunnerRef.current = null;
    }

    simulationAppliedDigitalInputLevelsRef.current.clear();
    simulationAppliedAnalogInputVoltagesRef.current.clear();
  };

  const setSimulationInteractionActive = (componentId, isActive) => {
    if (!componentId) {
      return;
    }

    setActiveSimulationInteractionIds((current) => {
      const next = new Set(current);

      if (isActive) {
        next.add(componentId);
      } else {
        next.delete(componentId);
      }

      if (next.size === current.size && Array.from(next).every((id) => current.has(id))) {
        return current;
      }

      return next;
    });
  };

  const updateSimulationSliderInteraction = (componentId, value) => {
    const numericValue = Number(value);

    if (!componentId || !Number.isFinite(numericValue)) {
      return;
    }

    setComponents((current) => current.map((component) => {
      if (component.id !== componentId) {
        return component;
      }

      const definition = getPassiveDefinitionForComponent(component);
      const normalizedSimulationModel = resolvePassiveComponentRuntimeSimulationModel(
        component,
        definition
      );
      const variableResistorState = resolveVariableResistorSimulationState(
        normalizedSimulationModel?.parameters || {},
        {
          totalResistanceOhms: Number(component?.valueOhms) > 0
            ? Number(component.valueOhms)
            : null,
        }
      );
      const dynamicParameterKey = variableResistorState.hasStimulusControl
        ? 'stimulusValue'
        : 'position';

      return {
        ...component,
        simulationModel: {
          ...normalizedSimulationModel,
          parameters: {
            ...(normalizedSimulationModel.parameters || {}),
            [dynamicParameterKey]: numericValue,
          },
        },
      };
    }));
  };

  const beginMomentarySimulationInteraction = (componentId, pointerId = null) => {
    setSimulationInteractionActive(componentId, true);

    if (Number.isFinite(pointerId)) {
      simulationInteractionPointerMapRef.current.set(pointerId, componentId);
    }
  };

  const releaseMomentarySimulationInteraction = (componentId) => {
    if (!componentId) {
      return;
    }

    setSimulationInteractionActive(componentId, false);

    for (const [pointerId, mappedComponentId] of simulationInteractionPointerMapRef.current.entries()) {
      if (mappedComponentId === componentId) {
        simulationInteractionPointerMapRef.current.delete(pointerId);
      }
    }
  };

  const clearMomentarySimulationInteractions = () => {
    const momentaryComponentIds = simulationInteractiveComponents
      .filter((entry) => entry.interaction === 'momentary_press')
      .map((entry) => entry.component.id);

    if (momentaryComponentIds.length === 0) {
      simulationInteractionPointerMapRef.current.clear();
      return;
    }

    setActiveSimulationInteractionIds((current) => {
      const next = new Set(current);
      let didChange = false;

      momentaryComponentIds.forEach((componentId) => {
        if (next.delete(componentId)) {
          didChange = true;
        }
      });

      return didChange ? next : current;
    });

    simulationInteractionPointerMapRef.current.clear();
  };

  const resetSimulationRuntime = () => {
    setSimulationBuiltinLedOn(false);
    setSimulationSerialOutput('');
    setSimulationPinStates(createInitialSimulationPinStates());
    simulationPendingPinTransitionEventsRef.current = [];
    setSimulationSnapshotVersion(0);
    setSimulationElapsedTime('0 ms');
    setSimulationCpuCycles(0);
  };

 

  const applySimulationSnapshot = (snapshot) => {
    const nextPinTransitionEvents = Array.isArray(snapshot?.pinTransitionEvents)
      ? snapshot.pinTransitionEvents
      : [];

    if (nextPinTransitionEvents.length > 0) {
      const trackedLcdPinNames = new Set(
        getTrackedLcdTransitionPinNames(simulationCircuitAnalysisRef.current)
      );

      const lcdPinTransitionEvents = nextPinTransitionEvents.filter((event) => (
        trackedLcdPinNames.has(String(event?.pinName || '').trim())
      ));

      if (lcdPinTransitionEvents.length > 0) {
        simulationPendingPinTransitionEventsRef.current.push(...lcdPinTransitionEvents);
      }
    }

    setSimulationBuiltinLedOn(Boolean(snapshot?.builtinLedOn));
    setSimulationSerialOutput(typeof snapshot?.serialOutput === 'string' ? snapshot.serialOutput : '');
    setSimulationPinStates(snapshot?.pinStates && typeof snapshot.pinStates === 'object'
      ? snapshot.pinStates
      : createInitialSimulationPinStates());
    setSimulationElapsedTime(
      typeof snapshot?.elapsedTimeText === 'string' && snapshot.elapsedTimeText
        ? snapshot.elapsedTimeText
        : '0 ms'
    );
    setSimulationCpuCycles(Number(snapshot?.cpuCycles) || 0);
    setSimulationSnapshotVersion((currentVersion) => currentVersion + 1);
  };

/*   const applySimulationSnapshot = (snapshot) => {
    const nextPinTransitionEvents = Array.isArray(snapshot?.pinTransitionEvents)
      ? snapshot.pinTransitionEvents
      : [];

    if (nextPinTransitionEvents.length > 0) {
      simulationPendingPinTransitionEventsRef.current.push(...nextPinTransitionEvents);
    }

    setSimulationBuiltinLedOn(Boolean(snapshot?.builtinLedOn));
    setSimulationSerialOutput(typeof snapshot?.serialOutput === 'string' ? snapshot.serialOutput : '');
    setSimulationPinStates(snapshot?.pinStates && typeof snapshot.pinStates === 'object'
      ? snapshot.pinStates
      : createInitialSimulationPinStates());
    setSimulationElapsedTime(
      typeof snapshot?.elapsedTimeText === 'string' && snapshot.elapsedTimeText
        ? snapshot.elapsedTimeText
        : '0 ms'
    );
    setSimulationCpuCycles(Number(snapshot?.cpuCycles) || 0);
    setSimulationSnapshotVersion((currentVersion) => currentVersion + 1);
  }; */

  const stopSimulation = (statusMessage = 'Simulation stopped.') => {
    simulationRequestIdRef.current += 1;
    disposeSimulationRunner();
    clearMomentarySimulationInteractions();
    setSimulationPhase('stopped');
    setSimulationStatus(statusMessage);
  };

  const openComponentModal = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    setShowComponentModal(true);
  };

  const syncExpandedSimulationLineNumberTrack = (scrollTop = 0) => {
    expandedSimulationEditorScrollTopRef.current = scrollTop;

    if (expandedSimulationLineNumberTrackRef.current) {
      expandedSimulationLineNumberTrackRef.current.style.transform = `translateY(${-scrollTop}px)`;
    }
  };

  const handleExpandedSimulationEditorScroll = (event) => {
    syncExpandedSimulationLineNumberTrack(event.currentTarget?.scrollTop || 0);
  };

  const setSimulationCopyFeedback = (nextState) => {
    if (simulationCopyFeedbackTimeoutRef.current) {
      window.clearTimeout(simulationCopyFeedbackTimeoutRef.current);
      simulationCopyFeedbackTimeoutRef.current = null;
    }

    setSimulationCopyState(nextState);

    if (nextState === 'idle') {
      return;
    }

    simulationCopyFeedbackTimeoutRef.current = window.setTimeout(() => {
      setSimulationCopyState('idle');
      simulationCopyFeedbackTimeoutRef.current = null;
    }, 1800);
  };

  const copySimulationCode = async () => {
    const codeToCopy = String(simulationCode || '');
    let copied = false;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(codeToCopy);
        copied = true;
      } else if (typeof document !== 'undefined') {
        const textArea = document.createElement('textarea');
        textArea.value = codeToCopy;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        textArea.style.pointerEvents = 'none';

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        copied = document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy simulation code:', error);
      copied = false;
    }

    setSimulationCopyFeedback(copied ? 'copied' : 'error');
  };

  const clampZoom = (value) => {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, value));
  };

  const getDefaultZoomLevel = () => {
    return isLessonMode
      ? clampZoom(resolveLessonDefaultZoom(activeLesson, BASE_ZOOM))
      : BASE_ZOOM;
  };

  const zoomIn = () => {
    setZoomLevel(current => clampZoom(current + ZOOM_STEP));
  };

  const zoomOut = () => {
    setZoomLevel(current => clampZoom(current - ZOOM_STEP));
  };

  const resetZoom = () => {
    setZoomLevel(getDefaultZoomLevel());
  };

  const getCanvasViewBox = () => {
    const viewWidth = CANVAS_WIDTH / zoomLevel;
    const viewHeight = CANVAS_HEIGHT / zoomLevel;

    const x = (CANVAS_WIDTH - viewWidth) / 2;
    const y = (CANVAS_HEIGHT - viewHeight) / 2;

    return `${x} ${y} ${viewWidth} ${viewHeight}`;
  };

  const getSourceSvgContent = (board) => String(
    board?.svg_source_content ||
    board?.svgSourceContent ||
    ''
  ).trim();

  const getRenderedSvgContent = (board) => String(
    board?.svg_content ||
    board?.svgContent ||
    ''
  ).trim();

  const getPassiveDefinitionForComponent = (component) => {
    if (!component || component.type !== 'passive') {
      return null;
    }

    return passiveComponentMap.get(component.passiveKey) || passiveComponentMap.get(component.passiveType) || null;
  };

  const getPassiveSvgContent = (component) => {
    const inlineSvg = String(component?.svgContent || '').trim();

    if (inlineSvg) {
      return inlineSvg;
    }

    return String(getPassiveDefinitionForComponent(component)?.svgContent || '').trim();
  };

  const hasRenderableServoActuator = (svgMarkup) => {
    const markup = String(svgMarkup || '').trim();

    if (!markup) {
      return false;
    }

    return (
      markup.includes('id="servo-actuator"') &&
      markup.includes('id="connector1pin"') &&
      markup.includes('id="connector2pin"') &&
      markup.includes('id="connector3pin"')
    );
  };

  const getPassiveSelectableOptions = (definition) => {
    if (!definition) {
      return [];
    }

    if (Array.isArray(definition.valueOptions) && definition.valueOptions.length > 0) {
      return definition.valueOptions;
    }

    if (definition.templateType === 'resistor') {
      return RESISTOR_VALUE_OPTIONS;
    }

    if (definition.templateType === 'potentiometer') {
      return POTENTIOMETER_VALUE_OPTIONS;
    }

    return [];
  };

  const getPassiveSelectableFieldLabel = (definition) => (
    definition?.templateType === 'seven_segment_display' ? 'Digits' : 'Value'
  );

  const getSevenSegmentCommonTypeForDefinition = (definition, fallbackValue) => (
    normalizeSevenSegmentCommonType(
      fallbackValue ??
      definition?.simulationModel?.parameters?.commonType ??
      definition?.simulationModel?.metadata?.commonType ??
      SEVEN_SEGMENT_DEFAULT_COMMON_TYPE
    )
  );

  const boardHasRenderableSvg = (board) => {
    return Boolean(getRenderedSvgContent(board) || getSourceSvgContent(board));
  };

  const mergeBoardRecords = (summaryBoard, detailedBoard) => {
    const mergedBoard = {
      ...(summaryBoard || {}),
      ...(detailedBoard || {}),
    };

    const resolvedRenderedSvg = getRenderedSvgContent(detailedBoard) || getRenderedSvgContent(summaryBoard);
    const resolvedSourceSvg = getSourceSvgContent(detailedBoard) || getSourceSvgContent(summaryBoard);

    if (resolvedRenderedSvg) {
      mergedBoard.svg_content = resolvedRenderedSvg;
    }

    if (resolvedSourceSvg) {
      mergedBoard.svg_source_content = resolvedSourceSvg;
    }

    return mergedBoard;
  };

  const fetchBoardSvgPayload = async (boardId) => {
    const data = await fetchApiJson(
      apiPath(`/project-builder/boards/${boardId}/svg`),
      'Project Builder board SVG endpoint'
    );

    if (data && !Array.isArray(data)) {
      return data.svg || data.data || data;
    }

    return data;
  };

  const ensureBoardSvgRecord = async (board) => {
    if (!board) {
      return null;
    }

    if (boardHasRenderableSvg(board)) {
      return board;
    }

    const cacheKey = String(board.id || board.slug || board.name || '').trim();
    if (!cacheKey) {
      return board;
    }

    const cachedRequest = boardSvgRequestsRef.current.get(cacheKey);

    if (cachedRequest) {
      const svgPayload = await cachedRequest;
      return mergeBoardRecords(board, svgPayload);
    }

    const request = fetchBoardSvgPayload(board.id)
      .then((svgPayload) => {
        const mergedBoard = mergeBoardRecords(board, svgPayload);

        setBoards((currentBoards) =>
          currentBoards.map((candidateBoard) =>
            String(candidateBoard.id) === String(board.id)
              ? normalize_board(mergeBoardRecords(candidateBoard, mergedBoard), candidateBoard.pins || [])
              : candidateBoard
          )
        );

        return mergedBoard;
      })
      .finally(() => {
        boardSvgRequestsRef.current.delete(cacheKey);
      });

    boardSvgRequestsRef.current.set(cacheKey, request);

    return request;
  };

  const getPreparedBoardSvg = (board) => {
    const renderedSvg = getRenderedSvgContent(board);
    const sourceSvg = getSourceSvgContent(board);
    const svg = normalizeBoardSvgOriginForBuilder(renderedSvg || sourceSvg);

    if (!svg) return '';

    return prepareInlineSvgMarkup(svg, {
      extraCssText: BOARD_EDITOR_SVG_CSS,
    });
  };

  const getSvgPointFromClient = (clientX, clientY) => {
    const svg = canvasRef.current;
    if (!svg) return null;

    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;

    const screenCtm = svg.getScreenCTM();
    if (!screenCtm) return null;

    const transformed = point.matrixTransform(screenCtm.inverse());

    return {
      x: transformed.x,
      y: transformed.y,
    };
  };

  const getCanvasPoint = (event) => {
    return getSvgPointFromClient(event.clientX, event.clientY);
  };

  const getPinTargetFromPointer = (event) => {
    const stackedElements = document.elementsFromPoint(event.clientX, event.clientY);

    const target = stackedElements.find((el) =>
      el instanceof Element &&
      el.matches('.builder-pin-hitbox')
    );

    if (!target) return null;

    const partId = target.getAttribute('data-part-id');
    const pinKey = target.getAttribute('data-pin-key');
    const comp = components.find((item) => item.id === partId);

    if (!comp || !pinKey) return null;

    return {
      partId,
      boardName: comp.board?.name || comp.name,
      pinKey,
    };
  };

  const startWireFromOverlayPin = (event, comp, pinKey, point) => {
    if (isCircuitEditingLocked) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearWireDropFeedback();

    const nextWireDrag = {
      pointerId: event.pointerId,
      from: {
        partId: comp.id,
        boardName: comp.board?.name || comp.name,
        pinKey,
      },
      startPoint: point,
      routePoints: [],
      currentPoint: point,
      color: '#2563eb',
      width: wireWidth,
    };

    wireDragRef.current = nextWireDrag;
    setWirePreview(nextWireDrag);
    setSelectedItem(null);
    setSelectedComponentIds(new Set());

    if (isBreadboardComponent(comp)) {
      drawBreadboardHoverHighlight(comp.id, pinKey);
    }
  };

  const getBreadboardComponentSnap = (drag, dx, dy, limits) => {
    if (!drag || !Array.isArray(drag.componentIds) || drag.componentIds.length !== 1) {
      return null;
    }

    const componentId = drag.componentIds[0];
    const component = componentsRef.current.find((item) => item.id === componentId);

    if (
      !component ||
      component.type !== 'passive' ||
      isBreadboardComponent(component)
    ) {
      return null;
    }

    const componentStart = drag.startPositions?.[componentId];
    const pinOffsets = drag.pinOffsetsByComponentId?.[componentId] || {};
    const targets = Array.isArray(drag.breadboardSnapTargets)
      ? drag.breadboardSnapTargets
      : [];

    if (!componentStart || targets.length === 0 || Object.keys(pinOffsets).length === 0) {
      return null;
    }

    const snapRadiusSquared = BREADBOARD_COMPONENT_SNAP_RADIUS * BREADBOARD_COMPONENT_SNAP_RADIUS;
    let bestCandidate = null;

    Object.entries(pinOffsets).forEach(([componentPinKey, offset]) => {
      if (
        !offset ||
        !Number.isFinite(offset.x) ||
        !Number.isFinite(offset.y)
      ) {
        return;
      }

      const componentPinPoint = {
        x: componentStart.x + dx + offset.x,
        y: componentStart.y + dy + offset.y,
      };

      targets.forEach((target) => {
        const targetPoint = target?.point;

        if (
          !targetPoint ||
          !Number.isFinite(targetPoint.x) ||
          !Number.isFinite(targetPoint.y)
        ) {
          return;
        }

        const deltaX = targetPoint.x - componentPinPoint.x;
        const deltaY = targetPoint.y - componentPinPoint.y;
        const distanceSquared = (deltaX * deltaX) + (deltaY * deltaY);

        if (distanceSquared > snapRadiusSquared) {
          return;
        }

        const nextDx = dx + deltaX;
        const nextDy = dy + deltaY;

        if (
          nextDx < limits.minDx ||
          nextDx > limits.maxDx ||
          nextDy < limits.minDy ||
          nextDy > limits.maxDy
        ) {
          return;
        }

        if (!bestCandidate || distanceSquared < bestCandidate.distanceSquared) {
          bestCandidate = {
            distanceSquared,
            dx: nextDx,
            dy: nextDy,
            componentPinKey,
            targetPartId: target.partId,
            targetPinKey: target.pinKey,
          };
        }
      });
    });

    return bestCandidate;
  };

  const snapCanvasPoint = (point) => {
    if (!point || !snapToGrid) return point;

    return {
      x: snapValueToGrid(point.x, gridSize),
      y: snapValueToGrid(point.y, gridSize),
    };
  };

  const getDistancePointToSegment = (point, start, end) => {
    const dx = end.x - start.x;
    const dy = end.y - start.y;

    if (dx === 0 && dy === 0) {
      return Math.hypot(point.x - start.x, point.y - start.y);
    }

    const t = Math.max(
      0,
      Math.min(
        1,
        ((point.x - start.x) * dx + (point.y - start.y) * dy) / (dx * dx + dy * dy)
      )
    );

    const projected = {
      x: start.x + t * dx,
      y: start.y + t * dy,
    };

    return Math.hypot(point.x - projected.x, point.y - projected.y);
  };

  const getClosestRouteInsertIndex = (connection, clickPoint) => {
    const fromPoint = pinLayoutByPartId[connection.from.partId]?.[connection.from.pinKey];
    const toPoint = pinLayoutByPartId[connection.to.partId]?.[connection.to.pinKey];

    if (!fromPoint || !toPoint || !clickPoint) {
      return (connection.routePoints || []).length;
    }

    const routePoints = connection.routePoints || [];
    const points = [fromPoint, ...routePoints, toPoint];

    let bestSegmentIndex = 0;
    let bestDistance = Infinity;

    for (let index = 0; index < points.length - 1; index += 1) {
      const distance = getDistancePointToSegment(
        clickPoint,
        points[index],
        points[index + 1]
      );

      if (distance < bestDistance) {
        bestDistance = distance;
        bestSegmentIndex = index;
      }
    }

    // Segment 0 means between fromPoint and routePoints[0],
    // so insert at routePoints index 0.
    // Segment 1 means after routePoints[0], etc.
    return bestSegmentIndex;
  };

  const startWireCornerDrag = (event, connection) => {
    if (isCircuitEditingLocked) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const clickPoint = getCanvasPoint(event);
    if (!clickPoint) return;

    const cornerPoint = snapCanvasPoint(clickPoint);
    const insertIndex = getClosestRouteInsertIndex(connection, cornerPoint);
    const historySnapshot = createWorkspaceHistorySnapshot();

    const nextRoutePoints = [
      ...(connection.routePoints || []).slice(0, insertIndex),
      cornerPoint,
      ...(connection.routePoints || []).slice(insertIndex),
    ];

    pushUndoSnapshot(historySnapshot);

    setConnections(current =>
      current.map(item =>
        item.id === connection.id
          ? {
              ...item,
              routePoints: nextRoutePoints,
            }
          : item
      )
    );

    setSelectedItem({ type: 'wire', id: connection.id });

    wireCornerDragRef.current = {
      pointerId: event.pointerId,
      connectionId: connection.id,
      routePointIndex: insertIndex,
    };

    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const addWireCorner = (point) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const wireDrag = wireDragRef.current;
    if (!wireDrag || !point) return;

    const cornerPoint = snapCanvasPoint(point);

    const nextWireDrag = {
      ...wireDrag,
      routePoints: [
        ...(wireDrag.routePoints || []),
        cornerPoint,
      ],
      currentPoint: cornerPoint,
    };

    wireDragRef.current = nextWireDrag;
    setWirePreview(nextWireDrag);
  };

  const areWireEndpointsEquivalent = (leftEndpoint, rightEndpoint) => (
    Boolean(leftEndpoint?.partId) &&
    Boolean(leftEndpoint?.pinKey) &&
    String(leftEndpoint.partId) === String(rightEndpoint?.partId || '') &&
    String(leftEndpoint.pinKey) === String(rightEndpoint?.pinKey || '')
  );

  const isValidWireConnectionTarget = (sourceEndpoint, targetEndpoint) => (
    Boolean(sourceEndpoint?.partId) &&
    Boolean(sourceEndpoint?.pinKey) &&
    Boolean(targetEndpoint?.partId) &&
    Boolean(targetEndpoint?.pinKey) &&
    !areWireEndpointsEquivalent(sourceEndpoint, targetEndpoint)
  );

  const finishWireAtTarget = (target) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const wireDrag = wireDragRef.current;

    if (!wireDrag || !target) return;

    if (!isValidWireConnectionTarget(wireDrag.from, target)) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setConnections(current => [
      ...current,
      {
        id: `wire_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        from: wireDrag.from,
        to: target,
        routePoints: wireDrag.routePoints || [],
        color: wireDrag.color || '#2563eb',
        width: wireDrag.width || wireWidth,
      },
    ]);

    wireDragRef.current = null;
    setWirePreview(null);
    clearWireDropFeedback();
    clearBreadboardHoverHighlight();
  };

  const cancelWireDrag = () => {
    wireDragRef.current = null;
    setWirePreview(null);
    clearWireDropFeedback();
    clearBreadboardHoverHighlight();
  };

  const getWireDropFeedback = (event, sourceEndpointOrPartId) => {
    const sourceEndpoint = typeof sourceEndpointOrPartId === 'object' && sourceEndpointOrPartId
      ? sourceEndpointOrPartId
      : { partId: sourceEndpointOrPartId };
    const target = getPinTargetFromPointer(event);

    if (!target) {
      return {
        status: null,
        target: null,
      };
    }

    return {
      status: isValidWireConnectionTarget(sourceEndpoint, target) ? 'valid' : 'invalid',
      target: {
        partId: String(target.partId),
        pinKey: String(target.pinKey),
      },
    };
  };

  const applyWireDropFeedback = (feedback) => {
    const nextStatus = feedback?.status || null;
    const nextTarget = feedback?.target && nextStatus
      ? {
          partId: String(feedback.target.partId),
          pinKey: String(feedback.target.pinKey),
          status: nextStatus,
        }
      : null;

    setWireDropStatus(nextStatus);
    setPinHoverTarget(current => {
      if (!nextTarget) {
        return current ? null : current;
      }

      if (
        current &&
        current.partId === nextTarget.partId &&
        current.pinKey === nextTarget.pinKey &&
        current.status === nextTarget.status
      ) {
        return current;
      }

      return nextTarget;
    });
  };

  const applyPinHoverFeedback = (target, status = 'hover') => {
    const nextTarget = target
      ? {
          partId: String(target.partId),
          pinKey: String(target.pinKey),
          status,
        }
      : null;

    setPinHoverTarget(current => {
      if (!nextTarget) {
        return current ? null : current;
      }

      if (
        current &&
        current.partId === nextTarget.partId &&
        current.pinKey === nextTarget.pinKey &&
        current.status === nextTarget.status
      ) {
        return current;
      }

      return nextTarget;
    });
  };

  const getActiveWireDropSourceEndpoint = () => {
    const rewireDrag = rewireDragRef.current;

    if (rewireDrag) {
      const fixedConnection = connectionsRef.current.find(c => c.id === rewireDrag.connectionId);
      return fixedConnection?.[rewireDrag.fixedSide] || null;
    }

    return wireDragRef.current?.from || null;
  };

  const startRewireDrag = (event, connection, side) => {
    if (isCircuitEditingLocked) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearWireDropFeedback();

    event.currentTarget.setPointerCapture?.(event.pointerId);

    const fixedSide = side === 'from' ? 'to' : 'from';
    const fixedPoint = pinLayoutByPartId[connection[fixedSide].partId]?.[connection[fixedSide].pinKey];

    if (!fixedPoint) return;

    rewireDragRef.current = {
      pointerId: event.pointerId,
      connectionId: connection.id,
      historySnapshot: createWorkspaceHistorySnapshot(),
      movingSide: side,
      fixedSide,
      fixedPoint,
      currentPoint: fixedPoint,
      color: connection.color || '#2563eb',
      width: connection.width || wireWidth,
    };

    setWirePreview({
      startPoint: fixedPoint,
      routePoints: connection.routePoints || [],
      currentPoint: fixedPoint,
      color: connection.color || '#2563eb',
      width: connection.width || wireWidth,
    });
  };

  
  const getPinElementCenter = (pinElement) => {
    return getSvgElementCenter(pinElement);
  };

  const parseSvgLength = (value) => {
    const text = String(value || '').trim();
    if (!text) {
      return null;
    }

    const match = text.match(/^([+-]?\d*\.?\d+)([a-z%]*)$/i);
    if (!match) {
      return null;
    }

    const amount = Number.parseFloat(match[1]);
    const unit = String(match[2] || 'px').toLowerCase();
    if (!Number.isFinite(amount)) {
      return null;
    }

    return {
      value: amount,
      unit,
      px: SVG_LENGTH_TO_PX[unit] ? amount * SVG_LENGTH_TO_PX[unit] : null,
    };
  };

  const parseViewBox = (value) => {
    const parts = String(value || '')
      .trim()
      .split(/[\s,]+/)
      .map((segment) => Number.parseFloat(segment))
      .filter((segment) => Number.isFinite(segment));

    if (parts.length >= 4) {
      return {
        minX: parts[0],
        minY: parts[1],
        width: parts[2],
        height: parts[3],
      };
    }

    return null;
  };

  const parseSvgMetrics = (rawSvgContent) => {
    const fallback = {
      width: DEFAULT_BOARD_TARGET_WIDTH,
      height: 180,
      label: 'Fallback size',
    };

    const markup = String(rawSvgContent || '').trim();
    if (!markup) {
      return fallback;
    }

    const logMetricResult = (result, source = 'unknown') => {
      if (DEBUG_PROJECT_BUILDER_DRAG) {
        console.log('[PB SVG metrics]', {
          source,
          widthValue,
          heightValue,
          viewBoxValue,
          parsedWidth: result.width,
          parsedHeight: result.height,
          aspectRatio: result.height / result.width,
          label: result.label,
        });
      }

      return result;
    };

    let widthValue = '';
    let heightValue = '';
    let viewBoxValue = '';

    if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(markup, 'image/svg+xml');
        const parseError = parsedDocument.querySelector('parsererror');
        const svg = parsedDocument.documentElement;

        if (!parseError && svg && svg.nodeName.toLowerCase().includes('svg')) {
          widthValue = svg.getAttribute('width') || '';
          heightValue = svg.getAttribute('height') || '';
          viewBoxValue = svg.getAttribute('viewBox') || '';
        }
      } catch (error) {
        // Fallback to regex below.
      }
    }

    if (!widthValue || !heightValue) {
      const widthMatch = markup.match(/\bwidth=["']([^"']+)["']/i);
      const heightMatch = markup.match(/\bheight=["']([^"']+)["']/i);
      widthValue = widthValue || widthMatch?.[1] || '';
      heightValue = heightValue || heightMatch?.[1] || '';
    }

    if (!viewBoxValue) {
      viewBoxValue = markup.match(/\bviewBox=["']([^"']+)["']/i)?.[1] || '';
    }

    const widthInfo = parseSvgLength(widthValue);
    const heightInfo = parseSvgLength(heightValue);
    const viewBox = parseViewBox(viewBoxValue);

    if (widthInfo?.px && heightInfo?.px) {
      const preferredUnit = widthInfo.unit === heightInfo.unit ? widthInfo.unit : 'svg-units';

      return logMetricResult({
        width: Math.max(widthInfo.px, 1),
        height: Math.max(heightInfo.px, 1),
        label: preferredUnit === 'px' ? 'SVG width/height scale' : `SVG ${preferredUnit} scale`,
      }, 'width-height-px');
    }

    if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
      return logMetricResult({
        width: viewBox.width,
        height: viewBox.height,
        label: 'SVG viewBox scale',
      }, 'viewBox');
    }

    if (widthInfo?.value && heightInfo?.value) {
      return logMetricResult({
        width: Math.max(widthInfo.value, 1),
        height: Math.max(heightInfo.value, 1),
        label: 'SVG attribute scale',
      }, 'width-height-raw');
    }

    return logMetricResult(fallback, 'fallback');

  };

  const formatSvgViewBoxNumber = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return String(Number(numericValue.toFixed(4)));
  };

  const normalizeBoardSvgOriginForBuilder = (rawSvgContent) => {
    const markup = String(rawSvgContent || '').trim();
    if (!markup || typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') {
      return markup;
    }

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(markup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svg = parsedDocument.documentElement;

      if (parseError || !svg || !svg.nodeName.toLowerCase().includes('svg')) {
        return markup;
      }

      const viewBox = parseViewBox(svg.getAttribute('viewBox') || '');
      if (!viewBox) {
        return markup;
      }

      const hasOffsetOrigin = Math.abs(viewBox.minX) > 0.01 || Math.abs(viewBox.minY) > 0.01;
      if (!hasOffsetOrigin) {
        return markup;
      }

      const widthInfo = parseSvgLength(svg.getAttribute('width') || '');
      const heightInfo = parseSvgLength(svg.getAttribute('height') || '');
      const widthValue = Number(widthInfo?.value);
      const heightValue = Number(heightInfo?.value);
      const widthTolerance = Math.max(0.25, Math.abs(viewBox.width) * 0.01);
      const heightTolerance = Math.max(0.25, Math.abs(viewBox.height) * 0.01);
      const widthMatchesViewBox = Number.isFinite(widthValue) && Math.abs(widthValue - viewBox.width) <= widthTolerance;
      const heightMatchesViewBox = Number.isFinite(heightValue) && Math.abs(heightValue - viewBox.height) <= heightTolerance;

      if (!widthMatchesViewBox || !heightMatchesViewBox) {
        return markup;
      }

      svg.setAttribute(
        'viewBox',
        `0 0 ${formatSvgViewBoxNumber(viewBox.width)} ${formatSvgViewBoxNumber(viewBox.height)}`
      );

      return new XMLSerializer().serializeToString(svg);
    } catch (error) {
      return markup;
    }
  };

  const getDisplayPinKey = (pinKey) => {
    return String(pinKey || '').replace(/__\d+$/, '');
  };

  const getCompactBoardName = (name) => {
    const words = String(name || '').trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) return 'Component';

    // Keep common short board names readable.
    if (words.length === 1) return words[0];

    return words[0];
  };

  const getCompactPinName = (pinKey, maxLength = 8) => {
    const cleanPin = getDisplayPinKey(pinKey);

    if (cleanPin.length <= maxLength) {
      return cleanPin;
    }

    return `${cleanPin.slice(0, maxLength - 1)}…`;
  };

  const getConnectionDisplayParts = (connection) => {
    return {
      fromBoard: getCompactBoardName(connection.from.boardName),
      fromPin: getCompactPinName(connection.from.pinKey),
      toBoard: getCompactBoardName(connection.to.boardName),
      toPin: getCompactPinName(connection.to.pinKey),
    };
  };

  const getWireRoleLabel = (connection) => {
    if (connection.wireColorRole === 'power') return 'Power';
    if (connection.wireColorRole === 'ground') return 'Ground';
    if (connection.wireColorRole === 'signal') {
      if (connection.electricalNote) {
        return connection.electricalNote;
      }

      if (connection.confidence) {
        return String(connection.confidence).toUpperCase();
      }

      return 'Signal';
    }

    if (connection.confidence) {
      return String(connection.confidence).toUpperCase();
    }

    return 'Wire';
  };

  const getBoardProjectBuilderScaleValue = (board) => {
    const projectBuilderScale = Number(
      board?.project_builder_scale ??
      board?.projectBuilderScale ??
      1
    );

    return Number.isFinite(projectBuilderScale) && projectBuilderScale > 0
      ? projectBuilderScale
      : 1;
  };

  const isBoardAllowedInProjectBuilder = (board) => {
    const rawValue = board?.project_builder_enabled ?? board?.projectBuilderEnabled;

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return true;
    }

    if (rawValue === false || rawValue === 0 || rawValue === '0') {
      return false;
    }

    if (typeof rawValue === 'string') {
      return !['false', 'no', 'off'].includes(rawValue.trim().toLowerCase());
    }

    return true;
  };

  const clampBoardTargetWidth = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return DEFAULT_BOARD_TARGET_WIDTH;
    }

    return Math.min(
      MAX_BOARD_TARGET_WIDTH,
      Math.max(MIN_BOARD_TARGET_WIDTH, numericValue)
    );
  };

  const getComponentFootprint = (comp) => {
    if (comp.type === 'passive') {
      const definition = getPassiveDefinitionForComponent(comp);
      const baseWidth = Number(
        comp.canvasWidth ||
        definition?.canvasWidth ||
        BUILT_IN_LED_PASSIVE.canvasWidth
      );
      const baseHeight = Number(
        comp.canvasHeight ||
        definition?.canvasHeight ||
        BUILT_IN_LED_PASSIVE.canvasHeight
      );

      return {
        width: Math.max(8, baseWidth) * Number(comp.scale || 1),
        height: Math.max(8, baseHeight) * Number(comp.scale || 1),
      };
    }

    const sourceSvg = getSourceSvgContent(comp.board);
    const renderedSvg = getRenderedSvgContent(comp.board);
    const metric = parseSvgMetrics(renderedSvg || sourceSvg);

    const targetWidth = comp.targetWidth || DEFAULT_BOARD_TARGET_WIDTH;

    const scale = Number(comp.scale || 1);
    const effectiveProjectBuilderScale = getBoardProjectBuilderScaleValue(comp.board);
    const aspectRatio = metric.height / metric.width;

    const footprint = {
      width: targetWidth * scale * effectiveProjectBuilderScale,
      height: targetWidth * scale * effectiveProjectBuilderScale * aspectRatio,
    };

    if (DEBUG_PROJECT_BUILDER_DRAG) {
      console.log('[PB footprint]', {
        name: comp.board?.name || comp.name,
        componentId: comp.id,
        targetWidth,
        scale,
        projectBuilderScale: effectiveProjectBuilderScale,
        metricWidth: metric.width,
        metricHeight: metric.height,
        metricLabel: metric.label,
        aspectRatio,
        footprint,
        rotation: comp.rotation || 0,
      });
    }

    return footprint;
  };

  const cropSvgToVisibleContent = (svgMarkup) => {
    if (!svgMarkup || typeof DOMParser === 'undefined') return svgMarkup;

    const parser = new DOMParser();
    const doc = parser.parseFromString(svgMarkup, 'image/svg+xml');
    const parsedSvg = doc.documentElement;

    if (!parsedSvg || parsedSvg.nodeName.toLowerCase() !== 'svg') {
      return svgMarkup;
    }

    const svg = document.importNode(parsedSvg, true);

    svg.querySelectorAll(`
      [data-micropinouts-callout="1"],
      [data-micropinouts-callout-style="1"]
    `).forEach(el => el.remove());

    const wrapper = document.createElement('div');
    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '-10000px';
    wrapper.style.width = '500px';
    wrapper.style.height = '500px';
    wrapper.style.visibility = 'hidden';

    wrapper.appendChild(svg);
    document.body.appendChild(wrapper);

    let bbox;

    try {
      bbox = svg.getBBox();
    } catch {
      document.body.removeChild(wrapper);
      return svgMarkup;
    }

    document.body.removeChild(wrapper);

    if (!bbox || bbox.width <= 0 || bbox.height <= 0) {
      return svgMarkup;
    }

    const padding = 4;

    svg.setAttribute(
      'viewBox',
      `${bbox.x - padding} ${bbox.y - padding} ${bbox.width + padding * 2} ${bbox.height + padding * 2}`
    );

    svg.removeAttribute('width');
    svg.removeAttribute('height');

    return new XMLSerializer().serializeToString(svg);
  };

  const getPreparedBoardIconSvg = (board) => {
    if (!board) return '';

    const cacheKey = String(board.id || board.slug || board.name || '');

    if (cacheKey && boardIconCacheRef.current[cacheKey]) {
      return boardIconCacheRef.current[cacheKey];
    }

    const sourceSvg = getSourceSvgContent(board);
    const renderedSvg = getRenderedSvgContent(board);

    const svg = normalizeBoardSvgOriginForBuilder(sourceSvg || renderedSvg);

    if (!svg) return '';

    const preparedSvg = prepareInlineSvgMarkup(svg, {
      extraCssText: BOARD_ICON_SVG_CSS,
    });

    const croppedSvg = cropSvgToVisibleContent(preparedSvg);

    if (cacheKey) {
      boardIconCacheRef.current[cacheKey] = croppedSvg;
    }

    return croppedSvg;
  };

  const getComponentSidebarIconMarkup = (comp) => {
    if (comp.type === 'passive') {
      return getPreparedPassiveSvg(comp);
    }

    return getPreparedBoardIconSvg(comp.board);
  };

  const getComponentDragBounds = (comp) => {
    const footprint = getComponentFootprint(comp);
    const rotation = ((comp.rotation || 0) % 360 + 360) % 360;

    const bounds = rotation === 90 || rotation === 270
      ? {
          width: footprint.height,
          height: footprint.width,
        }
      : footprint;

    if (DEBUG_PROJECT_BUILDER_DRAG) {
      console.log('[PB drag bounds]', {
        name: comp.board?.name || comp.name,
        componentId: comp.id,
        rotation,
        footprint,
        bounds,
      });
    }

    return bounds;
  };

  const clampAxisForBounds = (nextValue, viewStart, viewSize, itemSize, axis = 'axis') => {
    const normalMin = viewStart;
    const normalMax = viewStart + viewSize - itemSize;

    const isOversized = itemSize > viewSize;

    const clampedValue = !isOversized
      ? Math.min(Math.max(nextValue, normalMin), normalMax)
      : Math.min(Math.max(nextValue, normalMax), normalMin);

    if (DEBUG_PROJECT_BUILDER_DRAG) {
      console.log('[PB clamp axis]', {
        axis,
        nextValue,
        viewStart,
        viewSize,
        itemSize,
        normalMin,
        normalMax,
        isOversized,
        clampedValue,
        allowedRange: isOversized
          ? { min: normalMax, max: normalMin }
          : { min: normalMin, max: normalMax },
      });
    }

    return clampedValue;
  };

  const clampComponentPosition = (comp, nextX, nextY) => {
    const bounds = getComponentDragBounds(comp);
    const viewBounds = getCurrentViewBoxBounds();

    const clamped = {
      x: clampAxisForBounds(
        nextX,
        viewBounds.x,
        viewBounds.width,
        bounds.width,
        'x'
      ),
      y: clampAxisForBounds(
        nextY,
        viewBounds.y,
        viewBounds.height,
        bounds.height,
        'y'
      ),
    };

    if (DEBUG_PROJECT_BUILDER_DRAG) {
      console.log('[PB clamp component]', {
        name: comp.board?.name || comp.name,
        componentId: comp.id,
        next: { x: nextX, y: nextY },
        clamped,
        viewBounds,
        bounds,
      });
    }

    return clamped;
  };

  const getResolvedComponentPosition = (comp, nextX, nextY) => {
    const clampedPosition = clampComponentPosition(comp, nextX, nextY);

    if (!snapToGrid) {
      return clampedPosition;
    }

    const snappedPosition = {
      x: snapValueToGrid(clampedPosition.x, gridSize),
      y: snapValueToGrid(clampedPosition.y, gridSize),
    };

    return clampComponentPosition(comp, snappedPosition.x, snappedPosition.y);
  };

  const handleCanvasPointerDown = (event) => {
    if (isCircuitEditingLocked) {
      return;
    }

    // If a wire is active, let onClick add a corner.
    // Do not start box selection.
    if (wireDragRef.current) {
      return;
    }

    // Only start box-select from empty canvas.
    if (event.target !== event.currentTarget) return;

    const point = getCanvasPoint(event);
    if (!point) return;

    event.preventDefault();

    suppressCanvasClickRef.current = true;

    selectionDragRef.current = {
      startPoint: point,
      currentPoint: point,
      append: event.shiftKey || event.ctrlKey || event.metaKey,
      selectedIds: [],
    };

    setSelectionBox({
      x: point.x,
      y: point.y,
      width: 0,
      height: 0,
    });

    if (!(event.shiftKey || event.ctrlKey || event.metaKey)) {
      setSelectedComponentIds(new Set());
      setSelectedItem(null);
    }
  };

  const handleSimulationInteractionPointerDown = (event, comp) => {
    if (
      simulationPhase !== 'running' ||
      !showSimulationModal ||
      !comp ||
      comp.type !== 'passive'
    ) {
      return false;
    }

    const interactionEntry = simulationInteractiveComponentMap.get(comp.id);
    const interactionType = interactionEntry?.interaction || 'none';

    if (!['momentary_press', 'toggle'].includes(interactionType)) {
      return false;
    }

    event.preventDefault();
    event.stopPropagation();
    didDragComponentRef.current = true;

    if (interactionType === 'toggle') {
      setActiveSimulationInteractionIds((current) => {
        const next = new Set(current);

        if (next.has(comp.id)) {
          next.delete(comp.id);
        } else {
          next.add(comp.id);
        }

        return next;
      });

      return true;
    }

    beginMomentarySimulationInteraction(comp.id, event.pointerId);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    return true;
  };

  const handleSimulationInteractionControlPointerDown = (event, componentId, interactionType) => {
    if (simulationPhase !== 'running') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (interactionType === 'toggle') {
      setActiveSimulationInteractionIds((current) => {
        const next = new Set(current);

        if (next.has(componentId)) {
          next.delete(componentId);
        } else {
          next.add(componentId);
        }

        return next;
      });

      return;
    }

    if (interactionType === 'momentary_press') {
      beginMomentarySimulationInteraction(componentId, event.pointerId);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }
  };

  const handleSimulationInteractionControlPointerUp = (event, componentId, interactionType) => {
    if (interactionType !== 'momentary_press') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    releaseMomentarySimulationInteraction(componentId);
  };

  const handleComponentPointerDown = (event, comp) => {
    const historySnapshot = createWorkspaceHistorySnapshot();
    const target = event.target instanceof Element ? event.target : null;

    // Pins normally start wires, but placement-focused lesson steps let the
    // learner grab the component from either its body or its terminals.
    if (target?.closest('.pin-hole, .pin-rect')) {
      if (!shouldDragLessonComponentFromPin(comp)) {
        handlePinClick(event, comp);
        return;
      }
    }

    if (handleSimulationInteractionPointerDown(event, comp)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    event.currentTarget.setPointerCapture?.(event.pointerId);

    const point = getCanvasPoint(event);
    if (!point) return;

    const isMultiKey = event.shiftKey || event.ctrlKey || event.metaKey;

    let nextSelectedIds = selectedComponentIds;

    if (isMultiKey) {
      nextSelectedIds = new Set(selectedComponentIds);

      if (nextSelectedIds.has(comp.id)) {
        nextSelectedIds.delete(comp.id);
      } else {
        nextSelectedIds.add(comp.id);
      }

      setSelectedComponentIds(nextSelectedIds);
      setSelectedItem({ type: 'component', id: comp.id });
    } else if (!selectedComponentIds.has(comp.id)) {
      nextSelectedIds = new Set([comp.id]);
      setSelectedComponentIds(nextSelectedIds);
      setSelectedItem({ type: 'component', id: comp.id });
    }

    if (!canMoveComponent(comp)) {
      didDragComponentRef.current = false;
      return;
    }

    const dragIds = nextSelectedIds.has(comp.id)
      ? Array.from(nextSelectedIds)
      : [comp.id];

    const selectedComponents = components.filter(item => dragIds.includes(item.id));
    const pinOffsetsByComponentId = Object.fromEntries(
      selectedComponents.map((item) => [
        item.id,
        Object.fromEntries(
          Object.entries(pinLayoutByPartId[item.id] || {})
            .filter(([, pinPoint]) => (
              pinPoint &&
              Number.isFinite(pinPoint.x) &&
              Number.isFinite(pinPoint.y)
            ))
            .map(([pinKey, pinPoint]) => [
              pinKey,
              {
                x: pinPoint.x - item.x,
                y: pinPoint.y - item.y,
              },
            ])
        ),
      ])
    );
    const breadboardSnapTargets = components
      .filter((item) => (
        isComponentVisible(item) &&
        isBreadboardComponent(item) &&
        !dragIds.includes(item.id)
      ))
      .flatMap((item) => (
        Object.entries(pinLayoutByPartId[item.id] || {})
          .filter(([, pinPoint]) => (
            pinPoint &&
            Number.isFinite(pinPoint.x) &&
            Number.isFinite(pinPoint.y)
          ))
          .map(([pinKey, pinPoint]) => ({
            partId: item.id,
            pinKey,
            point: pinPoint,
          }))
      ));

    didDragComponentRef.current = false;
    setIsComponentDragActive(true);

    dragRef.current = {
      type: 'components',
      historySnapshot,
      pointerId: event.pointerId,
      anchorId: comp.id,
      componentIds: dragIds,
      lastAppliedDx: 0,
      lastAppliedDy: 0,
      pinOffsetsByComponentId,
      breadboardSnapTargets,
      startPoint: point,
      startPositions: Object.fromEntries(
        selectedComponents.map(item => [
          item.id,
          {
            x: item.x,
            y: item.y,
          },
        ])
      ),
    };
  };

  const handlePinClick = (event, comp) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const pinElement = event.target instanceof Element
      ? event.target.closest('.pin-hole, .pin-rect')
      : null;

    if (!pinElement) return;

    event.preventDefault();
    event.stopPropagation();

    const pinKey = getPinMarkerKey(pinElement);
    if (!pinKey) return;

    const pinCenter = pinLayoutByPartId[comp.id]?.[pinKey] || getPinElementCenter(pinElement);
    if (!pinCenter) return;

    const activeWire = wireDragRef.current;

    if (activeWire) {
      finishWireAtTarget({
        partId: comp.id,
        boardName: comp.board?.name || comp.name,
        pinKey,
      });
      return;
    }

    clearWireDropFeedback();

    wireDragRef.current = {
      from: {
        partId: comp.id,
        boardName: comp.board?.name || comp.name,
        pinKey,
      },
      startPoint: pinCenter,
      routePoints: [],
      currentPoint: pinCenter,
      color: '#2563eb',
      width: wireWidth,
    };

    setWirePreview(wireDragRef.current);

    if (isBreadboardComponent(comp)) {
      drawBreadboardHoverHighlight(comp.id, pinKey);
    }
  };

  const getWirePoints = (startPoint, routePoints = [], endPoint) => {
    return [
      startPoint,
      ...(Array.isArray(routePoints) ? routePoints : []),
      endPoint,
    ].filter(point =>
      point &&
      Number.isFinite(point.x) &&
      Number.isFinite(point.y)
    );
  };

  const getPolylinePath = (points) => {
    if (!Array.isArray(points) || points.length === 0) {
      return '';
    }

    const [firstPoint, ...restPoints] = points;

    return [
      `M ${firstPoint.x} ${firstPoint.y}`,
      ...restPoints.map(point => `L ${point.x} ${point.y}`),
    ].join(' ');
  };

  const getConnectionRoutePath = (startPoint, routePoints = [], endPoint) => {
    return getPolylinePath(getWirePoints(startPoint, routePoints, endPoint));
  };

  const getConnectionPath = (startPoint, endPoint) => {
    const dx = endPoint.x - startPoint.x;
    const dy = endPoint.y - startPoint.y;

    if (Math.abs(dx) >= Math.abs(dy)) {
      const bend = Math.min(120, Math.max(40, Math.abs(dx) * 0.35));
      const direction = dx >= 0 ? 1 : -1;

      return `
        M ${startPoint.x} ${startPoint.y}
        C ${startPoint.x + bend * direction} ${startPoint.y},
          ${endPoint.x - bend * direction} ${endPoint.y},
          ${endPoint.x} ${endPoint.y}
      `;
    }

    const bend = Math.min(120, Math.max(40, Math.abs(dy) * 0.35));
    const direction = dy >= 0 ? 1 : -1;

    return `
      M ${startPoint.x} ${startPoint.y}
      C ${startPoint.x} ${startPoint.y + bend * direction},
        ${endPoint.x} ${endPoint.y - bend * direction},
        ${endPoint.x} ${endPoint.y}
    `;
  };

  const getPinMarkerKey = (pinElement) => {
    return (
      pinElement.getAttribute('data-builder-marker-key') ||
      pinElement.getAttribute('data-pin') ||
      pinElement.getAttribute('data-svgid') ||
      pinElement.getAttribute('data-pin-number') ||
      `pin_${Date.now()}`
    );
  };

  const getCanvasPointFromElementRect = (element) => {
    const rect = element?.getBoundingClientRect?.();

    if (!rect || rect.width <= 0 || rect.height <= 0) {
      return null;
    }

    return getSvgPointFromClient(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2
    );
  };

  const rotateCanvasPoint = (point, center, rotationDegrees = 0) => {
    const normalizedRotation = ((Number(rotationDegrees) || 0) % 360 + 360) % 360;

    if (normalizedRotation === 0) {
      return {
        x: point.x,
        y: point.y,
      };
    }

    const radians = (normalizedRotation * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const dx = point.x - center.x;
    const dy = point.y - center.y;

    return {
      x: center.x + (dx * cos) - (dy * sin),
      y: center.y + (dx * sin) + (dy * cos),
    };
  };

  const getBoardLocalPinLayout = (board) => {
    if (!board || typeof document === 'undefined' || typeof DOMParser === 'undefined') {
      return null;
    }

    const renderedSvg = getRenderedSvgContent(board);
    const sourceSvg = getSourceSvgContent(board);
    const svgMarkup = normalizeBoardSvgOriginForBuilder(renderedSvg || sourceSvg);

    if (!svgMarkup) {
      return null;
    }

    const cacheKey = [
      String(board.id || board.slug || board.name || ''),
      String(board.updated_at || ''),
      String(svgMarkup.length),
    ].join('|');

    const cachedLayout = boardLocalPinLayoutCacheRef.current.get(cacheKey);
    if (cachedLayout) {
      return cachedLayout;
    }

    let wrapper = null;

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(svgMarkup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const parsedSvg = parsedDocument.documentElement;

      if (parseError || !parsedSvg || parsedSvg.nodeName.toLowerCase() !== 'svg') {
        return null;
      }

      const importedSvg = document.importNode(parsedSvg, true);
      const parsedViewBox = parseViewBox(importedSvg.getAttribute('viewBox') || '');
      const metrics = parseSvgMetrics(svgMarkup, {
        width: 100,
        height: 100,
        label: 'fallback',
      });
      const viewBox = parsedViewBox || {
        minX: 0,
        minY: 0,
        width: metrics.width,
        height: metrics.height,
      };
      const safeWidth = Math.max(Number(viewBox.width) || metrics.width || 1, 1);
      const safeHeight = Math.max(Number(viewBox.height) || metrics.height || 1, 1);

      importedSvg.setAttribute('width', String(safeWidth));
      importedSvg.setAttribute('height', String(safeHeight));
      importedSvg.style.width = `${safeWidth}px`;
      importedSvg.style.height = `${safeHeight}px`;
      importedSvg.style.display = 'block';
      importedSvg.style.overflow = 'visible';

      wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-10000px';
      wrapper.style.top = '-10000px';
      wrapper.style.width = `${safeWidth}px`;
      wrapper.style.height = `${safeHeight}px`;
      wrapper.style.visibility = 'hidden';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.overflow = 'hidden';
      wrapper.appendChild(importedSvg);
      document.body.appendChild(wrapper);

      const rootScreenCtm = importedSvg.getScreenCTM?.();
      if (!rootScreenCtm || typeof importedSvg.createSVGPoint !== 'function') {
        return null;
      }

      const points = {};
      const markerElements = Array.from(
        importedSvg.querySelectorAll('.pin-hole, .pin-rect')
      ).filter((element) => element.getAttribute('data-micropinouts-callout') !== '1');

      markerElements.forEach((pinElement, index) => {
        const logicalPinKey =
          pinElement.getAttribute('data-pin') ||
          pinElement.getAttribute('data-svgid') ||
          pinElement.getAttribute('data-pin-number') ||
          pinElement.getAttribute('id') ||
          `pin_${index}`;

        const markerKey = `${logicalPinKey}__${index}`;
        const rect = pinElement.getBoundingClientRect?.();

        if (!rect || rect.width <= 0 || rect.height <= 0) {
          return;
        }

        const point = importedSvg.createSVGPoint();
        point.x = rect.left + rect.width / 2;
        point.y = rect.top + rect.height / 2;

        const localPoint = point.matrixTransform(rootScreenCtm.inverse());

        points[markerKey] = {
          x: localPoint.x,
          y: localPoint.y,
        };
      });

      const result = {
        viewBox,
        points,
      };

      boardLocalPinLayoutCacheRef.current.set(cacheKey, result);
      return result;
    } catch {
      return null;
    } finally {
      if (wrapper?.parentNode) {
        wrapper.parentNode.removeChild(wrapper);
      }
    }
  };

  const getLocalPinCenterFromSvgElement = (pinElement) => {
    if (!pinElement) {
      return null;
    }

    const tagName = String(pinElement.tagName || '').toLowerCase();
    const getNumberAttribute = (name) => {
      const value = Number(pinElement.getAttribute(name));
      return Number.isFinite(value) ? value : null;
    };

    if (tagName === 'circle' || tagName === 'ellipse') {
      const cx = getNumberAttribute('cx');
      const cy = getNumberAttribute('cy');

      if (cx !== null && cy !== null) {
        return { x: cx, y: cy };
      }
    }

    if (tagName === 'rect') {
      const x = getNumberAttribute('x') ?? 0;
      const y = getNumberAttribute('y') ?? 0;
      const width = getNumberAttribute('width');
      const height = getNumberAttribute('height');

      if (width !== null && height !== null) {
        return {
          x: x + (width / 2),
          y: y + (height / 2),
        };
      }
    }

    return null;
  };

  const getBreadboardLocalPinLayout = (comp) => {
    if (!isBreadboardComponent(comp) || typeof DOMParser === 'undefined') {
      return null;
    }

    const sourceSvg = getPassiveSvgContent(comp) || breadboardSvg;

    if (!sourceSvg) {
      return null;
    }

    const pinOffsets = comp.pinOffsets || getPassiveDefinitionForComponent(comp)?.pinOffsets;
    const safeSourceSvg = String(sourceSvg);
    const cacheKey = [
      String(safeSourceSvg.length),
      safeSourceSvg.slice(0, 96),
      safeSourceSvg.slice(-96),
      JSON.stringify(pinOffsets || {}),
    ].join('|');

    if (breadboardLocalPinLayoutCacheRef.current.has(cacheKey)) {
      return breadboardLocalPinLayoutCacheRef.current.get(cacheKey);
    }

    try {
      const preparedMarkup = preparePassiveSvgBuilderMarkup({
        passiveType: BREADBOARD_TEMPLATE_TYPE,
        svgContent: safeSourceSvg,
        pinOffsets,
      });

      if (!preparedMarkup) {
        return null;
      }

      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(preparedMarkup, 'image/svg+xml');
      const parseError = parsedDocument.querySelector('parsererror');
      const svg = parsedDocument.documentElement;

      if (parseError || !svg || !svg.nodeName.toLowerCase().includes('svg')) {
        return null;
      }

      const metrics = parseSvgMetrics(preparedMarkup);
      const viewBox = parseViewBox(svg.getAttribute('viewBox') || '') || {
        minX: 0,
        minY: 0,
        width: metrics.width,
        height: metrics.height,
      };
      const points = {};
      const markerElements = Array.from(
        svg.querySelectorAll('.pin-hole, .pin-rect')
      ).filter((element) => element.getAttribute('data-micropinouts-callout') !== '1');

      markerElements.forEach((pinElement, index) => {
        const logicalPinKey =
          pinElement.getAttribute('data-pin') ||
          pinElement.getAttribute('data-svgid') ||
          pinElement.getAttribute('data-pin-number') ||
          pinElement.getAttribute('id') ||
          `pin_${index}`;
        const center = getLocalPinCenterFromSvgElement(pinElement);

        if (!center) {
          return;
        }

        points[`${logicalPinKey}__${index}`] = center;
      });

      const result = {
        viewBox,
        points,
      };

      breadboardLocalPinLayoutCacheRef.current.set(cacheKey, result);
      return result;
    } catch {
      return null;
    }
  };

  const getCanvasPointFromSvgLocalPoint = (target, x, y) => {
    const ownerSvg = target.ownerSVGElement || target.closest?.('svg');
    const ctm = target.getScreenCTM?.();

    if (!ownerSvg || !ctm || typeof ownerSvg.createSVGPoint !== 'function') {
      return null;
    }

    const point = ownerSvg.createSVGPoint();
    point.x = x;
    point.y = y;

    const screenPoint = point.matrixTransform(ctm);

    return getSvgPointFromClient(screenPoint.x, screenPoint.y);
  };

  const getPathPointCenter = (target, fraction = 0.5) => {
    if (
      target.tagName?.toLowerCase() !== 'path' ||
      typeof target.getTotalLength !== 'function'
    ) {
      return null;
    }

    try {
      const length = target.getTotalLength();

      if (!Number.isFinite(length) || length <= 0) {
        return null;
      }

      const pathPoint = target.getPointAtLength(length * fraction);

      return getCanvasPointFromSvgLocalPoint(target, pathPoint.x, pathPoint.y);
    } catch {
      return null;
    }
  };

  const isMobileCanvas = () => {
    if (typeof window === 'undefined') return false;

    return (
      window.matchMedia?.('(pointer: coarse)').matches ||
      window.innerWidth <= 768
    );
  };

  const getSvgElementCenter = (element) => {
    if (!element) return null;

    const rectPoint = getCanvasPointFromElementRect(element);
    if (rectPoint) {
      return rectPoint;
    }

    const getNumber = (target, attr) => {
      const value = Number.parseFloat(target.getAttribute?.(attr) || '');
      return Number.isFinite(value) ? value : null;
    };

    const getBBoxCenter = (target) => {
      if (
        typeof target.getBBox !== 'function' ||
        typeof target.getScreenCTM !== 'function'
      ) {
        return null;
      }

      try {
        const box = target.getBBox();

        if (
          !Number.isFinite(box.x) ||
          !Number.isFinite(box.y) ||
          !Number.isFinite(box.width) ||
          !Number.isFinite(box.height)
        ) {
          return null;
        }

        // For path/line pins, width or height can be 0.
        // That is okay. The center is still valid.
        return getCanvasPointFromSvgLocalPoint(
          target,
          box.x + box.width / 2,
          box.y + box.height / 2
        );
      } catch {
        return null;
      }
    };

    const candidates = [
      element,
      ...Array.from(
        element.querySelectorAll?.('circle, ellipse, rect, path, polygon, polyline, line, use') || []
      ),
    ].filter(Boolean);

    for (const target of candidates) {
      const tag = target.tagName?.toLowerCase();

      if (tag === 'circle' || tag === 'ellipse') {
        const cx = getNumber(target, 'cx');
        const cy = getNumber(target, 'cy');

        if (cx !== null && cy !== null) {
          const point = getCanvasPointFromSvgLocalPoint(target, cx, cy);
          if (point) return point;
        }
      }

      if (tag === 'rect') {
        const x = getNumber(target, 'x') ?? 0;
        const y = getNumber(target, 'y') ?? 0;
        const width = getNumber(target, 'width');
        const height = getNumber(target, 'height');

        if (width !== null && height !== null) {
          const point = getCanvasPointFromSvgLocalPoint(target, x + width / 2, y + height / 2);
          if (point) return point;
        }
      }

      if (tag === 'path') {
        const point =
          getPathPointCenter(target, 1) ||
          getPathPointCenter(target, 0.5) ||
          getPathPointCenter(target, 0);

        if (point) return point;
      }

      if (tag === 'polygon' || tag === 'polyline' || tag === 'line' || tag === 'use') {
        const point = getBBoxCenter(target);
        if (point) return point;
      }
    }

    // General fallback.
    for (const target of candidates) {
      const point = getBBoxCenter(target);
      if (point) return point;
    }

    // Final DOM fallback.
    for (const target of candidates) {
      const rect = target.getBoundingClientRect?.();

      if (rect && rect.width > 0 && rect.height > 0) {
        return getSvgPointFromClient(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2
        );
      }
    }

    return null;
  };

  const collectPinLayouts = () => {
    const nextLayout = {};
    breadboardHoverStateRef.current = null;
    if (breadboardHoverLayerRef.current) {
      while (breadboardHoverLayerRef.current.firstChild) {
        breadboardHoverLayerRef.current.removeChild(breadboardHoverLayerRef.current.firstChild);
      }
    }

    components.forEach((comp) => {
      if (isComponentHidden(comp)) {
        return;
      }

      nextLayout[comp.id] = {};

      if (comp.type === 'board') {
        const localLayout = getBoardLocalPinLayout(comp.board);

        if (localLayout && Object.keys(localLayout.points || {}).length > 0) {
          const footprint = getComponentFootprint(comp);
          const viewBoxWidth = Math.max(Number(localLayout.viewBox?.width) || 1, 1);
          const viewBoxHeight = Math.max(Number(localLayout.viewBox?.height) || 1, 1);
          const viewBoxMinX = Number(localLayout.viewBox?.minX) || 0;
          const viewBoxMinY = Number(localLayout.viewBox?.minY) || 0;
          const scaleX = footprint.width / viewBoxWidth;
          const scaleY = footprint.height / viewBoxHeight;
          const rotationCenter = {
            x: footprint.width / 2,
            y: footprint.height / 2,
          };

          Object.entries(localLayout.points).forEach(([markerKey, point]) => {
            const localPoint = {
              x: (point.x - viewBoxMinX) * scaleX,
              y: (point.y - viewBoxMinY) * scaleY,
            };
            const rotatedPoint = rotateCanvasPoint(
              localPoint,
              rotationCenter,
              comp.rotation || 0
            );

            nextLayout[comp.id][markerKey] = {
              x: comp.x + rotatedPoint.x,
              y: comp.y + rotatedPoint.y,
            };
          });

          return;
        }
      }

      if (isBreadboardComponent(comp)) {
        const localLayout = getBreadboardLocalPinLayout(comp);

        if (localLayout && Object.keys(localLayout.points || {}).length > 0) {
          const footprint = getComponentFootprint(comp);
          const viewBoxWidth = Math.max(Number(localLayout.viewBox?.width) || 1, 1);
          const viewBoxHeight = Math.max(Number(localLayout.viewBox?.height) || 1, 1);
          const viewBoxMinX = Number(localLayout.viewBox?.minX) || 0;
          const viewBoxMinY = Number(localLayout.viewBox?.minY) || 0;
          const scaleX = footprint.width / viewBoxWidth;
          const scaleY = footprint.height / viewBoxHeight;
          const rotationCenter = {
            x: footprint.width / 2,
            y: footprint.height / 2,
          };

          Object.entries(localLayout.points).forEach(([markerKey, point]) => {
            const localPoint = {
              x: (point.x - viewBoxMinX) * scaleX,
              y: (point.y - viewBoxMinY) * scaleY,
            };
            const rotatedPoint = rotateCanvasPoint(
              localPoint,
              rotationCenter,
              comp.rotation || 0
            );

            nextLayout[comp.id][markerKey] = {
              x: comp.x + rotatedPoint.x,
              y: comp.y + rotatedPoint.y,
            };
          });

          return;
        }
      }

      const host = partHostRefs.current[comp.id];
      if (!host) return;

      const pins = Array.from(host.querySelectorAll('.pin-hole, .pin-rect'))
        .filter((element) => element.getAttribute('data-micropinouts-callout') !== '1');

      pins.forEach((pinElement, index) => {
        const logicalPinKey =
          pinElement.getAttribute('data-pin') ||
          pinElement.getAttribute('data-svgid') ||
          pinElement.getAttribute('data-pin-number') ||
          pinElement.getAttribute('id') ||
          `pin_${index}`;

        const markerKey = `${logicalPinKey}__${index}`;

        pinElement.setAttribute('data-builder-marker-key', markerKey);

        const center = getPinElementCenter(pinElement);

        if (center) {
          nextLayout[comp.id][markerKey] = center;
        } else {
          console.log('no center for pin', {
            markerKey,
            tag: pinElement.tagName,
            id: pinElement.getAttribute('id'),
            className: pinElement.getAttribute('class'),
            outerHTML: pinElement.outerHTML?.slice(0, 600),
          });
        }
      });

    });

    setPinLayoutByPartId(nextLayout);
    return nextLayout;
  };

  useEffect(() => {
    fetch(LED_SVG_URL)
      .then(response => response.text())
      .then(setLedSvg)
      .catch(error => console.error('Error loading LED SVG:', error));

    fetch(SERVO_SVG_URL)
      .then(response => response.text())
      .then(setServoSvg)
      .catch(error => console.error('Error loading servo SVG:', error));

    fetch(LCD_16X2_SVG_URL)
      .then(response => response.text())
      .then(setLcd16x2Svg)
      .catch(error => console.error('Error loading LCD 16x2 SVG:', error));

    fetch(SEVEN_SEGMENT_SVG_URL)
      .then(response => response.text())
      .then(setSevenSegmentSvg)
      .catch(error => console.error('Error loading 7-segment display SVG:', error));

    fetch(BREADBOARD_SVG_URL)
      .then(response => response.text())
      .then(setBreadboardSvg)
      .catch(error => console.error('Error loading breadboard SVG:', error));
  }, []);

  useEffect(() => {
    fetchBoards();
  // Initial catalog load is intentionally one-shot; Retry handles manual reloads.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isCurrent = true;

    const fetchBuilderBannerAd = async () => {
      try {
        const response = await adsAPI.getAll();
        const nextBannerAd = findAdRecordBySlot(response.data, AD_SLOT_PROJECT_BUILDER_BANNER);

        if (!isCurrent) {
          return;
        }

        setBannerAd(nextBannerAd?.image_url ? nextBannerAd : null);
      } catch (adError) {
        console.error('Error loading Project Builder banner ad:', adError);

        if (isCurrent) {
          setBannerAd(null);
        }
      }
    };

    fetchBuilderBannerAd();

    return () => {
      isCurrent = false;
    };
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const handlePointerMove = (event) => {
      const wireDrag = wireDragRef.current;
      const rewireDrag = rewireDragRef.current;
      const wireCornerDrag = wireCornerDragRef.current;

      if (rewireDrag && rewireDrag.pointerId !== event.pointerId) return;
      if (wireDrag && wireDrag.pointerId !== event.pointerId) return;

      if (wireCornerDrag && wireCornerDrag.pointerId !== event.pointerId) return;

      if (wireCornerDrag) {
        const point = getCanvasPoint(event);
        if (!point) return;

        const nextPoint = snapCanvasPoint(point);

        setConnections(current =>
          current.map(connection => {
            if (connection.id !== wireCornerDrag.connectionId) {
              return connection;
            }

            const routePoints = [...(connection.routePoints || [])];

            routePoints[wireCornerDrag.routePointIndex] = nextPoint;

            return {
              ...connection,
              routePoints,
            };
          })
        );

        return;
      }

      if (rewireDrag) {

        const fixedConnection = connectionsRef.current.find(c => c.id === rewireDrag.connectionId);
        const fixedEndpoint = fixedConnection?.[rewireDrag.fixedSide] || null;

        applyWireDropFeedback(getWireDropFeedback(event, fixedEndpoint));

        const point = getCanvasPoint(event);
        if (!point) return;

        const nextRewireDrag = {
          ...rewireDrag,
          currentPoint: snapToGrid ? snapCanvasPoint(point) : point,
        };

        rewireDragRef.current = nextRewireDrag;

        setWirePreview({
          startPoint: rewireDrag.fixedPoint,
          routePoints: [],
          currentPoint: nextRewireDrag.currentPoint,
          color: rewireDrag.color || '#2563eb',
          width: rewireDrag.width || wireWidth,
        });
        return;
      }

      if (wireDrag) {
        applyWireDropFeedback(getWireDropFeedback(event, wireDrag.from));
        const point = getCanvasPoint(event);
        if (!point) return;

        const nextPreview = {
          ...wireDrag,
          currentPoint: snapCanvasPoint(point),
        };

        wireDragRef.current = nextPreview;
        setWirePreview(nextPreview);
        return;
      }

      const selectionDrag = selectionDragRef.current;

      if (selectionDrag) {
        const point = getCanvasPoint(event);
        if (!point) return;

        selectionDrag.currentPoint = point;

        const rect = getSelectionRect(selectionDrag.startPoint, point);
        setSelectionBox(rect);

        const selectedIds = getComponentsInsideSelection(rect);

        selectionDrag.selectedIds = selectedIds;

        setSelectedComponentIds(current => {
          if (!selectionDrag.append) {
            return new Set(selectedIds);
          }

          return new Set([
            ...Array.from(current),
            ...selectedIds,
          ]);
        });

        return;
      }

      const drag = dragRef.current;
      if (!drag) return;

      if (
        drag.pointerId !== undefined &&
        event.pointerId !== drag.pointerId
      ) {
        return;
      }

      const point = getCanvasPoint(event);
      if (!point) return;
      if (drag.type === 'components') {
        const rawDx = point.x - drag.startPoint.x;
        const rawDy = point.y - drag.startPoint.y;

        if (Math.abs(rawDx) > 2 || Math.abs(rawDy) > 2) {
          didDragComponentRef.current = true;
        }

        let minDx = -Infinity;
        let maxDx = Infinity;
        let minDy = -Infinity;
        let maxDy = Infinity;

        const viewBounds = getCurrentViewBoxBounds();

        for (const comp of componentsRef.current || []) {
          if (!drag.componentIds.includes(comp.id)) continue;

          const start = drag.startPositions[comp.id];
          if (!start) continue;

          const bounds = getComponentDragBounds(comp);

        const getAxisDeltaLimits = (viewStart, viewSize, itemSize, startValue) => {
          const normalMin = viewStart;
          const normalMax = viewStart + viewSize - itemSize;

          if (itemSize <= viewSize) {
            return {
              min: normalMin - startValue,
              max: normalMax - startValue,
            };
          }

          // Oversized component: allow dragging between bottom/right aligned and top/left aligned.
          return {
            min: normalMax - startValue,
            max: normalMin - startValue,
          };
        };

        const xLimits = getAxisDeltaLimits(
          viewBounds.x,
          viewBounds.width,
          bounds.width,
          start.x
        );

        const yLimits = getAxisDeltaLimits(
          viewBounds.y,
          viewBounds.height,
          bounds.height,
          start.y
        );

        if (DEBUG_PROJECT_BUILDER_DRAG) {
          console.log('[PB drag limits]', {
            name: comp.board?.name || comp.name,
            componentId: comp.id,
            start,
            bounds,
            viewBounds,
            xLimits,
            yLimits,
          });
        }

        minDx = Math.max(minDx, xLimits.min);
        maxDx = Math.min(maxDx, xLimits.max);

        minDy = Math.max(minDy, yLimits.min);
        maxDy = Math.min(maxDy, yLimits.max);
        }

        const dx = Math.min(Math.max(rawDx, minDx), maxDx);
        const dy = Math.min(Math.max(rawDy, minDy), maxDy);

        if (DEBUG_PROJECT_BUILDER_DRAG) {
          console.log('[PB drag move]', {
            dragIds: drag.componentIds,
            rawDx,
            rawDy,
            minDx,
            maxDx,
            minDy,
            maxDy,
            dx,
            dy,
            viewBounds,
          });
        }
        const anchorStart = drag.startPositions[drag.anchorId];

        const resolvedDx = snapToGrid && anchorStart
          ? Math.min(
              Math.max(snapValueToGrid(anchorStart.x + dx, gridSize) - anchorStart.x, minDx),
              maxDx
            )
          : dx;
        const resolvedDy = snapToGrid && anchorStart
          ? Math.min(
              Math.max(snapValueToGrid(anchorStart.y + dy, gridSize) - anchorStart.y, minDy),
              maxDy
            )
          : dy;
        const breadboardSnap = getBreadboardComponentSnap(
          drag,
          resolvedDx,
          resolvedDy,
          { minDx, maxDx, minDy, maxDy }
        );
        const finalDx = breadboardSnap ? breadboardSnap.dx : resolvedDx;
        const finalDy = breadboardSnap ? breadboardSnap.dy : resolvedDy;

        if (Number(drag.lastAppliedDx) === Number(finalDx) && Number(drag.lastAppliedDy) === Number(finalDy)) {
          return;
        }

        drag.lastAppliedDx = finalDx;
        drag.lastAppliedDy = finalDy;

        const draggedComponent = componentsRef.current.find(
          (component) => component.id === drag.anchorId
        );

        if (
          draggedComponent &&
          ['led', 'resistor'].includes(
            String(draggedComponent.passiveType || draggedComponent.passiveKey || '')
          )
        ) {
          drawBreadboardPlacementHighlights(
            getBreadboardPlacementMatches({
              componentStart: drag.startPositions?.[drag.anchorId],
              pinOffsets: drag.pinOffsetsByComponentId?.[drag.anchorId],
              targets: drag.breadboardSnapTargets,
              dx: finalDx,
              dy: finalDy,
              contactRadius: BREADBOARD_COMPONENT_CONTACT_RADIUS,
            })
          );
        } else {
          clearBreadboardHoverHighlight();
        }

        scheduleComponentDragFrame({
          componentIds: drag.componentIds,
          startPositions: drag.startPositions,
          dx: finalDx,
          dy: finalDy,
        });

        return;
      }
    };

    const handlePointerUp = (event) => {
      const interactionComponentId = simulationInteractionPointerMapRef.current.get(event.pointerId);

      if (interactionComponentId) {
        simulationInteractionPointerMapRef.current.delete(event.pointerId);

        if (simulationInteractiveComponentMap.get(interactionComponentId)?.interaction === 'momentary_press') {
          setSimulationInteractionActive(interactionComponentId, false);
        }
      }

      const wireDrag = wireDragRef.current;
      const rewireDrag = rewireDragRef.current;
      const wireCornerDrag = wireCornerDragRef.current;

      if (rewireDrag && rewireDrag.pointerId !== event.pointerId) return;
      if (wireDrag && wireDrag.pointerId !== event.pointerId) return;

      if (wireCornerDrag && wireCornerDrag.pointerId !== event.pointerId) return;

      if (wireCornerDrag) {
        wireCornerDragRef.current = null;
        return;
      }

      if (rewireDrag) {
        const target = getPinTargetFromPointer(event);
        const fixedConnection = connectionsRef.current.find(c => c.id === rewireDrag.connectionId);
        const fixedEndpoint = fixedConnection?.[rewireDrag.fixedSide] || null;

        if (target && isValidWireConnectionTarget(fixedEndpoint, target)) {
          if (
            !areWireEndpointsEquivalent(
              fixedConnection?.[rewireDrag.movingSide],
              target
            )
          ) {
            pushUndoSnapshot(rewireDrag.historySnapshot);
          }

          setConnections(current =>
            current.map(connection =>
              connection.id === rewireDrag.connectionId
                ? {
                    ...connection,
                    [rewireDrag.movingSide]: target,
                  }
                : connection
            )
          );
        }

        rewireDragRef.current = null;
        setWirePreview(null);
        clearWireDropFeedback();
        return;
      }
      
      if (wireDrag) {
        // Multipoint mode:
        // Do not finish the wire on pointerup.
        // The wire finishes when the user clicks another pin.
        return;
      }

      if (selectionDragRef.current) {
        const selectionDrag = selectionDragRef.current;
        const finalIds = selectionDrag.selectedIds || [];

        if (finalIds.length === 1) {
          setSelectedItem({ type: 'component', id: finalIds[0] });
        } else if (finalIds.length > 1) {
          setSelectedItem(null);
        }

        selectionDragRef.current = null;
        setSelectionBox(null);

        // Let the browser finish the click event after pointerup,
        // then allow normal canvas clicks again.
        window.setTimeout(() => {
          suppressCanvasClickRef.current = false;
        }, 0);

        return;
      }

      const drag = dragRef.current;

      if (drag) {
        if (
          drag.pointerId !== undefined &&
          event.pointerId !== drag.pointerId
        ) {
          return;
        }

        if (drag.type === 'components') {
          flushPendingComponentDragFrame();

          const moved = drag.componentIds.some((componentId) => {
            const startPosition = drag.startPositions[componentId];
            const currentComponent = componentsRef.current.find(
              (component) => component.id === componentId
            );

            if (!startPosition || !currentComponent) {
              return false;
            }

            return (
              Number(currentComponent.x) !== Number(startPosition.x) ||
              Number(currentComponent.y) !== Number(startPosition.y)
            );
          });

          if (moved) {
            pushUndoSnapshot(drag.historySnapshot);
          }
        }

        dragRef.current = null;
        setIsComponentDragActive(false);
        clearBreadboardHoverHighlight();
      }
    };

    const handlePointerCancel = (event) => {
      const interactionComponentId = simulationInteractionPointerMapRef.current.get(event.pointerId);

      const drag = dragRef.current;

      if (drag?.pointerId === event.pointerId) {
        flushPendingComponentDragFrame();
        dragRef.current = null;
        setIsComponentDragActive(false);
        clearBreadboardHoverHighlight();
      }

      if (!interactionComponentId) {
        return;
      }

      simulationInteractionPointerMapRef.current.delete(event.pointerId);

      if (simulationInteractiveComponentMap.get(interactionComponentId)?.interaction === 'momentary_press') {
        setSimulationInteractionActive(interactionComponentId, false);
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, [zoomLevel, components, selectedItem, selectedComponentIds, simulationInteractiveComponentMap, snapToGrid, gridSize]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isComponentDragActive) {
      return undefined;
    }

    const id = requestAnimationFrame(collectPinLayouts);
    return () => cancelAnimationFrame(id);
  }, [components, isComponentDragActive, zoomLevel]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!showComponentModal || componentAddMode !== 'board') {
      return;
    }

    setIsSearchingComponents(true);

    const timer = setTimeout(() => {
      setDeferredComponentSearch(componentSearch);
      setIsSearchingComponents(false);
    }, 150);

    return () => clearTimeout(timer);
  }, [componentSearch, showComponentModal, componentAddMode]);

  useEffect(() => {
    if (!showComponentModal) return;

    setComponentSearch('');
    setPassiveSearch('');
    setDeferredComponentSearch('');
    setSelectedBoardToAdd('');
    setIsSearchingComponents(false);
  }, [showComponentModal]);

  useEffect(() => {
    if (!showAnalysisModal) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
          setShowAnalysisModal(false);
          clearAnalysisHighlights();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAnalysisModal]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!showSimulationModal) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (simulationPhase === 'running' || simulationPhase === 'compiling') {
          simulationRequestIdRef.current += 1;
          disposeSimulationRunner();

          setSimulationPhase('stopped');
          setSimulationStatus('Simulation stopped when the modal closed.');
        }

        setShowSimulationModal(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSimulationModal, simulationPhase]);
  /* eslint-enable react-hooks/exhaustive-deps */

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (isArduinoSimulationEnabled) {
      return;
    }

    simulationRequestIdRef.current += 1;
    disposeSimulationRunner();

    setShowSimulationModal(false);
    setSimulationPhase('idle');
    setSimulationStatus('');
    setSimulationCompilerOutput('');
    setSimulationSerialOutput('');
    setSimulationBuiltinLedOn(false);
    setSimulationPinStates(createInitialSimulationPinStates());
    simulationPendingPinTransitionEventsRef.current = [];
    setSimulationSnapshotVersion(0);
    setSimulationElapsedTime('0 ms');
    setSimulationCpuCycles(0);
  }, [isArduinoSimulationEnabled]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!showSimulationModal) {
      return;
    }

    if (simulationPhase === 'compiling' || simulationPhase === 'error') {
      setIsSimulationPanelCollapsed(false);
    }
  }, [showSimulationModal, simulationPhase]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    return () => {
      simulationRequestIdRef.current += 1;
      disposeSimulationRunner();

      if (saveStatusTimeoutRef.current) {
        window.clearTimeout(saveStatusTimeoutRef.current);
        saveStatusTimeoutRef.current = null;
      }

      if (simulationAudioManagerRef.current) {
        simulationAudioManagerRef.current.dispose().catch(() => {});
        simulationAudioManagerRef.current = null;
      }
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    setSavedProjects(getSavedProjectsFromStorage());

    if (typeof window !== 'undefined') {
      setActiveProjectId(
        window.localStorage.getItem(PROJECT_BUILDER_LAST_PROJECT_KEY) || null
      );
    }
  }, []);

  useEffect(() => {
    if (!lessonModeRequested) {
      setManagedLessonLookup({
        lessonId: requestedLessonId,
        loading: false,
        lesson: null,
        error: '',
      });
      return undefined;
    }

    if (adminLessonPreviewRequested) {
      const previewLesson = readAdminLessonPreviewPayload(requestedLessonId);

      setManagedLessonLookup({
        lessonId: requestedLessonId,
        loading: false,
        lesson: previewLesson,
        error: previewLesson
          ? ''
          : 'Draft preview data was not found in this browser. Open Preview Draft or Preview Embed from Admin > Lessons; draft preview URLs cannot be opened directly or bookmarked.',
      });

      return undefined;
    }

    let isMounted = true;

    setManagedLessonLookup({
      lessonId: requestedLessonId,
      loading: true,
      lesson: null,
      error: '',
    });

    fetchApiJson(
      apiPath(`/project-builder/lessons/${encodeURIComponent(requestedLessonId)}`),
      'Project Builder lesson endpoint',
      { cache: 'no-store' }
    )
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        const lessonPayload = normalizeManagedBuilderLessonPayload(payload);

        setManagedLessonLookup({
          lessonId: requestedLessonId,
          loading: false,
          lesson: lessonPayload,
          error: lessonPayload ? '' : 'Lesson payload is invalid.',
        });
      })
      .catch((lessonError) => {
        if (!isMounted) {
          return;
        }

        setManagedLessonLookup({
          lessonId: requestedLessonId,
          loading: false,
          lesson: null,
          error: getManagedLessonLookupErrorMessage(lessonError, requestedLessonId),
        });
      });

    return () => {
      isMounted = false;
    };
  }, [adminLessonPreviewRequested, lessonModeRequested, requestedLessonId]);

  useEffect(() => {
    hasWorkspaceContentRef.current = hasWorkspaceContent;
    isProjectDirtyRef.current = hasWorkspaceContent;
  }, [hasWorkspaceContent]);

  const fetchBoards = async () => {
    try {
      setLoading(true);
      setError(null);

      let passivesLoadError = null;
      const boardsRequest = fetchApiJson(
        apiPath('/project-builder/boards?include_svg=0'),
        'Project Builder boards endpoint'
      );
      const passivesRequest = fetchApiJson(
        apiPath('/passive-components'),
        'Passive components endpoint'
      ).catch((passivesError) => {
        passivesLoadError = passivesError;
        return [];
      });

      const [boardsData, passivesData] = await Promise.all([
        boardsRequest,
        passivesRequest,
      ]);

      const boardsArray = Array.isArray(boardsData)
        ? boardsData
        : (boardsData.boards || boardsData.pinouts || boardsData.data || []);
      const passiveArray = Array.isArray(passivesData)
        ? passivesData
        : (passivesData.passives || passivesData.data || []);

      setBoards(
        boardsArray.map((board) => normalize_board(board, []))
      );
      setPassiveComponents(
        passiveArray.map(normalizePassiveComponentDefinition).filter(Boolean)
      );

      if (passivesLoadError) {
        console.error('Error loading passive components:', passivesLoadError);
        showSaveStatusMessage('Custom basic parts could not load. Built-in parts are available.');
      }

      try {
        const settingsData = await fetchApiJson(
          apiPath('/project-builder/settings'),
          'Project Builder settings endpoint',
          { cache: 'no-store' }
        );

        setProjectBuilderSettings(normalizeProjectBuilderSettings(settingsData));
      } catch (settingsError) {
        setProjectBuilderSettings(DEFAULT_PROJECT_BUILDER_SETTINGS);
        console.error('Error loading Project Builder settings:', settingsError);
      }
    } catch (error) {
      console.error('Error fetching Project Builder data:', error);

      if (isLessonMode) {
        setBoards([]);
        setPassiveComponents([]);
        setProjectBuilderSettings(DEFAULT_PROJECT_BUILDER_SETTINGS);
        setError(null);
        showSaveStatusMessage('Tutorial is using built-in lesson parts while the API is unavailable.');
        return;
      }

      setError(`Error loading Project Builder data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoardPins = async (boardId) => {
    const data = await fetchApiJson(
      apiPath(`/project-builder/boards/${boardId}/pins`),
      'Project Builder board pins endpoint'
    );

    return Array.isArray(data)
      ? data
      : (data.pins || data.board_pins || data.boardPins || data.data || []);
  };

  const fetchProjectBuilderExamples = async ({ force = false } = {}) => {
    if (projectBuilderExamplesLoading || (projectBuilderExamplesLoaded && !force)) {
      return;
    }

    try {
      setProjectBuilderExamplesLoading(true);
      setProjectBuilderExamplesError('');

      const response = await fetch(apiPath('/project-builder/examples'), {
        cache: 'no-store',
      });

      const payload = await readApiJsonResponse(response, 'Project Builder examples endpoint');
      const examplesArray = Array.isArray(payload)
        ? payload
        : (payload.examples || payload.data || []);

      setProjectBuilderExamples(
        examplesArray.map(normalizeProjectBuilderExampleRecord).filter((example) => example.projectPayload)
      );
      setProjectBuilderExamplesLoaded(true);
    } catch (examplesError) {
      console.error('Error loading Project Builder examples:', examplesError);
      setProjectBuilderExamplesError(
        examplesError?.message || 'Unable to load Project Builder examples right now.'
      );
      setProjectBuilderExamplesLoaded(false);
    } finally {
      setProjectBuilderExamplesLoading(false);
    }
  };

  const openExamplesModal = () => {
    setShowExamplesModal(true);

    if (!projectBuilderExamplesLoaded && !projectBuilderExamplesLoading) {
      fetchProjectBuilderExamples();
    }
  };

  const closeExamplesModal = useCallback(() => {
    if (loadingExampleId) {
      return;
    }

    setShowExamplesModal(false);
  }, [loadingExampleId]);

  const isTypingTarget = (target) => {
    if (!(target instanceof HTMLElement)) return false;

    const tagName = target.tagName.toLowerCase();

    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      target.isContentEditable
    );
  };

  const deleteSelectedItems = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    if (selectedComponentIds.size > 0) {
      const idsToDelete = Array.from(selectedComponentIds)
        .filter((componentId) => !isLessonComponentLocked(componentId));

      if (idsToDelete.length === 0) {
        showSaveStatusMessage('This lesson part is locked.');
        return;
      }

      pushUndoSnapshot(createWorkspaceHistorySnapshot());

      setComponents(current =>
        current.filter(comp => !idsToDelete.includes(comp.id))
      );

      setConnections(current =>
        current.filter(connection =>
          !idsToDelete.includes(connection.from.partId) &&
          !idsToDelete.includes(connection.to.partId)
        )
      );

      setSelectedComponentIds(new Set());
      setSelectedItem(null);
      clearAnalysisHighlights();
      return;
    }

    if (selectedItem?.type === 'component') {
      removeComponent(selectedItem.id);
      setSelectedComponentIds(new Set());
      clearAnalysisHighlights();
      return;
    }

    if (selectedItem?.type === 'wire') {
      removeConnection(selectedItem.id);
      clearAnalysisHighlights();
    }
  };

  const rotateSelectedComponent = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    const requestedIdsToRotate =
      selectedComponentIds.size > 0
        ? Array.from(selectedComponentIds)
        : selectedItem?.type === 'component'
          ? [selectedItem.id]
          : [];
    const idsToRotate = requestedIdsToRotate.filter((componentId) => (
      canRotateComponent(components.find((component) => component.id === componentId))
    ));

    if (idsToRotate.length === 0) {
      if (requestedIdsToRotate.length > 0) {
        showSaveStatusMessage('Select a movable part to rotate.');
      }

      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setComponents((current) => (
      current.map((component) => (
        idsToRotate.includes(component.id)
          ? {
              ...component,
              rotation: ((component.rotation || 0) + 90) % 360,
            }
          : component
      ))
    ));
  };

  const beginComponentScaleEdit = () => {
    if (isCircuitEditingLocked || componentScaleEditRef.current) {
      return;
    }

    componentScaleEditRef.current = {
      snapshot: createWorkspaceHistorySnapshot(),
      pushed: false,
    };
  };

  const finishComponentScaleEdit = () => {
    componentScaleEditRef.current = null;
  };

  const updateComponentScale = (componentId, nextScale) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const safeComponentId = String(componentId || '');

    if (!safeComponentId) {
      return;
    }

    const clampedScale = clampComponentScale(nextScale);
    const currentComponent = componentsRef.current.find(
      (component) => String(component.id) === safeComponentId
    );

    if (
      !currentComponent ||
      clampComponentScale(currentComponent.scale || 1) === clampedScale
    ) {
      return;
    }

    if (!componentScaleEditRef.current) {
      componentScaleEditRef.current = {
        snapshot: createWorkspaceHistorySnapshot(),
        pushed: false,
      };
    }

    if (!componentScaleEditRef.current.pushed) {
      pushUndoSnapshot(componentScaleEditRef.current.snapshot);
      componentScaleEditRef.current.pushed = true;
    }

    setComponents((current) => (
      current.map((component) => {
        if (String(component.id) !== safeComponentId) {
          return component;
        }

        const updatedComponent = {
          ...component,
          scale: clampedScale,
        };

        return {
          ...updatedComponent,
          ...getResolvedComponentPosition(
            updatedComponent,
            updatedComponent.x,
            updatedComponent.y
          ),
        };
      })
    ));
  };

  const updateComponentVisibility = (componentId, hidden) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const safeComponentId = String(componentId || '');

    if (!safeComponentId) {
      return;
    }

    const currentComponent = componentsRef.current.find(
      (component) => String(component.id) === safeComponentId
    );

    if (!currentComponent || isComponentHidden(currentComponent) === Boolean(hidden)) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setComponents((current) => (
      current.map((component) => (
        String(component.id) === safeComponentId
          ? {
              ...component,
              hidden: Boolean(hidden),
            }
          : component
      ))
    ));

    setSelectedItem({ type: 'component', id: safeComponentId });
    setSelectedComponentIds(new Set([safeComponentId]));
    clearAnalysisHighlights();
    clearBreadboardHoverHighlight();
  };

  const changeSelectedPassive = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    const selectedComponent =
      selectedItem?.type === 'component'
        ? components.find(comp => comp.id === selectedItem.id)
        : selectedComponentIds.size === 1
          ? components.find(comp => comp.id === Array.from(selectedComponentIds)[0])
          : null;

    if (!selectedComponent || selectedComponent.type !== 'passive') {
      return;
    }

    openPassiveEditModal(selectedComponent);
  };

  const removeComponent = (id) => {
    if (isCircuitEditingLocked) {
      return;
    }

    if (isLessonComponentLocked(id)) {
      showSaveStatusMessage('This lesson part is locked.');
      return;
    }

    if (!components.some((component) => component.id === id)) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setComponents(current => current.filter(c => c.id !== id));

    setConnections(current =>
      current.filter(connection =>
        connection.from.partId !== id &&
        connection.to.partId !== id
      )
    );

    if (selectedItem?.type === 'component' && selectedItem.id === id) {
      setSelectedItem(null);
    }
  };

  const removeConnection = (connectionId) => {
    if (isCircuitEditingLocked || !connectionId) {
      return;
    }

    if (!connections.some((connection) => connection.id === connectionId)) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setConnections((current) =>
      current.filter((connection) => connection.id !== connectionId)
    );

    if (selectedItem?.type === 'wire' && selectedItem.id === connectionId) {
      setSelectedItem(null);
    }
  };

  const updateSelectedWire = (changes) => {
    if (isCircuitEditingLocked || selectedItem?.type !== 'wire') return;

    const currentConnection = connections.find(
      (connection) => connection.id === selectedItem.id
    );

    if (!currentConnection) {
      return;
    }

    const hasChanges = Object.entries(changes || {}).some(
      ([key, value]) => currentConnection[key] !== value
    );

    if (!hasChanges) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setConnections(current =>
      current.map(connection =>
        connection.id === selectedItem.id
          ? { ...connection, ...changes }
          : connection
      )
    );
  };

  const serializeProjectComponent = (comp) => {
    const boardIdentity = comp.type === 'board'
      ? {
          id: comp.board?.id || comp.boardId || '',
          slug: comp.board?.slug || comp.boardSlug || '',
          name: comp.board?.name || comp.boardName || comp.name || '',
        }
      : null;
    const base = {
      id: comp.id,
      type: comp.type,
      x: comp.x,
      y: comp.y,
      scale: comp.scale || 1,
      rotation: comp.rotation || 0,
      name: comp.name || '',
      hidden: isComponentHidden(comp),
    };

    if (comp.type === 'board') {
      return {
        ...base,
        boardId: comp.boardId || comp.board?.id,
        boardSlug: boardIdentity.slug,
        boardName: boardIdentity.name,
        board: boardIdentity,
        targetWidth: clampBoardTargetWidth(
          comp.targetWidth || DEFAULT_BOARD_TARGET_WIDTH
        ),
      };
    }

    return {
      ...base,
      passiveKey: comp.passiveKey,
      passiveDefinitionId: comp.passiveDefinitionId,
      passiveType: comp.passiveType,
      showNoSimulationTag: Boolean(comp.showNoSimulationTag),
      pinOffsets: comp.pinOffsets,
      simulationModel: comp.simulationModel,
      selectedValue: comp.selectedValue,
      valueLabel: comp.valueLabel,
      valueOhms: comp.valueOhms,
      commonType: comp.commonType,
      digitCount: comp.digitCount,
      segmentStates: comp.segmentStates,
      color: comp.color,
      description: comp.description,
      canvasWidth: comp.canvasWidth,
      canvasHeight: comp.canvasHeight,
    };
  };

  const buildSerializableProject = (projectName) => {
    const now = new Date().toISOString();
    const persistedSimulationCode = hasSimulationUnoBoard
      ? getPersistableArduinoSketchSource(simulationCode)
      : '';

    return {
      schemaVersion: PROJECT_BUILDER_SCHEMA_VERSION,
      id: activeProjectId || createProjectId(),
      name: projectName,
      updatedAt: now,
      createdAt:
        savedProjects.find(project => project.id === activeProjectId)?.createdAt ||
        now,
      canvas: {
        zoomLevel,
        showGrid,
        snapToGrid,
        gridSize,
      },
      components: components.map(serializeProjectComponent),
      connections,
      ...(persistedSimulationCode ? {
        simulation: {
          code: persistedSimulationCode,
        },
      } : {}),
    };
  };

  const getDefaultProjectName = () => {
    const existingProject = savedProjects.find(
      (project) => project.id === activeProjectId
    );

    return existingProject?.name || `Untitled Project ${savedProjects.length + 1}`;
  };

  const normalizeBoardLookupToken = (value) => (
    String(value ?? '').trim().toLowerCase()
  );

  const getBoardLookupTokenVariants = (value) => {
    const normalized = normalizeBoardLookupToken(value);

    if (!normalized) {
      return [];
    }

    const slug = normalized
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const compact = normalized.replace(/[^a-z0-9]+/g, '');

    return Array.from(new Set([
      normalized,
      slug,
      compact,
    ].filter(Boolean)));
  };

  const getBoardLookupTokensFromValues = (values) => (
    (Array.isArray(values) ? values : [])
      .flatMap(getBoardLookupTokenVariants)
      .filter(Boolean)
  );

  const getBoardIdentityLookupTokens = (board) => getBoardLookupTokensFromValues([
    board?.slug,
    board?.name,
  ]);

  const getSavedBoardArduinoUnoAliases = (savedComp) => {
    const text = [
      savedComp?.id,
      savedComp?.name,
      savedComp?.boardName,
      savedComp?.board?.name,
      savedComp?.boardLookup?.kind,
    ].join(' ');

    if (
      String(savedComp?.id || '') !== 'lesson-arduino-uno' &&
      !/arduino[\s_-]*uno/i.test(text) &&
      savedComp?.boardLookup?.kind !== 'arduino_uno'
    ) {
      return [];
    }

    return [
      'arduino-uno',
      'arduino-uno-rev3',
      'arduino-uno-r3',
      'Arduino Uno',
      'Arduino UNO',
      'Arduino Uno (Rev3)',
      'Arduino Uno Rev3',
      'Arduino Uno R3',
    ];
  };

  const getSavedBoardIdentityLookupTokens = (savedComp) => getBoardLookupTokensFromValues([
    savedComp?.board?.slug,
    savedComp?.board?.name,
    savedComp?.boardSlug,
    savedComp?.boardName,
    savedComp?.name,
    ...(Array.isArray(savedComp?.boardLookup?.slugs) ? savedComp.boardLookup.slugs : []),
    ...(Array.isArray(savedComp?.boardLookup?.names) ? savedComp.boardLookup.names : []),
    ...(Array.isArray(savedComp?.boardLookup?.aliases) ? savedComp.boardLookup.aliases : []),
    ...getSavedBoardArduinoUnoAliases(savedComp),
  ]);

  const getSavedBoardIdLookupTokens = (savedComp) => getBoardLookupTokensFromValues([
    savedComp?.boardId,
    savedComp?.board?.id,
    savedComp?.boardLookup?.id,
  ]);

  const findSavedProjectBoard = (savedComp) => {
    const identityTokens = new Set(getSavedBoardIdentityLookupTokens(savedComp));

    if (identityTokens.size > 0) {
      const identityMatch = boards.find((board) => (
        getBoardIdentityLookupTokens(board).some((token) => identityTokens.has(token))
      ));

      if (identityMatch) {
        return identityMatch;
      }
    }

    const idTokens = new Set(getSavedBoardIdLookupTokens(savedComp));

    if (idTokens.size === 0) {
      return null;
    }

    return boards.find((board) => (
      getBoardLookupTokensFromValues([board?.id]).some((token) => idTokens.has(token))
    )) || null;
  };

  const getHydratedBoardForComponent = (component) => {
    if (!component || component.type !== 'board') {
      return component?.board || null;
    }

    const componentBoard = component.board || null;
    const matchedBoard = findSavedProjectBoard(component);

    if (!matchedBoard) {
      return componentBoard;
    }

    if (
      boardHasRenderableSvg(componentBoard) &&
      Array.isArray(componentBoard?.pins) &&
      componentBoard.pins.length > 0
    ) {
      return componentBoard;
    }

    const mergedBoard = mergeBoardRecords(componentBoard || matchedBoard, matchedBoard);
    const pins = Array.isArray(componentBoard?.pins) && componentBoard.pins.length > 0
      ? componentBoard.pins
      : (matchedBoard.pins || []);

    return normalize_board(mergedBoard, pins);
  };

  const hydrateSavedProjectComponents = async (savedComponents, options = {}) => {
    const hydrationTimeoutMs = Number(options.timeoutMs) || 0;
    const onHydrationStage = typeof options.onStage === 'function'
      ? options.onStage
      : null;
    const onHydrationIssue = typeof options.onIssue === 'function'
      ? options.onIssue
      : null;
    const hydratedComponents = [];

    for (const savedComp of savedComponents || []) {
      if (savedComp.type === 'board') {
        const board = findSavedProjectBoard(savedComp);

        if (!board) {
          hydratedComponents.push(savedComp);
          continue;
        }

        try {
          onHydrationStage?.(`board-svg:${savedComp.id || board.id}`);
          const detailedBoard = await withProjectBuilderTimeout(
            ensureBoardSvgRecord(board),
            hydrationTimeoutMs,
            `Board SVG ${board.name || board.id}`
          );

          onHydrationStage?.(`board-pins:${savedComp.id || board.id}`);
          const pins = await withProjectBuilderTimeout(
            fetchBoardPins(board.id),
            hydrationTimeoutMs,
            `Board pins ${board.name || board.id}`
          );

          hydratedComponents.push({
            ...savedComp,
            boardId: board.id,
            board: normalize_board(detailedBoard || board, pins),
            name: savedComp.name || detailedBoard?.name || board.name || 'Board',
          });
        } catch (error) {
          onHydrationIssue?.(error, savedComp, board);
          console.error('Error hydrating saved board:', error);

          hydratedComponents.push({
            ...savedComp,
            boardId: board.id,
            board: normalize_board(board, []),
            name: savedComp.name || board.name || 'Board',
          });
        }

        continue;
      }

      const passiveDefinition = passiveComponentMap.get(savedComp.passiveKey) || passiveComponentMap.get(savedComp.passiveType);

      if (!passiveDefinition) {
        hydratedComponents.push(savedComp);
        continue;
      }

      const selectedValue = savedComp.selectedValue ?? getDefaultPassiveValue(passiveDefinition);
      const hydratedScale = normalizeComponentScale(
        savedComp.scale,
        getDefaultPassiveScale(passiveDefinition)
      );
      const digitCount = passiveDefinition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentDigitCount(
            savedComp.digitCount ?? selectedValue ?? passiveDefinition.defaultValue
          )
        : undefined;
      const sevenSegmentCommonType = passiveDefinition.templateType === 'seven_segment_display'
        ? getSevenSegmentCommonTypeForDefinition(
            passiveDefinition,
            savedComp.commonType ?? savedComp.simulationModel?.parameters?.commonType
          )
        : undefined;
      const sevenSegmentCanvasSize = passiveDefinition.templateType === 'seven_segment_display'
        ? resolveSevenSegmentCanvasSize(digitCount, {
            baseCanvasWidth: passiveDefinition.canvasWidth,
            baseCanvasHeight: passiveDefinition.canvasHeight,
            baseDigitCount: passiveDefinition.defaultValue,
          })
        : null;
      const simulationModel = passiveDefinition.simulationModel
        ? JSON.parse(JSON.stringify(passiveDefinition.simulationModel))
        : (savedComp.simulationModel || {});

      if (passiveDefinition.templateType === 'seven_segment_display') {
        simulationModel.parameters = {
          ...(simulationModel.parameters || {}),
          commonType: sevenSegmentCommonType,
          digitCount,
        };
      }

      hydratedComponents.push({
        ...savedComp,
        passiveType: passiveDefinition.templateType,
        passiveKey: passiveDefinition.componentKey,
        passiveDefinitionId: passiveDefinition.id,
        svgContent: passiveDefinition.svgContent || savedComp.svgContent || '',
        description: passiveDefinition.description || savedComp.description || '',
        valueOptions: passiveDefinition.valueOptions || savedComp.valueOptions || [],
        defaultValue: passiveDefinition.defaultValue ?? savedComp.defaultValue ?? null,
        showNoSimulationTag: Boolean(
          passiveDefinition.showNoSimulationTag ?? savedComp.showNoSimulationTag
        ),
        pinOffsets: passiveDefinition.pinOffsets || savedComp.pinOffsets,
        simulationModel,
        selectedValue,
        valueLabel: getPassiveSelectedValueLabel(passiveDefinition, selectedValue),
        valueOhms: coercePassiveOhmsValue(
          passiveDefinition.templateType,
          selectedValue,
          passiveDefinition.simulationModel
        ),
        commonType: sevenSegmentCommonType,
        digitCount,
        segmentStates: passiveDefinition.templateType === 'seven_segment_display'
          ? normalizeSevenSegmentSegmentStates(savedComp.segmentStates, digitCount)
          : savedComp.segmentStates,
        canvasWidth: sevenSegmentCanvasSize?.width || passiveDefinition.canvasWidth || savedComp.canvasWidth,
        canvasHeight: sevenSegmentCanvasSize?.height || passiveDefinition.canvasHeight || savedComp.canvasHeight,
        scale: hydratedScale,
        name: savedComp.passiveType === 'led'
          ? savedComp.name
          : getPassiveInstanceName(passiveDefinition, { selectedValue: digitCount || selectedValue }),
      });
    }

    return hydratedComponents;
  };

  const getProjectLoadIssues = (projectComponents) => {
    const missingBoards = [];
    const missingPassives = [];

    for (const savedComp of projectComponents || []) {
      if (savedComp?.type === 'board') {
        const board = findSavedProjectBoard(savedComp);

        if (!board) {
          missingBoards.push(
            savedComp?.name ||
            (savedComp?.boardId ? `Board ${savedComp.boardId}` : 'Unknown board')
          );
        }

        continue;
      }

      const passiveDefinition = passiveComponentMap.get(savedComp?.passiveKey) || passiveComponentMap.get(savedComp?.passiveType);

      if (!passiveDefinition) {
        missingPassives.push(
          savedComp?.name ||
          savedComp?.passiveKey ||
          savedComp?.passiveType ||
          'Unknown basic part'
        );
      }
    }

    return {
      missingBoards,
      missingPassives,
    };
  };

  const buildProjectLoadErrorMessage = (projectName, loadIssues) => {
    const missingBoards = formatProjectLoadIssueList(loadIssues?.missingBoards);
    const missingPassives = formatProjectLoadIssueList(loadIssues?.missingPassives);
    const detailParts = [];

    if (missingBoards) {
      detailParts.push(`Missing board: ${missingBoards}.`);
    }

    if (missingPassives) {
      detailParts.push(`Missing basic part: ${missingPassives}.`);
    }

    if (detailParts.length === 0) {
      return `Cannot load "${projectName}". Some saved components are no longer available.`;
    }

    return `Cannot load "${projectName}". ${detailParts.join(' ')}`;
  };

  const normalizeImportedProjectPayload = (rawProject, fileName) => {
    if (!rawProject || typeof rawProject !== 'object' || Array.isArray(rawProject)) {
      return null;
    }

    if (!Array.isArray(rawProject.components) || !Array.isArray(rawProject.connections)) {
      return null;
    }

    const now = new Date().toISOString();
    const fallbackName = getProjectImportFallbackName(fileName);
    const normalizedName = String(rawProject.name || '').trim() || fallbackName;
    const normalizedId = String(rawProject.id || createProjectId()).trim() || createProjectId();
    const normalizedUpdatedAt = String(rawProject.updatedAt || '').trim() || now;
    const normalizedCreatedAt = String(rawProject.createdAt || '').trim() || normalizedUpdatedAt;
    const normalizedCanvas = rawProject.canvas && typeof rawProject.canvas === 'object'
      ? rawProject.canvas
      : {};
    const persistedSimulationCode = getPersistableArduinoSketchSource(
      rawProject?.simulation?.code || rawProject?.simulationCode || ''
    );

    return {
      schemaVersion: Number(rawProject.schemaVersion) || PROJECT_BUILDER_SCHEMA_VERSION,
      id: normalizedId,
      name: normalizedName,
      updatedAt: normalizedUpdatedAt,
      createdAt: normalizedCreatedAt,
      canvas: {
        zoomLevel: Number(normalizedCanvas.zoomLevel),
        showGrid: Boolean(normalizedCanvas.showGrid),
        snapToGrid: Boolean(normalizedCanvas.snapToGrid),
        gridSize: Number(normalizedCanvas.gridSize),
      },
      components: rawProject.components,
      connections: rawProject.connections,
      ...(persistedSimulationCode ? {
        simulation: {
          code: persistedSimulationCode,
        },
      } : {}),
    };
  };

  const loadProjectIntoWorkspace = async (
    project,
    {
      persistProject = false,
      successMessage = `Loaded "${project?.name || 'project'}".`,
    } = {}
  ) => {
    if (isCircuitEditingLocked) {
      return false;
    }

    const loadIssues = getProjectLoadIssues(project?.components);
    if (loadIssues.missingBoards.length > 0 || loadIssues.missingPassives.length > 0) {
      showSaveStatusMessage(buildProjectLoadErrorMessage(project?.name || 'project', loadIssues));
      return false;
    }

    const hydratedComponents = await hydrateSavedProjectComponents(project?.components);

    if (!Array.isArray(project?.components) || hydratedComponents.length !== project.components.length) {
      showSaveStatusMessage(`Cannot load "${project?.name || 'project'}". Some saved components are no longer available.`);
      return false;
    }

    const restoredSimulationCode = getSavedProjectArduinoSketchSource(project);

    if (isCircuitEditingLockedRef.current) {
      return false;
    }

    if (persistProject) {
      const nextProjects = [
        project,
        ...savedProjects.filter((item) => item.id !== project.id),
      ];

      saveProjectsToStorage(nextProjects);
      setSavedProjects(nextProjects);
    }

    setComponents(hydratedComponents);
    setConnections(Array.isArray(project.connections) ? project.connections : []);
    setSimulationCode(restoredSimulationCode);

    setZoomLevel(project.canvas?.zoomLevel || BASE_ZOOM);
    setShowGrid(Boolean(project.canvas?.showGrid));
    setSnapToGrid(Boolean(project.canvas?.snapToGrid));
    setGridSize(project.canvas?.gridSize || 10);

    setSelectedItem(null);
    setSelectedComponentIds(new Set());
    setSuggestedConnections([]);
    setSelectedSuggestionIds(new Set());
    setShowSuggestedConnections(false);
    setUndoStack([]);
    setRedoStack([]);
    clearAnalysisHighlights();

    setActiveProjectId(project.id);
    window.localStorage.setItem(PROJECT_BUILDER_LAST_PROJECT_KEY, project.id);

    showSaveStatusMessage(successMessage);
    return true;
  };

  const loadProjectBuilderExample = async (example) => {
    if (isCircuitEditingLocked || !example || loadingExampleId) {
      return;
    }

    const importedProject = normalizeImportedProjectPayload(
      example.projectPayload,
      example.sourceFileName || example.title
    );

    if (!importedProject) {
      setProjectBuilderExamplesError('This example is not a valid Project Builder project.');
      return;
    }

    try {
      setLoadingExampleId(example.id);
      setProjectBuilderExamplesError('');

      const loaded = await loadProjectIntoWorkspace(importedProject, {
        persistProject: false,
        successMessage: `Loaded example "${example.title || importedProject.name || 'Project Builder example'}".`,
      });

      if (!loaded) {
        return;
      }

      setShowExamplesModal(false);

      if (isArduinoSimulationEnabled && example.simulationCode.trim()) {
        setIsSimulationPanelCollapsed(false);
        setShowSimulationModal(true);
      }
    } catch (exampleLoadError) {
      console.error('Failed to load Project Builder example:', exampleLoadError);
      setProjectBuilderExamplesError(
        exampleLoadError?.message || 'Unable to load that example right now.'
      );
    } finally {
      setLoadingExampleId(null);
    }
  };

  const saveCurrentProject = () => {
    if (components.length === 0 && connections.length === 0) {
      showSaveStatusMessage('Nothing to save yet.');
      return false;
    }

    const projectName = window.prompt('Project name:', getDefaultProjectName());

    if (!projectName || !projectName.trim()) {
      return false;
    }

    const project = buildSerializableProject(projectName.trim());

    const nextProjects = [
      project,
      ...savedProjects.filter(item => item.id !== project.id),
    ];

    saveProjectsToStorage(nextProjects);
    setSavedProjects(nextProjects);
    setActiveProjectId(project.id);

    window.localStorage.setItem(PROJECT_BUILDER_LAST_PROJECT_KEY, project.id);

    isProjectDirtyRef.current = false;

    showSaveStatusMessage('Project saved in this browser.');

    return true;
  };

  const downloadCurrentProjectFile = () => {
    if (!hasWorkspaceContent) {
      showSaveStatusMessage('Nothing to download yet.');
      return;
    }

    try {
      const project = buildSerializableProject(getDefaultProjectName());
      const fileName = sanitizeProjectExportFileName(project.name, 'project-builder-circuit');
      const encodedProjectFileContents = encodeProjectFileContents(project);
      const projectBlob = new Blob(
        [encodedProjectFileContents],
        { type: 'application/octet-stream' }
      );
      const downloadUrl = URL.createObjectURL(projectBlob);
      const downloadLink = document.createElement('a');

      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();

      window.setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 0);

      showSaveStatusMessage('Project file downloaded.');
    } catch (downloadError) {
      console.error('Failed to download project file:', downloadError);
      showSaveStatusMessage('Unable to download project file.');
    }
  };

  const toggleMoreOptions = () => {
    setShowMoreOptions((currentValue) => !currentValue);
  };

  const closeMoreOptions = () => {
    setShowMoreOptions(false);
  };

  const handleToolbarDownloadPng = async () => {
    closeMoreOptions();
    await downloadAsPng();
  };

  const handleToolbarDownloadProjectFile = () => {
    closeMoreOptions();
    downloadCurrentProjectFile();
  };

  const openProjectFilePicker = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    projectFileInputRef.current?.click();
  };

  const handleProjectFileInputChange = async (event) => {
    const input = event.target;
    const selectedFile = input.files?.[0];

    input.value = '';

    if (!selectedFile) {
      return;
    }

    try {
      const rawFileContents = await selectedFile.text();
      const parsedProject = decodeProjectFileContents(rawFileContents);
      const importedProject = normalizeImportedProjectPayload(parsedProject, selectedFile.name);

      if (!importedProject) {
        showSaveStatusMessage('That file is not a valid Project Builder project.');
        return;
      }

      await loadProjectIntoWorkspace(importedProject, {
        persistProject: true,
        successMessage: `Imported "${importedProject.name}".`,
      });
    } catch (loadError) {
      console.error('Failed to load project file:', loadError);
      showSaveStatusMessage('Unable to load that project file.');
    }
  };

  saveCurrentProjectRef.current = saveCurrentProject;

  useEffect(() => {
    if (!showMoreOptions) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (moreOptionsRef.current?.contains(event.target)) {
        return;
      }

      setShowMoreOptions(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMoreOptions(false);
      }
    };

    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMoreOptions]);

  useEffect(() => {
    if (!showExamplesModal) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeExamplesModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeExamplesModal, showExamplesModal]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    window.history.pushState(
      { projectBuilderGuard: true },
      '',
      window.location.href
    );

    const handlePopState = () => {
      if (!isProjectDirtyRef.current || !hasWorkspaceContentRef.current) {
        return;
      }

      // Stay on the page while asking.
      window.history.pushState(
        { projectBuilderGuard: true },
        '',
        window.location.href
      );

      const shouldSave = window.confirm(
        'You have a project in the workspace. Save it before leaving?'
      );

      if (shouldSave) {
        const saved = saveCurrentProjectRef.current?.();

        if (saved) {
          isProjectDirtyRef.current = false;
          hasWorkspaceContentRef.current = false;
          window.history.back();
        }

        return;
      }

      const shouldDiscard = window.confirm(
        'Leave without saving this project?'
      );

      if (shouldDiscard) {
        isProjectDirtyRef.current = false;
        hasWorkspaceContentRef.current = false;
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const loadSavedProject = async (projectId) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const project = savedProjects.find(item => item.id === projectId);

    if (!project) return;

    await loadProjectIntoWorkspace(project, {
      successMessage: `Loaded "${project.name}".`,
    });
  };

  const deleteSavedProject = (projectId) => {
    const project = savedProjects.find(item => item.id === projectId);

    if (!project) return;

    const shouldDelete = window.confirm(`Delete "${project.name}" from this browser?`);

    if (!shouldDelete) return;

    const nextProjects = savedProjects.filter(item => item.id !== projectId);

    saveProjectsToStorage(nextProjects);
    setSavedProjects(nextProjects);

    if (activeProjectId === projectId) {
      setActiveProjectId(null);
      window.localStorage.removeItem(PROJECT_BUILDER_LAST_PROJECT_KEY);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isProjectDirtyRef.current || !hasWorkspaceContent) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasWorkspaceContent]);

  const downloadAsPng = async () => {
    if (!canvasRef.current) {
      return;
    }

    try {
      await downloadSvgElementAsPng(canvasRef.current, {
        fileName: `project-${Date.now()}.png`,
        backgroundColor: '#ffffff',
        outputWidth: CANVAS_WIDTH,
        outputHeight: CANVAS_HEIGHT,
        viewBox: {
          minX: 0,
          minY: 0,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
        },
        targetMaxSide: Math.max(CANVAS_WIDTH, CANVAS_HEIGHT),
        prepareClone: (svg) => {
          svg.querySelectorAll('.builder-export-ignore').forEach((element) => {
            element.remove();
          });
        },
      });
    } catch (error) {
      console.error('Failed to export project as PNG:', error);
    }
  };

  const stripHtml = (html) => {
    return String(html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const truncateText = (text, maxLength = DESCRIPTION_MAX_LENGTH) => {
    const cleanText = String(text || '').trim();

    if (cleanText.length <= maxLength) {
      return cleanText;
    }

    return `${cleanText.slice(0, maxLength).replace(/\s+\S*$/, '')}...`;
  };

  const getBoardInfoUrl = (comp) => {
    const slug = comp?.board?.slug;

    if (!slug) return '';

    return `/board/${slug}`;
  };

  const handleWheel = (event) => {
    if (!wheelZoomEnabled) {
      return;
    }

    event.preventDefault();

    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;

    setZoomLevel(current => clampZoom(current + delta));
  };

  const deleteWireCorner = (connectionId, routePointIndex) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const connection = connections.find((item) => item.id === connectionId);

    if (!connection?.routePoints?.[routePointIndex]) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setConnections(current =>
      current.map(connection => {
        if (connection.id !== connectionId) {
          return connection;
        }

        const routePoints = [...(connection.routePoints || [])];

        routePoints.splice(routePointIndex, 1);

        return {
          ...connection,
          routePoints,
        };
      })
    );
  };

  const moveSelectedBoard = (direction) => {
    if (visibleFilteredBoards.length === 0) return;

    const currentIndex = visibleFilteredBoards.findIndex(
      board => String(board.id) === String(selectedBoardToAdd)
    );

    const nextIndex =
      currentIndex < 0
        ? 0
        : Math.min(
            Math.max(currentIndex + direction, 0),
            visibleFilteredBoards.length - 1
          );

    setSelectedBoardToAdd(visibleFilteredBoards[nextIndex].id);
  };

  const getSelectionRect = (startPoint, endPoint) => {
    const x1 = Math.min(startPoint.x, endPoint.x);
    const y1 = Math.min(startPoint.y, endPoint.y);
    const x2 = Math.max(startPoint.x, endPoint.x);
    const y2 = Math.max(startPoint.y, endPoint.y);

    return {
      x: x1,
      y: y1,
      width: x2 - x1,
      height: y2 - y1,
    };
  };

  const rectsIntersect = (a, b) => {
    return !(
      a.x + a.width < b.x ||
      b.x + b.width < a.x ||
      a.y + a.height < b.y ||
      b.y + b.height < a.y
    );
  };

  const getComponentCanvasBounds = (comp) => {
    const bounds = getComponentDragBounds(comp);

    return {
      x: comp.x,
      y: comp.y,
      width: bounds.width,
      height: bounds.height,
    };
  };

  const getCurrentViewBoxBounds = () => {
    const viewWidth = CANVAS_WIDTH / zoomLevel;
    const viewHeight = CANVAS_HEIGHT / zoomLevel;

    const x = (CANVAS_WIDTH - viewWidth) / 2;
    const y = (CANVAS_HEIGHT - viewHeight) / 2;

    return {
      x,
      y,
      width: viewWidth,
      height: viewHeight,
    };
  };

  const getComponentsInsideSelection = (rect) => {
    return components
      .filter(comp => rectsIntersect(rect, getComponentCanvasBounds(comp)))
      .map(comp => comp.id);
  };

  const getComponentVisibleSvgBounds = (comp) => {
    const host = partHostRefs.current[comp.id];
    const rotateGroup = partRotateRefs.current[comp.id];

    if (!host || !rotateGroup) return null;

    const footprint = getComponentFootprint(comp);
    const previousTransform = rotateGroup.getAttribute('transform');

    rotateGroup.setAttribute('transform', '');

    const innerSvg = host.querySelector('svg');

    if (!innerSvg) {
      rotateGroup.setAttribute('transform', previousTransform || '');
      return null;
    }

    let bbox;

    try {
      bbox = innerSvg.getBBox();
    } catch {
      rotateGroup.setAttribute('transform', previousTransform || '');
      return null;
    }

    const viewBox = innerSvg.viewBox?.baseVal;

    rotateGroup.setAttribute('transform', previousTransform || '');

    if (!viewBox || !viewBox.width || !viewBox.height) return null;

    const scaleX = footprint.width / viewBox.width;
    const scaleY = footprint.height / viewBox.height;

    const padding = 10;

    return {
      x: (bbox.x - viewBox.x) * scaleX - padding,
      y: (bbox.y - viewBox.y) * scaleY - padding,
      width: bbox.width * scaleX + padding * 2,
      height: bbox.height * scaleY + padding * 2,
    };
  };

  const getPreparedPassiveSvg = (comp) => {
    const simulatedLedState = simulatedLedStatesByComponentId[comp.id];
    const simulatedLcdState = simulatedLcdStatesByComponentId[comp.id];
    const simulatedServoState = simulatedServoStatesByComponentId[comp.id];
    const simulatedSevenSegmentState = simulatedSevenSegmentStatesByComponentId[comp.id];
    const animatedServoAngleDegrees = Number(animatedServoAnglesByComponentId[comp.id]);
    const ledColor = comp.color || '#E60000';
    const lessonLedBrightness = (
      lessonPoweredLedComponentIds.has(comp.id) &&
      !ledCurrentWarningByComponentId[comp.id]
    )
      ? 0.9
      : 0;
    const ledBrightness = Math.max(
      clampUnitInterval(simulatedLedState?.brightness),
      lessonLedBrightness
    );
    const passiveSvgContent = getPassiveSvgContent(comp);
    const resolvedLcdSvgContent = comp.passiveType === 'lcd_16x2'
      ? (passiveSvgContent || lcd16x2Svg)
      : passiveSvgContent;
    const resolvedSevenSegmentSvgContent = comp.passiveType === 'seven_segment_display'
      ? (passiveSvgContent || sevenSegmentSvg)
      : resolvedLcdSvgContent;
    const resolvedServoSvgContent = comp.passiveType === 'servo'
      ? (
          hasRenderableServoActuator(passiveSvgContent)
            ? passiveSvgContent
            : (hasRenderableServoActuator(servoSvg) ? servoSvg : passiveSvgContent)
        )
      : '';
    const resolvedPassiveSvgContent = comp.passiveType === BREADBOARD_TEMPLATE_TYPE
      ? (passiveSvgContent || breadboardSvg)
      : resolvedSevenSegmentSvgContent;
    const pinOffsets = comp.pinOffsets || getPassiveDefinitionForComponent(comp)?.pinOffsets;
    const isBreadboardPassive = comp.passiveType === BREADBOARD_TEMPLATE_TYPE;

    const ledScopeClass = comp.passiveType === 'led'
      ? `pb-led-svg-${getCssSafeId(comp.id)}`
      : '';

    const ledPreviewCss = (
      comp.passiveType === 'led' &&
      ledBrightness > 0
    )
      ? buildActiveLedPreviewCss(ledColor, `.${ledScopeClass}`, ledBrightness)
      : '';

    const extraCssText = ledPreviewCss
      ? `${BOARD_EDITOR_SVG_CSS}\n${ledPreviewCss}`
      : BOARD_EDITOR_SVG_CSS;

    if (isBreadboardPassive) {
      const safeBreadboardSvgContent = String(resolvedPassiveSvgContent || '');
      const cacheKey = [
        String(safeBreadboardSvgContent.length),
        safeBreadboardSvgContent.slice(0, 96),
        safeBreadboardSvgContent.slice(-96),
        JSON.stringify(pinOffsets || {}),
      ].join('|');
      const cachedMarkup = breadboardPreparedSvgCacheRef.current.get(cacheKey);

      if (cachedMarkup) {
        return cachedMarkup;
      }

      const passiveMarkup = preparePassiveSvgBuilderMarkup({
        passiveType: comp.passiveType,
        svgContent: resolvedPassiveSvgContent,
        pinOffsets,
      });
      const preparedMarkup = prepareInlineSvgMarkup(passiveMarkup, {
        extraCssText,
      });

      breadboardPreparedSvgCacheRef.current.set(cacheKey, preparedMarkup);
      return preparedMarkup;
    }

    const passiveMarkup = preparePassiveSvgBuilderMarkup({
      passiveType: comp.passiveType,
      svgContent: resolvedPassiveSvgContent,
      pinOffsets,
      ledColor,
      ledSvgContent: ledSvg,
      lcdIsBacklightOn: Boolean(simulatedLcdState?.backlightOn),
      lcdIsDisplayEnabled: simulatedLcdState?.isDisplayEnabled !== false,
      lcdIsPowered: Boolean(simulatedLcdState?.isPowered),
      lcdTextRows: Array.isArray(simulatedLcdState?.rows)
        ? simulatedLcdState.rows
        : getBlankLcd16x2VisibleRows(),
      sevenSegmentDigitCount: normalizeSevenSegmentDigitCount(
        simulatedSevenSegmentState?.digitCount ?? comp.digitCount
      ),
      sevenSegmentIsPowered: Boolean(simulatedSevenSegmentState?.isPowered),
      sevenSegmentSegmentStates: normalizeSevenSegmentSegmentStates(
        simulatedSevenSegmentState?.segmentStates ?? comp.segmentStates,
        simulatedSevenSegmentState?.digitCount ?? comp.digitCount
      ),
      sevenSegmentSvgContent: resolvedSevenSegmentSvgContent,
      servoAngleDegrees: Number.isFinite(animatedServoAngleDegrees)
        ? animatedServoAngleDegrees
        : Number.isFinite(Number(simulatedServoState?.angleDegrees))
          ? Number(simulatedServoState.angleDegrees)
          : 0,
      servoSvgContent: resolvedServoSvgContent,
      valueOhms: comp.valueOhms || 10000,
    });

    const scopedPassiveMarkup = comp.passiveType === 'led'
      ? addSvgRootClass(passiveMarkup, ledScopeClass)
      : passiveMarkup;

    return prepareInlineSvgMarkup(
      scopedPassiveMarkup,
      {
        extraCssText,
      }
    );
  };

  const buildPassiveComponentDraft = (definition, options = {}) => {
    const selectedValue = definition.templateType === 'led'
      ? ''
      : (options.selectedValue ?? getDefaultPassiveValue(definition));
    const ledColor = options.color || '#E60000';
    const ledColorLabel = LED_COLOR_OPTIONS.find((option) => option.value === ledColor)?.label || 'Red';
    const digitCount = definition.templateType === 'seven_segment_display'
      ? normalizeSevenSegmentDigitCount(
          options.digitCount ?? selectedValue ?? definition.defaultValue
        )
      : undefined;
    const commonType = definition.templateType === 'seven_segment_display'
      ? getSevenSegmentCommonTypeForDefinition(definition, options.commonType)
      : undefined;
    const sevenSegmentCanvasSize = definition.templateType === 'seven_segment_display'
      ? resolveSevenSegmentCanvasSize(digitCount, {
          baseCanvasWidth: definition.canvasWidth,
          baseCanvasHeight: definition.canvasHeight,
          baseDigitCount: definition.defaultValue,
        })
      : null;
    const simulationModel = definition.simulationModel
      ? JSON.parse(JSON.stringify(definition.simulationModel))
      : {};
    const defaultScale = getDefaultPassiveScale(definition);
    const scale = normalizeComponentScale(options.scale, defaultScale);

    if (definition.templateType === 'seven_segment_display') {
      simulationModel.parameters = {
        ...(simulationModel.parameters || {}),
        commonType,
        digitCount,
      };
    }

    return {
      id: options.id || `part_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      type: 'passive',
      passiveType: definition.templateType,
      passiveKey: definition.componentKey,
      passiveDefinitionId: definition.id,
      svgContent: definition.svgContent || '',
      description: definition.description || '',
      valueOptions: Array.isArray(definition.valueOptions) ? definition.valueOptions : [],
      defaultValue: definition.defaultValue ?? null,
      showNoSimulationTag: Boolean(definition.showNoSimulationTag),
      simulationModel,
      selectedValue,
      valueLabel: getPassiveSelectedValueLabel(definition, selectedValue),
      valueOhms: coercePassiveOhmsValue(
        definition.templateType,
        selectedValue,
        definition.simulationModel
      ),
      color: definition.templateType === 'led' ? ledColor : undefined,
      commonType,
      digitCount,
      segmentStates: definition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentSegmentStates(options.segmentStates, digitCount)
        : undefined,
      pinOffsets: definition.pinOffsets,
      canvasWidth: sevenSegmentCanvasSize?.width || definition.canvasWidth,
      canvasHeight: sevenSegmentCanvasSize?.height || definition.canvasHeight,
      x: 0,
      y: 0,
      scale,
      name: getPassiveInstanceName(definition, {
        selectedValue: digitCount || selectedValue,
        ledColorLabel,
      }),
      rotation: 0,
    };
  };

  const addPassiveComponentToCanvas = (definition, options = {}) => {
    if (isCircuitEditingLocked) {
      return;
    }

    if (!definition) {
      return;
    }

    let newComponent = buildPassiveComponentDraft(definition, options);
    const requestedInitialPosition = typeof options.getInitialPosition === 'function'
      ? options.getInitialPosition(newComponent)
      : options.initialPosition;
    const initialPosition = (
      requestedInitialPosition &&
      Number.isFinite(Number(requestedInitialPosition.x)) &&
      Number.isFinite(Number(requestedInitialPosition.y))
    )
      ? {
          x: Number(requestedInitialPosition.x),
          y: Number(requestedInitialPosition.y),
        }
      : getInitialComponentPosition(newComponent);

    newComponent = {
      ...newComponent,
      ...getResolvedComponentPosition(
        newComponent,
        initialPosition.x,
        initialPosition.y
      ),
    };

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setComponents((current) => [
      ...current,
      newComponent,
    ]);

    setSelectedComponentIds(new Set([newComponent.id]));
    setSelectedItem({ type: 'component', id: newComponent.id });
  };

  const getPassivePreviewSelectedValue = (definition) => {
    if (!definition || definition.templateType === 'led') {
      return '';
    }

    if (
      definition.componentKey === selectedPassiveKey &&
      passiveSupportsSelectableValues(definition)
    ) {
      return newPassiveValue || getDefaultPassiveValue(definition);
    }

    return getDefaultPassiveValue(definition);
  };

  const getPreparedPassiveDefinitionIcon = (definition) => {
    if (!definition) {
      return '';
    }

    const previewComponent = buildPassiveComponentDraft(definition, {
      id: `preview_${definition.componentKey}`,
      color: definition.componentKey === selectedPassiveKey ? newLedColor : undefined,
      commonType: definition.templateType === 'seven_segment_display'
        ? newSevenSegmentCommonType
        : undefined,
      selectedValue: getPassivePreviewSelectedValue(definition),
    });

    return getPreparedPassiveSvg(previewComponent);
  };

  const getPassiveSearchMeta = (definition) => {
    if (!definition) {
      return 'Basic part';
    }

    const metaParts = [formatPassiveTemplateLabel(definition.templateType)];

    if (definition.templateType === 'led') {
      if (definition.componentKey === selectedPassiveKey) {
        const selectedColorLabel = LED_COLOR_OPTIONS.find(
          (option) => option.value === newLedColor
        )?.label;

        if (selectedColorLabel) {
          metaParts.push(selectedColorLabel);
        }
      }
    } else if (passiveSupportsSelectableValues(definition)) {
      const selectedValue = getPassivePreviewSelectedValue(definition);
      const selectedValueLabel = getPassiveSelectedValueLabel(definition, selectedValue);

      if (selectedValueLabel) {
        metaParts.push(selectedValueLabel);
      }

      if (
        definition.templateType === 'seven_segment_display' &&
        definition.componentKey === selectedPassiveKey
      ) {
        metaParts.push(getSevenSegmentCommonTypeLabel(newSevenSegmentCommonType));
      }
    }

    if (getPassiveNoSimulationTag(definition)) {
      metaParts.push('No Simulation');
    }

    return metaParts.join(' / ');
  };

  const filteredBoards = useMemo(() => {
    const search = deferredComponentSearch.trim().toLowerCase();
    const eligibleBoards = boards.filter(isBoardAllowedInProjectBuilder);

    if (!search) return eligibleBoards;

    return eligibleBoards.filter(board => {
      const name = String(board.name || '').toLowerCase();
      const manufacturer = String(board.manufacturer || '').toLowerCase();
      const category = String(board.category || '').toLowerCase();

      return (
        name.includes(search) ||
        manufacturer.includes(search) ||
        category.includes(search)
      );
    });
  }, [boards, deferredComponentSearch]);

  const visibleFilteredBoards = useMemo(() => {
    return filteredBoards.slice(0, 50);
  }, [filteredBoards]);

  const filteredPassiveComponents = useMemo(() => {
    const search = passiveSearch.trim().toLowerCase();

    if (!search) {
      return componentPickerPassiveComponents;
    }

    return componentPickerPassiveComponents.filter((definition) => {
      const name = String(definition.name || '').toLowerCase();
      const description = String(definition.description || '').toLowerCase();
      const componentKey = String(definition.componentKey || '').toLowerCase();
      const templateType = String(definition.templateType || '').toLowerCase();

      return (
        name.includes(search) ||
        description.includes(search) ||
        componentKey.includes(search) ||
        templateType.includes(search)
      );
    });
  }, [componentPickerPassiveComponents, passiveSearch]);

  const visibleFilteredPassiveComponents = useMemo(() => {
    return filteredPassiveComponents.slice(0, 50);
  }, [filteredPassiveComponents]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!showComponentModal || componentAddMode !== 'board') {
      return;
    }

    visibleFilteredBoards.forEach((board) => {
      if (boardHasRenderableSvg(board)) {
        return;
      }

      ensureBoardSvgRecord(board).catch((loadError) => {
        console.error(`Error loading board SVG for ${board?.name || board?.id}:`, loadError);
      });
    });
  }, [componentAddMode, showComponentModal, visibleFilteredBoards]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const suggestionPins = useMemo(() => {
    if (isComponentDragActive) {
      return suggestionPinsCacheRef.current;
    }

    const nextPins = get_project_pins_with_marker_keys(
      components,
      pinLayoutByPartId
    );

    suggestionPinsCacheRef.current = nextPins;
    return nextPins;
  }, [components, isComponentDragActive, pinLayoutByPartId]);

  const clearBreadboardHoverHighlight = (expectedHover = null) => {
    const currentHover = breadboardHoverStateRef.current;

    if (
      expectedHover &&
      (
        currentHover?.partId !== expectedHover.partId ||
        currentHover?.pinKey !== expectedHover.pinKey
      )
    ) {
      return;
    }

    const layer = breadboardHoverLayerRef.current;

    if (layer) {
      while (layer.firstChild) {
        layer.removeChild(layer.firstChild);
      }
    }

    breadboardHoverStateRef.current = null;
  };

  const drawBreadboardHoverHighlight = (partId, pinKey) => {
    const safePartId = String(partId || '');
    const safePinKey = String(pinKey || '');

    if (!safePartId || !safePinKey) {
      clearBreadboardHoverHighlight();
      return;
    }

    const currentHover = breadboardHoverStateRef.current;

    if (currentHover?.partId === safePartId && currentHover?.pinKey === safePinKey) {
      return;
    }

    const layer = breadboardHoverLayerRef.current;
    const component = componentsRef.current.find(
      (item) => String(item.id) === safePartId
    );

    if (!layer || !isBreadboardComponent(component)) {
      return;
    }

    while (layer.firstChild) {
      layer.removeChild(layer.firstChild);
    }

    const layout = pinLayoutByPartId[safePartId] || {};
    const markerKeys = getBreadboardConnectedMarkerKeys(
      safePinKey,
      Object.keys(layout)
    );

    markerKeys.forEach((markerKey) => {
      const point = layout[markerKey];

      if (
        !point ||
        !Number.isFinite(point.x) ||
        !Number.isFinite(point.y)
      ) {
        return;
      }

      const highlight = document.createElementNS(
        PROJECT_BUILDER_SVG_NAMESPACE,
        'circle'
      );

      highlight.setAttribute('cx', String(point.x));
      highlight.setAttribute('cy', String(point.y));
      highlight.setAttribute('r', '4.4');
      highlight.setAttribute('fill', 'rgba(250, 204, 21, 0.34)');
      highlight.setAttribute('stroke', '#eab308');
      highlight.setAttribute('stroke-width', '1.2');

      layer.appendChild(highlight);
    });

    breadboardHoverStateRef.current = {
      partId: safePartId,
      pinKey: safePinKey,
    };
  };

  const drawBreadboardPlacementHighlights = (matches = []) => {
    const layer = breadboardHoverLayerRef.current;

    if (!layer) {
      return;
    }

    while (layer.firstChild) {
      layer.removeChild(layer.firstChild);
    }

    matches.forEach((match) => {
      const point = match?.targetPoint;

      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) {
        return;
      }

      const ring = document.createElementNS(PROJECT_BUILDER_SVG_NAMESPACE, 'circle');
      ring.setAttribute('cx', String(point.x));
      ring.setAttribute('cy', String(point.y));
      ring.setAttribute('r', '4.6');
      ring.setAttribute('fill', 'rgba(34, 197, 94, 0.28)');
      ring.setAttribute('stroke', '#16a34a');
      ring.setAttribute('stroke-width', '1.6');

      const center = document.createElementNS(PROJECT_BUILDER_SVG_NAMESPACE, 'circle');
      center.setAttribute('cx', String(point.x));
      center.setAttribute('cy', String(point.y));
      center.setAttribute('r', '1.7');
      center.setAttribute('fill', '#16a34a');

      layer.appendChild(ring);
      layer.appendChild(center);
    });

    breadboardHoverStateRef.current = matches.length > 0
      ? { type: 'component-placement' }
      : null;
  };

  const runtimeSimulationComponents = useMemo(() => {
    if (isComponentDragActive) {
      return runtimeSimulationComponentsCacheRef.current;
    }

    const nextComponents = components.map((component) => {
      if (component?.type !== 'passive') {
        return component;
      }

      return {
        ...component,
        passiveDefinition: passiveComponentMap.get(component.passiveKey) ||
        passiveComponentMap.get(component.passiveType) ||
        null,
      };
    });

    runtimeSimulationComponentsCacheRef.current = nextComponents;
    return nextComponents;
  }, [components, isComponentDragActive, passiveComponentMap]);

  const suggestionPinsById = useMemo(() => {
    return new Map(
      suggestionPins.map((pin) => [String(pin.id), pin])
    );
  }, [suggestionPins]);
  const simulationCircuitAnalysis = useMemo(() => {
    if (isComponentDragActive && simulationCircuitAnalysisCacheRef.current) {
      return simulationCircuitAnalysisCacheRef.current;
    }

    const nextAnalysis = inspectArduinoSimulationCircuit({
      components: runtimeSimulationComponents,
      connections: logicalConnections,
      suggestionPins,
      simulationPinStates,
      pressedButtonIds: activeSimulationInteractionIds,
    });

    simulationCircuitAnalysisCacheRef.current = nextAnalysis;
    return nextAnalysis;
  }, [
    activeSimulationInteractionIds,
    isComponentDragActive,
    logicalConnections,
    runtimeSimulationComponents,
    simulationPinStates,
    suggestionPins,
  ]);

  const simulationCircuitAnalysisRef = useRef(simulationCircuitAnalysis);

  useEffect(() => {
    simulationCircuitAnalysisRef.current = simulationCircuitAnalysis;
  }, [simulationCircuitAnalysis]);

  const preferredSimulationExamplePinName =
    simulationCircuitAnalysis.preferredExternalLedPinName || '';
  const preferredSimulationExampleCode = useMemo(
    () => buildArduinoBlinkExampleSketch(
      preferredSimulationExamplePinName || 'LED_BUILTIN'
    ),
    [preferredSimulationExamplePinName]
  );
  const simulationCircuitDebugText = useMemo(
    () => buildSimulationCircuitDebugText(
      simulationCircuitAnalysis,
      logicalConnections,
      simulationInteractionSummaries
    ),
    [logicalConnections, simulationCircuitAnalysis, simulationInteractionSummaries]
  );

  const hasSimulationUnoBoard =
    Array.isArray(simulationCircuitAnalysis?.unoBoards) &&
    simulationCircuitAnalysis.unoBoards.length > 0;
  const hasSimulationBuzzers =
    Array.isArray(simulationCircuitAnalysis?.buzzerStates) &&
    simulationCircuitAnalysis.buzzerStates.length > 0;

  const showSimulationNoUnoWarning =
    isArduinoSimulationEnabled &&
    showSimulationModal &&
    !hasSimulationUnoBoard;

  const canRunSimulationPreview =
    hasSimulationUnoBoard &&
    !isSimulationCompiling;

  const simulatedLedStatesByComponentId = useMemo(() => {
    if (simulationPhase !== 'running') {
      return {};
    }

    return Object.fromEntries(
      (simulationCircuitAnalysis.ledPaths || []).map((ledPath) => {
        const brightness = resolveLedBrightnessFromSimulationPins(
          ledPath,
          simulationPinStates
        );
        const isOn = resolveLedIsOnFromSimulationPins(
          ledPath,
          simulationPinStates
        );

        return [
          ledPath.componentId,
          {
            brightness,
            isOn,
            anodePotentials: ledPath.anodePotentials || [],
            cathodePotentials: ledPath.cathodePotentials || [],
          },
        ];
      })
    );
  }, [simulationCircuitAnalysis, simulationPhase, simulationPinStates]);

  const simulatedServoStatesByComponentId = useMemo(() => {
    if (simulationPhase !== 'running') {
      return {};
    }

    return Object.fromEntries(
      (simulationCircuitAnalysis.servoStates || []).map((servoState) => [
        servoState.componentId,
        servoState,
      ])
    );
  }, [simulationCircuitAnalysis, simulationPhase]);

  const simulatedSevenSegmentStatesByComponentId = useMemo(() => {
    if (simulationPhase !== 'running') {
      return {};
    }

    return Object.fromEntries(
      (simulationCircuitAnalysis.sevenSegmentStates || []).map((displayState) => [
        displayState.componentId,
        displayState,
      ])
    );
  }, [simulationCircuitAnalysis, simulationPhase]);

  useEffect(() => {
    const lcdStates = Array.isArray(simulationCircuitAnalysis?.lcdStates)
      ? simulationCircuitAnalysis.lcdStates
      : [];

    if (simulationPhase !== 'running' || lcdStates.length === 0) {
      lcdSimulationStateByComponentIdRef.current = {};
      simulationPendingPinTransitionEventsRef.current = [];

      setSimulatedLcdStatesByComponentId((current) => (
        Object.keys(current).length > 0 ? {} : current
      ));
      return;
    }

    const pendingPinTransitionEvents = simulationPendingPinTransitionEventsRef.current;
    simulationPendingPinTransitionEventsRef.current = [];
    const previousEngineStates = lcdSimulationStateByComponentIdRef.current;
    const nextEngineStates = {};
    const nextRenderedStates = {};

    lcdStates.forEach((lcdState) => {
      const componentId = String(lcdState?.componentId || '').trim();

      if (!componentId) {
        return;
      }

      const signalSnapshot = buildLcd16x2SignalSnapshot(
        lcdState,
        simulationPinStates
      );
      const nextEngineState = advanceLcd16x2SimulationFromPinTransitionEvents(
        previousEngineStates[componentId],
        lcdState,
        simulationPinStates,
        pendingPinTransitionEvents
      );

      nextEngineStates[componentId] = nextEngineState;
      nextRenderedStates[componentId] = {
        ...lcdState,
        backlightOn: signalSnapshot.backlightOn,
        isDisplayEnabled: Boolean(signalSnapshot.isPowered) && Boolean(nextEngineState.displayOn),
        rows: getLcd16x2VisibleRows(nextEngineState, {
          powered: signalSnapshot.isPowered,
          displayOn: nextEngineState.displayOn,
        }),
      };
    });

    lcdSimulationStateByComponentIdRef.current = nextEngineStates;
    setSimulatedLcdStatesByComponentId(nextRenderedStates);
  }, [simulationCircuitAnalysis, simulationPhase, simulationPinStates, simulationSnapshotVersion]);

  useEffect(() => {
    const buzzerStates = Array.isArray(simulationCircuitAnalysis?.buzzerStates)
      ? simulationCircuitAnalysis.buzzerStates
      : [];

    if (
      simulationPhase !== 'running' ||
      !simulationAudioEnabled ||
      buzzerStates.length === 0
    ) {
      stopSimulationAudio();
      return;
    }

    const audioManager = getSimulationAudioManager();
    const nextActiveBuzzerIds = new Set();

    buzzerStates.forEach((buzzerState) => {
      const componentId = String(buzzerState?.componentId || '').trim();
      const frequencyHz = Number(buzzerState?.estimatedFrequencyHz);
      const isActive = Boolean(buzzerState?.isActive) && Number.isFinite(frequencyHz);

      if (!componentId) {
        return;
      }

      if (isActive) {
        nextActiveBuzzerIds.add(componentId);
      }

      audioManager.set_passive_buzzer_tone(componentId, isActive, frequencyHz);
    });

    for (const componentId of simulationActiveBuzzerIdsRef.current) {
      if (!nextActiveBuzzerIds.has(componentId)) {
        audioManager.stop_passive_buzzer(componentId);
      }
    }

    simulationActiveBuzzerIdsRef.current = nextActiveBuzzerIds;
  }, [simulationAudioEnabled, simulationCircuitAnalysis, simulationPhase]);

  const servoRenderTargetsByComponentId = useMemo(() => (
    Object.fromEntries(
      components
        .filter((component) => component?.type === 'passive' && component.passiveType === 'servo')
        .map((component) => {
          const passiveDefinition = passiveComponentMap.get(component.passiveKey) ||
            passiveComponentMap.get(component.passiveType) ||
            null;
          const simulationModel = resolvePassiveComponentRuntimeSimulationModel(
            component,
            passiveDefinition
          );
          const parameters = simulationModel?.parameters || {};
          const idleAngleDegrees = Number.isFinite(Number(parameters.idleAngleDegrees))
            ? Number(parameters.idleAngleDegrees)
            : 0;
          const simulatedServoState = simulatedServoStatesByComponentId[component.id];
          const targetAngleDegrees = Number.isFinite(Number(simulatedServoState?.angleDegrees))
            ? Number(simulatedServoState.angleDegrees)
            : idleAngleDegrees;

          return [
            component.id,
            {
              idleAngleDegrees,
              targetAngleDegrees,
              travelDegreesPerSecond: normalizeServoTravelDegreesPerSecond(
                parameters.travelDegreesPerSecond
              ),
            },
          ];
        })
    )
  ), [components, passiveComponentMap, simulatedServoStatesByComponentId]);

  useEffect(() => {
    animatedServoAnglesByComponentIdRef.current = animatedServoAnglesByComponentId;
  }, [animatedServoAnglesByComponentId]);

  useEffect(() => {
    const targetEntries = Object.entries(servoRenderTargetsByComponentId);

    if (servoAnimationFrameRef.current) {
      cancelAnimationFrame(servoAnimationFrameRef.current);
      servoAnimationFrameRef.current = 0;
    }

    if (targetEntries.length === 0) {
      if (Object.keys(animatedServoAnglesByComponentIdRef.current).length > 0) {
        animatedServoAnglesByComponentIdRef.current = {};
        setAnimatedServoAnglesByComponentId({});
      }

      return undefined;
    }

    let isCancelled = false;
    let lastTimestamp = null;

    const animateServoAngles = (timestamp) => {
      if (isCancelled) {
        return;
      }

      const previousAngles = animatedServoAnglesByComponentIdRef.current;
      const previousKeys = Object.keys(previousAngles);
      const deltaMilliseconds = lastTimestamp === null
        ? 16
        : Math.min(
            Math.max(timestamp - lastTimestamp, 0),
            MAX_SERVO_ANIMATION_FRAME_DELTA_MS
          );
      const deltaSeconds = deltaMilliseconds / 1000;
      const nextAngles = {};
      let didChange = previousKeys.length !== targetEntries.length;
      let hasPendingMotion = false;

      lastTimestamp = timestamp;

      targetEntries.forEach(([componentId, targetState]) => {
        const currentAngle = Number(previousAngles[componentId]);
        const baseAngle = Number.isFinite(currentAngle)
          ? currentAngle
          : Number(targetState.idleAngleDegrees) || 0;
        const targetAngle = Number(targetState.targetAngleDegrees) || 0;
        const maximumStep = targetState.travelDegreesPerSecond * deltaSeconds;
        const deltaAngle = targetAngle - baseAngle;
        let nextAngle = targetAngle;

        if (Math.abs(deltaAngle) > SERVO_ANGLE_ANIMATION_EPSILON_DEGREES) {
          if (maximumStep > 0 && maximumStep < Math.abs(deltaAngle)) {
            nextAngle = baseAngle + (Math.sign(deltaAngle) * maximumStep);
            hasPendingMotion = true;
          }
        }

        nextAngles[componentId] = nextAngle;

        if (
          !Number.isFinite(currentAngle) ||
          Math.abs(nextAngle - currentAngle) > SERVO_ANGLE_ANIMATION_EPSILON_DEGREES
        ) {
          didChange = true;
        }
      });

      if (didChange) {
        animatedServoAnglesByComponentIdRef.current = nextAngles;
        setAnimatedServoAnglesByComponentId(nextAngles);
      }

      if (hasPendingMotion) {
        servoAnimationFrameRef.current = requestAnimationFrame(animateServoAngles);
      } else {
        servoAnimationFrameRef.current = 0;
      }
    };

    servoAnimationFrameRef.current = requestAnimationFrame(animateServoAngles);

    return () => {
      isCancelled = true;

      if (servoAnimationFrameRef.current) {
        cancelAnimationFrame(servoAnimationFrameRef.current);
        servoAnimationFrameRef.current = 0;
      }
    };
  }, [servoRenderTargetsByComponentId]);

  useEffect(() => {
    if (simulationPhase !== 'running') {
      simulationAppliedDigitalInputLevelsRef.current.clear();
      simulationAppliedAnalogInputVoltagesRef.current.clear();
      return;
    }

    syncArduinoSimulationDigitalInputs({
      appliedLevelsRef: simulationAppliedDigitalInputLevelsRef,
      digitalInputsByPinName: simulationCircuitAnalysis.digitalInputsByPinName,
      pinStates: simulationPinStates,
      runner: simulationRunnerRef.current,
    });
    syncArduinoSimulationAnalogInputs({
      analogInputsByPinName: simulationCircuitAnalysis.analogInputsByPinName,
      appliedVoltagesRef: simulationAppliedAnalogInputVoltagesRef,
      runner: simulationRunnerRef.current,
    });
  }, [
    simulationCircuitAnalysis.analogInputsByPinName,
    simulationCircuitAnalysis.digitalInputsByPinName,
    simulationPhase,
    simulationPinStates,
  ]);

  const highlightedAnalysisPins = useMemo(() => {
    return highlightedAnalysisPinIds
      .map((pinId) => suggestionPinsById.get(String(pinId)))
      .filter(Boolean);
  }, [highlightedAnalysisPinIds, suggestionPinsById]);

  useEffect(() => {
    setActiveSimulationInteractionIds((current) => {
      const next = new Set(
        Array.from(current).filter((componentId) => simulationInteractiveComponentMap.has(componentId))
      );

      if (next.size === current.size && Array.from(next).every((componentId) => current.has(componentId))) {
        return current;
      }

      return next;
    });

    for (const [pointerId, componentId] of simulationInteractionPointerMapRef.current.entries()) {
      if (!simulationInteractiveComponentMap.has(componentId)) {
        simulationInteractionPointerMapRef.current.delete(pointerId);
      }
    }
  }, [simulationInteractiveComponentMap]);

  const analysisResultsBySeverity = useMemo(() => {
    return {
      error: analysisResults.filter((result) => result.severity === 'error'),
      warning: analysisResults.filter((result) => result.severity === 'warning'),
      suggestion: analysisResults.filter((result) => result.severity === 'suggestion'),
      info: analysisResults.filter((result) => result.severity === 'info'),
      pass: analysisResults.filter((result) => result.severity === 'pass'),
    };
  }, [analysisResults]);

  useEffect(() => {
    if (isComponentDragActive) {
      return;
    }

    if (!showAnalysisModal && analysisResults.length === 0) {
      return;
    }

    const analysis = analyze_circuit({
      components,
      connections: logicalConnections,
      pins: suggestionPins,
    });

    setAnalysisResults(analysis.results);
    setAnalysisSummary(analysis.summary);
  }, [analysisResults.length, components, isComponentDragActive, logicalConnections, suggestionPins, showAnalysisModal]);

  useEffect(() => {
    if (isComponentDragActive) {
      return;
    }

    if (!showAnalysisModal && wiringSteps.length === 0) {
      return;
    }

    setWiringSteps(generate_wiring_steps({
      components,
      connections: logicalConnections,
      pins: suggestionPins,
    }));
  }, [components, isComponentDragActive, logicalConnections, showAnalysisModal, suggestionPins, wiringSteps.length]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (componentAddMode !== 'board') return;

    if (isSearchingComponents) return;

    if (selectedBoardToAdd) {
      const selectedStillVisible = visibleFilteredBoards.some(
        board => String(board.id) === String(selectedBoardToAdd)
      );

      if (selectedStillVisible) return;
    }

    setSelectedBoardToAdd(visibleFilteredBoards[0]?.id || '');
  }, [
    componentAddMode,
    isSearchingComponents,
    visibleFilteredBoards,
    selectedBoardToAdd,
  ]);

  useEffect(() => {
    if (componentAddMode !== 'passive') {
      return;
    }

    if (selectedPassiveKey) {
      const selectedStillVisible = visibleFilteredPassiveComponents.some(
        (definition) => definition.componentKey === selectedPassiveKey
      );

      if (selectedStillVisible) {
        return;
      }
    }

    setSelectedPassiveKey(visibleFilteredPassiveComponents[0]?.componentKey || '');
  }, [
    componentAddMode,
    selectedPassiveKey,
    visibleFilteredPassiveComponents,
  ]);

  useEffect(() => {
    if (componentAddMode !== 'passive') {
      return;
    }

    if (!selectedPassiveDefinition) {
      const fallbackPassive = componentPickerPassiveComponents[0];

      if (fallbackPassive?.componentKey) {
        setSelectedPassiveKey(fallbackPassive.componentKey);
      }

      return;
    }

    if (selectedPassiveDefinition.templateType === 'led') {
      setNewPassiveValue('');
      return;
    }

    if (selectedPassiveDefinition.templateType === 'seven_segment_display') {
      setNewSevenSegmentCommonType(getSevenSegmentCommonTypeForDefinition(selectedPassiveDefinition));
    }

    if (!passiveSupportsSelectableValues(selectedPassiveDefinition)) {
      setNewPassiveValue('');
      return;
    }

    const defaultValue = getDefaultPassiveValue(selectedPassiveDefinition);
    setNewPassiveValue(String(defaultValue ?? ''));
  }, [
    componentAddMode,
    componentPickerPassiveComponents,
    selectedPassiveDefinition,
  ]);

  useEffect(() => {
    const svg = canvasRef.current;
    if (!svg) return;

    let frameA = null;
    let frameB = null;
    let timeoutId = null;

    const clearScheduledCollect = () => {
      if (frameA) cancelAnimationFrame(frameA);
      if (frameB) cancelAnimationFrame(frameB);
      if (timeoutId) window.clearTimeout(timeoutId);

      frameA = null;
      frameB = null;
      timeoutId = null;
    };

    const scheduleCollect = () => {
      clearScheduledCollect();

      frameA = requestAnimationFrame(() => {
        frameB = requestAnimationFrame(() => {
          collectPinLayouts();
        });
      });

      // Some embedded board SVGs inside foreignObject finish layout a bit later
      // than the outer canvas; retry once after the browser settles.
      timeoutId = window.setTimeout(() => {
        collectPinLayouts();
      }, 180);
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleCollect)
      : null;

    observer?.observe(svg);

    Object.values(partHostRefs.current).forEach((host) => {
      if (host) {
        observer?.observe(host);
      }
    });

    window.addEventListener('resize', scheduleCollect);
    window.addEventListener('orientationchange', scheduleCollect);

    scheduleCollect();

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', scheduleCollect);
      window.removeEventListener('orientationchange', scheduleCollect);

      clearScheduledCollect();
    };
  }, [components, zoomLevel]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!isCircuitEditingLocked) {
      return;
    }

    dragRef.current = null;
    selectionDragRef.current = null;
    breadboardHoverStateRef.current = null;
    if (breadboardHoverLayerRef.current) {
      while (breadboardHoverLayerRef.current.firstChild) {
        breadboardHoverLayerRef.current.removeChild(breadboardHoverLayerRef.current.firstChild);
      }
    }
    wireDragRef.current = null;
    rewireDragRef.current = null;
    wireCornerDragRef.current = null;
    suppressCanvasClickRef.current = false;
    didDragComponentRef.current = false;
    setWirePreview(null);
    clearWireDropFeedback();
    setSelectionBox(null);
  }, [isCircuitEditingLocked, clearWireDropFeedback]);

  useEffect(() => (
    () => {
      if (simulationCopyFeedbackTimeoutRef.current) {
        window.clearTimeout(simulationCopyFeedbackTimeoutRef.current);
      }
    }
  ), []);

  useEffect(() => {
    if (!showSimulationWorkspaceModal) {
      expandedSimulationEditorScrollTopRef.current = 0;

      if (expandedSimulationLineNumberTrackRef.current) {
        expandedSimulationLineNumberTrackRef.current.style.transform = 'translateY(0px)';
      }

      return;
    }

    if (expandedSimulationLineNumberTrackRef.current) {
      expandedSimulationLineNumberTrackRef.current.style.transform = `translateY(${-expandedSimulationEditorScrollTopRef.current}px)`;
    }
  }, [expandedSimulationLineNumbers.length, showSimulationWorkspaceModal]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showSimulationWorkspaceModal) {
        event.preventDefault();
        closeSimulationWorkspaceModal();
        return;
      }

      if (event.key === 'Escape' && wireDragRef.current) {
        event.preventDefault();
        cancelWireDrag();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSimulationWorkspaceModal]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isTypingTarget(event.target)) {
        return;
      }

      if (showComponentModal || showPassiveEditModal || showAnalysisModal || showSimulationWorkspaceModal || showExamplesModal) {
        return;
      }

      if (wireDragRef.current) {
        return;
      }

      const key = event.key.toLowerCase();
      const hasHistoryModifier = (event.metaKey || event.ctrlKey) && !event.altKey;

      if (hasHistoryModifier && key === 'z') {
        event.preventDefault();

        if (event.shiftKey) {
          redoProjectEdit();
        } else {
          undoProjectEdit();
        }

        return;
      }

      if (hasHistoryModifier && key === 'y') {
        event.preventDefault();
        redoProjectEdit();
        return;
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (
          selectedItem ||
          selectedComponentIds.size > 0
        ) {
          event.preventDefault();
          deleteSelectedItems();
        }

        return;
      }

      if (key === 'r') {
        if (
          selectedItem?.type === 'component' ||
          selectedComponentIds.size > 0
        ) {
          event.preventDefault();
          rotateSelectedComponent();
        }

        return;
      }

      if (key === 'c') {
        if (
          selectedItem?.type === 'component' ||
          selectedComponentIds.size === 1
        ) {
          event.preventDefault();
          changeSelectedPassive();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    selectedItem,
    selectedComponentIds,
    components,
    showComponentModal,
    showPassiveEditModal,
    showAnalysisModal,
    showSimulationWorkspaceModal,
    showExamplesModal,
    showSimulationModal,
    undoProjectEdit,
    redoProjectEdit,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const closeComponentModal = () => {
    setShowComponentModal(false);
    setComponentSearch('');
    setPassiveSearch('');
    setDeferredComponentSearch('');
    setSelectedBoardToAdd('');
    setNewSevenSegmentCommonType(SEVEN_SEGMENT_DEFAULT_COMMON_TYPE);
    setIsSearchingComponents(false);
    setComponentAddMode('board');
  };

  const getInitialComponentPosition = (componentDraft) => {
    const viewBounds = getCurrentViewBoxBounds();
    const newBounds = getComponentDragBounds(componentDraft);

    const spacing = 40;
    const margin = 24;

    const existingItems = components
      .filter(isComponentVisible)
      .map((comp) => {
        const bounds = getComponentDragBounds(comp);

        return {
          comp,
          bounds,
        };
      });

    const totalExistingWidth = existingItems.reduce(
      (sum, item) => sum + item.bounds.width,
      0
    );

    const totalSpacing = existingItems.length * spacing;

    const totalRowWidth =
      totalExistingWidth +
      totalSpacing +
      newBounds.width;

    const startX =
      viewBounds.x +
      Math.max((viewBounds.width - totalRowWidth) / 2, margin);

    const rowHeight = Math.max(
      newBounds.height,
      ...existingItems.map(item => item.bounds.height),
      0
    );

    const y =
      viewBounds.y +
      Math.max((viewBounds.height - rowHeight) / 2, margin);

    const x =
      startX +
      totalExistingWidth +
      totalSpacing;

    return {
      x,
      y: y + Math.max((rowHeight - newBounds.height) / 2, 0),
    };
  };

  const fitComponentScaleToView = (componentDraft, maxViewRatio = 0.78) => {
     const viewBounds = getCurrentViewBoxBounds();
     const bounds = getComponentDragBounds(componentDraft);

     const scaleX = (viewBounds.width * maxViewRatio) / bounds.width;
     const scaleY = (viewBounds.height * maxViewRatio) / bounds.height;
     const fitScale = Math.min(scaleX, scaleY, 1);

     return Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
  };

  const addSelectedBoardFromModal = async () => {
    if (isCircuitEditingLocked) {
      return;
    }

    if (!selectedBoardToAdd) return;

    const board = boards.find(b => String(b.id) === String(selectedBoardToAdd));
    if (!board) return;

    try {
      const detailedBoard = await ensureBoardSvgRecord(board);
      const pins = await fetchBoardPins(board.id);
      const boardWithPins = normalize_board(detailedBoard || board, pins);

      if (isCircuitEditingLockedRef.current) {
        return;
      }

      let newComponentDraft = {
        id: `part_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: 'board',
        boardId: board.id,
        board: boardWithPins,
        x: 0,
        y: 0,
        scale: 1,
        targetWidth: DEFAULT_BOARD_TARGET_WIDTH,
        name: detailedBoard?.name || board.name || `Component ${components.length + 1}`,
        rotation: 0,
      };

      newComponentDraft = {
        ...newComponentDraft,
        scale: fitComponentScaleToView(newComponentDraft),
      };

      const initialPosition = getInitialComponentPosition(newComponentDraft);
      const resolvedPosition = getResolvedComponentPosition(
        newComponentDraft,
        initialPosition.x,
        initialPosition.y
      );

      const newComponent = {
        ...newComponentDraft,
        ...resolvedPosition,
      };

      pushUndoSnapshot(createWorkspaceHistorySnapshot());
      setComponents(current => [...current, newComponent]);
      setSelectedComponentIds(new Set([newComponent.id]));
      setSelectedItem({ type: 'component', id: newComponent.id });
      closeComponentModal();
    } catch (error) {
      console.error('Error adding board pins:', error);
      if (isCircuitEditingLockedRef.current) {
        return;
      }

      const boardWithPins = normalize_board(board, []);

      let newComponentDraft = {
        id: `part_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        type: 'board',
        boardId: board.id,
        board: boardWithPins,
        x: 0,
        y: 0,
        scale: 1,
        targetWidth: DEFAULT_BOARD_TARGET_WIDTH,
        name: board.name || `Component ${components.length + 1}`,
        rotation: 0,
      };

      newComponentDraft = {
        ...newComponentDraft,
        scale: fitComponentScaleToView(newComponentDraft),
      };

      const initialPosition = getInitialComponentPosition(newComponentDraft);
      const resolvedPosition = getResolvedComponentPosition(
        newComponentDraft,
        initialPosition.x,
        initialPosition.y
      );

      const newComponent = {
        ...newComponentDraft,
        ...resolvedPosition,
      };

      pushUndoSnapshot(createWorkspaceHistorySnapshot());
      setComponents(current => [...current, newComponent]);
      closeComponentModal();
    }
  };

  const rotateComponent = (componentId) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const componentToRotate = components.find((component) => component.id === componentId);

    if (!canRotateComponent(componentToRotate)) {
      return;
    }

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setComponents(current =>
      current.map(comp =>
        comp.id === componentId
          ? {
              ...comp,
              rotation: ((comp.rotation || 0) + 90) % 360,
            }
          : comp
      )
    );
  };

  const openPassiveEditModal = (comp) => {
    if (isCircuitEditingLocked) {
      return;
    }

    const definition = getPassiveDefinitionForComponent(comp) || BUILT_IN_LED_PASSIVE;

    setPassiveEditComponentId(comp.id);
    setPassiveEditType(comp.passiveType || 'resistor');

    if (comp.passiveType === 'led') {
      setPassiveEditLedColor(comp.color || '#E60000');
    } else if (passiveSupportsSelectableValues(definition)) {
      setPassiveEditValue(String(comp.selectedValue ?? getDefaultPassiveValue(definition) ?? ''));
    } else {
      setPassiveEditValue('');
    }

    if (definition?.templateType === 'seven_segment_display') {
      setPassiveEditSevenSegmentCommonType(
        getSevenSegmentCommonTypeForDefinition(
          definition,
          comp.commonType ?? comp.simulationModel?.parameters?.commonType
        )
      );
    }

    setShowPassiveEditModal(true);
  };

  const closePassiveEditModal = () => {
    setShowPassiveEditModal(false);
    setPassiveEditComponentId(null);
    setPassiveEditType('resistor');
    setPassiveEditValue('');
    setPassiveEditLedColor('#E60000');
    setPassiveEditSevenSegmentCommonType(SEVEN_SEGMENT_DEFAULT_COMMON_TYPE);
  };

  const savePassiveEdit = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    const targetComponent = components.find((comp) => comp.id === passiveEditComponentId);

    if (!targetComponent) {
      closePassiveEditModal();
      return;
    }

    const definition = getPassiveDefinitionForComponent(targetComponent) || BUILT_IN_LED_PASSIVE;

    if (passiveEditType === 'led') {
      const selectedColor = LED_COLOR_OPTIONS.find(
        option => option.value === passiveEditLedColor
      );

      if ((targetComponent?.color || '#E60000') === passiveEditLedColor) {
        closePassiveEditModal();
        return;
      }

      pushUndoSnapshot(createWorkspaceHistorySnapshot());

      setComponents(current => {
        const nextComponents = current.map(comp =>
          comp.id === passiveEditComponentId
            ? {
                ...comp,
                color: passiveEditLedColor,
                name: getPassiveInstanceName(BUILT_IN_LED_PASSIVE, {
                  ledColorLabel: selectedColor?.label || 'Red',
                }),
              }
            : comp
        );

        return nextComponents;
      });

      closePassiveEditModal();
      return;
    }

    if (definition && passiveSupportsSelectableValues(definition)) {
      const nextSelectedValue = passiveEditValue !== ''
        ? passiveEditValue
        : String(getDefaultPassiveValue(definition) ?? '');
      const nextCommonType = definition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentCommonType(passiveEditSevenSegmentCommonType)
        : targetComponent?.commonType;
      const nextDigitCount = definition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentDigitCount(nextSelectedValue)
        : targetComponent?.digitCount;

      if (
        String(targetComponent?.selectedValue ?? '') === String(nextSelectedValue) &&
        String(targetComponent?.commonType ?? '') === String(nextCommonType ?? '') &&
        Number(targetComponent?.digitCount ?? 0) === Number(nextDigitCount ?? 0)
      ) {
        closePassiveEditModal();
        return;
      }

      pushUndoSnapshot(createWorkspaceHistorySnapshot());

      setComponents((current) => {
        const nextComponents = current.map((comp) => (
          comp.id === passiveEditComponentId
            ? (() => {
                const resolvedDigitCount = definition.templateType === 'seven_segment_display'
                  ? normalizeSevenSegmentDigitCount(nextSelectedValue)
                  : comp.digitCount;
                const sevenSegmentCanvasSize = definition.templateType === 'seven_segment_display'
                  ? resolveSevenSegmentCanvasSize(resolvedDigitCount, {
                      baseCanvasWidth: definition.canvasWidth,
                      baseCanvasHeight: definition.canvasHeight,
                      baseDigitCount: definition.defaultValue,
                    })
                  : null;
                const updatedComponent = {
                  ...comp,
                  selectedValue: nextSelectedValue,
                  valueLabel: getPassiveSelectedValueLabel(definition, nextSelectedValue),
                  valueOhms: coercePassiveOhmsValue(
                    definition.templateType,
                    nextSelectedValue,
                    definition.simulationModel
                  ),
                  commonType: definition.templateType === 'seven_segment_display'
                    ? nextCommonType
                    : comp.commonType,
                  digitCount: resolvedDigitCount,
                  segmentStates: definition.templateType === 'seven_segment_display'
                    ? normalizeSevenSegmentSegmentStates(comp.segmentStates, resolvedDigitCount)
                    : comp.segmentStates,
                  simulationModel: definition.templateType === 'seven_segment_display'
                    ? {
                        ...(comp.simulationModel || {}),
                        parameters: {
                          ...((comp.simulationModel || {}).parameters || {}),
                          commonType: nextCommonType,
                          digitCount: resolvedDigitCount,
                        },
                      }
                    : comp.simulationModel,
                  canvasWidth: sevenSegmentCanvasSize?.width || comp.canvasWidth,
                  canvasHeight: sevenSegmentCanvasSize?.height || comp.canvasHeight,
                  name: getPassiveInstanceName(definition, {
                    selectedValue: resolvedDigitCount || nextSelectedValue,
                  }),
                };

                return definition.templateType === 'seven_segment_display'
                  ? {
                      ...updatedComponent,
                      ...getResolvedComponentPosition(updatedComponent, comp.x, comp.y),
                    }
                  : updatedComponent;
              })()
            : comp
        ));

        return nextComponents;
      });
    }

    closePassiveEditModal();
  };

  const addBasicComponentFromModal = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    const definition = selectedPassiveDefinition || BUILT_IN_LED_PASSIVE;
    const lessonLimitStatus = isLessonMode
      ? getLessonComponentAddStatus(definition.componentKey || definition.templateType)
      : null;

    if (lessonLimitStatus?.disabled) {
      showSaveStatusMessage(lessonLimitStatus.message);
      return;
    }

    addPassiveComponentToCanvas(definition, {
      selectedValue: definition.templateType === 'led' ? '' : (newPassiveValue || undefined),
      commonType: definition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentCommonType(newSevenSegmentCommonType)
        : undefined,
      digitCount: definition.templateType === 'seven_segment_display'
        ? normalizeSevenSegmentDigitCount(newPassiveValue || definition.defaultValue)
        : undefined,
      color: definition.templateType === 'led' ? newLedColor : undefined,
    });

    closeComponentModal();
  };

  const currentLessonStep = isLessonMode
    ? activeLesson.steps[Math.min(lessonStepIndex, activeLesson.steps.length - 1)]
    : null;
  const lessonAllowsViewCode = Boolean(
    lessonHasSimulationCodePanel &&
    currentLessonStep &&
    lessonStepAllowsCodePanel(currentLessonStep)
  );
  const lessonAllowsEditCode = Boolean(
    lessonAllowsViewCode &&
    currentLessonStep?.allowedActions?.editCode
  );
  const lessonAllowsRunCode = Boolean(
    lessonAllowsViewCode &&
    currentLessonStep?.allowedActions?.runCode
  );
  const canShowLessonCodePanel = lessonAllowsViewCode;
  const currentLessonHintCount = currentLessonStep
    ? Number(lessonHintUsageByStepId[currentLessonStep.id] || 0)
    : 0;
  const lessonPartAvailability = useMemo(() => {
    const stepParts = Array.isArray(currentLessonStep?.availableComponents)
      ? currentLessonStep.availableComponents
      : [];

    return stepParts.reduce((availability, componentKey) => ({
      ...availability,
      [componentKey]: getLessonComponentAddStatus(componentKey),
    }), {});
  }, [currentLessonStep, getLessonComponentAddStatus]);
  const isLessonComponentLocked = useCallback((componentId) => (
    Boolean(
      isLessonMode &&
      (
        activeLesson?.lockedComponentIds?.includes(componentId) ||
        componentsRef.current.some((component) => (
          String(component.id) === String(componentId) &&
          Boolean(component.locked)
        ))
      )
    )
  ), [activeLesson, isLessonMode]);

  const canMoveComponent = useCallback((component) => {
    if (!component || isCircuitEditingLocked || isLessonComponentLocked(component.id)) {
      return false;
    }

    if (isLessonMode && currentLessonStep && !currentLessonStep.allowedActions?.moveComponents) {
      return false;
    }

    return true;
  }, [currentLessonStep, isCircuitEditingLocked, isLessonComponentLocked, isLessonMode]);

  const canRotateComponent = useCallback((component) => (
    canMoveComponent(component)
  ), [canMoveComponent]);

  const getUsableLessonPassivePinOffsets = (pinOffsets) => {
    if (!pinOffsets || typeof pinOffsets !== 'object' || Array.isArray(pinOffsets)) {
      return null;
    }

    return Object.keys(pinOffsets).length > 0 ? pinOffsets : null;
  };

  const buildLessonProjectState = useCallback(() => {
    if (!activeLesson) {
      return {
        components: [],
        connections: [],
        canvas: {},
      };
    }

    const lessonProject = activeLesson.initialProject || {};
    const lessonComponents = (lessonProject.components || []).map((component) => {
      if (component.type === 'board') {
        return component;
      }

      const passiveKey = component.passiveKey || component.passiveType;
      const definition = passiveComponentMap.get(passiveKey) ||
        passiveComponentMap.get(component.passiveType);

      if (!definition) {
        return {
          ...component,
          type: component.type || 'passive',
        };
      }

      const passiveDraft = buildPassiveComponentDraft(definition, {
        id: component.id,
        color: component.color,
        commonType: component.commonType,
        digitCount: component.digitCount,
        scale: component.scale,
        selectedValue: component.selectedValue,
      });
      const lessonPinOffsets = getUsableLessonPassivePinOffsets(component.pinOffsets);

      return {
        ...passiveDraft,
        ...component,
        type: 'passive',
        passiveType: definition.templateType,
        passiveKey: definition.componentKey,
        passiveDefinitionId: definition.id,
        pinOffsets: lessonPinOffsets || passiveDraft.pinOffsets,
        locked: Boolean(component.locked),
      };
    });

    return {
      components: lessonComponents,
      connections: Array.isArray(lessonProject.connections)
        ? cloneProjectBuilderHistoryValue(lessonProject.connections)
        : [],
      canvas: lessonProject.canvas || {},
      simulation: lessonProject.simulation && typeof lessonProject.simulation === 'object'
        ? cloneProjectBuilderHistoryValue(lessonProject.simulation)
        : {},
    };
  // Uses the existing builder draft helper without memoizing the large legacy helper stack.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLesson, passiveComponentMap]);

  const applyLessonProjectState = useCallback((projectState) => {
    const nextProjectState = projectState || buildLessonProjectState();

    setComponents(cloneProjectBuilderHistoryValue(nextProjectState.components || []));
    setConnections(cloneProjectBuilderHistoryValue(nextProjectState.connections || []));
    setSimulationCode(getSavedProjectArduinoSketchSource(nextProjectState));
    setZoomLevel(nextProjectState.canvas?.zoomLevel || activeLesson?.workspace?.initialZoom || BASE_ZOOM);
    setShowGrid(Boolean(nextProjectState.canvas?.showGrid));
    setSnapToGrid(Boolean(nextProjectState.canvas?.snapToGrid));
    setGridSize(nextProjectState.canvas?.gridSize || 10);
    setActiveProjectId(null);
    setSelectedItem(null);
    setSelectedComponentIds(new Set());
    setSuggestedConnections([]);
    setSelectedSuggestionIds(new Set());
    setShowSuggestedConnections(false);
    setUndoStack([]);
    setRedoStack([]);
    clearAnalysisHighlights();
  }, [activeLesson, buildLessonProjectState]);

  const hydrateLessonProjectState = useCallback(async (projectState, options = {}) => {
    const nextProjectState = projectState || buildLessonProjectState();
    const sourceComponents = Array.isArray(nextProjectState.components)
      ? nextProjectState.components
      : [];

    const hydratedComponents = await hydrateSavedProjectComponents(sourceComponents, options);

    return {
      ...nextProjectState,
      components: hydratedComponents,
      connections: Array.isArray(nextProjectState.connections)
        ? cloneProjectBuilderHistoryValue(nextProjectState.connections)
        : [],
      canvas: nextProjectState.canvas && typeof nextProjectState.canvas === 'object'
        ? { ...nextProjectState.canvas }
        : {},
      simulation: nextProjectState.simulation && typeof nextProjectState.simulation === 'object'
        ? cloneProjectBuilderHistoryValue(nextProjectState.simulation)
        : {},
    };
  // Reuses saved-project hydration so imported lesson boards fetch their SVG and pins.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boards, buildLessonProjectState, passiveComponentMap]);

  const lessonStarterSignature = useMemo(
    () => buildLessonStarterSignature(activeLesson),
    [activeLesson]
  );
  const requiresSavedProgressStarterSignature = useMemo(
    () => getRuntimeLessonStarterMode(activeLesson) === 'manual',
    [activeLesson]
  );

  const getMissingRequiredLessonStarterComponentIds = useCallback((savedProjectState, lessonInitialProjectState) => {
    const savedComponentIds = new Set(
      (savedProjectState?.components || [])
        .map((component) => String(component?.id || ''))
        .filter(Boolean)
    );
    const lockedComponentIds = new Set(
      (activeLesson?.lockedComponentIds || [])
        .map((componentId) => String(componentId || ''))
        .filter(Boolean)
    );

    return (lessonInitialProjectState?.components || [])
      .filter((component) => {
        const componentId = String(component?.id || '');

        if (!componentId) {
          return false;
        }

        return (
          requiresSavedProgressStarterSignature ||
          component.type === 'board' ||
          Boolean(component.locked) ||
          lockedComponentIds.has(componentId) ||
          component.passiveType === BREADBOARD_TEMPLATE_TYPE ||
          component.passiveKey === 'breadboard' ||
          component.passiveKey === 'dc_power'
        );
      })
      .map((component) => String(component.id))
      .filter((componentId) => !savedComponentIds.has(componentId));
  }, [activeLesson, requiresSavedProgressStarterSignature]);

  const getRequiredLessonStarterComponentIds = useCallback((lessonInitialProjectState) => {
    const lockedComponentIds = new Set(
      (activeLesson?.lockedComponentIds || [])
        .map((componentId) => String(componentId || ''))
        .filter(Boolean)
    );

    return (lessonInitialProjectState?.components || [])
      .filter((component) => {
        const componentId = String(component?.id || '');

        if (!componentId) {
          return false;
        }

        return (
          requiresSavedProgressStarterSignature ||
          component.type === 'board' ||
          Boolean(component.locked) ||
          lockedComponentIds.has(componentId) ||
          component.passiveType === BREADBOARD_TEMPLATE_TYPE ||
          component.passiveKey === 'breadboard' ||
          component.passiveKey === 'dc_power'
        );
      })
      .map((component) => String(component.id));
  }, [activeLesson, requiresSavedProgressStarterSignature]);

  const getHiddenRequiredLessonStarterComponentIds = useCallback((savedProjectState, lessonInitialProjectState) => {
    const requiredStarterIds = new Set(
      getRequiredLessonStarterComponentIds(lessonInitialProjectState)
    );

    if (requiredStarterIds.size === 0) {
      return [];
    }

    return (savedProjectState?.components || [])
      .filter((component) => (
        requiredStarterIds.has(String(component?.id || '')) &&
        isComponentHidden(component)
      ))
      .map((component) => String(component.id));
  }, [getRequiredLessonStarterComponentIds]);

  const resetLessonRuntimeState = useCallback((stepIndex = 0) => {
    setLessonStepIndex(stepIndex);
    setLessonHintUsageByStepId({});
    setLessonCompletedStepIds(new Set());
    setLessonSelections({});
    setLessonSelectedHole(null);
    setLessonInspectedTargets(new Set());
    setLessonTraceEnabled(false);
    setLessonCodeViewedStepIds(new Set());
    setLessonCodeEditedStepIds(new Set());
    setLessonCodeRunsByStepId({});
    setLessonStartedAt(Date.now());
    setLessonResumeMessage('');
    setLessonCompleted(false);
    lessonCompletedStepEventsRef.current = new Set();
  }, []);

  const lessonProjectState = useMemo(() => ({
    components,
    connections: logicalConnections,
    simulation: {
      code: simulationCode,
    },
  }), [components, logicalConnections, simulationCode]);
  const lessonLedCircuitResult = useMemo(() => (
    isLessonMode
      ? analyzeLessonLedCircuit(lessonProjectState)
      : { valid: false }
  ), [isLessonMode, lessonProjectState]);
  const lessonPoweredLedComponentIds = useMemo(() => {
    if (!isLessonMode || !lessonLedCircuitResult.valid) {
      return new Set();
    }

    return new Set(
      components
        .filter((component) => (
          component?.type === 'passive' &&
          (
            component.passiveType === 'led' ||
            component.passiveKey === 'led'
          )
        ))
        .map((component) => component.id)
    );
  }, [components, isLessonMode, lessonLedCircuitResult.valid]);

  const lessonValidationResult = useMemo(() => {
    if (!isLessonMode || !activeLesson || !currentLessonStep) {
      return { valid: false, code: 'inactive', message: '' };
    }

    return validateLessonStep({
      lesson: activeLesson,
      step: currentLessonStep,
      projectState: lessonProjectState,
      lessonState: {
        selectedHole: lessonSelectedHole,
        selections: lessonSelections,
        inspectedTargets: Array.from(lessonInspectedTargets),
        traceConnectionsEnabled: lessonTraceEnabled,
        codePanelOpen: showSimulationModal,
        codeViewed: currentLessonStep
          ? lessonCodeViewedStepIds.has(currentLessonStep.id)
          : false,
        codeEdited: currentLessonStep
          ? lessonCodeEditedStepIds.has(currentLessonStep.id)
          : false,
        codeRun: currentLessonStep
          ? lessonCodeRunsByStepId[currentLessonStep.id] || null
          : null,
        simulationPhase,
        simulationStatus,
      },
    });
  }, [
    activeLesson,
    currentLessonStep,
    isLessonMode,
    lessonInspectedTargets,
    lessonCodeEditedStepIds,
    lessonCodeRunsByStepId,
    lessonCodeViewedStepIds,
    lessonProjectState,
    lessonSelectedHole,
    lessonSelections,
    lessonTraceEnabled,
    showSimulationModal,
    simulationPhase,
    simulationStatus,
  ]);

  const lessonAllowsHoleSelection = Boolean(
    isLessonMode &&
    currentLessonStep &&
    (
      currentLessonStep.allowedActions?.selectBreadboardHoles ||
      currentLessonStep.allowedActions?.traceConnections ||
      lessonTraceEnabled
    )
  );

  const shouldDragLessonComponentFromPin = useCallback((component) => {
    if (
      !isLessonMode ||
      !currentLessonStep?.allowedActions?.moveComponents ||
      !component ||
      isBreadboardComponent(component) ||
      isLessonComponentLocked(component.id)
    ) {
      return false;
    }

    if (!currentLessonStep.allowedActions?.addWires) {
      return true;
    }

    const componentKey = String(
      component.passiveKey || component.passiveType || component.type || ''
    );

    return (currentLessonStep.availableComponents || [])
      .some((availableKey) => String(availableKey) === componentKey);
  }, [currentLessonStep, isLessonComponentLocked, isLessonMode]);

  const selectLessonBreadboardHole = useCallback((component, pinKey) => {
    if (!isLessonMode || !component || !pinKey) {
      return;
    }

    const selectedHole = getBreadboardHoleReference({
      breadboardId: component.id,
      pinKey,
    });

    setLessonSelectedHole(selectedHole);

    if (currentLessonStep?.allowedActions?.selectBreadboardHoles) {
      setLessonSelections((current) => ({
        ...current,
        [currentLessonStep.id]: selectedHole,
      }));
    }

    if (lessonTraceEnabled) {
      const traceTarget = classifyTraceSelection(lessonProjectState, selectedHole);

      if (traceTarget) {
        setLessonInspectedTargets((current) => new Set([...Array.from(current), traceTarget]));
      }
    }
  }, [currentLessonStep, isLessonMode, lessonProjectState, lessonTraceEnabled]);

  const commitCurrentLessonSelection = useCallback(() => {
    if (
      !lessonSelectedHole ||
      !currentLessonStep?.allowedActions?.selectBreadboardHoles
    ) {
      return;
    }

    setLessonSelections((current) => {
      if (current[currentLessonStep.id]?.holeId === lessonSelectedHole.holeId) {
        return current;
      }

      return {
        ...current,
        [currentLessonStep.id]: lessonSelectedHole,
      };
    });
  }, [currentLessonStep, lessonSelectedHole]);

  const markLessonCodeViewed = useCallback((stepId = currentLessonStep?.id) => {
    if (!isLessonMode || !stepId) {
      return;
    }

    setLessonCodeViewedStepIds((current) => {
      if (current.has(stepId)) {
        return current;
      }

      const next = new Set(current);
      next.add(stepId);
      return next;
    });
  }, [currentLessonStep, isLessonMode]);

  const markLessonCodeEdited = useCallback((stepId = currentLessonStep?.id) => {
    if (!isLessonMode || !stepId) {
      return;
    }

    setLessonCodeEditedStepIds((current) => {
      if (current.has(stepId)) {
        return current;
      }

      const next = new Set(current);
      next.add(stepId);
      return next;
    });
    setLessonCodeRunsByStepId((current) => {
      const previousRun = current[stepId];

      if (!previousRun) {
        return current;
      }

      return {
        ...current,
        [stepId]: {
          ...previousRun,
          status: 'stale',
          updatedAt: Date.now(),
        },
      };
    });
  }, [currentLessonStep, isLessonMode]);

  const recordLessonCodeRun = useCallback((status, details = {}, stepId = currentLessonStep?.id) => {
    if (!isLessonMode || !stepId) {
      return;
    }

    setLessonCodeRunsByStepId((current) => ({
      ...current,
      [stepId]: {
        ...(current[stepId] || {}),
        status,
        source: String(simulationCode || ''),
        updatedAt: Date.now(),
        ...details,
      },
    }));
  }, [currentLessonStep, isLessonMode, simulationCode]);

  const handleSimulationCodeChange = useCallback((nextCode) => {
    if (isLessonMode && !lessonAllowsEditCode) {
      return;
    }

    setSimulationCode(nextCode);
    markLessonCodeEdited();
  }, [isLessonMode, lessonAllowsEditCode, markLessonCodeEdited]);

  const goToLessonStep = (nextStepIndex) => {
    if (!activeLesson) {
      return;
    }

    const clampedStepIndex = Math.min(
      Math.max(Number(nextStepIndex) || 0, 0),
      activeLesson.steps.length - 1
    );
    const nextStep = activeLesson.steps[clampedStepIndex];

    setLessonStepIndex(clampedStepIndex);
    setLessonSelectedHole(null);
    setLessonTraceEnabled(Boolean(nextStep?.allowedActions?.traceConnections && lessonTraceEnabled));

    if (isLessonMode && showSimulationModal && !lessonStepAllowsCodePanel(nextStep)) {
      if (simulationPhase === 'running' || simulationPhase === 'compiling') {
        stopSimulation('Simulation stopped when the lesson step changed.');
      }

      setShowSimulationWorkspaceModal(false);
      setShowSimulationModal(false);
    }

    emitLessonAnalyticsEvent('lesson_step_started', {
      lessonId: activeLesson.id,
      lessonVersion: activeLesson.version,
      stepId: nextStep?.id,
      stepIndex: clampedStepIndex,
      hintsUsed: Number(lessonHintUsageByStepId[nextStep?.id] || 0),
      elapsedMs: Date.now() - lessonStartedAt,
    });
    postLessonMessage('lesson-step-changed', {
      lessonId: activeLesson.id,
      stepId: nextStep?.id,
      stepIndex: clampedStepIndex,
    });
  };

  const addLessonComponent = useCallback((componentKey) => {
    const definition = passiveComponentMap.get(componentKey);
    const lessonLimitStatus = getLessonComponentAddStatus(componentKey);

    if (!definition) {
      return;
    }

    if (lessonLimitStatus.disabled) {
      showSaveStatusMessage(lessonLimitStatus.message);
      return;
    }

    setComponentAddMode('passive');
    setSelectedPassiveKey(definition.componentKey);
    addPassiveComponentToCanvas(definition, {
      selectedValue: definition.templateType === 'resistor' ? 220 : undefined,
      color: definition.templateType === 'led' ? newLedColor : undefined,
      getInitialPosition: (component) => {
        const breadboard = componentsRef.current.find(isBreadboardComponent);

        if (!breadboard) {
          return null;
        }

        const stagedComponentCount = componentsRef.current.filter((item) => (
          !item.locked &&
          ['led', 'resistor'].includes(String(item.passiveType || item.passiveKey || ''))
        )).length;
        const footprint = getComponentFootprint(component);

        return {
          x: breadboard.x + 24 + (stagedComponentCount * 120),
          y: breadboard.y - footprint.height - 32,
        };
      },
    });

    emitLessonAnalyticsEvent('lesson_component_added', {
      lessonId: activeLesson?.id,
      lessonVersion: activeLesson?.version,
      stepId: currentLessonStep?.id,
      stepIndex: lessonStepIndex,
      componentKey,
    });
  // Uses the existing component insertion helper without memoizing the large legacy helper stack.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeLesson,
    currentLessonStep,
    getLessonComponentAddStatus,
    lessonStepIndex,
    newLedColor,
    passiveComponentMap,
  ]);

  const restartActiveLesson = useCallback(async () => {
    if (!activeLesson) {
      return;
    }

    const confirmed = window.confirm('Restart this lesson and reset the tutorial circuit?');

    if (!confirmed) {
      return;
    }

    if (activeLessonSource !== 'preview') {
      clearLessonProgress(activeLesson);
    }

    lessonRuntimeReadyRef.current = false;
    lessonRuntimeStageRef.current = 'restart-building-starter';
    lessonRuntimeErrorRef.current = '';
    let resetProjectState = buildLessonProjectState();

    try {
      lessonRuntimeStageRef.current = 'restart-hydrating-starter';
      resetProjectState = await hydrateLessonProjectState(resetProjectState, {
        timeoutMs: LESSON_STARTER_HYDRATION_TIMEOUT_MS,
        onStage: (stage) => {
          lessonRuntimeStageRef.current = `restart-hydrating-starter:${stage}`;
        },
        onIssue: (error) => {
          lessonRuntimeErrorRef.current = error?.message || String(error || '');
        },
      });
    } catch (lessonHydrationError) {
      lessonRuntimeErrorRef.current = lessonHydrationError?.message || String(lessonHydrationError || '');
      console.error('Error hydrating lesson project state:', lessonHydrationError);
    }

    lessonRuntimeStageRef.current = 'restart-applying-starter';
    applyLessonProjectState(resetProjectState);
    resetLessonRuntimeState(0);
    lessonRuntimeReadyRef.current = true;
    lessonRuntimeStageRef.current = 'ready';
    emitLessonAnalyticsEvent('lesson_restarted', {
      lessonId: activeLesson.id,
      lessonVersion: activeLesson.version,
    });
  }, [
    activeLesson,
    activeLessonSource,
    applyLessonProjectState,
    buildLessonProjectState,
    hydrateLessonProjectState,
    resetLessonRuntimeState,
  ]);

  const exitActiveLesson = useCallback(() => {
    if (activeLesson) {
      emitLessonAnalyticsEvent('lesson_exited', {
        lessonId: activeLesson.id,
        lessonVersion: activeLesson.version,
        stepId: currentLessonStep?.id,
        stepIndex: lessonStepIndex,
        hintsUsed: currentLessonHintCount,
        elapsedMs: Date.now() - lessonStartedAt,
      });
    }

    navigate('/builder');
  }, [
    activeLesson,
    currentLessonHintCount,
    currentLessonStep,
    lessonStartedAt,
    lessonStepIndex,
    navigate,
  ]);

  useEffect(() => {
    if (!isLessonMode || !activeLesson || loading) {
      return;
    }

    const initKey = [
      activeLesson.id,
      activeLesson.version,
      activeLessonSource,
      restartLessonRequested ? 'restart' : 'resume',
      requestedStepId,
      lessonStarterSignature,
      availablePassiveComponents.length,
      boards.length,
    ].join(':');

    if (lessonInitKeyRef.current === initKey) {
      return;
    }

    lessonInitKeyRef.current = initKey;

    let isMounted = true;

    const initializeLesson = async () => {
      lessonRuntimeReadyRef.current = false;
      lessonRuntimeStageRef.current = 'building-starter';
      lessonRuntimeErrorRef.current = '';

      try {

      if (restartLessonRequested && activeLessonSource !== 'preview') {
        clearLessonProgress(activeLesson);
      }

      const loadedSavedProgress = restartLessonRequested || activeLessonSource === 'preview'
        ? null
        : loadLessonProgress(activeLesson);
      const lessonInitialProjectState = buildLessonProjectState();
      const missingStarterComponentIds = loadedSavedProgress
        ? getMissingRequiredLessonStarterComponentIds(
            loadedSavedProgress.projectState,
            lessonInitialProjectState
          )
        : [];
      const hiddenStarterComponentIds = loadedSavedProgress
        ? getHiddenRequiredLessonStarterComponentIds(
            loadedSavedProgress.projectState,
            lessonInitialProjectState
          )
        : [];
      const hasLessonStarterComponents = (
        Array.isArray(lessonInitialProjectState.components) &&
        lessonInitialProjectState.components.length > 0
      );
      const hasLessonStarterState = Boolean(
        hasLessonStarterComponents ||
        (Array.isArray(lessonInitialProjectState.connections) &&
          lessonInitialProjectState.connections.length > 0) ||
        String(lessonInitialProjectState.simulation?.code || '').trim()
      );
      const savedProgressHasNoComponents = Boolean(
        loadedSavedProgress &&
        hasLessonStarterComponents &&
        (!Array.isArray(loadedSavedProgress.projectState?.components) ||
          loadedSavedProgress.projectState.components.length === 0)
      );
      const starterSignatureMismatch = Boolean(
        loadedSavedProgress &&
        hasLessonStarterState &&
        String(loadedSavedProgress.starterSignature || '') !== lessonStarterSignature
      );
      const shouldDiscardSavedProgress = Boolean(
        missingStarterComponentIds.length > 0 ||
        hiddenStarterComponentIds.length > 0 ||
        savedProgressHasNoComponents ||
        starterSignatureMismatch
      );
      const savedProgress = shouldDiscardSavedProgress
        ? null
        : loadedSavedProgress;

      if (shouldDiscardSavedProgress) {
        clearLessonProgress(activeLesson);
      }

      let initialProjectState = savedProgress?.projectState
        ? {
            ...savedProgress.projectState,
            simulation: savedProgress.projectState.simulation?.code
              ? savedProgress.projectState.simulation
              : lessonInitialProjectState.simulation,
          }
        : lessonInitialProjectState;

      if (savedProgress?.projectState) {
        const savedZoomLevel = Number(savedProgress.projectState.canvas?.zoomLevel);
        const lessonDefaultZoom = resolveLessonDefaultZoom(activeLesson, BASE_ZOOM);

        if (!Number.isFinite(savedZoomLevel) || Math.abs(savedZoomLevel - BASE_ZOOM) < 0.001) {
          initialProjectState = {
            ...initialProjectState,
            canvas: {
              ...(initialProjectState.canvas || {}),
              zoomLevel: lessonDefaultZoom,
            },
          };
        }
      }

      const lessonSteps = Array.isArray(activeLesson.steps) ? activeLesson.steps : [];
      const initialStepId = requestedStepId || savedProgress?.currentStepId || lessonSteps[0]?.id;
      const initialStepIndex = getLessonStepIndex(activeLesson, initialStepId);

      if (!isMounted || lessonInitKeyRef.current !== initKey) {
        return;
      }

      lessonRuntimeStageRef.current = 'applying-starter';
      applyLessonProjectState(initialProjectState);
      resetLessonRuntimeState(initialStepIndex);
      setLessonCompletedStepIds(new Set(savedProgress?.completedStepIds || []));
      setLessonHintUsageByStepId(savedProgress?.hintUsageByStepId || {});
      setLessonSelections(savedProgress?.selections || {});
      setLessonInspectedTargets(new Set(savedProgress?.inspectedTargets || []));
      setLessonCompleted(Boolean(savedProgress?.completed));
      setLessonStartedAt(savedProgress?.startedAt || Date.now());
      setComponentAddMode('passive');
      setSelectedPassiveKey('led');

      let hydratedInitialProjectState = initialProjectState;

      try {
        lessonRuntimeStageRef.current = 'hydrating-starter';
        hydratedInitialProjectState = await hydrateLessonProjectState(initialProjectState, {
          timeoutMs: LESSON_STARTER_HYDRATION_TIMEOUT_MS,
          onStage: (stage) => {
            lessonRuntimeStageRef.current = `hydrating-starter:${stage}`;
          },
          onIssue: (error) => {
            lessonRuntimeErrorRef.current = error?.message || String(error || '');
          },
        });
      } catch (lessonHydrationError) {
        lessonRuntimeErrorRef.current = lessonHydrationError?.message || String(lessonHydrationError || '');
        console.error('Error hydrating lesson project state:', lessonHydrationError);
      }

      if (!isMounted || lessonInitKeyRef.current !== initKey) {
        return;
      }

      lessonRuntimeStageRef.current = 'applying-hydrated-starter';
      applyLessonProjectState(hydratedInitialProjectState);
      resetLessonRuntimeState(initialStepIndex);
      setLessonCompletedStepIds(new Set(savedProgress?.completedStepIds || []));
      setLessonHintUsageByStepId(savedProgress?.hintUsageByStepId || {});
      setLessonSelections(savedProgress?.selections || {});
      setLessonInspectedTargets(new Set(savedProgress?.inspectedTargets || []));
      setLessonCompleted(Boolean(savedProgress?.completed));
      setLessonStartedAt(savedProgress?.startedAt || Date.now());
      setComponentAddMode('passive');
      setSelectedPassiveKey('led');
      lessonRuntimeReadyRef.current = true;
      lessonRuntimeStageRef.current = 'ready';

      if (savedProgress && !requestedStepId) {
        setLessonResumeMessage('Resumed your saved lesson progress.');
      } else if (shouldDiscardSavedProgress && !requestedStepId) {
        setLessonResumeMessage('The starter circuit changed, so the lesson was restarted.');
      }

      emitLessonAnalyticsEvent('lesson_started', {
        lessonId: activeLesson.id,
        lessonVersion: activeLesson.version,
        stepId: lessonSteps[initialStepIndex]?.id,
        stepIndex: initialStepIndex,
        resumed: Boolean(savedProgress),
      });
      postLessonMessage('lesson-ready', {
        lessonId: activeLesson.id,
      });
      postLessonMessage('lesson-step-changed', {
        lessonId: activeLesson.id,
        stepId: lessonSteps[initialStepIndex]?.id,
        stepIndex: initialStepIndex,
      });
      } catch (lessonInitializationError) {
        lessonRuntimeStageRef.current = 'error';
        lessonRuntimeErrorRef.current = lessonInitializationError?.message || String(lessonInitializationError || '');
        console.error('Error initializing lesson runtime:', lessonInitializationError);
      }
    };

    initializeLesson();

    return () => {
      isMounted = false;
      lessonRuntimeReadyRef.current = false;
    };
  }, [
    activeLesson,
    activeLessonSource,
    applyLessonProjectState,
    availablePassiveComponents.length,
    boards.length,
    buildLessonProjectState,
    getHiddenRequiredLessonStarterComponentIds,
    getMissingRequiredLessonStarterComponentIds,
    hydrateLessonProjectState,
    isLessonMode,
    loading,
    lessonStarterSignature,
    requestedStepId,
    resetLessonRuntimeState,
    restartLessonRequested,
  ]);

  useEffect(() => {
    if (!isLessonMode || !activeLesson || !currentLessonStep || !lessonValidationResult.valid) {
      return;
    }

    setLessonCompletedStepIds((current) => {
      if (current.has(currentLessonStep.id)) {
        return current;
      }

      const next = new Set(current);
      next.add(currentLessonStep.id);
      return next;
    });

    commitCurrentLessonSelection();

    const eventKey = `${activeLesson.id}:${currentLessonStep.id}`;

    if (!lessonCompletedStepEventsRef.current.has(eventKey)) {
      lessonCompletedStepEventsRef.current.add(eventKey);
      emitLessonAnalyticsEvent('lesson_step_completed', {
        lessonId: activeLesson.id,
        lessonVersion: activeLesson.version,
        stepId: currentLessonStep.id,
        stepIndex: lessonStepIndex,
        hintsUsed: currentLessonHintCount,
        elapsedMs: Date.now() - lessonStartedAt,
      });
    }
  }, [
    activeLesson,
    commitCurrentLessonSelection,
    currentLessonHintCount,
    currentLessonStep,
    isLessonMode,
    lessonStartedAt,
    lessonStepIndex,
    lessonValidationResult.valid,
  ]);

  useEffect(() => {
    if (
      !isLessonMode ||
      !activeLesson ||
      !currentLessonStep ||
      !lessonInitKeyRef.current ||
      !lessonRuntimeReadyRef.current ||
      activeLessonSource === 'preview'
    ) {
      return;
    }

    saveLessonProgress(activeLesson, {
      currentStepId: currentLessonStep.id,
      starterSignature: lessonStarterSignature,
      projectState: {
        components,
        connections,
        canvas: {
          zoomLevel,
          showGrid,
          snapToGrid,
          gridSize,
        },
        simulation: simulationCode.trim()
          ? {
              code: normalizeArduinoSketchSource(simulationCode),
            }
          : {},
      },
      completedStepIds: Array.from(lessonCompletedStepIds),
      hintUsageByStepId: lessonHintUsageByStepId,
      selections: lessonSelections,
      inspectedTargets: Array.from(lessonInspectedTargets),
      completed: lessonCompleted,
      startedAt: lessonStartedAt,
    });
  }, [
    activeLesson,
    activeLessonSource,
    components,
    connections,
    currentLessonStep,
    gridSize,
    isLessonMode,
    lessonCompleted,
    lessonCompletedStepIds,
    lessonHintUsageByStepId,
    lessonInspectedTargets,
    lessonSelections,
    lessonStartedAt,
    lessonStarterSignature,
    simulationCode,
    showGrid,
    snapToGrid,
    zoomLevel,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const hostname = String(window.location.hostname || '');
    const canExposeDebugHelper = (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === ''
    );

    if (!canExposeDebugHelper) {
      return undefined;
    }

    const summarizeComponent = (component) => {
      const matchedBoard = component?.type === 'board'
        ? boards.find((board) => {
            const componentBoardId = String(component?.boardId || component?.board?.id || '').trim();
            const componentBoardSlug = String(component?.boardSlug || component?.board?.slug || '').trim().toLowerCase();
            const componentBoardName = String(component?.boardName || component?.board?.name || component?.name || '').trim().toLowerCase();

            return (
              (componentBoardId && String(board?.id || '').trim() === componentBoardId) ||
              (componentBoardSlug && String(board?.slug || '').trim().toLowerCase() === componentBoardSlug) ||
              (componentBoardName && String(board?.name || '').trim().toLowerCase() === componentBoardName)
            );
          })
        : null;

      return {
        id: component?.id,
        type: component?.type,
        passiveKey: component?.passiveKey,
        passiveType: component?.passiveType,
        boardId: component?.boardId || component?.board?.id,
        boardSlug: component?.boardSlug || component?.board?.slug,
        boardName: component?.boardName || component?.board?.name,
        matchedBoardId: matchedBoard?.id,
        hidden: isComponentHidden(component),
        locked: Boolean(component?.locked),
        hasBoardSvg: Boolean(component?.board?.svg || component?.board?.svg_content),
        hasMatchedBoardSvg: Boolean(
          matchedBoard?.svg_content ||
          matchedBoard?.svgContent ||
          matchedBoard?.svg_source_content ||
          matchedBoard?.svgSourceContent
        ),
        hasPinOffsets: Boolean(component?.pinOffsets && Object.keys(component.pinOffsets).length > 0),
        x: component?.x,
        y: component?.y,
      };
    };

    const debugLessonRuntime = () => {
      const renderedComponents = Array.from(
        document.querySelectorAll('[data-builder-component-id]')
      ).map((node) => ({
        id: node.getAttribute('data-builder-component-id'),
        type: node.getAttribute('data-builder-component-type'),
        passiveKey: node.getAttribute('data-builder-passive-key'),
      }));

      return {
        href: window.location.href,
        requestedLessonId,
        adminLessonPreviewRequested,
        lessonModeRequested,
        isLessonMode,
        activeLessonSource,
        loading,
        boardCount: boards.length,
        passiveCount: passiveComponents.length,
        lookup: {
          lessonId: managedLessonLookup.lessonId,
          loading: managedLessonLookup.loading,
          hasLesson: Boolean(managedLessonLookup.lesson),
          error: managedLessonLookup.error,
        },
        activeLesson: activeLesson
          ? {
              id: activeLesson.id,
              version: activeLesson.version,
              starterMode: getRuntimeLessonStarterMode(activeLesson),
              initialComponentCount: activeLesson.initialProject?.components?.length || 0,
              initialSimulationCodeLength: String(activeLesson.initialProject?.simulation?.code || '').length,
              initialSimulationCodePreview: String(activeLesson.initialProject?.simulation?.code || '').slice(0, 80),
              initialComponents: (activeLesson.initialProject?.components || []).map(summarizeComponent),
            }
          : null,
        runtime: {
          initKey: lessonInitKeyRef.current,
          runtimeReady: lessonRuntimeReadyRef.current,
          stage: lessonRuntimeStageRef.current,
          error: lessonRuntimeErrorRef.current,
          stepIndex: lessonStepIndex,
          stepId: currentLessonStep?.id,
          starterSignatureLength: lessonStarterSignature.length,
        },
        state: {
          componentCount: components.length,
          components: components.map(summarizeComponent),
          connectionCount: connections.length,
          simulationCodeLength: String(simulationCode || '').length,
          simulationCodePreview: String(simulationCode || '').slice(0, 80),
        },
        rendered: {
          componentGroupCount: renderedComponents.length,
          components: renderedComponents,
        },
        storage: activeLesson?.id && activeLesson?.version
          ? {
              progressKey: `micropinouts.lesson.${activeLesson.id}.v${activeLesson.version}`,
              previewKey: `micropinouts.adminLessonPreview.${requestedLessonId}`,
            }
          : {
              previewKey: `micropinouts.adminLessonPreview.${requestedLessonId}`,
            },
      };
    };

    window.__microLessonDebug = debugLessonRuntime;

    return () => {
      if (window.__microLessonDebug === debugLessonRuntime) {
        delete window.__microLessonDebug;
      }
    };
  }, [
    activeLesson,
    activeLessonSource,
    adminLessonPreviewRequested,
    boards,
    boards.length,
    components,
    connections.length,
    currentLessonStep,
    isLessonMode,
    lessonModeRequested,
    lessonStarterSignature,
    lessonStepIndex,
    loading,
    managedLessonLookup,
    passiveComponents.length,
    requestedLessonId,
    simulationCode,
  ]);

  useEffect(() => {
    if (!isEmbedMode || !isLessonMode) {
      return;
    }

    const postHeight = () => {
      postLessonMessage('content-height-changed', {
        height: Math.ceil(document.documentElement?.scrollHeight || document.body?.scrollHeight || 0),
      });
    };

    postHeight();
    window.addEventListener('resize', postHeight);

    return () => {
      window.removeEventListener('resize', postHeight);
    };
  }, [isEmbedMode, isLessonMode, lessonStepIndex, components.length, connections.length]);

  const showNextLessonStep = () => {
    if (!activeLesson || !currentLessonStep || !lessonValidationResult.valid) {
      return;
    }

    commitCurrentLessonSelection();

    if (lessonStepIndex >= activeLesson.steps.length - 1) {
      setLessonCompleted(true);
      emitLessonAnalyticsEvent('lesson_completed', {
        lessonId: activeLesson.id,
        lessonVersion: activeLesson.version,
        stepId: currentLessonStep.id,
        stepIndex: lessonStepIndex,
        hintsUsed: currentLessonHintCount,
        elapsedMs: Date.now() - lessonStartedAt,
      });
      postLessonMessage('lesson-completed', {
        lessonId: activeLesson.id,
      });
      return;
    }

    goToLessonStep(lessonStepIndex + 1);
  };

  const showPreviousLessonStep = () => {
    goToLessonStep(lessonStepIndex - 1);
  };

  const showNextLessonHint = () => {
    if (!currentLessonStep) {
      return;
    }

    setLessonHintUsageByStepId((current) => ({
      ...current,
      [currentLessonStep.id]: Math.min(
        (current[currentLessonStep.id] || 0) + 1,
        currentLessonStep.hints?.length || 0
      ),
    }));

    emitLessonAnalyticsEvent('lesson_hint_opened', {
      lessonId: activeLesson?.id,
      lessonVersion: activeLesson?.version,
      stepId: currentLessonStep.id,
      stepIndex: lessonStepIndex,
      hintsUsed: currentLessonHintCount + 1,
      elapsedMs: Date.now() - lessonStartedAt,
    });
  };

  const tutorialCanvasGuidance = useMemo(() => {
    const emptyGuidance = {
      areaHighlights: [],
      componentHighlights: [],
      pinHighlights: [],
    };

    if (!isLessonMode || !activeLesson || !currentLessonStep) {
      return emptyGuidance;
    }

    const pinHighlightsByKey = new Map();
    const componentHighlightsByKey = new Map();
    const areaHighlightsByKey = new Map();
    const connectivityModel = buildBreadboardConnectivityModel(lessonProjectState);

    const addPinHighlight = ({
      partId,
      pinKey,
      tone = 'target',
      label = '',
      radius = 3.5,
    }) => {
      const partLayout = pinLayoutByPartId?.[partId] || {};
      const matchingMarkerKeys = getBreadboardMarkerKeysForPinKey(
        pinKey,
        Object.keys(partLayout)
      );

      if (matchingMarkerKeys.length === 0) {
        return;
      }

      matchingMarkerKeys.forEach((markerKey) => {
        const point = partLayout[markerKey];

        if (!point) {
          return;
        }

        const key = `${partId}:${markerKey}:${tone}`;
        pinHighlightsByKey.set(key, {
          key,
          partId,
          pinKey: markerKey,
          point,
          tone,
          label,
          radius,
        });
      });
    };

    const addComponentHighlight = (component, tone = 'target') => {
      if (!component) {
        return;
      }

      const bounds = getComponentVisibleSvgBounds(component);

      if (!bounds) {
        return;
      }

      componentHighlightsByKey.set(`${component.id}:${tone}`, {
        key: `${component.id}:${tone}`,
        component,
        bounds,
        tone,
      });
    };

    const addAreaHighlight = ({
      key,
      points,
      tone = 'target',
      padding = 12,
    }) => {
      const finitePoints = (Array.isArray(points) ? points : [])
        .filter((point) => (
          point &&
          Number.isFinite(point.x) &&
          Number.isFinite(point.y)
        ));

      if (finitePoints.length === 0) {
        return;
      }

      const xValues = finitePoints.map((point) => point.x);
      const yValues = finitePoints.map((point) => point.y);
      const minX = Math.min(...xValues);
      const maxX = Math.max(...xValues);
      const minY = Math.min(...yValues);
      const maxY = Math.max(...yValues);

      areaHighlightsByKey.set(key, {
        key,
        x: minX - padding,
        y: minY - padding,
        width: Math.max(1, maxX - minX + (padding * 2)),
        height: Math.max(1, maxY - minY + (padding * 2)),
        tone,
      });
    };

    const getBreadboardId = (highlight) => (
      highlight?.breadboardId ||
      activeLesson.initialProject?.components?.find((component) => component.passiveType === BREADBOARD_TEMPLATE_TYPE)?.id ||
      components.find(isBreadboardComponent)?.id ||
      ''
    );

    const addConnectedHoleGroup = (hole, tone = 'connected') => {
      if (!hole?.holeId) {
        return;
      }

      getConnectedHoleIds(hole.holeId, lessonProjectState).forEach((holeId) => {
        const { partId, pinKey } = parseEndpointId(holeId);
        addPinHighlight({
          partId,
          pinKey,
          tone,
          radius: tone === 'trace' ? 4 : 3.5,
        });
      });
    };

    const addOppositeTerminalGroup = (hole, tone = 'target') => {
      if (!hole?.holeId && !hole?.pinKey) {
        return;
      }

      const parsedHole = parseEndpointId(hole.holeId || '');
      const breadboardId = hole.breadboardId || parsedHole.partId;
      const pinKey = hole.pinKey || parsedHole.pinKey;

      getBreadboardOppositeTerminalPinKeys(pinKey).forEach((oppositePinKey) => {
        addPinHighlight({
          partId: breadboardId,
          pinKey: oppositePinKey,
          tone,
          radius: 3.5,
        });
      });
    };

    const addPowerRail = (breadboardId, rail) => {
      Object.keys(pinLayoutByPartId?.[breadboardId] || {}).forEach((pinKey) => {
        const matchesRail = rail === 'ground'
          ? isBreadboardGroundRailHole(pinKey)
          : isBreadboardPositiveRailHole(pinKey);

        if (matchesRail) {
          addPinHighlight({
            partId: breadboardId,
            pinKey,
            tone: rail === 'ground' ? 'ground' : 'positive',
            radius: 3.5,
          });
        }
      });
    };

    const addTerminalArea = (breadboardId) => {
      const terminalGroups = {
        upper: [],
        lower: [],
      };

      Object.entries(pinLayoutByPartId?.[breadboardId] || {}).forEach(([pinKey, point]) => {
        const logicalPinKey = stripBreadboardMarkerIndex(pinKey);
        const [, rawColumn, rawLetter] = logicalPinKey.match(/^pin(\d+)([A-J])$/i) || [];
        const column = Number(rawColumn);
        const letter = String(rawLetter || '').toUpperCase();

        if (!isBreadboardTerminalHole(logicalPinKey) || column < 8 || column > 18) {
          return;
        }

        if (letter >= 'A' && letter <= 'E') {
          terminalGroups.upper.push(point);
        } else if (letter >= 'F' && letter <= 'J') {
          terminalGroups.lower.push(point);
        }
      });

      Object.entries(terminalGroups).forEach(([groupKey, points]) => {
        addAreaHighlight({
          key: `${breadboardId}:terminal-area:${groupKey}`,
          points,
          tone: 'target',
          padding: 14,
        });
      });
    };

    const addHighlight = (highlight, tone = 'target') => {
      if (!highlight) {
        return;
      }

      if (highlight.type === 'breadboard-region' && highlight.region === 'terminal-area') {
        addTerminalArea(getBreadboardId(highlight));
        return;
      }

      if (highlight.type === 'breadboard-group') {
        const breadboardId = getBreadboardId(highlight);
        (highlight.pinKeys || []).forEach((pinKey) => {
          addPinHighlight({ partId: breadboardId, pinKey, tone, radius: 3.5 });
        });
        return;
      }

      if (highlight.type === 'power-rail') {
        addPowerRail(getBreadboardId(highlight), highlight.rail);
        return;
      }

      if (highlight.type === 'selection-reference') {
        addConnectedHoleGroup(lessonSelections[highlight.selectionId], 'reference');
        return;
      }

      if (highlight.type === 'opposite-terminal-group') {
        addOppositeTerminalGroup(lessonSelections[highlight.selectionId]);
        return;
      }

      if (highlight.type === 'component-terminal') {
        addPinHighlight({
          partId: highlight.componentId,
          pinKey: highlight.pinKey,
          tone,
          radius: 7,
        });
        return;
      }

      if (highlight.type === 'component') {
        const component = highlight.componentId
          ? components.find((item) => String(item.id) === String(highlight.componentId))
          : components.find((item) => (
              String(item.passiveType || item.passiveKey || item.type) === String(highlight.componentType)
            ));

        addComponentHighlight(component, tone);
      }
    };

    (currentLessonStep.highlights || []).forEach((highlight) => addHighlight(highlight));
    (currentLessonStep.hints || [])
      .slice(0, currentLessonHintCount)
      .forEach((hint) => addHighlight(hint.highlight, 'hint'));

    const componentById = new Map(
      components.map((component) => [String(component.id), component])
    );

    (lessonProjectState.connections || [])
      .filter((connection) => connection?.isBreadboardPlacementContact)
      .forEach((connection) => {
        const passivePartId = String(connection.from?.partId || '');
        const passiveComponent = componentById.get(passivePartId);

        if (!isLedComponent(passiveComponent) && !isResistorComponent(passiveComponent)) {
          return;
        }

        addPinHighlight({
          partId: connection.to?.partId,
          pinKey: connection.to?.pinKey,
          tone: 'placement',
          radius: 4.5,
        });
      });

    if (lessonSelectedHole?.holeId) {
      const { partId, pinKey } = parseEndpointId(lessonSelectedHole.holeId);
      addPinHighlight({
        partId,
        pinKey,
        tone: 'selected',
        radius: 4.5,
      });
    }

    if (lessonTraceEnabled && lessonSelectedHole?.holeId) {
      const traceNet = connectivityModel.getNetForEndpoint(lessonSelectedHole.holeId);
      const traceTone = traceNet?.powerState === 'positive'
        ? 'positive'
        : traceNet?.powerState === 'ground'
          ? 'ground'
          : traceNet?.powerState === 'conflict'
            ? 'conflict'
            : 'trace';

      (traceNet?.holeIds || []).forEach((holeId) => {
        const { partId, pinKey } = parseEndpointId(holeId);
        addPinHighlight({
          partId,
          pinKey,
          tone: traceTone,
          radius: 4,
        });
      });
    }

    return {
      areaHighlights: Array.from(areaHighlightsByKey.values()),
      componentHighlights: Array.from(componentHighlightsByKey.values()),
      pinHighlights: Array.from(pinHighlightsByKey.values()),
    };
  // Uses existing canvas geometry helpers without memoizing the large legacy helper stack.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeLesson,
    components,
    currentLessonHintCount,
    currentLessonStep,
    isLessonMode,
    lessonProjectState,
    lessonSelectedHole,
    lessonSelections,
    lessonTraceEnabled,
    pinLayoutByPartId,
  ]);

  const formatResistance = (valueOhms) => {
    const value = Number(valueOhms);

    if (!Number.isFinite(value)) {
      return '';
    }

    if (value >= 1000000) {
      return `${value / 1000000} MΩ`;
    }

    if (value >= 1000) {
      return `${value / 1000} kΩ`;
    }

    return `${value} Ω`;
  };

  const getSelectedComponent = () => {
    if (selectedItem?.type !== 'component') return null;
    return components.find(comp => comp.id === selectedItem.id) || null;
  };
  const selectedComponentIdsForActions = selectedComponentIds.size > 0
    ? Array.from(selectedComponentIds)
    : selectedItem?.type === 'component'
      ? [selectedItem.id]
      : [];
  const canRotateSelectedComponents = selectedComponentIdsForActions.some((componentId) => (
    canRotateComponent(components.find((component) => component.id === componentId))
  ));

  const getComponentDescription = (comp) => {
    if (!comp) return '';

    const passiveDefinition = getPassiveDefinitionForComponent(comp);

    if (comp.type === 'passive' && comp.passiveType === 'resistor') {
      return passiveDefinition?.description || 'Two-terminal resistor component.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'led') {
      return 'Light-emitting diode component.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'push_button') {
      return passiveDefinition?.description || 'Momentary push button switch component.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'potentiometer') {
      return passiveDefinition?.description || 'Variable resistor with two end terminals and one adjustable wiper terminal.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'buzzer') {
      return passiveDefinition?.description || 'Two-pin buzzer or piezo sounder component. Connect positive to a signal/output pin and negative to ground.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'servo') {
      return passiveDefinition?.description || 'Three-wire hobby servo with signal, power, and ground terminals.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'lcd_16x2') {
      return passiveDefinition?.description || 'HD44780-compatible 16x2 LCD module with a 4-bit parallel interface.';
    }

    if (comp.type === 'passive' && comp.passiveType === 'seven_segment_display') {
      return passiveDefinition?.description || 'Multiplexed seven-segment display with A-G/DP segment pins and DIG1-DIG4 digit common pins.';
    }

    if (comp.type === 'passive' && comp.passiveType === BREADBOARD_TEMPLATE_TYPE) {
      return passiveDefinition?.description || 'Solderless breadboard with split terminal columns and continuous power rails.';
    }

    if (comp.type === 'passive' && (comp.passiveType === 'generic_2pin' || comp.passiveType === 'generic_3pin')) {
      return passiveDefinition?.description || 'Generic basic part.';
    }

    return stripHtml(
      comp.board?.description ||
      comp.board?.summary ||
      comp.board?.short_description ||
      comp.board?.details ||
      'No description available for this component.'
    );
  };

  const refreshConnectionEndpointNames = (connectionsToUpdate, nextComponents) => {
    const safeConnections = Array.isArray(connectionsToUpdate)
      ? connectionsToUpdate
      : [];

    const safeComponents = Array.isArray(nextComponents)
      ? nextComponents
      : [];

    const badConnections = safeConnections.filter((connection) => (
      !connection ||
      !connection.from ||
      !connection.to ||
      !connection.from.partId ||
      !connection.to.partId
    ));

    if (badConnections.length > 0) {
      console.warn('[ProjectBuilder] Invalid connections found:', badConnections);
    }

    return safeConnections
      .filter((connection) => (
        connection &&
        connection.from &&
        connection.to &&
        connection.from.partId &&
        connection.to.partId
      ))
      .map((connection) => {
        const fromComponent = safeComponents.find(
          comp => comp.id === connection.from.partId
        );

        const toComponent = safeComponents.find(
          comp => comp.id === connection.to.partId
        );

        return {
          ...connection,
          from: {
            ...connection.from,
            boardName: fromComponent?.board?.name || fromComponent?.name || connection.from.boardName || 'Component',
          },
          to: {
            ...connection.to,
            boardName: toComponent?.board?.name || toComponent?.name || connection.to.boardName || 'Component',
          },
        };
      });
  };

  const suggestProjectWiring = () => {
    setShowSuggestedConnections(false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const freshPinLayout = collectPinLayouts();

        const freshSuggestionPins = get_project_pins_with_marker_keys(
          components,
          freshPinLayout
        );

        const suggestions = generate_project_wiring_suggestions(
          freshSuggestionPins,
          connections
        );

        setSuggestedConnections(suggestions);
        setSelectedSuggestionIds(new Set(suggestions.map(item => item.id)));
        setShowSuggestedConnections(suggestions.length > 0);

        console.log('ProjectBuilder wiring suggestions:', suggestions);
      });
    });
  };

  const applySuggestedConnections = () => {
    if (isCircuitEditingLocked) {
      return;
    }

    const selectedConnections = getSelectedSuggestedConnections();

    if (selectedConnections.length === 0) return;

    pushUndoSnapshot(createWorkspaceHistorySnapshot());

    setConnections(current => [
      ...current,
      ...selectedConnections.map((suggestion) => ({
        ...suggestion,
        id: `wire_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        width: wireWidth,
        suggested: false,
        suggestedBy: 'auto',
      })),
    ]);

    const selectedIds = new Set(selectedConnections.map(item => item.id));
    const remainingSuggestions = suggestedConnections.filter(
      suggestion => !selectedIds.has(suggestion.id)
    );

    setSuggestedConnections(remainingSuggestions);
    setSelectedSuggestionIds(new Set(remainingSuggestions.map(item => item.id)));
    setShowSuggestedConnections(remainingSuggestions.length > 0);
  };

  const clearSuggestedConnections = () => {
    setSuggestedConnections([]);
    setSelectedSuggestionIds(new Set());
    setShowSuggestedConnections(false);
  };

  const getSelectedSuggestedConnections = () => {
    return suggestedConnections.filter((connection) =>
      selectedSuggestionIds.has(connection.id)
    );
  };

  const toggleSuggestionSelection = (suggestionId) => {
    setSelectedSuggestionIds((current) => {
      const next = new Set(current);

      if (next.has(suggestionId)) {
        next.delete(suggestionId);
      } else {
        next.add(suggestionId);
      }

      return next;
    });
  };

  const selectAllSuggestions = () => {
    setSelectedSuggestionIds(new Set(suggestedConnections.map(item => item.id)));
  };

  const clearSuggestionSelection = () => {
    setSelectedSuggestionIds(new Set());
  };

  const buildProjectSnapshot = () => {
    return {
      components,
      connections,
      pins: suggestionPins,
    };
  };

  const refreshProjectAnalysis = () => {
    const analysis = analyze_circuit(buildProjectSnapshot());

    setAnalysisResults(analysis.results);
    setAnalysisSummary(analysis.summary);
    setShowAnalysisModal(true);
    setAnalysisModalTab('analysis');

    return analysis;
  };

  const refreshWiringSteps = () => {
    const nextSteps = generate_wiring_steps(buildProjectSnapshot());

    setWiringSteps(nextSteps);
    setShowAnalysisModal(true);
    setAnalysisModalTab('steps');

    return nextSteps;
  };

  const openSimulationModal = () => {
    if (!isArduinoSimulationEnabled) {
      return;
    }

    if (isLessonMode && !canShowLessonCodePanel) {
      return;
    }

    if (!simulationCode.trim() || (!isLessonMode && isAutoGeneratedBlinkSketch(simulationCode))) {
      setSimulationCode(preferredSimulationExampleCode);
    }

    setShowSimulationWorkspaceModal(false);
    setIsSimulationPanelCollapsed(false);
    setShowSimulationModal(true);
    markLessonCodeViewed();
  };

  const openSimulationWorkspaceModal = () => {
    if (!showSimulationModal) {
      return;
    }

    setShowSimulationWorkspaceModal(true);
  };

  const closeSimulationWorkspaceModal = () => {
    setShowSimulationWorkspaceModal(false);
  };

  const closeSimulationModal = () => {
    if (simulationPhase === 'running' || simulationPhase === 'compiling') {
      stopSimulation('Simulation stopped when the modal closed.');
    }

    clearMomentarySimulationInteractions();
    setShowSimulationWorkspaceModal(false);
    setShowSimulationModal(false);
  };

  const toggleSimulationPanelFromToolbar = () => {
    if (!isArduinoSimulationEnabled) {
      return;
    }

    if (showSimulationModal) {
      closeSimulationModal();
      return;
    }

    openSimulationModal();
  };

    const toggleSimulationPanelCollapsed = () => {
    setIsSimulationPanelCollapsed((current) => !current);
  };

  const resetSimulationExample = () => {
    setSimulationCode(
      isLessonMode && activeLessonSimulationCode.trim()
        ? activeLessonSimulationCode
        : preferredSimulationExampleCode
    );
  };

  const runSimulationPreview = async () => {
    const lessonRunStepId = currentLessonStep?.id;

    if (isLessonMode && !lessonAllowsRunCode) {
      setSimulationPhase('error');
      setSimulationStatus('Running code is not enabled for this lesson step.');
      setSimulationCompilerOutput('This step does not allow running the Arduino sketch.');
      return;
    }

    if (!hasSimulationUnoBoard) {
      recordLessonCodeRun('error', {
        message: 'No Arduino Uno was detected in the current project.',
      }, lessonRunStepId);
      setSimulationPhase('error');
      setSimulationStatus('Add an Arduino Uno board to the canvas before running the simulation.');
      setSimulationCompilerOutput('No Arduino Uno was detected in the current project.');
      return;
    }

    const trimmedSource = simulationCode.trim();

    if (!trimmedSource) {
      recordLessonCodeRun('error', {
        message: 'No sketch source was provided.',
      }, lessonRunStepId);
      setSimulationPhase('error');
      setSimulationStatus('Enter an Arduino sketch before running the simulator.');
      setSimulationCompilerOutput('No sketch source was provided.');
      return;
    }

    await unlockSimulationAudio(simulationAudioEnabled);

    const requestId = simulationRequestIdRef.current + 1;
    simulationRequestIdRef.current = requestId;
    disposeSimulationRunner();
    clearMomentarySimulationInteractions();
    resetSimulationRuntime();
    setSimulationPhase('compiling');
    setSimulationStatus('Compiling sketch...');
    setSimulationCompilerOutput('');
    recordLessonCodeRun('compiling', {
      message: 'Compiling sketch...',
    }, lessonRunStepId);

    let hexSource = '';
    let nextStatus = 'Simulation running in your browser.';

    try {
      try {
        const compileResult = await compileArduinoSketch(simulationCode);

        if (simulationRequestIdRef.current !== requestId) {
          return;
        }

        const compilerOutput = buildSimulationCompilerOutput(compileResult);
        setSimulationCompilerOutput(
          compilerOutput || 'Compilation succeeded without warnings.'
        );

        hexSource = String(compileResult?.hex || '').trim();

        if (!hexSource) {
          throw new Error(compilerOutput || 'Compiler did not return a runnable HEX program.');
        }
      } catch (error) {
        if (simulationRequestIdRef.current !== requestId) {
          return;
        }

        const shouldUseFallback = trimmedSource === DEFAULT_ARDUINO_SIMULATION_SKETCH.trim();

        if (!shouldUseFallback) {
          throw error;
        }

        hexSource = DEFAULT_ARDUINO_SIMULATION_HEX;
        nextStatus = 'Running the bundled Blink fallback in your browser.';
        setSimulationCompilerOutput([
          'The remote compiler was unavailable, so the bundled Blink HEX fallback is being used.',
          error?.message ? `Compiler details: ${error.message}` : '',
        ].filter(Boolean).join('\n\n'));
      }

      if (simulationRequestIdRef.current !== requestId) {
        return;
      }

      const initialSimulationPinStates = createInitialSimulationPinStates();
      const initialSimulationCircuitAnalysis = inspectArduinoSimulationCircuit({
        components: runtimeSimulationComponents,
        connections: logicalConnections,
        suggestionPins,
        simulationPinStates: initialSimulationPinStates,
        pressedButtonIds: activeSimulationInteractionIds,
      });
      const trackedLcdTransitionPinNames = getTrackedLcdTransitionPinNames(
        initialSimulationCircuitAnalysis
      );

      const runner = new ArduinoUnoSimulator({
        hex: hexSource,
        onUpdate: (snapshot) => {
          if (simulationRequestIdRef.current !== requestId) {
            return;
          }

          applySimulationSnapshot(snapshot);
        },
        onError: (error) => {
          if (simulationRequestIdRef.current !== requestId) {
            return;
          }

          disposeSimulationRunner();
          setSimulationPhase('error');
          setSimulationStatus(error?.message || 'The simulator stopped because the emulator hit an error.');
          recordLessonCodeRun('error', {
            message: error?.message || 'The simulator stopped because the emulator hit an error.',
          }, lessonRunStepId);
          setSimulationCompilerOutput((currentOutput) => (
            currentOutput || 'The emulator stopped before any compiler output was captured.'
          ));
        },
        trackedPinTransitionNames: trackedLcdTransitionPinNames,
      });

      simulationRunnerRef.current = runner;
      syncArduinoSimulationDigitalInputs({
        appliedLevelsRef: simulationAppliedDigitalInputLevelsRef,
        digitalInputsByPinName: initialSimulationCircuitAnalysis.digitalInputsByPinName,
        pinStates: initialSimulationPinStates,
        runner,
      });
      syncArduinoSimulationAnalogInputs({
        analogInputsByPinName: initialSimulationCircuitAnalysis.analogInputsByPinName,
        appliedVoltagesRef: simulationAppliedAnalogInputVoltagesRef,
        runner,
      });
      setSimulationPhase('running');
      setSimulationStatus(nextStatus);
      recordLessonCodeRun('running', {
        message: nextStatus,
      }, lessonRunStepId);
      runner.start();

      if (simulationRequestIdRef.current !== requestId) {
        runner.dispose();
        return;
      }
    } catch (error) {
      if (simulationRequestIdRef.current !== requestId) {
        return;
      }

      disposeSimulationRunner();
      setSimulationPhase('error');
      setSimulationStatus('Simulation could not start. Review the compiler output.');
      recordLessonCodeRun('error', {
        message: error?.message || 'Simulation could not start.',
      }, lessonRunStepId);
      setSimulationCompilerOutput((currentOutput) => (
        currentOutput || error?.message || 'Unknown simulation error.'
      ));
    }
  };

  const closeAnalysisModal = () => {
    setShowAnalysisModal(false);
    clearAnalysisHighlights();
  };

  const clearAnalysisHighlights = () => {
    setHighlightedAnalysisWireIds(new Set());
    setHighlightedAnalysisPinIds([]);
    setSelectedAnalysisResultId('');
  };

  const focusRelatedItems = (relatedItems = {}) => {
    const componentIds = Array.from(new Set(relatedItems.component_ids || []));
    const wireIds = Array.from(new Set(relatedItems.wire_ids || []));
    const pinIds = Array.from(new Set(relatedItems.pin_ids || []));

    setHighlightedAnalysisWireIds(new Set(wireIds));
    setHighlightedAnalysisPinIds(pinIds);

    if (componentIds.length > 0) {
      setSelectedComponentIds(new Set(componentIds));
    }

    if (wireIds.length === 1) {
      setSelectedItem({ type: 'wire', id: wireIds[0] });
      return;
    }

    if (componentIds.length === 1) {
      setSelectedItem({ type: 'component', id: componentIds[0] });
      return;
    }

    if (componentIds.length === 0) {
      setSelectedComponentIds(new Set());
    }

    setSelectedItem(null);
  };

  const handleSelectAnalysisResult = (result) => {
    setSelectedAnalysisResultId(result.id);
    focusRelatedItems(result.related_items);
  };

  const handleSelectWiringStep = (step) => {
    setSelectedAnalysisResultId(`step:${step.id}`);
    focusRelatedItems(step.related_items);
  };

  const selectConnection = (connectionId) => {
    setSelectedItem({ type: 'wire', id: connectionId });
    setSelectedComponentIds(new Set());
    clearAnalysisHighlights();
  };

  const renderCompactConnectionRow = (connection, options = {}) => {
    const parts = getConnectionDisplayParts(connection);
    const tagLabel = getWireRoleLabel(connection);
    const tagColor = connection.color || '#64748b';

    return (
        <WiringItem
          key={options.key || connection.id}
          title={connection.reason || ''}
          $clickable={options.clickable !== false}
          $selected={selectedItem?.type === 'wire' && selectedItem.id === connection.id}
          onClick={() => {
            if (options.clickable === false) return;
            selectConnection(connection.id);
          }}
        >
        <WiringEndpoint title={`${connection.from.boardName} ${getDisplayPinKey(connection.from.pinKey)}`}>
          <WiringEndpointBoard>{parts.fromBoard}</WiringEndpointBoard>
          <WiringEndpointPin>{parts.fromPin}</WiringEndpointPin>
        </WiringEndpoint>

        <WiringArrow>→</WiringArrow>

        <WiringEndpoint title={`${connection.to.boardName} ${getDisplayPinKey(connection.to.pinKey)}`}>
          <WiringEndpointBoard>{parts.toBoard}</WiringEndpointBoard>
          <WiringEndpointPin>{parts.toPin}</WiringEndpointPin>
        </WiringEndpoint>

        <WireTag $color={tagColor}>
          {tagLabel}
        </WireTag>

        {options.showDelete ? (
        <DeleteButton
          type="button"
          disabled={isCircuitEditingLocked}
          onClick={(event) => {
            event.stopPropagation();
            removeConnection(connection.id);
          }}
        >
          <Trash2 size={14} />
        </DeleteButton>
        ) : (
          <span />
        )}
      </WiringItem>
    );
  };

  const selectedSuggestionCount = selectedSuggestionIds.size;
  const allSuggestionsSelected =
    suggestedConnections.length > 0 &&
    selectedSuggestionCount === suggestedConnections.length;
  const hasSuggestedConnections =
    showSuggestedConnections &&
    suggestedConnections.length > 0;
  const hasAnalysisResults = analysisResults.length > 0;
  const canSuggestProjectWiring = suggestionPins.filter(
    pin => pin.component_type === 'board' && pin.has_canvas_marker
  ).length >= 2;
  const showSuggestionOverlay = hasSuggestedConnections;

  const showExpandedSimulationPanel =
    isArduinoSimulationEnabled &&
    showSimulationModal &&
    !isSimulationPanelCollapsed;

  const showCollapsedSimulationPanel =
    isArduinoSimulationEnabled &&
    showSimulationModal &&
    isSimulationPanelCollapsed;
  const activeWireDropSourceEndpoint = wirePreview
    ? getActiveWireDropSourceEndpoint()
    : null;

  // eslint-disable-next-line no-unused-vars
  const recalculateLedConnectionMetadata = (nextComponents) => {
    const nextSuggestionPins = get_project_pins_with_marker_keys(
      nextComponents,
      pinLayoutByPartId
    );

    const findPin = (endpoint) => {
      return nextSuggestionPins.find((pin) =>
        pin.component_id === endpoint.partId &&
        pin.marker_key === endpoint.pinKey
      ) || null;
    };

    setConnections(currentConnections => {
      const renamedConnections = refreshConnectionEndpointNames(
        currentConnections,
        nextComponents
      );

      return renamedConnections.map((connection) => {
        const fromPin = findPin(connection.from);
        const toPin = findPin(connection.to);

        if (!fromPin || !toPin) {
          return connection;
        }

        const involvedPins = [fromPin, toPin];

        const hasResistorTerminal = involvedPins.some(pin =>
          pin.passive_type === 'resistor'
        );

        const hasLedTerminal = involvedPins.some(pin =>
          pin.passive_type === 'led'
        );

        // We only directly update the resistor-to-LED wire here.
        if (!hasResistorTerminal && !hasLedTerminal) {
          return connection;
        }

        const resistorPin =
          fromPin.passive_type === 'resistor' ? fromPin :
          toPin.passive_type === 'resistor' ? toPin :
          null;

        const ledPin =
          fromPin.passive_type === 'led' ? fromPin :
          toPin.passive_type === 'led' ? toPin :
          null;

        if (!resistorPin || !ledPin) {
          return connection;
        }

        // Find GPIO wire connected to the same resistor component.
        const gpioConnection = currentConnections.find((item) => {
          const itemFrom = findPin(item.from);
          const itemTo = findPin(item.to);

          if (!itemFrom || !itemTo) return false;

          const resistorSide =
            itemFrom.passive_type === 'resistor' ? itemFrom :
            itemTo.passive_type === 'resistor' ? itemTo :
            null;

          const gpioSide =
            itemFrom.component_type === 'board' ? itemFrom :
            itemTo.component_type === 'board' ? itemTo :
            null;

          return (
            resistorSide &&
            gpioSide &&
            resistorSide.component_id === resistorPin.component_id
          );
        });

        if (!gpioConnection) {
          return connection;
        }

        const gpioFrom = findPin(gpioConnection.from);
        const gpioTo = findPin(gpioConnection.to);

        const gpioPin =
          gpioFrom?.component_type === 'board' ? gpioFrom :
          gpioTo?.component_type === 'board' ? gpioTo :
          null;

        if (!gpioPin) {
          return connection;
        }

        const sourceConnection = currentConnections.find((item) => {
          const itemFrom = findPin(item.from);
          const itemTo = findPin(item.to);

          if (!itemFrom || !itemTo) return false;

          const itemResistorPin =
            itemFrom.passive_type === 'resistor' ? itemFrom :
            itemTo.passive_type === 'resistor' ? itemTo :
            null;

          const itemBoardPin =
            itemFrom.component_type === 'board' ? itemFrom :
            itemTo.component_type === 'board' ? itemTo :
            null;

          return (
            itemResistorPin &&
            itemBoardPin &&
            itemResistorPin.component_id === resistorPin.component_id &&
            isBoardPowerSourcePin(itemBoardPin)
          );
        });

        const groundConnection = currentConnections.find((item) => {
          const itemFrom = findPin(item.from);
          const itemTo = findPin(item.to);

          if (!itemFrom || !itemTo) return false;

          const itemLedPin =
            itemFrom.passive_type === 'led' ? itemFrom :
            itemTo.passive_type === 'led' ? itemTo :
            null;

          const itemBoardPin =
            itemFrom.component_type === 'board' ? itemFrom :
            itemTo.component_type === 'board' ? itemTo :
            null;

          return (
            itemLedPin &&
            itemBoardPin &&
            itemLedPin.component_id === ledPin.component_id &&
            isBoardGroundPin(itemBoardPin)
          );
        });

        const sourceFrom = sourceConnection ? findPin(sourceConnection.from) : null;
        const sourceTo = sourceConnection ? findPin(sourceConnection.to) : null;

        const sourcePin =
          isBoardPowerSourcePin(sourceFrom) ? sourceFrom :
          isBoardPowerSourcePin(sourceTo) ? sourceTo :
          null;

        if (sourcePin && groundConnection) {
          const currentMa = calculateLedCurrentMa({
            sourcePin,
            resistorPin,
            ledPin,
            components: nextComponents,
          });

          if (Number.isFinite(currentMa)) {
            return {
              ...connection,
              confidence: currentMa > LED_OVERCURRENT_LIMIT_MA ? 'warning' : 'good',
              score: currentMa > LED_OVERCURRENT_LIMIT_MA ? 35 : 95,
              reason: `Power pin -> resistor -> LED -> GND path detected. Estimated current is ${Math.round(currentMa)} mA.`,
              electricalNote: currentMa > LED_OVERCURRENT_LIMIT_MA
                ? `Over-current: ${Math.round(currentMa)} mA`
                : `LED current: ${Math.round(currentMa)} mA`,
              estimatedCurrentMa: currentMa,
            };
          }
        }

        return connection;
      })
    });
  };

  // eslint-disable-next-line no-unused-vars
  const renderProjectHelperPanel = () => {
    const hasComponents = components.length > 0;
    const hasConnections = connections.length > 0;
    const hasAnalysis = analysisResults.length > 0;
    const hasWiringSteps = wiringSteps.length > 0;
    const reviewFlagCount = analysisSummary.error + analysisSummary.warning;

    if (!hasComponents) {
      return (
        <HelperPanel>
          <HelperTitle>
            <Lightbulb size={18} />
            Build your circuit visually
          </HelperTitle>

          <HelperText>
            Add boards, modules, and basic parts, then click a pin, click canvas to add corners, then click the destination pin..
          </HelperText>
          <HelperActions>
            <HelperButton
              type="button"
              $primary
              onClick={openComponentModal}
              disabled={isCircuitEditingLocked}
            >
              Add Component
            </HelperButton>
          </HelperActions>
        </HelperPanel>
      );
    }

    return (
      <HelperPanel>
        <HelperTitle>
          <Info size={18} />
          Project status
        </HelperTitle>

        <HelperText>
          {hasAnalysis
            ? 'Circuit review is ready. Open the analysis results to inspect warnings and suggestions.'
            : hasConnections
              ? 'Your circuit has connections. Run Circuit Review to check for power, protocol, and pin issues.'
              : 'Components are on the canvas. Drag from one pin to another to start wiring.'}
        </HelperText>

        <ProjectStats>
          <ProjectStat>
            <ProjectStatValue>{components.length}</ProjectStatValue>
            <ProjectStatLabel>Components</ProjectStatLabel>
          </ProjectStat>

          <ProjectStat>
            <ProjectStatValue>{connections.length}</ProjectStatValue>
            <ProjectStatLabel>Connections</ProjectStatLabel>
          </ProjectStat>

          <ProjectStat>
            <ProjectStatValue>{reviewFlagCount}</ProjectStatValue>
            <ProjectStatLabel>Review Flags</ProjectStatLabel>
          </ProjectStat>
        </ProjectStats>

        <HelperActions>
          <HelperButton
            type="button"
            $primary
            onClick={
              hasAnalysis
                ? () => {
                    setShowAnalysisModal(true);
                    setAnalysisModalTab('analysis');
                  }
                : refreshProjectAnalysis
            }
            disabled={components.length === 0}
          >
            {hasAnalysis ? 'View Analysis' : 'Analyze Circuit'}
          </HelperButton>

          <HelperSecondaryButton
            type="button"
            onClick={
              hasWiringSteps
                ? () => {
                    setShowAnalysisModal(true);
                    setAnalysisModalTab('steps');
                  }
                : refreshWiringSteps
            }
            disabled={connections.length === 0}
          >
            {hasWiringSteps ? 'View Build Instructions' : 'Build Instructions'}
          </HelperSecondaryButton>
        </HelperActions>
      </HelperPanel>
    );
  };

  const renderSimulationEditorWorkspace = ({ expanded = false, compact = false, simpleLessonControls = false } = {}) => {
    const isSidebarMode = !expanded;
    const isCompactEditor = Boolean(compact || (isLessonMode && !expanded));
    const isLessonCodeReadOnly = Boolean(isLessonMode && !lessonAllowsEditCode);
    const isRunCodeDisabled = Boolean(
      isSimulationCompiling ||
      (isLessonMode && !lessonAllowsRunCode)
    );
    const isResetCodeDisabled = Boolean(
      isSimulationCompiling ||
      (isLessonMode && !lessonAllowsEditCode)
    );

    return (
      <SimulationWorkspaceSection $expanded={expanded}>
        {!expanded && (
          <SimulationWorkspaceHeader>
            <div>
              <SimulationPanelTitle>Sketch Editor</SimulationPanelTitle>
{/*             <SimulationPanelMeta>
              Write the Arduino sketch you want to compile and run against the Uno on your canvas.
            </SimulationPanelMeta> */}
            </div>
          </SimulationWorkspaceHeader>
        )}

        {expanded ? (
          <SimulationExpandedEditorFrame>
            <SimulationExpandedLineNumberGutter aria-hidden="true">
              <SimulationExpandedLineNumberTrack ref={expandedSimulationLineNumberTrackRef}>
                {expandedSimulationLineNumbers.map((lineNumber) => (
                  <SimulationExpandedLineNumber key={lineNumber}>
                    {lineNumber}
                  </SimulationExpandedLineNumber>
                ))}
              </SimulationExpandedLineNumberTrack>
            </SimulationExpandedLineNumberGutter>

            <SimulationExpandedEditorViewport
              onScroll={handleExpandedSimulationEditorScroll}
            >
              <SimulationEditor
                $expanded
                highlight={highlightArduinoCode}
                insertSpaces
                tabSize={2}
                value={simulationCode}
                onValueChange={handleSimulationCodeChange}
                readOnly={isLessonCodeReadOnly}
                aria-readonly={isLessonCodeReadOnly}
                spellCheck={false}
                data-simulation-editor-root="expanded"
                aria-label="Expanded Arduino simulation code editor"
              />
            </SimulationExpandedEditorViewport>
          </SimulationExpandedEditorFrame>
        ) : (
          <SimulationEditor
            highlight={highlightArduinoCode}
            insertSpaces
            tabSize={2}
            value={simulationCode}
            onValueChange={handleSimulationCodeChange}
            readOnly={isLessonCodeReadOnly}
            aria-readonly={isLessonCodeReadOnly}
            spellCheck={false}
            $compact={isCompactEditor}
            aria-label="Arduino simulation code editor"
          />
        )}

        <SimulationToolbar $compact={isSidebarMode}>
          <SimulationActionGroup $compact={isSidebarMode}>
            <SimulationRunButton
              type="button"
              onClick={runSimulationPreview}
              disabled={isRunCodeDisabled}
              $iconOnly={isSidebarMode}
              aria-label={isSimulationCompiling ? 'Compiling simulation sketch' : 'Run simulation'}
              title={isSimulationCompiling ? 'Compiling...' : 'Run'}
            >
              <Play size={16} />
              {!isSidebarMode && (isSimulationCompiling ? 'Compiling...' : 'Run')}
            </SimulationRunButton>

            <SimulationSecondaryButton
              type="button"
              onClick={() => stopSimulation('Simulation stopped.')}
              disabled={!canStopSimulation}
              $iconOnly={isSidebarMode}
              aria-label="Stop simulation"
              title="Stop"
            >
              <Square size={14} />
              {!isSidebarMode && 'Stop'}
            </SimulationSecondaryButton>

            <SimulationSecondaryButton
              type="button"
              onClick={resetSimulationExample}
              disabled={isResetCodeDisabled}
              $iconOnly={isSidebarMode}
              aria-label="Reset simulation example"
              title="Reset Example"
            >
              <RotateCcw size={14} />
              {!isSidebarMode && 'Reset Example'}
            </SimulationSecondaryButton>

            {!simpleLessonControls && hasSimulationBuzzers && (
              <SimulationSecondaryButton
                type="button"
                onClick={handleSimulationAudioToggle}
                $iconOnly={isSidebarMode}
                aria-label={simulationAudioEnabled ? 'Mute simulation audio' : 'Enable simulation audio'}
                title={simulationAudioEnabled ? 'Mute Audio' : 'Enable Audio'}
              >
                {simulationAudioEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                {!isSidebarMode && (simulationAudioEnabled ? 'Mute Audio' : 'Enable Audio')}
              </SimulationSecondaryButton>
            )}
          </SimulationActionGroup>

          <SimulationStatus $tone={simulationStatusTone}>
            {simulationStatus || 'Compile a sketch to start the browser-side Arduino Uno emulator.'}
          </SimulationStatus>
        </SimulationToolbar>
      </SimulationWorkspaceSection>
    );
  };

  const renderLessonArduinoCodePanel = () => (
    <SimulationDockPanel $lessonMode>
      <SimulationDockHeader>
        <div>
          <AnalysisModalTitle id="arduino-simulation-title">
            Arduino Code
          </AnalysisModalTitle>
        </div>

        <SimulationHeaderActions>
          <SimulationCheckboxLabel>
            <input
              type="checkbox"
              checked={showLessonSerialMonitor}
              onChange={(event) => setShowLessonSerialMonitor(event.target.checked)}
            />
            Serial Monitor
          </SimulationCheckboxLabel>

          <ModalCloseButton
            type="button"
            aria-label="Close code panel"
            onClick={closeSimulationModal}
          >
            X
          </ModalCloseButton>
        </SimulationHeaderActions>
      </SimulationDockHeader>

      <SimulationDockBody>
        {renderSimulationEditorWorkspace({
          compact: true,
          simpleLessonControls: true,
        })}

        <SimulationGrid>
          <SimulationPanel>
            <SimulationPanelTitle>Compiler Output</SimulationPanelTitle>
            <SimulationPre>
              {simulationCompilerOutput || 'Compiler output will appear here after you run a sketch.'}
            </SimulationPre>
          </SimulationPanel>

          {showLessonSerialMonitor ? (
            <SimulationPanel>
              <SimulationPanelTitle>Serial Monitor</SimulationPanelTitle>
              <SimulationPre>
                {simulationSerialOutput || 'No serial output yet.'}
              </SimulationPre>
            </SimulationPanel>
          ) : null}
        </SimulationGrid>
      </SimulationDockBody>
    </SimulationDockPanel>
  );

  const renderArduinoSimulationPanel = () => {
    if (!isArduinoSimulationEnabled || !showSimulationModal) {
      return null;
    }

    if (isLessonMode) {
      return renderLessonArduinoCodePanel();
    }

    return (
      <SimulationDockPanel $lessonMode={isLessonMode}>
        <SimulationDockHeader>
          <div>
            <AnalysisModalTitle id="arduino-simulation-title">
              Arduino Simulation
            </AnalysisModalTitle>
          </div>

          <SimulationHeaderActions>
            <SimulationHeaderButton
              type="button"
              aria-label={showSimulationWorkspaceModal ? 'Hide expanded editor' : 'Expand simulation editor'}
              title={showSimulationWorkspaceModal ? 'Hide expanded editor' : 'Expand simulation editor'}
              onClick={showSimulationWorkspaceModal ? closeSimulationWorkspaceModal : openSimulationWorkspaceModal}
            >
              {showSimulationWorkspaceModal ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </SimulationHeaderButton>

            <SimulationHeaderButton
              type="button"
              aria-label={isSimulationPanelCollapsed ? 'Expand simulation panel' : 'Collapse simulation panel'}
              title={isSimulationPanelCollapsed ? 'Expand simulation panel' : 'Collapse simulation panel'}
              onClick={toggleSimulationPanelCollapsed}
            >
              {isSimulationPanelCollapsed ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </SimulationHeaderButton>

            <ModalCloseButton
              type="button"
              aria-label="Close simulation"
              onClick={closeSimulationModal}
            >
              X
            </ModalCloseButton>
          </SimulationHeaderActions>
        </SimulationDockHeader>

        {isSimulationPanelCollapsed ? (
          <SimulationDockCollapsedBody>
            {showSimulationNoUnoWarning && (
              <SimulationWarningBox>
                <strong>No Arduino Uno detected</strong>
                Add an Arduino Uno board to the canvas before running the simulation.
              </SimulationWarningBox>
            )}
            <SimulationToolbar $compact>
              <SimulationActionGroup $compact>
                <SimulationRunButton
                  type="button"
                  onClick={runSimulationPreview}
                  disabled={!canRunSimulationPreview}
                  $iconOnly
                  aria-label={isSimulationCompiling ? 'Compiling simulation sketch' : 'Run simulation'}
                  title={isSimulationCompiling ? 'Compiling...' : 'Run'}
                >
                  <Play size={16} />
                </SimulationRunButton>

                <SimulationSecondaryButton
                  type="button"
                  onClick={() => stopSimulation('Simulation stopped.')}
                  disabled={!canStopSimulation}
                  $iconOnly
                  aria-label="Stop simulation"
                  title="Stop"
                >
                  <Square size={14} />
                </SimulationSecondaryButton>

                <SimulationSecondaryButton
                  type="button"
                  onClick={resetSimulationExample}
                  disabled={isSimulationCompiling}
                  $iconOnly
                  aria-label="Reset simulation example"
                  title="Reset Example"
                >
                  <RotateCcw size={14} />
                </SimulationSecondaryButton>

                {hasSimulationBuzzers && (
                  <SimulationSecondaryButton
                    type="button"
                    onClick={handleSimulationAudioToggle}
                    $iconOnly
                    aria-label={simulationAudioEnabled ? 'Mute simulation audio' : 'Enable simulation audio'}
                    title={simulationAudioEnabled ? 'Mute Audio' : 'Enable Audio'}
                  >
                    {simulationAudioEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  </SimulationSecondaryButton>
                )}
              </SimulationActionGroup>

              <SimulationStatus $tone={simulationStatusTone}>
                {simulationStatus || 'Compile a sketch to start the browser-side Arduino Uno emulator.'}
              </SimulationStatus>
            </SimulationToolbar>

{/*             <SimulationCollapsedMeta>
              Panel collapsed so the circuit stays in view. Phase: {simulationPhase}. Built-in LED on D13: {simulationBuiltinLedOn ? 'ON' : 'OFF'}. Elapsed: {simulationElapsedTime}. Starter target: {preferredSimulationExampleLabel}.
            </SimulationCollapsedMeta> */}
          </SimulationDockCollapsedBody>
        ) : (
          <SimulationDockBody>
            {showSimulationNoUnoWarning && (
              <SimulationWarningBox>
                <strong>No Arduino Uno detected</strong>
                Add an Arduino Uno board to the canvas before running the simulation. The simulator can still compile code, but it cannot map GPIO pins to your circuit until an Uno is present.
              </SimulationWarningBox>
            )}
            {showSimulationWorkspaceModal ? (
              <SimulationWorkspaceNotice>
                <div>
                  <SimulationPanelTitle>Sketch Editor Expanded</SimulationPanelTitle>
                  <SimulationPanelMeta>
                    The code editor and run controls are currently shown in the centered workspace modal for more room.
                  </SimulationPanelMeta>
                </div>

                <SimulationWorkspaceActions>
                  <SimulationSecondaryButton
                    type="button"
                    onClick={closeSimulationWorkspaceModal}
                  >
                    <Minimize2 size={14} />
                    Hide Modal
                  </SimulationSecondaryButton>
                </SimulationWorkspaceActions>
              </SimulationWorkspaceNotice>
            ) : (
              renderSimulationEditorWorkspace()
            )}

            <SimulationGrid>
              <SimulationPanelStack>
                <SimulationPanel>
                  <SimulationPanelTitle>Compiler Output</SimulationPanelTitle>
                  <SimulationPre>
                    {simulationCompilerOutput || 'Compiler output will appear here after you run a sketch.'}
                  </SimulationPre>
                </SimulationPanel>

                <SimulationPanel>
                  <SimulationPanelTitle>Serial Monitor</SimulationPanelTitle>
                  <SimulationPre>
                    {simulationSerialOutput || 'No serial output yet.'}
                  </SimulationPre>
                </SimulationPanel>
              </SimulationPanelStack>

              <SimulationPanelStack>
                <SimulationPanel>
                  <SimulationPanelTitle>Runtime</SimulationPanelTitle>

                  <SimulationRuntimeGrid>
                    <SimulationRuntimeStat>
                      <SimulationRuntimeLabel>Phase</SimulationRuntimeLabel>
                      <SimulationRuntimeValue>{simulationPhase}</SimulationRuntimeValue>
                    </SimulationRuntimeStat>

                    <SimulationRuntimeStat>
                      <SimulationRuntimeLabel>Elapsed</SimulationRuntimeLabel>
                      <SimulationRuntimeValue>{simulationElapsedTime}</SimulationRuntimeValue>
                    </SimulationRuntimeStat>

                    <SimulationRuntimeStat>
                      <SimulationRuntimeLabel>CPU Cycles</SimulationRuntimeLabel>
                      <SimulationRuntimeValue>{simulationCpuCycles}</SimulationRuntimeValue>
                    </SimulationRuntimeStat>

                    <SimulationRuntimeStat>
                      <SimulationRuntimeLabel>D13 LED</SimulationRuntimeLabel>
                      <SimulationRuntimeValue>
                        {simulationBuiltinLedOn ? 'ON' : 'OFF'}
                      </SimulationRuntimeValue>
                    </SimulationRuntimeStat>
                  </SimulationRuntimeGrid>

                  <SimulationLedRow>
                    <SimulationLedLabel>Built-in LED D13</SimulationLedLabel>
                    <SimulationLedIndicator $active={simulationBuiltinLedOn} />
                  </SimulationLedRow>
                </SimulationPanel>

                <SimulationPanel>
                  <SimulationPanelTitle>Interactive Parts</SimulationPanelTitle>

                  {simulationInteractionSummaries.length === 0 ? (
                    <PanelHelperText>No interactive parts detected.</PanelHelperText>
                  ) : (
                    <SimulationInteractionList>
                      {simulationInteractionSummaries.map((entry) => (
                        <SimulationInteractionCard
                          key={entry.componentId}
                          $active={entry.isActive}
                        >
                          <SimulationInteractionTop>
                            <div>
                              <SimulationInteractionName>
                                {entry.componentName}
                              </SimulationInteractionName>
                              <SimulationInteractionMeta>
                              {entry.interaction === 'slider'
                                ? `${entry.interactionLabel} · ${entry.runtimeStateLabel}`
                                : entry.interactionLabel}
                              </SimulationInteractionMeta>
                            </div>

                            <SimulationInteractionBadge $active={entry.isActive}>
                              {entry.interaction === 'slider'
                                ? entry.sliderReadoutText
                                : entry.isActive ? 'ACTIVE' : 'IDLE'}
                            </SimulationInteractionBadge>
                          </SimulationInteractionTop>

                          {entry.interaction === 'slider' ? (
                            <SimulationInteractionSliderRow>
                              <SimulationSliderInput
                                type="range"
                                min={entry.sliderMin}
                                max={entry.sliderMax}
                                step={entry.sliderStep}
                                value={entry.sliderValue}
                                disabled={simulationPhase !== 'running'}
                                onChange={(event) => updateSimulationSliderInteraction(
                                  entry.componentId,
                                  event.target.value
                                )}
                              />
                              <SimulationSliderReadout>
                                {entry.sliderReadoutText}
                              </SimulationSliderReadout>
                            </SimulationInteractionSliderRow>
                          ) : (
                            <SimulationInteractionButtonRow>
                              <SimulationSecondaryButton
                                type="button"
                                disabled={simulationPhase !== 'running'}
                                onPointerDown={(event) =>
                                  handleSimulationInteractionControlPointerDown(
                                    event,
                                    entry.componentId,
                                    entry.interaction
                                  )
                                }
                                onPointerUp={(event) =>
                                  handleSimulationInteractionControlPointerUp(
                                    event,
                                    entry.componentId,
                                    entry.interaction
                                  )
                                }
                                onPointerCancel={(event) =>
                                  handleSimulationInteractionControlPointerUp(
                                    event,
                                    entry.componentId,
                                    entry.interaction
                                  )
                                }
                              >
                                {entry.interaction === 'toggle'
                                  ? entry.isActive ? 'Toggle Off' : 'Toggle On'
                                  : 'Press'}
                              </SimulationSecondaryButton>
                            </SimulationInteractionButtonRow>
                          )}
                        </SimulationInteractionCard>
                      ))}
                    </SimulationInteractionList>
                  )}
                </SimulationPanel>

                <SimulationPanel>
                  <SimulationPanelTitle>Pin States</SimulationPanelTitle>

                  <SimulationPinGrid>
                    {simulationPinList.map((pin) => (
                      <SimulationPinCard
                        key={pin.name}
                        $state={pin.outputMode === 'pwm'
                          ? 'pwm'
                          : pin.isOutput
                            ? (pin.isHigh ? 'high' : 'low')
                            : 'input'}
                      >
                        <SimulationPinName>{pin.name}</SimulationPinName>
                        <SimulationPinValue>
                          {pin.isOutput
                            ? formatSimulationOutputState(pin)
                            : (() => {
                                const analogInput = simulationCircuitAnalysis.analogInputsByPinName?.[pin.name];

                                if (analogInput?.status === 'resolved') {
                                  return `${pin.mode} · ${formatSimulationAnalogVoltage(analogInput.voltage)}`;
                                }

                                if (analogInput?.status === 'conflict') {
                                  return `${pin.mode} · conflict`;
                                }

                                return pin.mode;
                              })()}
                        </SimulationPinValue>
                      </SimulationPinCard>
                    ))}
                  </SimulationPinGrid>
                </SimulationPanel>

                <SimulationPanel>
                  <SimulationPanelTitle>Circuit Detection</SimulationPanelTitle>
                  <SimulationPre>{simulationCircuitDebugText}</SimulationPre>
                </SimulationPanel>
              </SimulationPanelStack>
            </SimulationGrid>
          </SimulationDockBody>
        )}
      </SimulationDockPanel>
    );
  };

  const renderSimulationWorkspaceModal = () => {
    if (!showSimulationWorkspaceModal || !showSimulationModal) {
      return null;
    }

    return (
      <SimulationModalOverlay
        $show={showSimulationWorkspaceModal}
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeSimulationWorkspaceModal();
          }
        }}
      >
        <SimulationModalContent
          role="dialog"
          aria-modal="true"
          aria-labelledby="simulation-workspace-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <SimulationModalHeader>
            <div>
              <AnalysisModalTitle id="simulation-workspace-title">
                Arduino Editor
              </AnalysisModalTitle>
{/*               <SimulationLead>
                More room for writing your sketch while keeping the run and stop controls within reach.
              </SimulationLead> */}
            </div>

            <SimulationHeaderActions>
              <SimulationModalActionButton
                type="button"
                onClick={copySimulationCode}
              >
                <Copy size={14} />
                {simulationCopyButtonLabel}
              </SimulationModalActionButton>

              <SimulationModalActionButton
                type="button"
                onClick={closeSimulationWorkspaceModal}
              >
                <Minimize2 size={14} />
                Hide to Sidebar
              </SimulationModalActionButton>

              <ModalCloseButton
                type="button"
                aria-label="Hide expanded editor"
                onClick={closeSimulationWorkspaceModal}
              >
                ×
              </ModalCloseButton>
            </SimulationHeaderActions>
          </SimulationModalHeader>

          <SimulationModalBody>
            {showSimulationNoUnoWarning && (
              <SimulationWarningBox>
                <strong>No Arduino Uno detected</strong>
                Add an Arduino Uno board to the canvas before running the simulation. The simulator can still compile code, but it cannot map GPIO pins to your circuit until an Uno is present.
              </SimulationWarningBox>
            )}

            {renderSimulationEditorWorkspace({ expanded: true })}
          </SimulationModalBody>
        </SimulationModalContent>
      </SimulationModalOverlay>
    );
  };

  const renderExamplesModal = () => {
    if (!showExamplesModal) {
      return null;
    }

    return (
      <ExamplesModalOverlay
        $show={showExamplesModal}
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeExamplesModal();
          }
        }}
      >
        <ExamplesModalContent
          role="dialog"
          aria-modal="true"
          aria-labelledby="project-builder-examples-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <ExamplesModalHeader>
            <div>
              <AnalysisModalTitle id="project-builder-examples-title">
                Open Examples
              </AnalysisModalTitle>
              <PanelHelperText>
                Load a ready-made circuit into the canvas. If the example includes Arduino code, it will also be placed in the simulator editor.
              </PanelHelperText>
            </div>

            <ModalCloseButton
              type="button"
              aria-label="Close examples"
              onClick={closeExamplesModal}
            >
              Ã—
            </ModalCloseButton>
          </ExamplesModalHeader>

          <ExamplesModalBody>
            {projectBuilderExamplesError ? (
              <ExampleLibraryEmpty>
                {projectBuilderExamplesError}
              </ExampleLibraryEmpty>
            ) : null}

            {projectBuilderExamplesLoading && projectBuilderExamples.length === 0 ? (
              <ExampleLibraryEmpty>
                Loading ready-made circuits...
              </ExampleLibraryEmpty>
            ) : null}

            {!projectBuilderExamplesLoading && projectBuilderExamples.length === 0 && !projectBuilderExamplesError ? (
              <ExampleLibraryEmpty>
                Ready-made circuits will appear here once they are uploaded in Admin &gt; Project Builder.
              </ExampleLibraryEmpty>
            ) : null}

            {projectBuilderExamples.length > 0 ? (
              <ExamplesList>
                {projectBuilderExamples.map((example) => {
                  const isLoadingExample = String(loadingExampleId) === String(example.id);

                  return (
                    <ExampleLibraryCard
                      key={example.id}
                      type="button"
                      onClick={() => loadProjectBuilderExample(example)}
                      disabled={isCircuitEditingLocked || loadingExampleId !== null}
                    >
                      <ExampleLibraryHeader>
                        <ExampleLibraryTitleWrap>
                          <ExampleLibraryTitle>
                            {example.title || 'Project Builder example'}
                          </ExampleLibraryTitle>
                          <ExampleLibraryDescription>
                            {example.description || example.sourceFileName || 'Ready-made circuit example'}
                          </ExampleLibraryDescription>
                        </ExampleLibraryTitleWrap>

                        <ExampleLibraryAction>
                          {isLoadingExample ? 'Loading...' : 'Load Example'}
                        </ExampleLibraryAction>
                      </ExampleLibraryHeader>

                      <ExampleLibraryMetaRow>
                        <ExampleLibraryBadge>
                          <CircuitBoard size={14} />
                          {example.componentCount} part{example.componentCount === 1 ? '' : 's'}
                        </ExampleLibraryBadge>
                        <ExampleLibraryBadge>
                          <CircuitBoard size={14} />
                          {example.connectionCount} wire{example.connectionCount === 1 ? '' : 's'}
                        </ExampleLibraryBadge>
                        <ExampleLibraryBadge $tone="code">
                          <Code2 size={14} />
                          {example.hasSimulationCode ? 'Code included' : 'Circuit only'}
                        </ExampleLibraryBadge>
                      </ExampleLibraryMetaRow>
                    </ExampleLibraryCard>
                  );
                })}
              </ExamplesList>
            ) : null}
          </ExamplesModalBody>
        </ExamplesModalContent>
      </ExamplesModalOverlay>
    );
  };

  const lessonGuidanceAnchorBreadboardId = String(
    components.find(isBreadboardComponent)?.id || ''
  );

  const renderLessonHoleGuidanceLayer = () => (
    isLessonMode ? (
      <g className="builder-export-ignore lesson-hole-guidance-layer" style={{ pointerEvents: 'none' }}>
        {tutorialCanvasGuidance.areaHighlights.map((highlight) => {
          const toneColor = getLessonHighlightToneColor(highlight.tone);

          return (
            <rect
              key={highlight.key}
              x={highlight.x}
              y={highlight.y}
              width={highlight.width}
              height={highlight.height}
              rx={12}
              fill={`${toneColor}14`}
              stroke={toneColor}
              strokeWidth={2}
              strokeDasharray="8 6"
            />
          );
        })}

        {tutorialCanvasGuidance.pinHighlights.map((highlight) => {
          const toneColor = getLessonHighlightToneColor(highlight.tone);

          return (
            <g key={highlight.key}>
              <circle
                cx={highlight.point.x}
                cy={highlight.point.y}
                r={highlight.radius + 1.5}
                fill={`${toneColor}${highlight.tone === 'target' ? '22' : '18'}`}
                stroke={toneColor}
                strokeWidth={highlight.tone === 'target' || highlight.tone === 'placement' ? 2 : 1.5}
                strokeDasharray={highlight.tone === 'selected' || highlight.tone === 'placement' ? 'none' : '5 4'}
              />
              <circle
                cx={highlight.point.x}
                cy={highlight.point.y}
                r={Math.max(1.75, highlight.radius - 1.5)}
                fill="none"
                stroke={toneColor}
                strokeWidth={2.3}
              />
            </g>
          );
        })}
      </g>
    ) : null
  );

  const renderBreadboardOverlayLayer = () => (
    <>
      <g
        ref={breadboardHoverLayerRef}
        className="builder-export-ignore breadboard-hover-highlight-layer"
        style={{ pointerEvents: 'none' }}
      />
      {renderLessonHoleGuidanceLayer()}
    </>
  );

  const renderLessonComponentGuidanceLayer = () => (
    isLessonMode ? (
      <g className="builder-export-ignore lesson-component-guidance-layer" style={{ pointerEvents: 'none' }}>
        {tutorialCanvasGuidance.componentHighlights
          .filter((highlight) => {
            const component = highlight?.component;

            return !isLedComponent(component) && !isResistorComponent(component);
          })
          .map((highlight) => {
          const toneColor = getLessonHighlightToneColor(highlight.tone);

          return (
            <rect
              key={highlight.key}
              x={highlight.component.x + highlight.bounds.x - 8}
              y={highlight.component.y + highlight.bounds.y - 8}
              width={highlight.bounds.width + 16}
              height={highlight.bounds.height + 16}
              rx={10}
              fill={`${toneColor}18`}
              stroke={toneColor}
              strokeWidth={2.5}
              strokeDasharray="8 5"
            />
          );
        })}
      </g>
    ) : null
  );

  return (
    <ProjectBuilderContent $embed={isEmbedMode}>
      <ProjectBuilderShell $embed={isEmbedMode}>
      {!isEmbedMode && (
      <HeaderGrid $hasBanner={Boolean(bannerAd?.image_url)}>
        <PageHeader>
          <TitleWrap>
            <Title>{isLessonMode ? activeLesson.title : 'Project Builder'}</Title>
            <BetaBadge>Beta</BetaBadge>
          </TitleWrap>

          <Subtitle>
            {isLessonMode
              ? (activeLesson.description || 'Follow the guided tutorial inside the real Project Builder.')
              : 'Design your circuit by selecting components and wiring them together'}
          </Subtitle>
        </PageHeader>

        {bannerAd?.image_url ? (
          <SponsoredBannerCard>
            {bannerAd.click_url ? (
              <SponsoredBannerLink
                href={bannerAd.click_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                aria-label={bannerAd.alt_text || 'Open sponsored Project Builder banner ad'}
              >
                <SponsoredBannerImage
                  src={bannerAd.image_url}
                  alt={bannerAd.alt_text || 'Sponsored Project Builder banner ad'}
                  loading="lazy"
                />
              </SponsoredBannerLink>
            ) : (
              <SponsoredBannerImage
                src={bannerAd.image_url}
                alt={bannerAd.alt_text || 'Sponsored Project Builder banner ad'}
                loading="lazy"
              />
            )}
          </SponsoredBannerCard>
        ) : null}

        {/* {renderProjectHelperPanel()}  */}
      </HeaderGrid>
      )}

        {isUnknownLessonMode && (
          <ErrorContainer>
            <AlertCircle size={20} />
            <ErrorText>
              <strong>Lesson not found:</strong> {requestedLessonId}
              {managedLessonLookup.error ? (
                <>
                  <br />
                  {managedLessonLookup.error}
                </>
              ) : null}
              <br />
              <RetryButton onClick={() => navigate('/builder')}>
                Back to Project Builder
              </RetryButton>
            </ErrorText>
          </ErrorContainer>
        )}

        {error && (
          <ErrorContainer>
            <AlertCircle size={20} />
            <ErrorText>
              <strong>Error loading boards:</strong> {error}
              <br />
              <RetryButton onClick={fetchBoards}>
                Retry
              </RetryButton>
            </ErrorText>
          </ErrorContainer>
        )}

        {isUnknownLessonMode ? null : (isLessonLookupLoading || loading) ? (
          <LoadingContainer>
            {isLessonLookupLoading ? 'Loading lesson...' : 'Loading boards... This may take a moment.'}
          </LoadingContainer>
        ) : (
          <ProjectContent $tutorialMode={isLessonMode}>
            <input
              ref={projectFileInputRef}
              type="file"
              accept={PROJECT_FILE_ACCEPT}
              onChange={handleProjectFileInputChange}
              style={{ display: 'none' }}
            />

            {isLessonMode && (
              <LessonSideColumn>
                <BuilderTutorialPanel
                  lesson={activeLesson}
                  step={currentLessonStep}
                  stepIndex={lessonStepIndex}
                  validationResult={lessonValidationResult}
                  hintCount={currentLessonHintCount}
                  isCompleted={lessonCompleted}
                  traceEnabled={lessonTraceEnabled}
                  canShowCodePanel={canShowLessonCodePanel}
                  codePanelOpen={canShowLessonCodePanel && showSimulationModal}
                  onAddComponent={addLessonComponent}
                  partAvailability={lessonPartAvailability}
                  onExit={exitActiveLesson}
                  onNext={showNextLessonStep}
                  onPrevious={showPreviousLessonStep}
                  onRestart={restartActiveLesson}
                  onShowHint={showNextLessonHint}
                  onToggleCodePanel={toggleSimulationPanelFromToolbar}
                  onToggleTrace={() => {
                    setLessonResumeMessage('');
                    setLessonTraceEnabled((current) => !current);
                  }}
                />

                {canShowLessonCodePanel && showSimulationModal
                  ? renderArduinoSimulationPanel()
                  : null}
              </LessonSideColumn>
            )}

            <CanvasWorkspaceColumn>
              <CanvasToolbarDock>
                <CanvasToolbarToggle
                  type="button"
                  onClick={() => setIsMobileToolbarOpen((currentValue) => !currentValue)}
                  aria-expanded={isMobileToolbarOpen}
                  aria-controls="project-builder-toolbar"
                >
                  <Wrench size={16} />
                  {isMobileToolbarOpen ? 'Hide Tools' : 'Show Tools'}
                </CanvasToolbarToggle>

                <CanvasToolbar
                  id="project-builder-toolbar"
                  $mobileOpen={isMobileToolbarOpen}
                  onWheel={(event) => event.stopPropagation()}
                >
                  <CanvasToolbarSection>
                    <CanvasToolbarSectionTitle>
                      View
                    </CanvasToolbarSectionTitle>

                    <CanvasToolButton
                      type="button"
                      $iconOnly
                      onClick={zoomOut}
                      disabled={zoomLevel <= MIN_ZOOM}
                      title="Zoom out"
                      aria-label="Zoom out"
                    >
                      <Minus size={16} />
                    </CanvasToolButton>

                    <ZoomLabel title="Current zoom level">
                      {Math.round((zoomLevel / BASE_ZOOM) * 100)}%
                    </ZoomLabel>

                    <CanvasToolButton
                      type="button"
                      $iconOnly
                      onClick={zoomIn}
                      disabled={zoomLevel >= MAX_ZOOM}
                      title="Zoom in"
                      aria-label="Zoom in"
                    >
                      <Plus size={16} />
                    </CanvasToolButton>

                    <CanvasToolButton
                      type="button"
                      onClick={resetZoom}
                      title="Reset view"
                      aria-label="Reset view"
                    >
                      <RotateCcw size={16} />
                      Reset View
                    </CanvasToolButton>
                  </CanvasToolbarSection>

                  <CanvasToolbarSection>
                    <CanvasToolbarSectionTitle>
                      Canvas
                    </CanvasToolbarSectionTitle>

                    <CanvasToolButton
                      type="button"
                      onClick={() => setShowGrid((currentValue) => !currentValue)}
                      $active={showGrid}
                      title={showGrid ? 'Hide grid' : 'Show grid'}
                      aria-label={showGrid ? 'Hide grid' : 'Show grid'}
                      aria-pressed={showGrid}
                    >
                      <Grid3X3 size={16} />
                      Grid
                    </CanvasToolButton>

                    <CanvasToolbarCheckboxLabel
                      $checked={snapToGrid}
                      title="Snap components to the grid"
                    >
                      <input
                        type="checkbox"
                        checked={snapToGrid}
                        onChange={(event) => setSnapToGrid(event.target.checked)}
                      />
                      Snap
                    </CanvasToolbarCheckboxLabel>

                    <CanvasToolbarField title="Snap size">
                      <CanvasToolbarFieldLabel>
                        Size
                      </CanvasToolbarFieldLabel>

                      <CanvasToolbarNumberInput
                        id="project-builder-snap-size"
                        type="number"
                        min="2"
                        max="100"
                        step="1"
                        value={gridSize}
                        onChange={(event) => {
                          const nextGridSize = Number(event.target.value);
                          setGridSize(Number.isFinite(nextGridSize) && nextGridSize > 0 ? nextGridSize : 10);
                        }}
                        aria-label="Snap size"
                      />
                    </CanvasToolbarField>
                  </CanvasToolbarSection>

                  <CanvasToolbarSection>
                    <CanvasToolbarSectionTitle>
                      Edit
                    </CanvasToolbarSectionTitle>

                    <CanvasToolButton
                      type="button"
                      onClick={undoProjectEdit}
                      disabled={!canUndo}
                      title="Undo (Ctrl/Cmd+Z)"
                      aria-label="Undo"
                    >
                      <Undo2 size={16} />
                      Undo
                    </CanvasToolButton>

                    <CanvasToolButton
                      type="button"
                      onClick={redoProjectEdit}
                      disabled={!canRedo}
                      title="Redo (Ctrl/Cmd+Y)"
                      aria-label="Redo"
                    >
                      <Redo2 size={16} />
                      Redo
                    </CanvasToolButton>

                    {selectedComponentIdsForActions.length > 0 && (
                      <CanvasToolButton
                        type="button"
                        onClick={rotateSelectedComponent}
                        disabled={!canRotateSelectedComponents}
                        title="Rotate selected component (R)"
                        aria-label="Rotate selected component"
                      >
                        <RotateCw size={16} />
                        Rotate
                      </CanvasToolButton>
                    )}
                  </CanvasToolbarSection>

                  <CanvasToolbarSection $alignEnd>
                    <CanvasToolbarSectionTitle>
                      Actions
                    </CanvasToolbarSectionTitle>

                    {!isLessonMode && (
                    <CanvasToolbarPopoverAnchor ref={moreOptionsRef}>
                      <CanvasToolButton
                        type="button"
                        onClick={toggleMoreOptions}
                        title="More project actions"
                        aria-label="More project actions"
                        aria-haspopup="dialog"
                        aria-expanded={showMoreOptions}
                        $active={showMoreOptions}
                      >
                        More
                        {showMoreOptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </CanvasToolButton>

                      {showMoreOptions && (
                        <CanvasToolbarPopover
                          role="dialog"
                          aria-modal="false"
                          aria-label="More Project Builder actions"
                        >
                          <CanvasToolbarPopoverTitle>
                            Project actions
                          </CanvasToolbarPopoverTitle>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              openComponentModal();
                            }}
                            disabled={isCircuitEditingLocked}
                          >
                            Add Component
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              suggestProjectWiring();
                            }}
                            disabled={!canSuggestProjectWiring}
                          >
                            {hasSuggestedConnections ? 'Refresh Wiring Suggestions' : 'Generate Wiring Suggestions'}
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();

                              if (hasAnalysisResults) {
                                setShowAnalysisModal(true);
                                setAnalysisModalTab('analysis');
                                return;
                              }

                              refreshProjectAnalysis();
                            }}
                          >
                            {hasAnalysisResults ? 'Show Wiring Issues' : 'Run Circuit Review'}
                          </CanvasToolbarPopoverButton>

                          {isArduinoSimulationEnabled && (!isLessonMode || canShowLessonCodePanel) && (
                            <CanvasToolbarPopoverButton
                              type="button"
                              onClick={() => {
                                closeMoreOptions();
                                toggleSimulationPanelFromToolbar();
                              }}
                            >
                              {showSimulationModal ? 'Hide Code Editor' : 'View Code'}
                            </CanvasToolbarPopoverButton>
                          )}

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              openExamplesModal();
                            }}
                            disabled={loadingExampleId !== null}
                          >
                            Open Examples
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              saveCurrentProject();
                            }}
                            disabled={components.length === 0 && connections.length === 0}
                          >
                            Save Project in Browser
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              setWheelZoomEnabled((currentValue) => !currentValue);
                            }}
                          >
                            {wheelZoomEnabled ? 'Disable Scroll Zoom' : 'Enable Scroll Zoom'}
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={handleToolbarDownloadPng}
                          >
                            Download PNG Snapshot
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={() => {
                              closeMoreOptions();
                              openProjectFilePicker();
                            }}
                            disabled={isCircuitEditingLocked}
                          >
                            Import Project File
                          </CanvasToolbarPopoverButton>

                          <CanvasToolbarPopoverButton
                            type="button"
                            onClick={handleToolbarDownloadProjectFile}
                            disabled={!hasWorkspaceContent}
                          >
                            Export Project File
                          </CanvasToolbarPopoverButton>
                        </CanvasToolbarPopover>
                      )}
                    </CanvasToolbarPopoverAnchor>
                    )}

                    {(saveStatus || lessonResumeMessage) && (
                      <CanvasToolbarStatusBadge>
                        {saveStatus || lessonResumeMessage}
                      </CanvasToolbarStatusBadge>
                    )}
                  </CanvasToolbarSection>
                </CanvasToolbar>
              </CanvasToolbarDock>

              <CanvasContainer onWheel={handleWheel}>
                <CanvasOverlayStack>
                  {showSuggestionOverlay && (
                    <CanvasSuggestionPanel onWheel={(event) => event.stopPropagation()}>
                      <SuggestedWiringHeader>
                        <SuggestedWiringTitle>
                          Suggested Connections
                        </SuggestedWiringTitle>

                        <WireTag $color="#2563eb">
                          {suggestedConnections.length}
                        </WireTag>
                      </SuggestedWiringHeader>

                      <SuggestedWiringActions>
                        <CanvasToolbarStatusBadge>
                          {selectedSuggestionCount} of {suggestedConnections.length} selected
                        </CanvasToolbarStatusBadge>

                        <CanvasToolbarActionButton
                          type="button"
                          onClick={allSuggestionsSelected ? clearSuggestionSelection : selectAllSuggestions}
                        >
                          {allSuggestionsSelected ? 'Unselect All' : 'Select All'}
                        </CanvasToolbarActionButton>

                        <CanvasToolbarActionButton
                          type="button"
                          $primary
                          onClick={applySuggestedConnections}
                          disabled={isCircuitEditingLocked || selectedSuggestionCount === 0}
                        >
                          Apply Selected ({selectedSuggestionCount})
                        </CanvasToolbarActionButton>

                        <CanvasToolbarActionButton
                          type="button"
                          onClick={clearSuggestedConnections}
                        >
                          Clear Suggestions
                        </CanvasToolbarActionButton>
                      </SuggestedWiringActions>

                      {isMobileCanvas() && connections.length > 0 && (
                        <PanelHelperText style={{ padding: '10px 12px 0 12px' }}>
                          Mobile wire preview is temporarily hidden because wire positions may not align on actual mobile devices. Your saved connections are still listed below.
                        </PanelHelperText>
                      )}

                      <SuggestedWiringList>
                        {suggestedConnections.map((connection) => (
                          <SuggestedConnectionItem key={`suggested-row-${connection.id}`}>
                            <SuggestionCheckbox
                              type="checkbox"
                              checked={selectedSuggestionIds.has(connection.id)}
                              onChange={() => toggleSuggestionSelection(connection.id)}
                              title="Include this suggestion"
                            />

                            {renderCompactConnectionRow(connection, {
                              key: `suggested-row-content-${connection.id}`,
                              showDelete: false,
                              clickable: false,
                            })}
                          </SuggestedConnectionItem>
                        ))}
                      </SuggestedWiringList>
                    </CanvasSuggestionPanel>
                  )}
                </CanvasOverlayStack>
              {!hasWorkspaceContent && (
                <CanvasEmptyState aria-hidden="true">
                  <CanvasEmptyStateCard>
                    <CanvasEmptyStateIcon>
                      <CircuitBoard size={18} />
                    </CanvasEmptyStateIcon>
                    <CanvasEmptyStateTitle>
                      Start by adding a board or component
                    </CanvasEmptyStateTitle>
                    <CanvasEmptyStateHint>
                      Use &ldquo;Add Component&rdquo; to place parts on the canvas.
                    </CanvasEmptyStateHint>
                  </CanvasEmptyStateCard>
                </CanvasEmptyState>
              )}
              <SVGCanvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                viewBox={getCanvasViewBox()}
                $wireDropStatus={wireDropStatus}
                $showGrid={showGrid}
                $gridSize={gridSize}
                onPointerDown={handleCanvasPointerDown}
                onClick={(event) => {
                  if (event.target !== event.currentTarget) return;

                  if (suppressCanvasClickRef.current) {
                    suppressCanvasClickRef.current = false;
                    return;
                  }

                  const wireDrag = wireDragRef.current;

                  if (wireDrag) {
                    const point = getCanvasPoint(event);
                    if (point) {
                      addWireCorner(point);
                    }
                    return;
                  }

                  setSelectedItem(null);
                  setSelectedComponentIds(new Set());
                  clearAnalysisHighlights();
                }}
              >

                {components.map((comp) => {
                  if (isComponentHidden(comp)) {
                    return null;
                  }

                  const renderBoard = comp.type === 'board'
                    ? getHydratedBoardForComponent(comp)
                    : null;
                  const markup = comp.type === 'passive'
                    ? getPreparedPassiveSvg(comp)
                    : getPreparedBoardSvg(renderBoard);
                  const interactionEntry = simulationInteractiveComponentMap.get(comp.id) || null;
                  const canInteractInSimulation = Boolean(
                    interactionEntry &&
                    showSimulationModal &&
                    simulationPhase === 'running' &&
                    ['momentary_press', 'toggle'].includes(interactionEntry.interaction)
                  );
                  const isSimulationInteractionActive = activeSimulationInteractionIds.has(comp.id);
                  const footprint = getComponentFootprint(comp);
                  const rotation = comp.rotation || 0;
                  const centerX = footprint.width / 2;
                  const centerY = footprint.height / 2;
                  const visibleBounds = getComponentVisibleSvgBounds(comp);
                  const showCanvasSliderControl = Boolean(
                    comp.type === 'passive' &&
                    interactionEntry?.interaction === 'slider' &&
                    selectedComponentIds.size === 1 &&
                    selectedComponentIds.has(comp.id)
                  );
                  const sliderState = showCanvasSliderControl
                    ? getSimulationSliderState(interactionEntry.simulationModel, comp?.valueOhms)
                    : null;
                  const sliderCopy = showCanvasSliderControl
                    ? getSimulationSliderCopy(interactionEntry.simulationModel, comp?.valueOhms)
                    : null;
                  const canvasSliderOverlayLayout = (
                    showCanvasSliderControl &&
                    visibleBounds
                  )
                    ? getCanvasSliderOverlayLayout({
                        canvasHeight: CANVAS_HEIGHT,
                        canvasWidth: CANVAS_WIDTH,
                        componentX: comp.x,
                        componentY: comp.y,
                        footprintHeight: footprint.height,
                        footprintWidth: footprint.width,
                        rotation,
                        visibleBounds,
                      })
                    : null;
                  const ledCurrentWarning = isLedComponent(comp)
                    ? ledCurrentWarningByComponentId[comp.id]
                    : null;
                  const canDragComponent = canMoveComponent(comp);
                  const hideLessonPlacementOutline = Boolean(
                    isLessonMode &&
                    (isLedComponent(comp) || isResistorComponent(comp))
                  );

                  return (
                    <React.Fragment key={comp.id}>
                    <g
                      data-builder-component-id={comp.id}
                      data-builder-component-type={comp.type}
                      data-builder-passive-key={comp.passiveKey || comp.passiveType || ''}
                      transform={`translate(${comp.x}, ${comp.y})`}
                      onPointerDown={(event) => handleComponentPointerDown(event, comp)}
                      onClick={(event) => {
                        const target = event.target instanceof Element ? event.target : null;

                        if (target?.closest('.pin-hole, .pin-rect')) {
                          event.stopPropagation();
                          return;
                        }

                        event.stopPropagation();
                        if (didDragComponentRef.current) {
                          didDragComponentRef.current = false;
                          return;
                        }
                        const isMultiKey = event.shiftKey || event.ctrlKey || event.metaKey;

                        if (isMultiKey) {
                          setSelectedComponentIds(current => {
                            const next = new Set(current);

                            if (next.has(comp.id)) {
                              next.delete(comp.id);
                            } else {
                              next.add(comp.id);
                            }

                            return next;
                          });

                          setSelectedItem({ type: 'component', id: comp.id });
                          return;
                        }

                        setSelectedComponentIds(new Set([comp.id]));
                        setSelectedItem({ type: 'component', id: comp.id });
                      }}
                    >
                      <g
                        ref={(node) => {
                          if (node) {
                            partRotateRefs.current[comp.id] = node;
                          } else {
                            delete partRotateRefs.current[comp.id];
                          }
                        }}
                        transform={`rotate(${rotation}, ${centerX}, ${centerY})`}
                      >

                      {visibleBounds && (
                        <rect
                          x={visibleBounds.x}
                          y={visibleBounds.y}
                          width={visibleBounds.width}
                          height={visibleBounds.height}
                          rx={10}
                          fill="transparent"
                          style={{
                            pointerEvents: 'none',
                          }}
                        />
                      )}

                      {selectedComponentIds.has(comp.id) && visibleBounds && !hideLessonPlacementOutline && (
                        <rect
                          className="builder-export-ignore"
                          x={visibleBounds.x}
                          y={visibleBounds.y}
                          width={visibleBounds.width}
                          height={visibleBounds.height}
                          rx={10}
                          fill="rgba(37, 99, 235, 0.08)"
                          stroke="#2563eb"
                          strokeWidth={2}
                          strokeDasharray="6 4"
                          style={{ pointerEvents: 'none' }}
                        />
                      )}

                      {canInteractInSimulation && visibleBounds && (
                        <g className="builder-export-ignore" style={{ pointerEvents: 'none' }}>
                          <rect
                            x={visibleBounds.x}
                            y={visibleBounds.y}
                            width={visibleBounds.width}
                            height={visibleBounds.height}
                            rx={10}
                            fill={isSimulationInteractionActive ? 'rgba(34, 197, 94, 0.12)' : 'rgba(14, 165, 233, 0.08)'}
                            stroke={isSimulationInteractionActive ? '#16a34a' : '#0ea5e9'}
                            strokeWidth={2}
                            strokeDasharray={isSimulationInteractionActive ? 'none' : '6 4'}
                          />
                          <text
                            x={visibleBounds.x + visibleBounds.width / 2}
                            y={Math.max(14, visibleBounds.y - 8)}
                            textAnchor="middle"
                            fontSize="11"
                            fontWeight="bold"
                            fill={isSimulationInteractionActive ? '#166534' : '#0369a1'}
                          >
                            {interactionEntry.interaction === 'toggle'
                              ? (isSimulationInteractionActive ? 'SWITCH ON' : 'SWITCH OFF')
                              : (isSimulationInteractionActive ? 'PRESSED' : 'PRESS')}
                          </text>
                        </g>
                      )}

                      {markup ? (
                      <>
                          <foreignObject
                            x={0}
                            y={0}
                            width={footprint.width}
                            height={footprint.height}
                            style={{
                              pointerEvents: 'auto',
                              overflow: 'visible',
                              cursor: canDragComponent ? 'grab' : 'default',
                            }}
                          >
                          <BoardSvgHost
                            xmlns="http://www.w3.org/1999/xhtml"
                            ref={(node) => {
                              if (node) {
                                partHostRefs.current[comp.id] = node;
                              } else {
                                delete partHostRefs.current[comp.id];
                              }
                            }}
                            dangerouslySetInnerHTML={{ __html: markup }}
                          />
                        </foreignObject>
                        </>
                      ) : (
                        <>
                          <rect
                            width="160"
                            height="100"
                            fill="#f0f9ff"
                            stroke="#3b82f6"
                            strokeWidth="2"
                            rx="4"
                          />
                          <text
                            x="80"
                            y="50"
                            textAnchor="middle"
                            fontSize="12"
                            fill="#1e40af"
                            fontWeight="bold"
                          >
                            {comp.board?.name || comp.name}
                          </text>
                        </>
                      )}

                      {visibleBounds && canDragComponent && (
                        <rect
                          className="builder-export-ignore builder-component-drag-hitbox"
                          x={visibleBounds.x}
                          y={visibleBounds.y}
                          width={visibleBounds.width}
                          height={visibleBounds.height}
                          rx={10}
                          fill="transparent"
                          stroke="none"
                        />
                      )}

                      {ledCurrentWarning && visibleBounds && (() => {
                        const badgeX = visibleBounds.x + visibleBounds.width - 104;

                        // More negative = higher. Less negative = lower.
                        const badgeY = visibleBounds.y - 42;

                        // Text baseline inside the badge.
                        const textY = badgeY + 15;

                        return (
                          <g
                            className="builder-export-ignore"
                            style={{ pointerEvents: 'none' }}
                          >
                            <rect
                              x={badgeX}
                              y={badgeY}
                              width={100}
                              height={22}
                              rx={11}
                              fill={ledCurrentWarning.type === 'over_current' ? '#fef2f2' : '#fffbeb'}
                              stroke={ledCurrentWarning.type === 'over_current' ? '#dc2626' : '#f59e0b'}
                              strokeWidth={1.5}
                            />

                            <text
                              x={badgeX + 10}
                              y={textY}
                              fontSize="13"
                              fontWeight="900"
                              fill={ledCurrentWarning.type === 'over_current' ? '#dc2626' : '#d97706'}
                            >
                              ⚠
                            </text>

                            <text
                              x={badgeX + 30}
                              y={textY}
                              fontSize="10"
                              fontWeight="900"
                              fill={ledCurrentWarning.type === 'over_current' ? '#991b1b' : '#92400e'}
                            >
                              {ledCurrentWarning.label}
                            </text>
                          </g>
                        );
                      })()}

                    </g>
                    {canvasSliderOverlayLayout && sliderState && (
                      <foreignObject
                        className="builder-export-ignore"
                        x={canvasSliderOverlayLayout.x}
                        y={canvasSliderOverlayLayout.y}
                        width={canvasSliderOverlayLayout.width}
                        height={canvasSliderOverlayLayout.height}
                        style={{
                          overflow: 'visible',
                          pointerEvents: 'auto',
                        }}
                      >
                        <CanvasComponentSliderCard
                          xmlns="http://www.w3.org/1999/xhtml"
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <CanvasComponentSliderLabel>
                            {sliderCopy?.canvasLabel || 'Slider'}
                          </CanvasComponentSliderLabel>

                          <CanvasComponentSliderTrackWrap>
                            <CanvasComponentSliderInput
                              type="range"
                              min={sliderState.min}
                              max={sliderState.max}
                              step={sliderState.step}
                              value={sliderState.controlValue}
                              onChange={(event) => updateSimulationSliderInteraction(
                                comp.id,
                                event.target.value
                              )}
                              aria-label={`Adjust ${comp.name || 'variable component'} slider position`}
                            />
                          </CanvasComponentSliderTrackWrap>

                          <CanvasComponentSliderValue>
                            {sliderState.controlReadoutText}
                          </CanvasComponentSliderValue>

                          <CanvasComponentSliderHint>
                            {sliderState.controlHintText || `${sliderCopy?.canvasHintPrefix || 'Position'}: ${sliderState.position.toFixed(2)}`}
                          </CanvasComponentSliderHint>
                        </CanvasComponentSliderCard>
                      </foreignObject>
                    )}
                  </g>
                      {String(comp.id) === lessonGuidanceAnchorBreadboardId
                        ? renderBreadboardOverlayLayer()
                        : null}
                    </React.Fragment>
                  );
                  })}

                {!isMobileCanvas() && connections.map((connection) => {
                  const fromPoint = pinLayoutByPartId[connection.from.partId]?.[connection.from.pinKey];
                  const toPoint = pinLayoutByPartId[connection.to.partId]?.[connection.to.pinKey];

                  if (!fromPoint || !toPoint) return null;

                  return (
                    <g key={`wire-line-${connection.id}`} style={{ pointerEvents: 'none' }}>
                      {((selectedItem?.type === 'wire' && selectedItem.id === connection.id) ||
                        highlightedAnalysisWireIds.has(connection.id)) && (
                        <path
                          className="builder-export-ignore"
                          d={getConnectionRoutePath(
                            fromPoint,
                            connection.routePoints || [],
                            toPoint
                          )}
                          fill="none"
                          stroke="#93c5fd"
                          strokeWidth={(connection.width || wireWidth) + 5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          opacity="0.35"
                        />
                      )}

                      <path
                        d={getConnectionRoutePath(
                          fromPoint,
                          connection.routePoints || [],
                          toPoint
                        )}
                        fill="none"
                        stroke={connection.color || '#2563eb'}
                        strokeWidth={connection.width || wireWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        opacity="0.9"
                      />
                    </g>
                  );
                })}

                {showSuggestedConnections && !isMobileCanvas() && suggestedConnections.map((connection) => {
                  const fromPoint = pinLayoutByPartId[connection.from.partId]?.[connection.from.pinKey];
                  const toPoint = pinLayoutByPartId[connection.to.partId]?.[connection.to.pinKey];

                  if (!fromPoint || !toPoint) return null;

                  return (
                    <g key={`suggested-wire-${connection.id}`} style={{ pointerEvents: 'none' }}>
                      <path
                        d={getConnectionPath(fromPoint, toPoint)}
                        fill="none"
                        stroke={connection.color || '#10b981'}
                        strokeWidth={wireWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeDasharray="10 8"
                        opacity="0.75"
                      />

                      <circle
                        cx={fromPoint.x}
                        cy={fromPoint.y}
                        r={tipRadius}
                        fill="#ffffff"
                        stroke={connection.color || '#10b981'}
                        strokeWidth={tipStroke}
                      />

                      <circle
                        cx={toPoint.x}
                        cy={toPoint.y}
                        r={tipRadius}
                        fill="#ffffff"
                        stroke={connection.color || '#10b981'}
                        strokeWidth={tipStroke}
                      />

                      <text
                        x={(fromPoint.x + toPoint.x) / 2}
                        y={(fromPoint.y + toPoint.y) / 2 - 8}
                        textAnchor="middle"
                        fontSize="12"
                        fill={connection.color || '#10b981'}
                        fontWeight="bold"
                      >
                        {String(connection.confidence || '').toUpperCase()}
                      </text>
                    </g>
                  );
                })}

                {wirePreview && (
                  <g style={{ pointerEvents: 'none' }}>
                    <path
                      d={getConnectionRoutePath(
                        wirePreview.startPoint,
                        wirePreview.routePoints || [],
                        wirePreview.currentPoint
                      )}
                      fill="none"
                      stroke={wirePreview.color || '#2563eb'}
                      strokeWidth={wirePreview.width || wireWidth}
                      strokeDasharray="10 8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.95"
                    />
                    {(wirePreview.routePoints || []).map((point, index) => (
                      <circle
                        key={`wire-preview-corner-${index}`}
                        cx={point.x}
                        cy={point.y}
                        r={4}
                        fill="#ffffff"
                        stroke={wirePreview.color || '#2563eb'}
                        strokeWidth={2}
                      />
                    ))}
                    <circle
                      cx={wirePreview.startPoint.x}
                      cy={wirePreview.startPoint.y}
                      r="5"
                      fill="#ffffff"
                      stroke={wirePreview.color || '#2563eb'}
                      strokeWidth="2"
                    />
                    <circle
                      cx={wirePreview.currentPoint.x}
                      cy={wirePreview.currentPoint.y}
                      r={tipRadius}
                      fill="#ffffff"
                      stroke={wirePreview.color || '#2563eb'}
                      strokeWidth={tipStroke}
                    />
                  </g>
                )}

                {!isMobileCanvas() && connections.map((connection) => {
                  const fromPoint = pinLayoutByPartId[connection.from.partId]?.[connection.from.pinKey];
                  const toPoint = pinLayoutByPartId[connection.to.partId]?.[connection.to.pinKey];

                  if (!fromPoint || !toPoint) return null;

                  const color = connection.color || '#2563eb';

                  return (
                    <g key={`wire-controls-${connection.id}`}>
                      <path
                        d={getConnectionRoutePath(fromPoint, connection.routePoints || [], toPoint)}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={Math.max((connection.width || wireWidth) + 18, 20)}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ pointerEvents: 'stroke', cursor: 'grab' }}
                        onPointerDown={(event) => startWireCornerDrag(event, connection)}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setSelectedItem({ type: 'wire', id: connection.id });
                        }}
                      />
                      {(connection.routePoints || []).map((point, index) => (
                        <g key={`wire-corner-${connection.id}-${index}`}>
                          <circle
                            cx={point.x}
                            cy={point.y}
                            r={12}
                            fill="transparent"
                            style={{ pointerEvents: 'all', cursor: 'grab' }}
                            onPointerDown={(event) => {
                              if (isCircuitEditingLocked) {
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();

                              setSelectedItem({ type: 'wire', id: connection.id });

                              wireCornerDragRef.current = {
                                pointerId: event.pointerId,
                                connectionId: connection.id,
                                routePointIndex: index,
                              };

                              event.currentTarget.setPointerCapture?.(event.pointerId);
                            }}
                            onDoubleClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();

                              deleteWireCorner(connection.id, index);
                            }}
                          />

                          <circle
                            cx={point.x}
                            cy={point.y}
                            r={tipRadius}
                            fill="#ffffff"
                            stroke={color}
                            strokeWidth={tipStroke}
                            style={{ pointerEvents: 'none' }}
                          />
                        </g>
                      ))}
                      <circle
                        cx={fromPoint.x}
                        cy={fromPoint.y}
                        r={tipRadius}
                        fill="#ffffff"
                        stroke={color}
                        strokeWidth={tipStroke}
                        style={{ pointerEvents: 'none' }}
                      />

                      <circle
                        cx={toPoint.x}
                        cy={toPoint.y}
                        r={tipRadius}
                        fill="#ffffff"
                        stroke={color}
                        strokeWidth={tipStroke}
                        style={{ pointerEvents: 'none' }}
                      />

                      <circle
                        cx={fromPoint.x}
                        cy={fromPoint.y}
                        r={12}
                        fill="transparent"
                        style={{ pointerEvents: 'all', cursor: 'grab' }}
                        onPointerDown={(event) => startRewireDrag(event, connection, 'from')}
                      />

                      <circle
                        cx={toPoint.x}
                        cy={toPoint.y}
                        r={12}
                        fill="transparent"
                        style={{ pointerEvents: 'all', cursor: 'grab' }}
                        onPointerDown={(event) => startRewireDrag(event, connection, 'to')}
                      />
                    </g>
                  );
                })}

                {renderLessonComponentGuidanceLayer()}

                {!isComponentDragActive && (
                  <g className="builder-pin-hitbox-layer">
                    {components.flatMap((comp) => {
                      if (isComponentHidden(comp)) {
                        return [];
                      }

                      const shouldUseDragPinCursor = shouldDragLessonComponentFromPin(comp);
                      const shouldRenderBreadboardHitboxes = (
                        !isBreadboardComponent(comp) ||
                        Boolean(wirePreview) ||
                        lessonAllowsHoleSelection ||
                        selectedComponentIds.has(comp.id) ||
                        (
                          selectedItem?.type === 'component' &&
                          selectedItem.id === comp.id
                        )
                      );

                      if (!shouldRenderBreadboardHitboxes) {
                        return [];
                      }

                      return Object.entries(pinLayoutByPartId[comp.id] || {}).map(([pinKey, point]) => {
                        if (
                          !point ||
                          !Number.isFinite(point.x) ||
                          !Number.isFinite(point.y)
                        ) {
                          return null;
                        }

                        const pinTargetEndpoint = {
                          partId: comp.id,
                          boardName: comp.board?.name || comp.name,
                          pinKey,
                        };
                        const wirePinDropStatus = activeWireDropSourceEndpoint
                          ? (isValidWireConnectionTarget(activeWireDropSourceEndpoint, pinTargetEndpoint) ? 'valid' : 'invalid')
                          : null;
                        const isPinHoverTarget = Boolean(
                          pinHoverTarget &&
                          String(pinHoverTarget.partId) === String(comp.id) &&
                          String(pinHoverTarget.pinKey) === String(pinKey)
                        );
                        const pinHoverStatus = isPinHoverTarget
                          ? (pinHoverTarget.status || wirePinDropStatus || 'hover')
                          : null;
                        const pinHoverDotColor = pinHoverStatus === 'valid'
                          ? '#22c55e'
                          : (pinHoverStatus === 'invalid' ? '#ef4444' : '#2563eb');

                        return (
                          <g key={`${comp.id}-${pinKey}-pin-overlay`}>
                            {pinHoverStatus && (
                              <circle
                                className={[
                                  'builder-export-ignore',
                                  'builder-pin-hover-dot',
                                  pinHoverStatus === 'valid' ? 'builder-pin-hover-dot-valid' : '',
                                  pinHoverStatus === 'invalid' ? 'builder-pin-hover-dot-invalid' : '',
                                ].filter(Boolean).join(' ')}
                                cx={point.x}
                                cy={point.y}
                                r={5.5}
                                fill={pinHoverDotColor}
                                stroke="#ffffff"
                                strokeWidth={1.5}
                                style={{ pointerEvents: 'none' }}
                              />
                            )}

                            <circle
                              className={[
                                'builder-pin-hitbox',
                                shouldUseDragPinCursor ? 'builder-component-drag-pin-hitbox' : '',
                                wirePinDropStatus === 'valid' ? 'builder-wire-drop-valid-pin-hitbox' : '',
                                wirePinDropStatus === 'invalid' ? 'builder-wire-drop-invalid-pin-hitbox' : '',
                              ].filter(Boolean).join(' ')}
                              data-part-id={comp.id}
                              data-pin-key={pinKey}
                              data-breadboard-bus={isBreadboardComponent(comp) ? getBreadboardBusKeyForPinKey(pinKey) : undefined}
                              cx={point.x}
                              cy={point.y}
                              r={9}
                              fill="transparent"
                              stroke="none"
                              style={{
                                pointerEvents: 'all',
                                cursor: wirePinDropStatus === 'valid'
                                  ? 'copy'
                                  : (wirePinDropStatus === 'invalid' ? 'not-allowed' : 'crosshair'),
                              }}
                              onPointerDown={(event) => {
                                if (lessonAllowsHoleSelection && isBreadboardComponent(comp)) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  selectLessonBreadboardHole(comp, pinKey);
                                  return;
                                }

                                const activeWire = wireDragRef.current;

                                if (activeWire) {
                                  event.preventDefault();
                                  event.stopPropagation();

                                  finishWireAtTarget({
                                    partId: comp.id,
                                    boardName: comp.board?.name || comp.name,
                                    pinKey,
                                  });

                                  return;
                                }

                                if (shouldDragLessonComponentFromPin(comp)) {
                                  handleComponentPointerDown(event, comp);
                                  return;
                                }

                                startWireFromOverlayPin(event, comp, pinKey, point);
                              }}
                              onPointerEnter={() => {
                                if (activeWireDropSourceEndpoint) {
                                  applyWireDropFeedback({
                                    status: wirePinDropStatus,
                                    target: {
                                      partId: comp.id,
                                      pinKey,
                                    },
                                  });
                                } else {
                                  applyPinHoverFeedback({
                                    partId: comp.id,
                                    pinKey,
                                  });
                                }

                                if (isBreadboardComponent(comp) && wireDragRef.current) {
                                  drawBreadboardHoverHighlight(comp.id, pinKey);
                                }
                              }}
                              onPointerLeave={() => {
                                if (isPinHoverTarget) {
                                  clearWireDropFeedback();
                                }

                                if (!isBreadboardComponent(comp)) {
                                  return;
                                }

                                clearBreadboardHoverHighlight({
                                  partId: String(comp.id),
                                  pinKey: String(pinKey),
                                });
                              }}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                              }}
                            />
                          </g>
                        );
                      });
                    })}
                  </g>
                )}

                {highlightedAnalysisPins.map((pin) => {
                  const point = pinLayoutByPartId[pin.component_id]?.[pin.marker_key];

                  if (!point) {
                    return null;
                  }

                  return (
                    <g key={`analysis-pin-${pin.component_id}-${pin.marker_key}`}>
                      <circle
                        className="builder-export-ignore"
                        cx={point.x}
                        cy={point.y}
                        r={10}
                        fill="rgba(37, 99, 235, 0.14)"
                        stroke="#2563eb"
                        strokeWidth={2}
                        style={{ pointerEvents: 'none' }}
                      />
                      <circle
                        className="builder-export-ignore"
                        cx={point.x}
                        cy={point.y}
                        r={16}
                        fill="none"
                        stroke="#93c5fd"
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        style={{ pointerEvents: 'none' }}
                      />
                    </g>
                  );
                })}

                {selectionBox && (
                  <rect
                    className="builder-export-ignore"
                    x={selectionBox.x}
                    y={selectionBox.y}
                    width={selectionBox.width}
                    height={selectionBox.height}
                    fill="rgba(37, 99, 235, 0.08)"
                    stroke="#2563eb"
                    strokeWidth={1.5}
                    strokeDasharray="6 4"
                    style={{ pointerEvents: 'none' }}
                  />
                )}
              </SVGCanvas>
            </CanvasContainer>
            </CanvasWorkspaceColumn>

            {isLessonMode ? null : showExpandedSimulationPanel ? (
              renderArduinoSimulationPanel()
            ) : (
              <SidePanel>
                {showCollapsedSimulationPanel && renderArduinoSimulationPanel()}
              {/* Project Management Section */}
              <PanelSection>
                <SectionTitle>
                  <Save size={20} />
                  Saved Projects
                </SectionTitle>

                <ActionButtons style={{ marginBottom: 12 }}>
                  <ActionButton
                    type="button"
                    onClick={openProjectFilePicker}
                    disabled={isCircuitEditingLocked}
                  >
                    <Upload size={18} />
                    Load Project File
                  </ActionButton>

                  <ActionButton
                    type="button"
                    onClick={downloadCurrentProjectFile}
                    disabled={!hasWorkspaceContent}
                  >
                    <Download size={18} />
                    Download Project File
                  </ActionButton>
                </ActionButtons>

                {savedProjects.length === 0 ? (
                  <EmptyStateText>
                    <strong>No saved projects yet</strong>
                    Projects saved in this browser will appear here.
                  </EmptyStateText>
                ) : (
                  <ComponentList>
                    {savedProjects.map(project => (
                      <ComponentListItem
                        key={project.id}
                        $selected={activeProjectId === project.id}
                      >
                        <ComponentListIcon>
                          <Save size={18} />
                        </ComponentListIcon>

                        <ComponentListName
                          type="button"
                          title={project.name}
                          onClick={() => loadSavedProject(project.id)}
                          disabled={isCircuitEditingLocked}
                        >
                          {project.name}
                        </ComponentListName>

                        <ComponentItemActions>
                          <DeleteButton
                            type="button"
                            title="Delete saved project"
                            onClick={(event) => {
                              event.stopPropagation();
                              deleteSavedProject(project.id);
                            }}
                          >
                            <Trash2 size={14} />
                          </DeleteButton>
                        </ComponentItemActions>
                      </ComponentListItem>
                    ))}
                  </ComponentList>
                )}

                <PanelHelperText>
                  Saved projects stay in this browser. Download a custom project file for a portable backup, or load one back into the builder later.
                </PanelHelperText>
              </PanelSection>
              {/* Components Section */}
              <PanelSection>
                <SectionTitle>
                  <Lightbulb size={20} />
                  Components
                </SectionTitle>
                <ActionButtons style={{ marginBottom: 12 }}>
                  <ActionButton
                    type="button"
                    $primary
                    onClick={openComponentModal}
                    disabled={isCircuitEditingLocked}
                  >
                    <CircuitBoard size={18} />
                    Add Component
                  </ActionButton>
                </ActionButtons>

                {components.length === 0 ? (
                  <EmptyStateText>
                    <strong>No components yet</strong>
                    Add an Arduino, sensor, LED, resistor, or other part to begin.
                  </EmptyStateText>
                ) : (
                  <ComponentList>
                    {components.map(comp => (
                      <ComponentListItem
                        key={comp.id}
                        $selected={selectedComponentIds.has(comp.id)}
                        $muted={isComponentHidden(comp)}
                      >
                        <ComponentListIcon>
                          <ComponentListIconSvg
                            dangerouslySetInnerHTML={{
                              __html: getComponentSidebarIconMarkup(comp),
                            }}
                          />
                        </ComponentListIcon>
                        <ComponentListName
                          type="button"
                          title={comp.name}
                          onClick={(event) => {
                            const isMultiKey = event.shiftKey || event.ctrlKey || event.metaKey;

                            if (isMultiKey) {
                              setSelectedComponentIds(current => {
                                const next = new Set(current);

                                if (next.has(comp.id)) {
                                  next.delete(comp.id);
                                } else {
                                  next.add(comp.id);
                                }

                                return next;
                              });

                              setSelectedItem({ type: 'component', id: comp.id });
                              return;
                            }

                            setSelectedComponentIds(new Set([comp.id]));
                            setSelectedItem({ type: 'component', id: comp.id });
                          }}
                        >
                          {comp.name}{isComponentHidden(comp) ? ' (hidden)' : ''}
                        </ComponentListName>

                        <ComponentItemActions>
                          {isBreadboardComponent(comp) && (
                            <DeleteButton
                              type="button"
                              title={isComponentHidden(comp) ? 'Show breadboard' : 'Hide breadboard'}
                              disabled={isCircuitEditingLocked}
                              onClick={(event) => {
                                event.stopPropagation();
                                updateComponentVisibility(comp.id, !isComponentHidden(comp));
                              }}
                              style={{ background: isComponentHidden(comp) ? '#2563eb' : '#64748b' }}
                            >
                              {isComponentHidden(comp) ? <Eye size={14} /> : <EyeOff size={14} />}
                            </DeleteButton>
                          )}

                          {comp.type === 'passive' && (
                            <DeleteButton
                              type="button"
                              title="Edit basic part"
                              disabled={isCircuitEditingLocked}
                              onClick={(event) => {
                                event.stopPropagation();
                                openPassiveEditModal(comp);
                              }}
                              style={{ background: '#6b7280' }}
                            >
                              <Wrench size={14} />
                            </DeleteButton>
                          )}

                          <DeleteButton
                            type="button"
                            title="Rotate component"
                            disabled={isCircuitEditingLocked}
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedItem({ type: 'component', id: comp.id });
                              rotateComponent(comp.id);
                            }}
                            style={{ background: '#2c9fa4' }}
                          >
                            <RotateCw size={14} />
                          </DeleteButton>

                          <DeleteButton
                            type="button"
                            title="Delete component"
                            disabled={isCircuitEditingLocked}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeComponent(comp.id);
                            }}
                          >
                            <Trash2 size={14} />
                          </DeleteButton>
                        </ComponentItemActions>
                      </ComponentListItem>
                    ))}
                  </ComponentList>
                )}

              </PanelSection>

              {/* Connections Section */}
              <PanelSection>
                <SectionTitle>
                  <Zap size={20} />
                  Connections
                </SectionTitle>

                {connections.length === 0 ? (
                  <EmptyStateText>
                    <strong>No wires yet</strong>
                    Manual and suggested connections will appear here.
                  </EmptyStateText>
                ) : (
                  <WiringList>
                    {connections.map(connection =>
                      renderCompactConnectionRow(connection, {
                        key: `saved-row-${connection.id}`,
                        showDelete: true,
                      })
                    )}
                  </WiringList>
                )}
              </PanelSection>

              {/* Properties Section */}
              <PanelSection>
              <SectionTitle>
                <Info size={20} />
                Properties
              </SectionTitle>

              {selectedComponentIds.size > 1 ? (
                <div>
                  <Label>{selectedComponentIds.size} components selected</Label>
                  <p style={{
                    margin: 0,
                    color: '#6b7280',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                  }}>
                    Drag any selected component to move the group.
                  </p>
                </div>
              ) : !selectedItem ? (
                <EmptyStateText>
                  <strong>Select a component or wire</strong>
                  Details and editable settings will appear here.
                </EmptyStateText>
              ) : selectedItem.type === 'wire' ? (
                <ActionButtons>
                  <PropertyRow>
                    <Label>Color</Label>
                    <input
                      type="color"
                      value={
                        connections.find(c => c.id === selectedItem.id)?.color || '#2563eb'
                      }
                      disabled={isCircuitEditingLocked}
                      onChange={(event) => updateSelectedWire({ color: event.target.value })}
                    />
                  </PropertyRow>

                  <PropertyRow>
                    <Label>Thickness</Label>
                    <Select
                      value={connections.find(c => c.id === selectedItem.id)?.width || wireWidth}
                      disabled={isCircuitEditingLocked}
                      onChange={(event) => updateSelectedWire({ width: Number(event.target.value) })}
                    >
                     <option value="2.5">Thin</option>
                    <option value="5.5">Normal</option>
                    <option value="7">Thick</option>
                    <option value="9">Extra Thick</option>
                    </Select>
                  </PropertyRow>
                </ActionButtons>
                ) : (
                  (() => {
                    const selectedComponent = getSelectedComponent();

                    if (!selectedComponent) {
                      return <EmptyStateText>Component not found</EmptyStateText>;
                    }

                    return (
                      <div>
                        <Label>{selectedComponent.name}</Label>

                        {(() => {
                          const description = getComponentDescription(selectedComponent);
                          const boardInfoUrl = getBoardInfoUrl(selectedComponent);

                          return (
                            <>
                              <p style={{
                                margin: '0 0 8px 0',
                                color: '#4b5563',
                                fontSize: '0.9rem',
                                lineHeight: 1.5,
                              }}>
                                {truncateText(description)}
                              </p>

                              {boardInfoUrl && (
                                <Link
                                  to={boardInfoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: '#2563eb',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                  }}
                                >
                                  View board info
                                </Link>
                              )}

                              {selectedComponent.type === 'passive' && getPassiveNoSimulationTag(selectedComponent) && (
                                <div style={{ marginTop: boardInfoUrl ? 10 : 8 }}>
                                  <SimulationInteractionBadge $active={false}>
                                    No Simulation
                                  </SimulationInteractionBadge>
                                </div>
                              )}
                            </>
                          );
                        })()}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'resistor' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            Resistance: {formatResistance(selectedComponent.valueOhms || 10000)}
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'led' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            LED color: {LED_COLOR_OPTIONS.find(option => option.value === selectedComponent.color)?.label || 'Custom'}
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'push_button' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            Momentary switch with two terminals.
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'potentiometer' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            Value: {formatResistance(selectedComponent.valueOhms || 10000)} • Pins: A, W, B
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'buzzer' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            Pins: + signal, - ground
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'servo' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            {(() => {
                              const servoState = simulatedServoStatesByComponentId[selectedComponent.id];
                              const renderedAngleDegrees = Number(animatedServoAnglesByComponentId[selectedComponent.id]);
                              const angleText = (
                                Number.isFinite(renderedAngleDegrees)
                                  ? renderedAngleDegrees
                                  : Number(servoState?.angleDegrees)
                              );
                              const angleLabel = Number.isFinite(angleText)
                                ? `Angle: ${Math.round(angleText)} deg • `
                                : '';

                              return `${angleLabel}Pins: SIG, V+, GND`;
                            })()}
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'lcd_16x2' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            {(() => {
                              const lcdState = simulatedLcdStatesByComponentId[selectedComponent.id];
                              const rowPreview = Array.isArray(lcdState?.rows)
                                ? lcdState.rows.map((row) => row.trimEnd() || ' ').join(' | ')
                                : '';
                              const stateLabel = lcdState?.isPowered
                                ? (lcdState?.isDisplayEnabled ? 'ON' : 'DISPLAY OFF')
                                : 'UNPOWERED';

                              return rowPreview
                                ? `State: ${stateLabel} • ${rowPreview}`
                                : 'Pins: VSS, VDD, V0, RS, RW, E, D0-D7, A, K';
                            })()}
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'seven_segment_display' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            {(() => {
                              const displayState = simulatedSevenSegmentStatesByComponentId[selectedComponent.id];
                              const resolvedDigitCount = normalizeSevenSegmentDigitCount(
                                displayState?.digitCount ?? selectedComponent.digitCount
                              );
                              const digitPinsLabel = getSevenSegmentDigitPinKeys(resolvedDigitCount).join(', ');
                              const commonTypeLabel = getSevenSegmentCommonTypeLabel(
                                displayState?.commonType ?? selectedComponent.commonType
                              );
                              const stateLabel = displayState?.isPowered ? 'ON' : 'UNPOWERED';
                              const segmentSummary = formatSevenSegmentStateSummary(
                                displayState?.segmentStates ?? selectedComponent.segmentStates
                              );

                              return `Digits: ${resolvedDigitCount} • ${commonTypeLabel} • State: ${stateLabel} • Pins: A, B, C, D, E, F, G, DP, ${digitPinsLabel} • Segments: ${segmentSummary}`;
                            })()}
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === BREADBOARD_TEMPLATE_TYPE && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            Columns are connected A-E and F-J across the center gap; each power rail is connected along its full row.
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === BREADBOARD_TEMPLATE_TYPE && (
                          <InspectorSliderPanel>
                            <Label as="div" style={{ marginBottom: 10 }}>
                              Visibility
                            </Label>

                            <InspectorInlineButton
                              type="button"
                              $active={isComponentHidden(selectedComponent)}
                              disabled={isCircuitEditingLocked}
                              onClick={() => updateComponentVisibility(
                                selectedComponent.id,
                                !isComponentHidden(selectedComponent)
                              )}
                            >
                              {isComponentHidden(selectedComponent) ? <Eye size={16} /> : <EyeOff size={16} />}
                              {isComponentHidden(selectedComponent) ? 'Show Breadboard' : 'Hide Breadboard'}
                            </InspectorInlineButton>

                            <InspectorSliderMeta>
                              Hidden breadboards stay in the project list but are removed from the canvas and pin calculations.
                            </InspectorSliderMeta>

                            <Label as="div" style={{ marginTop: 14, marginBottom: 10 }}>
                              Size
                            </Label>

                            <InspectorSliderRow>
                              <InspectorRangeInput
                                type="range"
                                min={COMPONENT_SCALE_MIN}
                                max={COMPONENT_SCALE_MAX}
                                step={COMPONENT_SCALE_STEP}
                                value={clampComponentScale(
                                  selectedComponent.scale || BREADBOARD_DEFAULT_SCALE
                                )}
                                disabled={isCircuitEditingLocked}
                                onFocus={beginComponentScaleEdit}
                                onPointerDown={beginComponentScaleEdit}
                                onPointerUp={finishComponentScaleEdit}
                                onBlur={finishComponentScaleEdit}
                                onChange={(event) => updateComponentScale(
                                  selectedComponent.id,
                                  event.target.value
                                )}
                                aria-label={`Resize ${selectedComponent.name || 'Breadboard'}`}
                                aria-valuetext={formatComponentScalePercent(
                                  selectedComponent.scale || BREADBOARD_DEFAULT_SCALE
                                )}
                              />
                              <InspectorSliderReadout>
                                {formatComponentScalePercent(
                                  selectedComponent.scale || BREADBOARD_DEFAULT_SCALE
                                )}
                              </InspectorSliderReadout>
                            </InspectorSliderRow>

                            <InspectorSliderMeta>
                              Match larger boards such as Arduino Uno while keeping every breadboard bus connection intact.
                            </InspectorSliderMeta>
                          </InspectorSliderPanel>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'generic_2pin' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            {selectedComponent.valueLabel ? `Value: ${selectedComponent.valueLabel} • ` : ''}Pins: 1, 2
                          </p>
                        )}

                        {selectedComponent.type === 'passive' && selectedComponent.passiveType === 'generic_3pin' && (
                          <p style={{
                            margin: 0,
                            color: '#6b7280',
                            fontSize: '0.85rem',
                          }}>
                            {selectedComponent.valueLabel ? `Value: ${selectedComponent.valueLabel} • ` : ''}Pins: 1, 2, 3
                          </p>
                        )}
                        {selectedComponent.type === 'passive' && (() => {
                          const interactionEntry = simulationInteractiveComponentMap.get(selectedComponent.id);

                          if (!interactionEntry || interactionEntry.interaction !== 'slider') {
                            return null;
                          }

                          const sliderState = getSimulationSliderState(
                            interactionEntry.simulationModel,
                            selectedComponent.valueOhms
                          );
                          const sliderCopy = getSimulationSliderCopy(
                            interactionEntry.simulationModel,
                            selectedComponent.valueOhms
                          );
                          const drivenAnalogPins = Object.entries(
                            simulationCircuitAnalysis?.analogInputsByPinName || {}
                          )
                            .filter(([, inputAnalysis]) => (
                              Array.isArray(inputAnalysis?.sourceAnalyses) &&
                              inputAnalysis.sourceAnalyses.some((analysis) => (
                                analysis?.componentId === selectedComponent.id
                              ))
                            ))
                            .map(([pinName, inputAnalysis]) => {
                              const voltage = Number(inputAnalysis?.voltage);
                              return inputAnalysis?.status === 'resolved' && Number.isFinite(voltage)
                                ? `${pinName} (${formatSimulationAnalogVoltage(voltage)})`
                                : pinName;
                            });

                          return (
                            <InspectorSliderPanel>
                              <Label style={{ marginBottom: 10 }}>
                                {sliderCopy.inspectorLabel}
                              </Label>

                              <InspectorSliderRow>
                                <InspectorRangeInput
                                  type="range"
                                  min={sliderState.min}
                                  max={sliderState.max}
                                  step={sliderState.step}
                                  value={sliderState.controlValue}
                                  onChange={(event) => updateSimulationSliderInteraction(
                                    selectedComponent.id,
                                    event.target.value
                                  )}
                                />
                                <InspectorSliderReadout>
                                  {sliderState.controlReadoutText}
                                </InspectorSliderReadout>
                              </InspectorSliderRow>

                              <InspectorSliderMeta>
                                {sliderCopy.inspectorDescription}
                                {sliderState.resistanceText
                                  ? ` Current resistance: ${sliderState.resistanceText}.`
                                  : ''}
                                {drivenAnalogPins.length > 0
                                  ? ` Driving ${drivenAnalogPins.join(', ')}.`
                                  : ''}
                              </InspectorSliderMeta>
                            </InspectorSliderPanel>
                          );
                        })()}
                      </div>
                    );
                  })()
                )}
              </PanelSection>

              </SidePanel>
            )}
          </ProjectContent>
        )}

        {/* Add Component Modal */}
        <AddComponentModal $show={showComponentModal}>
          <ModalContent>
            <ModalTitle>Add Component</ModalTitle>

            <ModalTabs>
              <ModalTab
                type="button"
                $active={componentAddMode === 'board'}
                onClick={() => setComponentAddMode('board')}
              >
                Board / Module
              </ModalTab>

            <ModalTab
              type="button"
              $active={componentAddMode === 'passive'}
              onClick={() => setComponentAddMode('passive')}
            >
              Basic Parts
            </ModalTab>
            </ModalTabs>

            {componentAddMode === 'board' ? (
              <>
                <FormGroup>
                  <Label>Search Component</Label>
                  <SearchInput
                    value={componentSearch}
                    onChange={(event) => {
                      setComponentSearch(event.target.value);
                      setSelectedBoardToAdd('');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        moveSelectedBoard(1);
                        return;
                      }

                      if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        moveSelectedBoard(-1);
                        return;
                      }

                      if (event.key === 'Enter' && selectedBoardToAdd && !isSearchingComponents) {
                        event.preventDefault();
                        addSelectedBoardFromModal();
                      }
                    }}
                    placeholder="Search by board, module, manufacturer..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Results</Label>

                <SearchResults>
                  {isSearchingComponents ? (
                    <SearchResultLoading>
                      <SearchSpinner />
                      Searching components...
                    </SearchResultLoading>
                  ) : filteredBoards.length === 0 ? (
                    <SearchResultEmpty>
                      No matching components found
                    </SearchResultEmpty>
                  ) : (
                    <>
                      {visibleFilteredBoards.map(board => (
                        <SearchResultItem
                          key={board.id}
                          type="button"
                          $selected={String(selectedBoardToAdd) === String(board.id)}
                          onClick={() => setSelectedBoardToAdd(board.id)}
                          title={board.name}
                        >
                          <SearchResultIcon
                            dangerouslySetInnerHTML={{
                              __html: getPreparedBoardIconSvg(board),
                            }}
                          />

                          <SearchResultText>
                            <SearchResultName>
                              {board.name}
                            </SearchResultName>

                            <SearchResultMeta>
                              {[board.manufacturer, board.category]
                                .filter(Boolean)
                                .join(' • ') || 'Board / module'}
                            </SearchResultMeta>
                          </SearchResultText>
                        </SearchResultItem>
                      ))}

                      {filteredBoards.length > visibleFilteredBoards.length && (
                        <SearchResultEmpty>
                          Showing first {visibleFilteredBoards.length} of {filteredBoards.length}. Keep typing to narrow results.
                        </SearchResultEmpty>
                      )}
                    </>
                  )}
                </SearchResults>
                </FormGroup>
              </>
            ) : (
              <>
                <FormGroup>
                  <Label>Search Basic Parts</Label>
                  <SearchInput
                    value={passiveSearch}
                    onChange={(event) => setPassiveSearch(event.target.value)}
                    placeholder="Search by part, type, or description..."
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Results</Label>
                  <SearchResults>
                    {filteredPassiveComponents.length === 0 ? (
                      <SearchResultEmpty>
                        No matching basic parts found
                      </SearchResultEmpty>
                    ) : (
                      <>
                        {visibleFilteredPassiveComponents.map((definition) => (
                          <SearchResultItem
                            key={definition.componentKey}
                            type="button"
                            $selected={selectedPassiveKey === definition.componentKey}
                            onClick={() => setSelectedPassiveKey(definition.componentKey)}
                            title={definition.name}
                          >
                            <SearchResultIcon
                              dangerouslySetInnerHTML={{
                                __html: getPreparedPassiveDefinitionIcon(definition),
                              }}
                            />

                            <SearchResultText>
                              <SearchResultName>
                                {definition.name}
                              </SearchResultName>

                              <SearchResultMeta>
                                {getPassiveSearchMeta(definition)}
                              </SearchResultMeta>
                            </SearchResultText>
                          </SearchResultItem>
                        ))}

                        {filteredPassiveComponents.length > visibleFilteredPassiveComponents.length && (
                          <SearchResultEmpty>
                            Showing first {visibleFilteredPassiveComponents.length} of {filteredPassiveComponents.length}. Keep typing to narrow results.
                          </SearchResultEmpty>
                        )}
                      </>
                    )}
                  </SearchResults>
                </FormGroup>

                <FormGroup>
                  <Label>Selected Part</Label>
                  <PanelHelperText>
                    {selectedPassiveDefinition?.description || 'Choose a basic part to add to the canvas.'}
                  </PanelHelperText>
                  {getPassiveNoSimulationTag(selectedPassiveDefinition) && (
                    <>
                      <div style={{ marginTop: 8 }}>
                        <SimulationInteractionBadge $active={false}>
                          No Simulation
                        </SimulationInteractionBadge>
                      </div>
                      <PanelHelperText style={{ marginTop: 8 }}>
                        This basic part does not have a working browser simulator model yet.
                      </PanelHelperText>
                    </>
                  )}
                </FormGroup>

                {selectedPassiveDefinition?.templateType === 'led' ? (
                  <FormGroup>
                    <Label>LED Color</Label>
                    <Select
                      value={newLedColor}
                      onChange={(event) => setNewLedColor(event.target.value)}
                    >
                      {LED_COLOR_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                ) : passiveSupportsSelectableValues(selectedPassiveDefinition) ? (
                  <>
                    <FormGroup>
                      <Label>{getPassiveSelectableFieldLabel(selectedPassiveDefinition)}</Label>
                      <Select
                        value={newPassiveValue}
                        onChange={(event) => setNewPassiveValue(event.target.value)}
                      >
                        {getPassiveSelectableOptions(selectedPassiveDefinition).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                    {selectedPassiveDefinition?.templateType === 'seven_segment_display' && (
                      <FormGroup>
                        <Label>Common Type</Label>
                        <Select
                          value={newSevenSegmentCommonType}
                          onChange={(event) => setNewSevenSegmentCommonType(event.target.value)}
                        >
                          {SEVEN_SEGMENT_COMMON_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                    )}
                  </>
                ) : (
                  <PanelHelperText>
                    This basic part does not have selectable values.
                  </PanelHelperText>
                )}
              </>
            )}

            <ModalButtons>
              <ModalButton
                className="cancel"
                type="button"
                onClick={closeComponentModal}
              >
                Cancel
              </ModalButton>

              <ModalButton
                className="confirm"
                type="button"
                onClick={
                  componentAddMode === 'board'
                    ? addSelectedBoardFromModal
                    : addBasicComponentFromModal
                }
                disabled={
                  isCircuitEditingLocked ||
                  (componentAddMode === 'board' && !selectedBoardToAdd) ||
                  (componentAddMode === 'passive' && !selectedPassiveDefinition) ||
                  (componentAddMode === 'passive' && selectedPassiveLessonLimitStatus?.disabled)
                }
              >
                Add to Canvas
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </AddComponentModal>
        <AddComponentModal $show={showPassiveEditModal}>
          <ModalContent>
            <ModalTitle>
              {passiveEditDefinition?.name ? `Edit ${passiveEditDefinition.name}` : 'Edit Component'}
            </ModalTitle>
            {passiveEditType === 'led' ? (
              <FormGroup>
                <Label>LED Color</Label>
                <Select
                  value={passiveEditLedColor}
                  disabled={isCircuitEditingLocked}
                  onChange={(event) => setPassiveEditLedColor(event.target.value)}
                >
                  {LED_COLOR_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            ) : passiveSupportsSelectableValues(passiveEditDefinition) ? (
              <>
                <FormGroup>
                  <Label>{getPassiveSelectableFieldLabel(passiveEditDefinition)}</Label>
                  <Select
                    value={passiveEditValue}
                    disabled={isCircuitEditingLocked}
                    onChange={(event) => setPassiveEditValue(event.target.value)}
                  >
                    {getPassiveSelectableOptions(passiveEditDefinition).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
                {passiveEditDefinition?.templateType === 'seven_segment_display' && (
                  <FormGroup>
                    <Label>Common Type</Label>
                    <Select
                      value={passiveEditSevenSegmentCommonType}
                      disabled={isCircuitEditingLocked}
                      onChange={(event) => setPassiveEditSevenSegmentCommonType(event.target.value)}
                    >
                      {SEVEN_SEGMENT_COMMON_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                )}
              </>
            ) : (
              <PanelHelperText>
                This basic part does not have editable values.
              </PanelHelperText>
            )}

            <ModalButtons>
              <ModalButton
                className="cancel"
                type="button"
                onClick={closePassiveEditModal}
              >
                Cancel
              </ModalButton>

              <ModalButton
                className="confirm"
                type="button"
                onClick={savePassiveEdit}
                disabled={isCircuitEditingLocked}
              >
                Save
              </ModalButton>
            </ModalButtons>
          </ModalContent>
        </AddComponentModal>
        {renderExamplesModal()}
        {renderSimulationWorkspaceModal()}
        <AnalysisModalOverlay
          $show={showAnalysisModal}
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
                closeAnalysisModal();
            }
          }}
        >
          <AnalysisModalContent
            role="dialog"
            aria-modal="true"
            aria-labelledby="circuit-analysis-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <AnalysisModalHeader>
              <div>
                <AnalysisModalTitle id="circuit-analysis-title">
                  Circuit Review
                </AnalysisModalTitle>
                <PanelHelperText>
                  Review issues, warnings, suggestions, and generated wiring steps for the current project.
                </PanelHelperText>
              </div>

              <ModalCloseButton
                type="button"
                aria-label="Close analysis"
                onClick={closeAnalysisModal}
              >
                ×
              </ModalCloseButton>
            </AnalysisModalHeader>

            <AnalysisModalTabs>
              <AnalysisModalTab
                type="button"
                $active={analysisModalTab === 'analysis'}
                onClick={() => setAnalysisModalTab('analysis')}
              >
                Analysis
              </AnalysisModalTab>

              <AnalysisModalTab
                type="button"
                $active={analysisModalTab === 'steps'}
                onClick={() => {
                  if (wiringSteps.length === 0) {
                    setWiringSteps(generate_wiring_steps(buildProjectSnapshot()));
                  }

                  setAnalysisModalTab('steps');
                }}
              >
                Build Instructions
              </AnalysisModalTab>
            </AnalysisModalTabs>

            <AnalysisModalBody>
              {analysisModalTab === 'analysis' ? (
                <>
                  <AnalysisSummaryGrid>
                    <AnalysisSummaryCard>
                      <AnalysisSummaryCount>{analysisSummary.pass}</AnalysisSummaryCount>
                      <AnalysisSummaryLabel>Passed</AnalysisSummaryLabel>
                    </AnalysisSummaryCard>
                    <AnalysisSummaryCard>
                      <AnalysisSummaryCount>{analysisSummary.warning}</AnalysisSummaryCount>
                      <AnalysisSummaryLabel>Warnings</AnalysisSummaryLabel>
                    </AnalysisSummaryCard>
                    <AnalysisSummaryCard>
                      <AnalysisSummaryCount>{analysisSummary.error}</AnalysisSummaryCount>
                      <AnalysisSummaryLabel>Issues</AnalysisSummaryLabel>
                    </AnalysisSummaryCard>
                  </AnalysisSummaryGrid>

                  {analysisResults.length === 0 ? (
                    <EmptyStateText>No analysis results yet</EmptyStateText>
                  ) : (
                    ['error', 'warning', 'suggestion', 'info', 'pass'].map((severity) => {
                      const group = analysisResultsBySeverity[severity];

                      if (!group || group.length === 0) {
                        return null;
                      }

                      return (
                        <AnalysisGroup key={`analysis-modal-group-${severity}`}>
                          <AnalysisGroupTitle>
                            {severity === 'error' ? 'Errors' :
                              severity === 'warning' ? 'Warnings' :
                              severity === 'suggestion' ? 'Suggestions' :
                              severity === 'info' ? 'Info' :
                              'Passed'}
                          </AnalysisGroupTitle>

                          <AnalysisResultList>
                            {group.map((result) => (
                              <AnalysisResultButton
                                key={result.id}
                                type="button"
                                $severity={result.severity}
                                $active={selectedAnalysisResultId === result.id}
                                onClick={() => handleSelectAnalysisResult(result)}
                              >
                                <AnalysisResultHeader>
                                  <AnalysisResultTitle>{result.title}</AnalysisResultTitle>
                                  <AnalysisResultBadge $severity={result.severity}>
                                    {result.severity}
                                  </AnalysisResultBadge>
                                </AnalysisResultHeader>

                                <AnalysisResultMeta>
                                  {result.category} • {result.confidence} confidence
                                </AnalysisResultMeta>

                                <AnalysisResultMessage>
                                  {result.message}
                                </AnalysisResultMessage>
                              </AnalysisResultButton>
                            ))}
                          </AnalysisResultList>
                        </AnalysisGroup>
                      );
                    })
                  )}
                </>
              ) : (
                <>
                  {wiringSteps.length === 0 ? (
                    <EmptyStateText>No saved connections to describe yet</EmptyStateText>
                  ) : (
                    <WiringStepsList>
                      {wiringSteps.map((step) => (
                        <WiringStepButton
                          key={step.id}
                          type="button"
                          $active={selectedAnalysisResultId === `step:${step.id}`}
                          onClick={() => handleSelectWiringStep(step)}
                        >
                          <WiringStepNumber>{step.order}</WiringStepNumber>
                          <WiringStepText>{step.text}</WiringStepText>
                        </WiringStepButton>
                      ))}
                    </WiringStepsList>
                  )}
                </>
              )}
            </AnalysisModalBody>
          </AnalysisModalContent>
        </AnalysisModalOverlay>
      </ProjectBuilderShell>
      {!isEmbedMode && (
        <Copyright>
          <strong>&copy; 2026 micropinouts.com</strong><br />
          All rights reserved.<br />
          Educational use and community sharing encouraged under applicable open-source licenses.
        </Copyright>
      )}
    </ProjectBuilderContent>
  );
};

export default ProjectBuilder;
